// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseUrl: `${string}://${string}` | (string & {});
};

/**
 * AccessPermission
 *
 * Types of access rights.
 *
 * IMPORTANT: The order of the permissions can never change (e.g. read needs to stay 1), otherwise we shift around the
 * permissions.
 */
export enum AccessPermission {
  _0 = 0,
  _1 = 1,
  _2 = 2,
  _4 = 4,
}

/**
 * AccessVisibility
 *
 * Types of access rights.
 *
 * IMPORTANT: The order of the permissions can never change (e.g. read needs to stay 1), otherwise we shift around the
 * permissions.
 */
export enum AccessVisibility {
  VISIBLE = 'visible',
  PRIVATE = 'private',
}

/**
 * ActionTriggerMode
 *
 * The verdict rule an :class:`ActionTrigger` applies, independent of what it watches.
 *
 * ``PlaybookTriggerType`` says what a trigger is attached to (an option, an evaluation's findings,
 * a group's members); this says what verdict it reaches over them. The two are orthogonal, which is
 * why the same modes serve both :class:`FindingThresholdTrigger` and :class:`GroupTrigger`.
 *
 * The count each mode reasons over differs per trigger kind: findings at/above ``min_severity``
 * for a finding-threshold trigger, member evaluations at/above ``min_risk_level`` for a group
 * trigger. The modes themselves mean the same thing on both.
 */
export enum ActionTriggerMode {
  AT_LEAST = 'at_least',
  NONE_FOUND = 'none_found',
  LESS_THAN = 'less_than',
  ALWAYS = 'always',
}

/**
 * ActiveOrganisationInDB
 *
 * The caller's active organisation, plus what its signing UI needs: the providers it can sign
 * with right now, preferred first (the first is what a send without a provider uses unless
 * ``default_signing_provider`` is among them).
 */
export type ActiveOrganisationInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Identifier
   */
  identifier: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Allowed Insights Credits
   */
  allowed_insights_credits: number;
  allowed_insights_credits_period: BillingPeriod;
  /**
   * Billing Start Date
   */
  billing_start_date: string | null;
  /**
   * Features
   */
  features: Array<OrgFeature>;
  default_signing_provider?: SigningProvider | null;
  /**
   * Signing Expiration Days
   */
  signing_expiration_days: number;
  /**
   * Is Admin
   */
  is_admin: boolean;
  /**
   * Available Signing Providers
   */
  available_signing_providers?: Array<SigningProvider>;
};

/**
 * ActiveOrganisationUpdate
 *
 * Body of the org-admin ``PUT /active-organisation``: what an organisation's own admins may change.
 * Deliberately narrow — credits and billing stay with ``OrganisationUpdate`` (platform admins).
 */
export type ActiveOrganisationUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Signing Expiration Days
   */
  signing_expiration_days?: number | null;
  default_signing_provider?: SigningProvider | null;
};

/**
 * ActualAmountsAndShares
 */
export type ActualAmountsAndShares = {
  /**
   * Pro Rata Amount
   */
  pro_rata_amount?: string | null;
  /**
   * Pro Rata Shares
   */
  pro_rata_shares?: number | null;
  /**
   * Loan Amount
   */
  loan_amount?: string | null;
  /**
   * Loan Shares
   */
  loan_shares?: number | null;
  /**
   * Investment Amount
   */
  investment_amount?: string | null;
  /**
   * Investment Shares
   */
  investment_shares?: number | null;
};

/**
 * AgentApiRequestResponse
 *
 * Response model for agent API requests.
 *
 * This model provides detailed information about the result of processing
 * an agent API request, including success status, user messages, and
 * evaluation details when applicable.
 */
export type AgentApiRequestResponse = {
  /**
   * The status of the request processing. Possible values: 'success', 'error', 'pending'
   */
  status: TaskStatus;
  /**
   * Message
   *
   * Human-readable message describing the result of the request processing
   */
  message: string;
  /**
   * Evaluation Uid
   *
   * Unique identifier for the evaluation that was created. Only present when a pipelinewas successfully initiated and prompt collections are configured.
   */
  evaluation_uid?: string | null;
  /**
   * Evaluation Url
   *
   * Direct URL to view the evaluation results in the insights interface. Only presentwhen evaluation_uid is available.
   */
  evaluation_url?: string | null;
};

/**
 * AgreementState
 */
export type AgreementState = {
  /**
   * Value
   */
  value: string;
  /**
   * Title
   */
  title: string;
};

/**
 * AllUsableFilesSelection
 *
 * Target every usable file currently in the data room.
 */
export type AllUsableFilesSelection = {
  /**
   * Selection Type
   */
  selection_type?: 'all_usable';
};

/**
 * AllowListCreate
 *
 * Schema for creating a new allow list entry.
 *
 * Args:
 * email: Email of the allow list entry
 * domain: Domain of the allow list entry
 * is_user: Whether the allow list entry is a user
 * is_domain: Whether the allow list entry is a domain
 * owner_intake_agent_id: ID of the intake agent
 */
export type AllowListCreate = {
  /**
   * Email
   */
  email: string;
  /**
   * Domain
   */
  domain: string;
  /**
   * Is User
   */
  is_user: boolean;
  /**
   * Is Domain
   */
  is_domain: boolean;
  /**
   * Owner Intake Agent Id
   */
  owner_intake_agent_id?: number | null;
};

/**
 * AllowListInDB
 *
 * Slim schema for allow list entries stored in database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * email: Email of the allow list entry
 * domain: Domain of the allow list entry
 * is_user: Whether the allow list entry is a user
 * is_domain: Whether the allow list entry is a domain
 * owner_intake_agent_id: ID of the intake agent
 */
export type AllowListInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Domain
   */
  domain: string;
  /**
   * Is User
   */
  is_user: boolean;
  /**
   * Is Domain
   */
  is_domain: boolean;
  /**
   * Owner Intake Agent Id
   */
  owner_intake_agent_id: number;
};

/**
 * AllowListSearchResult
 */
export type AllowListSearchResult = {
  /**
   * Results
   */
  results: Array<AllowListInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * AllowListUpdate
 *
 * Schema for updating an existing allow list entry.
 *
 * Args:
 * email: New email of the allow list entry
 * domain: New domain of the allow list entry
 * is_user: New is_user of the allow list entry
 * is_domain: New is_domain of the allow list entry
 */
export type AllowListUpdate = {
  /**
   * Email
   */
  email?: string | null;
  /**
   * Domain
   */
  domain?: string | null;
  /**
   * Is User
   */
  is_user?: boolean | null;
  /**
   * Is Domain
   */
  is_domain?: boolean | null;
};

/**
 * AndCriterion
 */
export type AndCriterionInput = {
  /**
   * Criterion Type
   */
  criterion_type?: 'and';
  /**
   * Criteria
   */
  criteria: Array<CriterionInput>;
};

/**
 * AndCriterion
 */
export type AndCriterionOutput = {
  /**
   * Criterion Type
   */
  criterion_type?: 'and';
  /**
   * Criteria
   */
  criteria: Array<CriterionOutput>;
};

/**
 * AnnotationCoordinate
 */
export type AnnotationCoordinate = {
  scope: AnnotationScope;
  /**
   * Paragraph Index
   */
  paragraph_index: number;
  /**
   * Run Index
   */
  run_index: number;
  /**
   * Offset In Run
   */
  offset_in_run: number;
  /**
   * Snippet Before
   */
  snippet_before?: string;
  /**
   * Snippet After
   */
  snippet_after?: string;
};

/**
 * AnnotationEntityBinding
 *
 * Bind a sub-field of a named entity instance.
 *
 * Multiple annotations sharing `(entity_type, instance_alias)` reference the
 * same entity. The materializer emits `{{ <entity_type>_<instance_alias>.<sub_field> }}`,
 * matching the existing schema-extractor's name-based entity-type inference
 * (e.g. `natural_person_seller` → NATURAL_PERSON).
 */
export type AnnotationEntityBinding = {
  /**
   * Kind
   */
  kind?: 'entity';
  /**
   * Entity Type
   */
  entity_type: 'legal_entity' | 'natural_person';
  /**
   * Instance Alias
   */
  instance_alias: string;
  /**
   * Sub Field
   */
  sub_field: string;
};

/**
 * AnnotationFilter
 */
export type AnnotationFilter = {
  /**
   * Name
   */
  name: string;
  /**
   * Args
   */
  args?: Array<string>;
};

/**
 * AnnotationInstance
 *
 * Aggregated view of an entity/object alias used across annotations.
 */
export type AnnotationInstance = {
  /**
   * Kind
   */
  kind: 'entity' | 'object';
  /**
   * Entity Type
   */
  entity_type?: 'legal_entity' | 'natural_person' | null;
  /**
   * Alias
   */
  alias: string;
  /**
   * Used Sub Fields
   */
  used_sub_fields: Array<string>;
};

/**
 * AnnotationObjectBinding
 *
 * Bind a sub-field of a generic named object — emits `{{ <alias>.<sub_field> }}`.
 */
export type AnnotationObjectBinding = {
  /**
   * Kind
   */
  kind?: 'object';
  /**
   * Instance Alias
   */
  instance_alias: string;
  /**
   * Sub Field
   */
  sub_field: string;
};

/**
 * AnnotationOriginSignature
 *
 * Identifies an annotation's original position in the source .docx.
 *
 * Set only on annotations that came from `TagDiscoverer` parsing existing
 * `{{ }}` / `{% %}` syntax. The materializer uses (paragraph_offset,
 * original_text) to locate the original tag span and replace or remove it
 * when the user edits or deletes the annotation.
 */
export type AnnotationOriginSignature = {
  /**
   * Paragraph Offset
   */
  paragraph_offset: number;
  /**
   * Original Text
   */
  original_text: string;
};

/**
 * AnnotationPrimitiveBinding
 *
 * Explicit primitive binding. Equivalent to `binding=None` — kept so callers
 * that always send a discriminator have a non-null option.
 */
export type AnnotationPrimitiveBinding = {
  /**
   * Kind
   */
  kind?: 'primitive';
};

/**
 * AnnotationScope
 *
 * Where in a docx an annotation lives.
 *
 * `kind=body` is the document body. `header`/`footer` carry an `index` that
 * enumerates the part as iterated through `doc.part.rels`. `cell_path` is a
 * list of (table_index, row_index, cell_index) tuples — its length captures
 * nested-table depth, so an annotation inside a cell of a table nested within
 * a cell carries two entries.
 */
export type AnnotationScope = {
  /**
   * Kind
   */
  kind?: 'body' | 'header' | 'footer';
  /**
   * Index
   */
  index?: number;
  /**
   * Cell Path
   */
  cell_path?: Array<[number, number, number]>;
};

/**
 * AnnotationsResponse
 */
export type AnnotationsResponse = {
  /**
   * Annotations
   */
  annotations: Array<TemplateAnnotationWithPath>;
  /**
   * Instances
   */
  instances?: Array<AnnotationInstance>;
};

/**
 * AnnotationsUpdateRequest
 *
 * One save for the merged tag + guidance editor.
 *
 * ``field_metadata`` is keyed by the paths the document will have **after** this save — the
 * same ``schema_path`` values the response reports. It rides along with the tag write because
 * the two used to be impossible to save in sequence: rewriting the .docx nulls
 * ``extracted_schema`` and flips the template to running, and ``PUT /fields`` answers 409
 * until the worker lands the new schema.
 */
export type AnnotationsUpdateRequest = {
  /**
   * Annotations
   */
  annotations: Array<TemplateAnnotation>;
  /**
   * Field Metadata
   */
  field_metadata?: Array<ContractGenerationTemplateFieldUpdate> | null;
};

/**
 * ApplicationMessage
 *
 * Schema for generic application messages.
 *
 * A simple schema that wraps a text message to be returned to the client.
 * Commonly used for success messages or generic responses where details
 * should be abstracted.
 *
 * Attributes:
 * message: The text message to be returned to the client
 */
export type ApplicationMessage = {
  /**
   * Message
   */
  message: string;
};

/**
 * ArchiveSkipReason
 *
 * Why an archive found while unpacking was left packed.
 */
export enum ArchiveSkipReason {
  MAX_DEPTH_REACHED = 'max_depth_reached',
  INVALID_ARCHIVE = 'invalid_archive',
  ENCRYPTED = 'encrypted',
  TOO_LARGE = 'too_large',
}

/**
 * BillingPeriod
 */
export enum BillingPeriod {
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
}

/**
 * Body_add_column_v1_app_contract_management_views__view_uid__add_column_post
 */
export type BodyAddColumnV1AppContractManagementViewsViewUidAddColumnPost = {
  column_create_schema: ContractManagementViewColumnCreate;
  question_create_schema: ContractManagementViewColumnQuestionCreate;
};

/**
 * Body_create_organisation_v1_app_account_organisation__post
 */
export type BodyCreateOrganisationV1AppAccountOrganisationPost = {
  organisation_in: OrganisationCreate;
  model_settings: OrganisationModelSettingsCreate;
  member_access?: OrganisationMemberAccess | null;
};

/**
 * Body_expose_tool_confirmation_schemas_v1_app_technical_websocket_tool_confirmation_schemas_post
 */
export type BodyExposeToolConfirmationSchemasV1AppTechnicalWebsocketToolConfirmationSchemasPost = {
  interrupt: ToolConfirmationInterrupt;
  answer: ToolConfirmationAnswer;
};

/**
 * Body_expose_user_question_schemas_v1_app_technical_websocket_user_question_schemas_post
 */
export type BodyExposeUserQuestionSchemasV1AppTechnicalWebsocketUserQuestionSchemasPost = {
  interrupt: UserQuestionInterrupt;
  answer: UserQuestionAnswer;
};

/**
 * Body_preview_with_sample_data_v1_app_contract_generation_template_authoring_preview_sample_post
 */
export type BodyPreviewWithSampleDataV1AppContractGenerationTemplateAuthoringPreviewSamplePost = {
  /**
   * File
   */
  file: Blob | File;
};

/**
 * Body_replace_template_variant_file_v1_app_contract_generation_template__template_uid__variant__variant_uid__file_put
 */
export type BodyReplaceTemplateVariantFileV1AppContractGenerationTemplateTemplateUidVariantVariantUidFilePut =
  {
    /**
     * File
     */
    file: Blob | File;
  };

/**
 * Body_upload_data_room_file_chunk_v1_app_review_project__external_uid__external_access_data_room_file_chunk__upload_id__post
 */
export type BodyUploadDataRoomFileChunkV1AppReviewProjectExternalUidExternalAccessDataRoomFileChunkUploadIdPost =
  {
    /**
     * Chunk
     */
    chunk: Blob | File;
  };

/**
 * Body_upload_vault_file_chunk_v1_app_repository_vault_file_chunk__upload_id__post
 */
export type BodyUploadVaultFileChunkV1AppRepositoryVaultFileChunkUploadIdPost = {
  /**
   * Chunk
   */
  chunk: Blob | File;
};

/**
 * Body_validate_template_file_v1_app_contract_generation_template_authoring_validate_post
 */
export type BodyValidateTemplateFileV1AppContractGenerationTemplateAuthoringValidatePost = {
  /**
   * File
   */
  file: Blob | File;
};

export type BoolCriterion = EqualsCriterion;

/**
 * CalendarEvent
 */
export type CalendarEvent = {
  /**
   * Recurrence Type
   *
   * How the date repeats: 'never' (a single date), 'count' (a fixed number of occurrences, recurrence_count), 'until_date' (until recurrence_until_date) or 'infinite' (without end).
   */
  recurrence_type: 'count' | 'until_date' | 'infinite' | 'never';
  /**
   * Recurrence Count
   *
   * Sets the total number of occurrences for the recurring date
   */
  recurrence_count?: number | null;
  /**
   * Recurrence Until Date
   *
   * The date until which the date repeats. It MUST be in ISO format.
   */
  recurrence_until_date?: string | null;
  /**
   * Recurrence Frequency
   *
   * In what interval the date repeats (e.g. every day, week or month.)
   */
  recurrence_frequency?: 'day' | 'week' | 'month' | 'quarter' | 'year' | null;
  /**
   * Recurrence Interval
   *
   * Specifies the interval between each occurrence (e.g., INTERVAL=2 for every two weeks).
   */
  recurrence_interval?: number | null;
  /**
   * Title
   *
   * Optional short name of the event, e.g. 'Termination notice deadline'.
   */
  title?: string | null;
  /**
   * Event Date
   *
   * The date the event is happening. If it repeats the first occurrence.
   */
  event_date: string;
};

/**
 * CalendarEventCriterion
 */
export type CalendarEventCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'calendar_event';
  event_date?: TimeCriterion | null;
  recurrence_type?: StringCriterion | null;
  recurrence_count?: NumberCriterion | null;
  recurrence_until_date?: TimeCriterion | null;
  recurrence_frequency?: StringCriterion | null;
  recurrence_interval?: NumberCriterion | null;
};

/**
 * CalendarPreviewQuery
 *
 * Query for previewing a calendar's resolved (contract file, extraction) pairs.
 *
 * Subclasses ``FilterableSearchQuery`` for ``text``/``limit``/``offset``/``sort_key``/
 * ``sort_direction``; ``column_filters`` is inherited but unused for now. ``sort_key``
 * accepts ``"date"`` (the earliest upcoming extracted date) or ``"name"`` (the parent
 * contract's name).
 */
export type CalendarPreviewQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: 'name' | 'date';
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
  /**
   * Only Value Extracted
   */
  only_value_extracted?: boolean;
  /**
   * Only Future
   */
  only_future?: boolean;
};

/**
 * CalendarPreviewResult
 */
export type CalendarPreviewResult = {
  /**
   * Results
   */
  results: Array<
    [
      ContractManagementContractFileInDbExtended,
      ContractManagementExtractionInDb | null,
      Array<ContractManagementDateInDb>,
    ]
  >;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * CalendarRangeQuery
 *
 * Query for the calendar's files with an occurrence (extracted or fixed date, recurrence
 * expanded) in ``[start, end)`` — the month view. Unpaginated, so the span is capped.
 */
export type CalendarRangeQuery = {
  /**
   * Start
   */
  start: string;
  /**
   * End
   */
  end: string;
  /**
   * Text
   */
  text?: string | null;
  /**
   * Only Future
   */
  only_future?: boolean;
};

/**
 * CapTableExport
 */
export type CapTableExport = {
  /**
   * Full Company Name
   */
  full_company_name: string;
  /**
   * Cap Table
   */
  cap_table: Array<
    | PythonServicesApisGatewayV1EndpointsFunctionsExportSchemasExportCapTableRowStakeholder
    | PythonServicesApisGatewayV1EndpointsFunctionsExportSchemasExportCapTableRowEsopPool
  >;
  /**
   * Share Classes
   */
  share_classes: Array<PythonServicesApisGatewayV1EndpointsFunctionsExportSchemasExportShareClass>;
};

/**
 * CapTableExportResponse
 */
export type CapTableExportResponse = {
  /**
   * File
   */
  file: string;
};

/**
 * CapTableRowEsopPool
 */
export type CapTableRowEsopPoolOutput = {
  /**
   * Id
   */
  id: string;
  type: CapTableRowTypeEnum;
  /**
   * Number Of Shares
   */
  number_of_shares: string;
  /**
   * Share Class Id
   */
  share_class_id: string;
  /**
   * Tag
   */
  tag?: string | null;
  /**
   * Esop Pool Id
   */
  esop_pool_id: string;
  esop_pool: EsopPool;
};

/**
 * CapTableRowStakeholder
 */
export type CapTableRowStakeholderOutput = {
  /**
   * Id
   */
  id: string;
  type: CapTableRowTypeEnum;
  /**
   * Number Of Shares
   */
  number_of_shares: number;
  /**
   * Number Of Burdened Shares
   */
  number_of_burdened_shares?: number | null;
  /**
   * Share Class Id
   */
  share_class_id: string;
  /**
   * Tag
   */
  tag?: string | null;
  /**
   * Contractual Party Id
   */
  contractual_party_id: string;
  contractual_party: ContractualParty;
};

/**
 * CapTableRowTypeEnum
 */
export enum CapTableRowTypeEnum {
  CAP_TABLE_ROW_STAKEHOLDER = 'cap_table_row_stakeholder',
  CAP_TABLE_ROW_WARRANT = 'cap_table_row_warrant',
  CAP_TABLE_ROW_ESOP_POOL = 'cap_table_row_esop_pool',
  CAP_TABLE_ROW_TREASURY = 'cap_table_row_treasury',
}

/**
 * ChangelogCreate
 */
export type ChangelogCreate = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Html
   */
  html: string;
};

/**
 * ChangelogInDB
 *
 * Read view for all platform users — author FKs are intentionally not exposed.
 */
export type ChangelogInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Html
   */
  html: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
};

/**
 * ChangelogListResult
 */
export type ChangelogListResult = {
  /**
   * Results
   */
  results: Array<ChangelogInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ChangelogUpdate
 */
export type ChangelogUpdate = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Html
   */
  html?: string | null;
};

/**
 * ChatAttentionReason
 *
 * Why a chat thread awaits its owner (the ``ChatThreadAttention`` item's reason).
 *
 * Set by the chat-run worker when a run needs input (``INTERRUPT``) or settled
 * (``DONE`` / ``FAILED``); cleared when the owner opens the thread, acknowledges the
 * item or starts the next run on it.
 */
export enum ChatAttentionReason {
  INTERRUPT = 'interrupt',
  DONE = 'done',
  FAILED = 'failed',
}

/**
 * ChatBackend
 *
 * Discriminator for the three chat backends, each with its own thread/checkpoint tables.
 */
export enum ChatBackend {
  GENERAL = 'general',
  TEMPLATING = 'templating',
  REVIEW_PROJECT = 'review_project',
}

/**
 * ChatClientSurface
 *
 * Which client a general-chat message came from — decides live-document tool availability.
 *
 * The document open in Word lives client-side and is read over a ``document_tool`` interrupt
 * the add-in answers with Office.js. The web app cannot answer one and a run bound to those
 * tools would hang forever, so this is a capability declaration rather than a cosmetic hint:
 * ``WORD_ADDIN`` means "I will answer ``document_tool`` interrupts". Sent per message (and per
 * resume, since the tools are rebuilt then), never persisted.
 */
export enum ChatClientSurface {
  WEB_APP = 'web_app',
  WORD_ADDIN = 'word_addin',
}

/**
 * ChatConfirmationMode
 *
 * Per-message policy for which chat tools pause on a user confirmation.
 *
 * Not persisted: the client sends it with every stream/resume request, and it is
 * snapshotted at run start, so it holds for the whole run. A resume must carry the
 * same mode as the turn that raised the interrupt — a laxer mode would rebuild the
 * interrupted tool without confirmation and drop the pending answer. The mode maps
 * to tool *effects* via ``python_services.core.llm.chat._confirmation``.
 */
export enum ChatConfirmationMode {
  READ = 'read',
  AUTO = 'auto',
}

/**
 * ChatMessageResponse
 *
 * Serialized message from LangGraph checkpointer state.
 */
export type ChatMessageResponse = {
  /**
   * Role
   */
  role: string;
  /**
   * Content
   */
  content: string;
  /**
   * Tool Calls
   */
  tool_calls?: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Tool Call Id
   */
  tool_call_id?: string | null;
  /**
   * Name
   */
  name?: string | null;
};

/**
 * ChatResumeDecision
 */
export type ChatResumeDecision = {
  /**
   * Type
   */
  type: string;
  /**
   * Edited Action
   */
  edited_action?: {
    [key: string]: unknown;
  } | null;
  /**
   * Message
   */
  message?: string | null;
};

/**
 * ChatRunInfo
 *
 * Public run state, embedded in ``ChatThreadWithMessages.active_run``.
 */
export type ChatRunInfo = {
  /**
   * Run Id
   */
  run_id: string;
  status: TaskStatus;
  /**
   * Created At
   */
  created_at: string;
};

/**
 * ChatStatistics
 */
export type ChatStatistics = {
  /**
   * Total Threads
   */
  total_threads: number;
  /**
   * Average User Messages Per Thread
   */
  average_user_messages_per_thread: number;
};

/**
 * ChatStatusUpdate
 *
 * One live status frame for the chat UI — fire-and-forget, never persisted, never seen by the model.
 *
 * A ``group_start``/``group_end`` pair brackets one tool invocation (or one synthetic model
 * phase); ``detail`` frames are sub-statuses inside the group named by ``group_id``. A run
 * terminator (interrupt/done/error) implicitly closes all open groups — the backend never
 * emits closing frames at run end (an interrupted tool's group is intentionally left open;
 * its resume re-run opens a fresh group).
 */
export type ChatStatusUpdate = {
  /**
   * Type
   */
  type?: 'status_update';
  /**
   * Kind
   */
  kind: 'group_start' | 'group_end' | 'detail';
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Label
   */
  label?: string | null;
};

/**
 * ChatThreadAttentionInDB
 */
export type ChatThreadAttentionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  organisation: UserAttentionOrganisation | null;
  /**
   * Type
   */
  type?: 'chat_thread';
  reason: ChatAttentionReason;
  /**
   * Thread Uid
   */
  thread_uid: string;
  backend: ChatBackend;
  /**
   * Title
   */
  title?: string | null;
};

/**
 * ChatThreadInDB
 */
export type ChatThreadInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Title
   */
  title: string | null;
  /**
   * Thread Metadata
   */
  thread_metadata: {
    [key: string]: unknown;
  };
  /**
   * Is Archived
   */
  is_archived: boolean;
  /**
   * Archived At
   */
  archived_at: string | null;
  /**
   * Is Public
   */
  is_public?: boolean;
  /**
   * Human Message Count
   */
  human_message_count: number;
  llm_provider: LlmProvider;
  /**
   * Llm Model
   */
  llm_model: string;
};

/**
 * ChatThreadSearchResult
 */
export type ChatThreadSearchResult = {
  /**
   * Results
   */
  results: Array<ChatThreadInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ChatThreadUpdate
 *
 * Schema for updating an existing chat thread.
 */
export type ChatThreadUpdate = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Thread Metadata
   */
  thread_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Is Archived
   */
  is_archived?: boolean | null;
  /**
   * Is Public
   */
  is_public?: boolean | null;
  /**
   * Human Message Count
   */
  human_message_count?: number | null;
};

/**
 * ChatThreadWithMessages
 *
 * Thread metadata with paginated message history.
 */
export type ChatThreadWithMessages = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Title
   */
  title: string | null;
  /**
   * Thread Metadata
   */
  thread_metadata: {
    [key: string]: unknown;
  };
  /**
   * Is Archived
   */
  is_archived: boolean;
  /**
   * Archived At
   */
  archived_at: string | null;
  /**
   * Is Public
   */
  is_public?: boolean;
  /**
   * Human Message Count
   */
  human_message_count: number;
  llm_provider: LlmProvider;
  /**
   * Llm Model
   */
  llm_model: string;
  /**
   * Messages
   */
  messages: Array<ChatMessageResponse>;
  /**
   * Total Count
   */
  total_count: number;
  /**
   * Pending Interrupt
   */
  pending_interrupt?: {
    [key: string]: unknown;
  } | null;
  active_run?: ChatRunInfo | null;
};

/**
 * ChatToken
 */
export type ChatToken = {
  /**
   * Type
   */
  type?: 'token';
  /**
   * Content
   */
  content: string;
};

/**
 * CheckboxFieldCreate
 */
export type CheckboxFieldCreate = {
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'checkbox';
  /**
   * Size
   */
  size?: number;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Name
   */
  name?: string | null;
};

/**
 * ChunkingConfiguration
 */
export type ChunkingConfiguration = {
  /**
   * Type
   *
   * In max mode, the LLM context will be maxed out. If in fixed or auto mode, n pages will be used as chunks.
   */
  type?: 'fixed' | 'auto' | 'max';
  /**
   * Page Count
   *
   * If type is fixed, the chunk is $page_count pages long. If type is auto, a chunk of size $page_count is tried, and if it not fits, the chunk size is decreased.
   */
  page_count?: number;
  /**
   * Overlap Percent
   *
   * If type is max, overlap_percent (relative to chunk size) will be added at the start and end of every chunk.
   */
  overlap_percent?: number;
  /**
   * Overlap Pages
   *
   * If type is fixed or auto, $overlap_pages will be added at the start and end of every chunk.
   */
  overlap_pages?: number;
};

/**
 * ClassificationEvaluationInDB
 */
export type ClassificationEvaluationInDb = {
  /**
   * Evaluation Type
   */
  evaluation_type?: 'classification';
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Extraction Prompt
   */
  extraction_prompt: string;
  /**
   * Multiple Choices
   */
  multiple_choices: boolean;
  /**
   * Options
   */
  options?: Array<ClassificationOptionInDb>;
};

/**
 * ClassificationEvaluationResultInDB
 */
export type ClassificationEvaluationResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Reasoning
   */
  reasoning?: string | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  /**
   * Page Number
   */
  page_number?: number | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Evaluation Uid
   */
  evaluation_uid: string;
  /**
   * Matched Option Uids
   */
  matched_option_uids?: Array<string>;
  /**
   * Triggered By Action Result Uid
   */
  triggered_by_action_result_uid?: string | null;
  /**
   * Action Results
   */
  action_results?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionResultInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionResultInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionResultInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionResultInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionResultInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionResultInDb)
  >;
  /**
   * Evaluation Type
   */
  evaluation_type?: 'classification';
};

/**
 * ClassificationOptionInDB
 */
export type ClassificationOptionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Definition
   */
  definition: string;
  /**
   * Color
   */
  color?: string | null;
  risk_level?: PlaybookRiskLevel | null;
  /**
   * Trigger Uid
   */
  trigger_uid?: string | null;
  /**
   * Actions
   */
  actions?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb)
  >;
  /**
   * Evaluation Type
   */
  evaluation_type?: 'classification';
};

/**
 * ClauseCategory
 *
 * Categories of contract clauses.
 */
export enum ClauseCategory {
  CONFIDENTIALITY = 'Confidentiality',
  TERMINATION = 'Termination',
  PAYMENT = 'Payment',
  DELIVERY = 'Delivery',
  INTELLECTUAL_PROPERTY = 'Intellectual Property',
  LIABILITY = 'Liability',
  FORCE_MAJEURE = 'Force Majeure',
  GOVERNING_LAW = 'Governing Law',
  JURISDICTION = 'Jurisdiction',
  AMENDMENT = 'Amendment',
  ASSIGNMENT = 'Assignment',
  SEVERABILITY = 'Severability',
  NOTICES = 'Notices',
  SURVIVAL = 'Survival',
  DISPUTE_RESOLUTION = 'Dispute Resolution',
  OTHER = 'Other',
}

/**
 * ClauseCreate
 *
 * Schema for creating a new clause.
 *
 * Args:
 * name: Name of the    clause
 * jurisdiction: Jurisdiction of the clause
 * language: Language of the clause
 * content: Content of the clause
 * source: Source of the clause
 * gaia_template: Whether the clause is a GAIA template
 */
export type ClauseCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Jurisdiction
   */
  jurisdiction: string;
  /**
   * Contract Type Uids
   */
  contract_type_uids?: Array<string>;
  clause_category: ClauseCategory;
  /**
   * Language
   */
  language: string;
  /**
   * Content
   */
  content: string;
  /**
   * Source
   */
  source?: string | null;
};

/**
 * ClauseInDB
 */
export type ClauseInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Jurisdiction
   */
  jurisdiction: string;
  /**
   * Language
   */
  language: string;
  /**
   * Contract Types
   */
  contract_types?: Array<ContractTypeRef>;
  clause_category?: ClauseCategory | null;
  /**
   * Content
   */
  content: string;
  /**
   * Source
   */
  source?: string | null;
};

/**
 * ClauseSearchQuery
 */
export type ClauseSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
};

/**
 * ClauseSearchResult
 */
export type ClauseSearchResult = {
  /**
   * Results
   */
  results: Array<ClauseInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ClauseSlim
 */
export type ClauseSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name: string;
  /**
   * Jurisdiction
   */
  jurisdiction: string;
  /**
   * Language
   */
  language: string;
  /**
   * Contract Types
   */
  contract_types?: Array<ContractTypeRef>;
  clause_category?: ClauseCategory | null;
  /**
   * Content
   */
  content: string;
};

/**
 * ClauseUpdate
 *
 * Schema for updating an existing clause.
 *
 * Args:
 * name: New name of the clause
 * jurisdiction: New jurisdiction of the clause
 * language: New language of the clause
 * content: New content of the clause
 * source: New source of the clause
 */
export type ClauseUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Jurisdiction
   */
  jurisdiction?: string | null;
  /**
   * Language
   */
  language?: string | null;
  /**
   * Contract Type Uids
   */
  contract_type_uids?: Array<string> | null;
  clause_category?: ClauseCategory | null;
  /**
   * Content
   */
  content?: string | null;
  /**
   * Source
   */
  source?: string | null;
};

/**
 * ClauseValidationEvaluationInDB
 */
export type ClauseValidationEvaluationInDb = {
  /**
   * Evaluation Type
   */
  evaluation_type?: 'clause_validation';
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Extraction Prompt
   */
  extraction_prompt: string;
  missing_clause_tier?: ClauseValidationOptionType;
  /**
   * Options
   */
  options?: Array<ClauseValidationOptionInDb>;
};

/**
 * ClauseValidationEvaluationResultInDB
 */
export type ClauseValidationEvaluationResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Reasoning
   */
  reasoning?: string | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  /**
   * Page Number
   */
  page_number?: number | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Evaluation Uid
   */
  evaluation_uid: string;
  /**
   * Matched Option Uids
   */
  matched_option_uids?: Array<string>;
  /**
   * Triggered By Action Result Uid
   */
  triggered_by_action_result_uid?: string | null;
  /**
   * Action Results
   */
  action_results?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionResultInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionResultInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionResultInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionResultInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionResultInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionResultInDb)
  >;
  /**
   * Evaluation Type
   */
  evaluation_type?: 'clause_validation';
  /**
   * Matched Clause Id
   */
  matched_clause_id?: number | null;
};

/**
 * ClauseValidationOptionInDB
 */
export type ClauseValidationOptionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Definition
   */
  definition: string;
  /**
   * Color
   */
  color?: string | null;
  risk_level?: PlaybookRiskLevel | null;
  /**
   * Trigger Uid
   */
  trigger_uid?: string | null;
  /**
   * Actions
   */
  actions?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb)
  >;
  /**
   * Evaluation Type
   */
  evaluation_type?: 'clause_validation';
  option_type?: ClauseValidationOptionType | null;
  clause?: ClauseSlim | null;
};

/**
 * ClauseValidationOptionType
 *
 * Tier of a playbook option.
 */
export enum ClauseValidationOptionType {
  PREFERRED = 'preferred',
  FALLBACK = 'fallback',
  ESCALATION = 'escalation',
}

/**
 * CommentingOptions
 *
 * Options for controlling comment generation in DOCX files.
 */
export type CommentingOptions = {
  /**
   * Include Agents
   *
   * Which agents to include in export.
   */
  include_agents?: Array<CommentingOptionsIncludeAgents>;
  /**
   * Allowed Evaluation Answers
   *
   * List of evaluation answers to include. If empty, all evaluations are included
   */
  allowed_evaluation_answers?: Array<string>;
};

/**
 * CommentingOptionsIncludeAgents
 */
export enum CommentingOptionsIncludeAgents {
  EXTRACTION = 'extraction',
  REVIEW = 'review',
  SUGGESTION = 'suggestion',
}

/**
 * ConditionCompareNode
 *
 * Binary comparison (`==`, `!=`, `<`, `<=`, `>`, `>=`, `in`, `not in`).
 */
export type ConditionCompareNode = {
  /**
   * Kind
   */
  kind?: 'compare';
  /**
   * Op
   */
  op: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'in' | 'not in';
  /**
   * Left
   */
  left:
    | ({
        kind: 'path';
      } & ConditionPathNode)
    | ({
        kind: 'literal';
      } & ConditionLiteralNode)
    | ({
        kind: 'list';
      } & ConditionListNode)
    | ({
        kind: 'compare';
      } & ConditionCompareNode)
    | ({
        kind: 'logical';
      } & ConditionLogicalNode)
    | ({
        kind: 'not';
      } & ConditionNotNode);
  /**
   * Right
   */
  right:
    | ({
        kind: 'path';
      } & ConditionPathNode)
    | ({
        kind: 'literal';
      } & ConditionLiteralNode)
    | ({
        kind: 'list';
      } & ConditionListNode)
    | ({
        kind: 'compare';
      } & ConditionCompareNode)
    | ({
        kind: 'logical';
      } & ConditionLogicalNode)
    | ({
        kind: 'not';
      } & ConditionNotNode);
};

/**
 * ConditionListNode
 *
 * List of values, used as the RHS of `in` / `not in` comparisons.
 */
export type ConditionListNode = {
  /**
   * Kind
   */
  kind?: 'list';
  /**
   * Items
   */
  items?: Array<
    | ({
        kind: 'path';
      } & ConditionPathNode)
    | ({
        kind: 'literal';
      } & ConditionLiteralNode)
    | ({
        kind: 'list';
      } & ConditionListNode)
    | ({
        kind: 'compare';
      } & ConditionCompareNode)
    | ({
        kind: 'logical';
      } & ConditionLogicalNode)
    | ({
        kind: 'not';
      } & ConditionNotNode)
  >;
};

/**
 * ConditionLiteralNode
 *
 * Inline literal value (`"employment"`, `50000`, `true`, `null`).
 */
export type ConditionLiteralNode = {
  /**
   * Kind
   */
  kind?: 'literal';
  /**
   * Value
   */
  value?: string | number | number | boolean | null;
};

/**
 * ConditionLogicalNode
 *
 * Boolean combinator (`and` / `or`). Short-circuit semantics.
 */
export type ConditionLogicalNode = {
  /**
   * Kind
   */
  kind?: 'logical';
  /**
   * Op
   */
  op: 'and' | 'or';
  /**
   * Left
   */
  left:
    | ({
        kind: 'path';
      } & ConditionPathNode)
    | ({
        kind: 'literal';
      } & ConditionLiteralNode)
    | ({
        kind: 'list';
      } & ConditionListNode)
    | ({
        kind: 'compare';
      } & ConditionCompareNode)
    | ({
        kind: 'logical';
      } & ConditionLogicalNode)
    | ({
        kind: 'not';
      } & ConditionNotNode);
  /**
   * Right
   */
  right:
    | ({
        kind: 'path';
      } & ConditionPathNode)
    | ({
        kind: 'literal';
      } & ConditionLiteralNode)
    | ({
        kind: 'list';
      } & ConditionListNode)
    | ({
        kind: 'compare';
      } & ConditionCompareNode)
    | ({
        kind: 'logical';
      } & ConditionLogicalNode)
    | ({
        kind: 'not';
      } & ConditionNotNode);
};

/**
 * ConditionNotNode
 *
 * Logical negation of an inner expression.
 */
export type ConditionNotNode = {
  /**
   * Kind
   */
  kind?: 'not';
  /**
   * Operand
   */
  operand:
    | ({
        kind: 'path';
      } & ConditionPathNode)
    | ({
        kind: 'literal';
      } & ConditionLiteralNode)
    | ({
        kind: 'list';
      } & ConditionListNode)
    | ({
        kind: 'compare';
      } & ConditionCompareNode)
    | ({
        kind: 'logical';
      } & ConditionLogicalNode)
    | ({
        kind: 'not';
      } & ConditionNotNode);
};

/**
 * ConditionPathNode
 *
 * Reference to a form-data path, e.g. `contract_type` or `client.first_name`.
 *
 * The frontend resolves `path` against the current form values (with dot
 * traversal) to get the runtime value of this node.
 */
export type ConditionPathNode = {
  /**
   * Kind
   */
  kind?: 'path';
  /**
   * Path
   */
  path: string;
};

/**
 * ContainsCriterion
 */
export type ContainsCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'in';
  /**
   * Value
   */
  value: string;
};

/**
 * ContractBulkShareUser
 *
 * Schema for bulk sharing contracts with users and roles.
 */
export type ContractBulkShareUser = {
  /**
   * Contract Uids
   */
  contract_uids?: Array<string>;
  /**
   * User Uids
   */
  user_uids?: Array<string>;
  /**
   * Role Uids
   */
  role_uids?: Array<string>;
  permissions: AccessPermission;
};

/**
 * ContractFileWithRelations
 *
 * Contract file with its related contract, vault file, revision, and pages.
 *
 * This schema represents a contract file in the detail view, including the contract it belongs to,
 * the vault file it references, the specific revision the entity was extracted from, and the pages
 * that contain chunks related to the extracted model.
 *
 * Attributes:
 * contract_file: The contract file information
 * contract: The contract this file belongs to
 * vault_file: The vault file referenced by this contract file
 * revision: The specific vault file revision the entity was extracted from
 * is_latest_revision: Whether the revision is the current (latest) revision of the vault file
 * pages: List of pages that contain chunks related to the extracted model for this contract file
 */
export type ContractFileWithRelations = {
  contract_file: ContractManagementContractFileInDb;
  contract: ContractManagementContractInDb;
  vault_file?: VaultFileInDbSlim | null;
  revision?: VaultFileRevisionInDb | null;
  /**
   * Is Latest Revision
   */
  is_latest_revision?: boolean;
  /**
   * Pages
   */
  pages: Array<FilePageInDb>;
};

/**
 * ContractFileWithRelationsSearchResult
 *
 * Search result for contract files with relations.
 */
export type ContractFileWithRelationsSearchResult = {
  /**
   * Results
   */
  results: Array<ContractFileWithRelations>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractGenerationAnswerOperator
 *
 * How an ordered answer compares the drafting value against its own operand.
 *
 * Carried by ``NUMBER`` and ``DATE`` answers only. A selection answer is an equality by
 * construction — it names the one value its variant serves — so it has no operator column and
 * no way to disagree with itself about one.
 *
 * The operator belongs to the **variant's answer**, never to the question a user fills in:
 * somebody drafting supplies a plain value, and each variant decides for itself what it will
 * accept. That is what keeps a growing operator set from ever reaching the drafting form.
 *
 * ``BETWEEN`` is the only member taking a second operand, and it is half-open — ``[value,
 * value_max)``. Bands authored that way tile the line exactly: ``[0, 50000)`` and ``[50000, ...)``
 * neither overlap nor gap at the boundary, which closed intervals cannot manage without an
 * off-by-one argument every time. It stays a single member rather than becoming two answers ANDed
 * together, because one answer per question per variant is what the unique constraint — and the
 * whole "replace the answer set" write path — is built on.
 *
 * ``EQ`` and ``NE`` are exact, and safely so: the numeric column is ``Numeric``, not a float, and
 * the date column is a whole date. Equality against a binary float would be a trap worth
 * refusing; against these it is what the author means.
 */
export enum ContractGenerationAnswerOperator {
  EQ = 'eq',
  NE = 'ne',
  LT = 'lt',
  LTE = 'lte',
  GT = 'gt',
  GTE = 'gte',
  BETWEEN = 'between',
}

/**
 * ContractGenerationDraftCreateRequest
 *
 * Start a draft against one variant: a vault file in *parent_directory_uid* (the organisation's vault root
 * when not given) whose templated revision holds the entered values.
 *
 * The variant is named outright. Which variant that should be is a separate question, answered
 * by ``POST /template/{uid}/match`` — matching narrows, it does not resolve, so several variants
 * can fit one set of answers and the choice belongs to whoever is drafting.
 */
export type ContractGenerationDraftCreateRequest = {
  /**
   * Template Variant Uid
   */
  template_variant_uid: string;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Parent Directory Uid
   */
  parent_directory_uid?: string | null;
  /**
   * Form Data
   */
  form_data?: {
    [key: string]: unknown;
  };
};

/**
 * ContractGenerationDraftInDB
 *
 * Detail view returned when one draft is opened — adds the entered values.
 *
 * ``extracted_schema`` is deliberately absent: the wizard re-fetches it from the localized
 * template file so a draft resumed after the template changed reconciles against the live
 * field structure instead of a stale copy.
 */
export type ContractGenerationDraftInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Revision Uid
   */
  revision_uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Parent Directory Uid
   */
  parent_directory_uid?: string | null;
  /**
   * Template Variant Uid
   */
  template_variant_uid: string;
  /**
   * Template Uid
   */
  template_uid: string;
  /**
   * Template Name
   */
  template_name: string;
  /**
   * Variant Name
   */
  variant_name: string;
  template_status?: TaskStatus | null;
  /**
   * Form Data
   */
  form_data: {
    [key: string]: unknown;
  };
};

/**
 * ContractGenerationDraftSearchResult
 */
export type ContractGenerationDraftSearchResult = {
  /**
   * Results
   */
  results: Array<ContractGenerationDraftSummary>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractGenerationDraftSummary
 *
 * Slim view for the drafts list: a vault file whose current revision is templated.
 *
 * ``uid`` is the vault file's, ``revision_uid`` its templated revision's (the one the vault's preview and
 * download routes take). Everything but the entered values, so the page can render a row — the template it came
 * from and the variant — without shipping every draft's whole form state. ``template_status`` lets the list flag a
 * draft whose template has since broken or is being re-validated.
 */
export type ContractGenerationDraftSummary = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Revision Uid
   */
  revision_uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Parent Directory Uid
   */
  parent_directory_uid?: string | null;
  /**
   * Template Variant Uid
   */
  template_variant_uid: string;
  /**
   * Template Uid
   */
  template_uid: string;
  /**
   * Template Name
   */
  template_name: string;
  /**
   * Variant Name
   */
  variant_name: string;
  template_status?: TaskStatus | null;
};

/**
 * ContractGenerationDraftUpdateRequest
 *
 * The autosave payload, applied to the draft's templated revision in place. Every field is optional
 * (skip-None semantics), so the wizard can push just what changed — but ``form_data`` is replaced wholesale, not
 * merged: a key the user cleared has to be able to disappear. Renaming and moving a draft are the vault's own
 * file operations.
 *
 * ``template_variant_uid`` is here so switching variant mid-draft keeps the same draft (and the
 * same URL) instead of forcing a new one. It must name another variant of the draft's own
 * template: the entered values were collected against that template's schema, so a jump
 * elsewhere would render a different document from values never meant for it — and mostly
 * silently, since strict mode only raises on bare undefined variables, not on fields behind
 * ``{% if %}`` / ``{% for %}`` or a ``default`` filter.
 */
export type ContractGenerationDraftUpdateRequest = {
  /**
   * Template Variant Uid
   */
  template_variant_uid?: string | null;
  /**
   * Form Data
   */
  form_data?: {
    [key: string]: unknown;
  } | null;
};

/**
 * ContractGenerationDraftabilityReason
 *
 * Why a template cannot currently be drafted from.
 *
 * Reported rather than raised, so the questions editor can show the author exactly what to fix
 * instead of the user meeting a bare error when they try to start a draft.
 *
 * Deliberately short. Several variants matching the same answers is the *normal* case — the user
 * picks from the list — and a variant that has not answered a question simply does not constrain
 * it. The only thing that takes a template out of service is a question the author explicitly
 * marked as one every variant must answer.
 */
export enum ContractGenerationDraftabilityReason {
  NO_VARIANTS = 'no_variants',
  UNANSWERED_REQUIRED_QUESTIONS = 'unanswered_required_questions',
}

/**
 * ContractGenerationFinalizeRequest
 */
export type ContractGenerationFinalizeRequest = {
  signing?: ContractGenerationFinalizeSigning | null;
};

/**
 * ContractGenerationFinalizeResponse
 */
export type ContractGenerationFinalizeResponse = {
  /**
   * Vault File Uid
   */
  vault_file_uid: string;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid: string;
  /**
   * Contract Uid
   */
  contract_uid: string;
  /**
   * Signing Request Uid
   */
  signing_request_uid?: string | null;
};

/**
 * ContractGenerationFinalizeSigning
 *
 * Send the finalized document for signing in the same call ("finish draft" = "send").
 *
 * The signers are the template's signing slots (``TemplateField.signer``), each filled with a
 * contractual party — a legal entity through its ``representative_uid``. The rest mirrors
 * ``SigningRequestCreate`` with one-click defaults; no ``provider`` means the organisation's
 * default (``default_signing_provider``, else the first one it can use).
 */
export type ContractGenerationFinalizeSigning = {
  provider?: SigningProvider | null;
  signature_type?: SignatureType;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Ordered Signers
   */
  ordered_signers?: boolean;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
  /**
   * Signer Order
   */
  signer_order?: Array<string> | null;
};

/**
 * ContractGenerationQuestionType
 *
 * What kind of answer a template question takes.
 *
 * The value is also the polymorphic discriminator for ``cg_variant_answer``: a question's type
 * decides which answer subtype a variant's answer is mapped as, so the two are always read
 * together and a question's type is immutable once it has been created.
 *
 * ``NUMBER`` and ``DATE`` are the same shape — a comparison operator and one or two operands —
 * and differ only in column type, which is why they cannot share a table. The real axis here is
 * *enumerated versus ordered*, not "value type"; a boolean question is a selection with two
 * values and should not become a fourth member.
 */
export enum ContractGenerationQuestionType {
  SELECTION = 'selection',
  NUMBER = 'number',
  DATE = 'date',
}

/**
 * ContractGenerationRenderRequest
 */
export type ContractGenerationRenderRequest = {
  /**
   * Template Variant Uid
   */
  template_variant_uid: string;
  /**
   * Context Data
   */
  context_data: {
    [key: string]: unknown;
  };
};

/**
 * ContractGenerationTemplateCreateRequest
 *
 * The multipart form of ``POST /template/new``: the .docx source plus the template's fields.
 *
 * ``file`` lives in the model on purpose: FastAPI reads a form model's fields flat only when
 * the model is the route's sole form parameter — a separate ``UploadFile`` parameter makes it
 * expect one field literally named after the parameter.
 */
export type ContractGenerationTemplateCreateRequest = {
  /**
   * File
   */
  file: Blob | File;
  /**
   * Name
   */
  name: string;
  /**
   * Variant Name
   */
  variant_name: string;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractGenerationTemplateDraftability
 *
 * Whether this template can be used right now, and why not.
 *
 * Short by design. Several variants matching one set of answers is the normal case — the caller
 * picks — and a variant that has not answered a question simply does not constrain it. The only
 * thing that takes a template out of service is a question the author marked as one every variant
 * must answer.
 */
export type ContractGenerationTemplateDraftability = {
  /**
   * Is Draftable
   */
  is_draftable: boolean;
  /**
   * Reasons
   */
  reasons?: Array<ContractGenerationDraftabilityReason>;
};

/**
 * ContractGenerationTemplateFieldInDB
 *
 * The authoring view of a metadata row: adds its own uid and whether its field still exists.
 */
export type ContractGenerationTemplateFieldInDb = {
  /**
   * Path
   */
  path: string;
  /**
   * Parent Path
   */
  parent_path: string;
  /**
   * Label
   */
  label?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Guidance
   */
  guidance?: string | null;
  /**
   * Order Index
   */
  order_index?: number | null;
  /**
   * Sort Position
   */
  sort_position: number;
  /**
   * Uid
   */
  uid: string;
  /**
   * Orphaned
   */
  orphaned?: boolean;
};

/**
 * ContractGenerationTemplateFieldMetadata
 *
 * The author-written half of one template input field.
 *
 * Served alongside ``extracted_schema`` rather than merged into it, keyed by ``path``. The
 * schema is left byte-identical on purpose: the wizard decides whether a re-fetched template
 * "changed" by stringifying the schema's field values, so folding this metadata in would make
 * every edited description look like a structural change to someone mid-form.
 *
 * ``label`` being ``None`` means "no name authored" — render the humanized last path segment,
 * which is what the wizard has always shown.
 */
export type ContractGenerationTemplateFieldMetadata = {
  /**
   * Path
   */
  path: string;
  /**
   * Parent Path
   */
  parent_path: string;
  /**
   * Label
   */
  label?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Guidance
   */
  guidance?: string | null;
  /**
   * Order Index
   */
  order_index?: number | null;
  /**
   * Sort Position
   */
  sort_position: number;
};

/**
 * ContractGenerationTemplateFieldUpdate
 *
 * One entry of the bulk metadata write.
 *
 * Every entry states the field's **complete** authored state: an omitted optional clears the
 * stored value. That is deliberate — it sidesteps ``stage_update``'s skip-``None`` semantics,
 * under which "remove this description" would be impossible to express.
 */
export type ContractGenerationTemplateFieldUpdate = {
  /**
   * Path
   */
  path: string;
  /**
   * Label
   */
  label?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Guidance
   */
  guidance?: string | null;
  /**
   * Order Index
   */
  order_index?: number | null;
};

/**
 * ContractGenerationTemplateFieldsResponse
 *
 * Everything the field-authoring panel needs, independent of the template's validation state.
 *
 * Served from its own route rather than off ``extracted_schema`` so an author mid-edit doesn't
 * watch the panel empty out for a whole re-validation round trip — every re-extraction blanks
 * the schema before the worker lands the new one.
 */
export type ContractGenerationTemplateFieldsResponse = {
  /**
   * Fields
   */
  fields: Array<ContractGenerationTemplateFieldInDb>;
  /**
   * Orphaned
   */
  orphaned: Array<ContractGenerationTemplateFieldInDb>;
};

/**
 * ContractGenerationTemplateFieldsUpdateRequest
 *
 * Replaces the authored metadata for one localized file in a single transaction.
 *
 * Absolute values, no server-side re-sequencing — the same contract as the kanban board's
 * sort-index route. Paths absent from the payload keep whatever they had; paths that no field
 * in the current schema owns are refused rather than stored against nothing.
 */
export type ContractGenerationTemplateFieldsUpdateRequest = {
  /**
   * Fields
   */
  fields: Array<ContractGenerationTemplateFieldUpdate>;
};

/**
 * ContractGenerationTemplateInDB
 */
export type ContractGenerationTemplateInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractGenerationTemplateMatchAnswer
 *
 * One answer from the person drafting — a value, where a variant's answer is a constraint.
 *
 * Which field applies is decided by the question's type, exactly as on the variant side. An
 * answer omitted from the request is simply not filtered on, so it widens the result rather than
 * emptying it.
 *
 * Unlike the variant side, ``value`` must be one the question already has — the picker offers a
 * closed list, so a value no variant answers means the caller is working from a stale one, and
 * that is a 422 rather than an empty result.
 */
export type ContractGenerationTemplateMatchAnswer = {
  /**
   * Question Uid
   */
  question_uid: string;
  /**
   * Value
   */
  value?: string | null;
  /**
   * Number Value
   */
  number_value?: number | string | null;
  /**
   * Date Value
   */
  date_value?: string | null;
};

/**
 * ContractGenerationTemplateMatchRequest
 */
export type ContractGenerationTemplateMatchRequest = {
  /**
   * Answers
   */
  answers?: Array<ContractGenerationTemplateMatchAnswer>;
};

/**
 * ContractGenerationTemplateMatchResponse
 *
 * Every variant consistent with the answers, with what each one stands for so the caller can
 * tell them apart when there is more than one.
 */
export type ContractGenerationTemplateMatchResponse = {
  /**
   * Variants
   */
  variants?: Array<ContractGenerationVariantAnswersSummary>;
};

/**
 * ContractGenerationTemplateQuestionCreateRequest
 *
 * A new question, appended last in ask-order.
 *
 * A selection question states no admissible values here, because it has none until a variant
 * answers it. An ordered one may state **boundaries** — the range the question is about — which
 * are inclusive at both ends, individually optional, and refused for a selection question.
 *
 * ``question_type`` can only be set at creation: it is the discriminator both the question row
 * and every answer to it are mapped by, so changing it later would strand them all as the wrong
 * kind of row. The boundaries share that route, for the reason the update request gives.
 */
export type ContractGenerationTemplateQuestionCreateRequest = {
  /**
   * Label
   */
  label: string;
  /**
   * Hint
   */
  hint?: string | null;
  question_type: ContractGenerationQuestionType;
  /**
   * Required For Variants
   */
  required_for_variants?: boolean;
  /**
   * Number Min
   */
  number_min?: number | string | null;
  /**
   * Number Max
   */
  number_max?: number | string | null;
  /**
   * Date Min
   */
  date_min?: string | null;
  /**
   * Date Max
   */
  date_max?: string | null;
};

/**
 * ContractGenerationTemplateQuestionInDB
 *
 * One question, plus what a picker should offer for it.
 *
 * ``values`` is populated for selection questions only, and is derived rather than stored — the
 * distinct answers this template's variants have given, sorted. It is empty for a brand-new
 * selection question, which is not an error: nothing can be picked until some variant declares
 * what it stands for.
 *
 * The boundary fields are populated for the matching ordered type only, and are the inclusive
 * range this question is about. All four null means unbounded, which is the ordinary case — a
 * form should treat them as input limits, not as something that must be filled in.
 */
export type ContractGenerationTemplateQuestionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Label
   */
  label: string;
  /**
   * Hint
   */
  hint?: string | null;
  question_type: ContractGenerationQuestionType;
  /**
   * Required For Variants
   */
  required_for_variants: boolean;
  /**
   * Values
   */
  values?: Array<string>;
  /**
   * Number Min
   */
  number_min?: string | null;
  /**
   * Number Max
   */
  number_max?: string | null;
  /**
   * Date Min
   */
  date_min?: string | null;
  /**
   * Date Max
   */
  date_max?: string | null;
};

/**
 * ContractGenerationTemplateQuestionUpdateRequest
 *
 * Skip-None semantics. ``question_type`` is deliberately absent — see the create request.
 *
 * The boundaries are absent too, and for a reason skip-None makes plain: under these semantics
 * ``None`` means "leave alone", so there would be no way to say "remove the upper bound" — and a
 * half-editable boundary is worse than one that is set once. Moving a bound also has to answer for
 * the answers already outside it, which nobody has asked for. Delete and recreate, as with the
 * type.
 */
export type ContractGenerationTemplateQuestionUpdateRequest = {
  /**
   * Label
   */
  label?: string | null;
  /**
   * Hint
   */
  hint?: string | null;
  /**
   * Required For Variants
   */
  required_for_variants?: boolean | null;
};

/**
 * ContractGenerationTemplateQuestionsResponse
 *
 * The whole authoring payload in one read: the questions, where every variant stands, and the
 * verdict. Every write returns it, because a write is exactly when the verdict can change.
 */
export type ContractGenerationTemplateQuestionsResponse = {
  /**
   * Questions
   */
  questions?: Array<ContractGenerationTemplateQuestionInDb>;
  /**
   * Variants
   */
  variants?: Array<ContractGenerationVariantAnswersSummary>;
  draftability: ContractGenerationTemplateDraftability;
};

/**
 * ContractGenerationTemplateRoleShareCreate
 *
 * Schema for creating a new template role share.
 */
export type ContractGenerationTemplateRoleShareCreate = {
  permissions: AccessPermission;
};

/**
 * ContractGenerationTemplateRoleShareInDB
 *
 * Schema for template role share — includes both template and role.
 */
export type ContractGenerationTemplateRoleShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  template: ContractGenerationTemplateInDb;
  role: PermissionRoleInDb;
};

/**
 * ContractGenerationTemplateRoleShareInDBRole
 *
 * Schema for template role share when querying by role — includes the template.
 */
export type ContractGenerationTemplateRoleShareInDbRole = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  template: ContractGenerationTemplateInDb;
};

/**
 * ContractGenerationTemplateRoleShareInDBTemplate
 *
 * Schema for template role share when querying by template — includes the role.
 */
export type ContractGenerationTemplateRoleShareInDbTemplate = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  role: PermissionRoleInDb;
};

/**
 * ContractGenerationTemplateRoleShareSearchResultRole
 */
export type ContractGenerationTemplateRoleShareSearchResultRole = {
  /**
   * Results
   */
  results: Array<ContractGenerationTemplateRoleShareInDbRole>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractGenerationTemplateRoleShareSearchResultTemplate
 */
export type ContractGenerationTemplateRoleShareSearchResultTemplate = {
  /**
   * Results
   */
  results: Array<ContractGenerationTemplateRoleShareInDbTemplate>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractGenerationTemplateRoleShareUpdate
 *
 * Schema for updating an existing template role share.
 *
 * All fields are optional to allow partial updates.
 */
export type ContractGenerationTemplateRoleShareUpdate = {
  permissions?: AccessPermission | null;
};

/**
 * ContractGenerationTemplateSearchResult
 */
export type ContractGenerationTemplateSearchResult = {
  /**
   * Results
   */
  results: Array<ContractGenerationTemplateInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractGenerationTemplateUpdate
 */
export type ContractGenerationTemplateUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractGenerationTemplateVariantCreateRequest
 *
 * A new variant.
 *
 * It answers nothing to begin with, which is a legitimate state: an unanswered question does not
 * constrain a variant, so a new one matches everything until an author narrows it through
 * ``PUT /template/{uid}/variant/{uid}/answers``. Only a question marked
 * ``required_for_variants`` makes that a problem, and it says so on the template's verdict.
 *
 * The multipart form of ``POST /template/{uid}/variant``: the .docx source plus the variant's
 * name (``file`` sits in the model for the reason given on ``ContractGenerationTemplateCreateRequest``).
 */
export type ContractGenerationTemplateVariantCreateRequest = {
  /**
   * File
   */
  file: Blob | File;
  /**
   * Name
   */
  name: string;
};

/**
 * ContractGenerationTemplateVariantInDB
 *
 * Detail view returned when one specific variant is fetched.
 */
export type ContractGenerationTemplateVariantInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Mime Type
   */
  mime_type: string;
  /**
   * Vault File Uid
   */
  vault_file_uid?: string | null;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  template_status?: TaskStatus | null;
  extracted_schema?: TemplateSchema | null;
  /**
   * Validation Errors
   */
  validation_errors?: Array<TemplateValidationError> | null;
  /**
   * Field Metadata
   */
  field_metadata?: {
    [key: string]: ContractGenerationTemplateFieldMetadata;
  } | null;
};

/**
 * ContractGenerationTemplateVariantSummary
 *
 * Slim view returned by the list endpoint.
 *
 * Carries the metadata the overview UI needs to render each variant row plus the
 * vault UIDs the frontend uses to call the existing vault-file preview endpoints
 * so the user can preview the underlying .docx without us re-implementing
 * rendering. The full extracted schema lives on the detail (`InDB`) view since
 * the overview never displays it.
 */
export type ContractGenerationTemplateVariantSummary = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Mime Type
   */
  mime_type: string;
  /**
   * Vault File Uid
   */
  vault_file_uid?: string | null;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  template_status?: TaskStatus | null;
};

/**
 * ContractGenerationTemplateVariantUpdate
 */
export type ContractGenerationTemplateVariantUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * File Name
   */
  file_name?: string | null;
};

/**
 * ContractGenerationVariantAnswerInDB
 *
 * A stored answer, flattened across the subtypes.
 *
 * One shape for all three types rather than a union, so a client rendering "what does this
 * variant stand for?" reads the same fields whatever it is looking at.
 */
export type ContractGenerationVariantAnswerInDb = {
  /**
   * Question Uid
   */
  question_uid: string;
  question_type: ContractGenerationQuestionType;
  /**
   * Value
   */
  value?: string | null;
  operator?: ContractGenerationAnswerOperator | null;
  /**
   * Number Value
   */
  number_value?: string | null;
  /**
   * Number Value Max
   */
  number_value_max?: string | null;
  /**
   * Date Value
   */
  date_value?: string | null;
  /**
   * Date Value Max
   */
  date_value_max?: string | null;
};

/**
 * ContractGenerationVariantAnswerUpdate
 *
 * One variant's answer to one question — a constraint, not a value.
 *
 * Exactly one field group applies, chosen by the question's type: ``value`` for a selection, the
 * ``number_`` fields for a number, the ``date_`` fields for a date.
 *
 * ``value`` is free text and may be one no other variant has used: that is how a selection
 * question acquires its values in the first place. It is trimmed and folded onto a sibling's
 * casing before being stored, so answering "female" where another variant said "Female" joins
 * that answer rather than forking the picker.
 *
 * For the ordered types, ``operator`` says how the drafting value is compared against
 * ``*_value``. ``*_value_max`` applies to ``between`` alone — half-open, lower inclusive and
 * upper exclusive — and is refused for every other operator, because a second operand the
 * comparison ignores is a silent way to mean two different things.
 */
export type ContractGenerationVariantAnswerUpdate = {
  /**
   * Question Uid
   */
  question_uid: string;
  /**
   * Value
   */
  value?: string | null;
  operator?: ContractGenerationAnswerOperator | null;
  /**
   * Number Value
   */
  number_value?: number | string | null;
  /**
   * Number Value Max
   */
  number_value_max?: number | string | null;
  /**
   * Date Value
   */
  date_value?: string | null;
  /**
   * Date Value Max
   */
  date_value_max?: string | null;
};

/**
 * ContractGenerationVariantAnswersSummary
 *
 * Where one variant stands, and what it still owes.
 *
 * ``template_status`` rides along because the questions editor and the variant list are the same
 * screen: a variant can be fully answered and still unusable because its document failed to
 * parse, and the author needs both facts at once.
 */
export type ContractGenerationVariantAnswersSummary = {
  /**
   * Variant Uid
   */
  variant_uid: string;
  /**
   * Variant Name
   */
  variant_name: string;
  template_status?: TaskStatus | null;
  /**
   * Answers
   */
  answers?: Array<ContractGenerationVariantAnswerInDb>;
  /**
   * Unanswered Required Question Uids
   */
  unanswered_required_question_uids?: Array<string>;
};

/**
 * ContractGenerationVariantAnswersUpdateRequest
 *
 * One variant's *complete* answer set.
 *
 * Replace, not merge. A question absent from the list is unanswered afterwards, which is the only
 * way to express "this variant does not care about that question" — and an unanswered question
 * never constrains the variant.
 */
export type ContractGenerationVariantAnswersUpdateRequest = {
  /**
   * Answers
   */
  answers?: Array<ContractGenerationVariantAnswerUpdate>;
};

/**
 * ContractManagementCalendarCreate
 *
 * Schema for creating a new ContractManagementCalendar.
 */
export type ContractManagementCalendarCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Receiver Emails
   */
  receiver_emails: Array<string>;
  /**
   * Prompt
   */
  prompt?: string | null;
  /**
   * Example Data
   */
  example_data?: Array<string> | null;
  /**
   * Date Types
   */
  date_types?: Array<ContractManagementDateType>;
  /**
   * First Reminder Weeks
   */
  first_reminder_weeks?: number | null;
  /**
   * Second Reminder Weeks
   */
  second_reminder_weeks?: number | null;
  contract_criteria?: ContractManagementCalendarCriteria | null;
};

/**
 * ContractManagementCalendarCriteria
 *
 * The contract search whose matches a calendar sees — now and as contracts come to match — the
 * contract search page's own text and column filters (``type``, ``tags``). Empty text and no
 * filters match every contract the calendar's creator may read. A calendar has criteria OR picked
 * directories and contracts, never both.
 */
export type ContractManagementCalendarCriteria = {
  /**
   * Text
   */
  text?: string;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
};

/**
 * ContractManagementCalendarInDB
 */
export type ContractManagementCalendarInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Receiver Emails
   */
  receiver_emails: Array<string>;
  /**
   * Prompt
   */
  prompt?: string | null;
  /**
   * Example Data
   */
  example_data?: Array<string> | null;
  /**
   * Date Types
   */
  date_types?: Array<ContractManagementDateType>;
  /**
   * First Reminder Weeks
   */
  first_reminder_weeks?: number | null;
  /**
   * Second Reminder Weeks
   */
  second_reminder_weeks?: number | null;
  contract_criteria?: ContractManagementCalendarCriteria | null;
  /**
   * Last Sent
   */
  last_sent?: string | null;
};

/**
 * ContractManagementCalendarLinkRequest
 *
 * Request body for adding or removing related models to/from a calendar (one or more uids).
 */
export type ContractManagementCalendarLinkRequest = {
  /**
   * Uids
   */
  uids: Array<string>;
};

/**
 * ContractManagementCalendarLinkResult
 *
 * Response for add/remove calendar-relationship requests: counts and per-uid outcomes.
 */
export type ContractManagementCalendarLinkResult = {
  /**
   * Total
   */
  total: number;
  /**
   * Successful
   */
  successful: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Results
   */
  results: Array<ContractManagementCalendarLinkResultItem>;
};

/**
 * ContractManagementCalendarLinkResultItem
 *
 * Outcome for one uid in an add/remove calendar-relationship request.
 */
export type ContractManagementCalendarLinkResultItem = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Success
   */
  success: boolean;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * ContractManagementCalendarSearchResult
 */
export type ContractManagementCalendarSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementCalendarInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementCalendarUpdate
 *
 * Schema for updating an existing ContractManagementCalendar.
 */
export type ContractManagementCalendarUpdate = {
  /**
   * Name
   */
  name: string;
  /**
   * Receiver Emails
   */
  receiver_emails: Array<string>;
  /**
   * Prompt
   */
  prompt?: string | null;
  /**
   * Example Data
   */
  example_data: Array<string> | null;
  /**
   * Date Types
   */
  date_types?: Array<ContractManagementDateType>;
  /**
   * First Reminder Weeks
   */
  first_reminder_weeks?: number | null;
  /**
   * Second Reminder Weeks
   */
  second_reminder_weeks?: number | null;
  contract_criteria?: ContractManagementCalendarCriteria | null;
};

/**
 * ContractManagementClauseCreate
 *
 * Schema for creating a new ContractManagementClause.
 */
export type ContractManagementClauseCreate = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Text
   */
  text: string;
  category: ClauseCategory;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Clause Number
   */
  clause_number?: string | null;
  /**
   * Section Path
   */
  section_path?: string | null;
};

/**
 * ContractManagementClauseInDB
 */
export type ContractManagementClauseInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Text
   */
  text: string;
  category: ClauseCategory;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Clause Number
   */
  clause_number?: string | null;
  /**
   * Section Path
   */
  section_path?: string | null;
};

/**
 * ContractManagementClauseSearchResult
 */
export type ContractManagementClauseSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementClauseInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementClauseUpdate
 *
 * Schema for updating an existing ContractManagementClause.
 */
export type ContractManagementClauseUpdate = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Text
   */
  text?: string | null;
  category?: ClauseCategory | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Clause Number
   */
  clause_number?: string | null;
  /**
   * Section Path
   */
  section_path?: string | null;
};

/**
 * ContractManagementColumnExtractionScope
 *
 * How much of a view's existing backlog to re-extract when a column gets a new question.
 */
export enum ContractManagementColumnExtractionScope {
  NEW_ONLY = 'new_only',
  LAST_5 = 'last_5',
  LAST_10 = 'last_10',
  ALL = 'all',
}

/**
 * ContractManagementContractCreate
 *
 * Schema for creating a new ContractManagementContract.
 *
 * Args:
 * contract_type_uid: One of the organisation's contract types; None leaves the contract
 * without one.
 */
export type ContractManagementContractCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Contract Type Uid
   */
  contract_type_uid?: string | null;
  /**
   * Tags
   */
  tags: Array<string>;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementContractFileCreateUser
 *
 * Schema for filing a vault file into a contract (user-facing, uses UIDs).
 *
 * The user names only the file: its type and description come from ``classify_contract_file``,
 * its state is its current revision's. All of them can be edited afterwards.
 */
export type ContractManagementContractFileCreateUser = {
  /**
   * Vault File Uid
   */
  vault_file_uid: string;
};

/**
 * ContractManagementContractFileInDB
 */
export type ContractManagementContractFileInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: ContractManagementContractFileType | null;
  /**
   * Description
   */
  description: string | null;
  vault_file?: VaultFileInDbSlim | null;
  /**
   * Its current revision's; None while the file has no revision yet (still uploading).
   */
  readonly effective_state: EffectiveState | null;
  /**
   * Needs Attention Reasons
   */
  readonly needs_attention_reasons: Array<NeedsAttentionReason>;
  /**
   * Signature Failure Reason
   */
  readonly signature_failure_reason: string | null;
};

/**
 * ContractManagementContractFileInDBExtended
 */
export type ContractManagementContractFileInDbExtended = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: ContractManagementContractFileType | null;
  /**
   * Description
   */
  description: string | null;
  vault_file?: VaultFileInDbSlim | null;
  contract: ContractManagementContractInDb;
  /**
   * Dates
   */
  dates?: Array<ContractManagementDateInDb>;
  /**
   * Its current revision's; None while the file has no revision yet (still uploading).
   */
  readonly effective_state: EffectiveState | null;
  /**
   * Needs Attention Reasons
   */
  readonly needs_attention_reasons: Array<NeedsAttentionReason>;
  /**
   * Signature Failure Reason
   */
  readonly signature_failure_reason: string | null;
};

/**
 * ContractManagementContractFileMoveUser
 *
 * Schema for moving a contract file to another contract (user-facing).
 *
 * Provide exactly one of:
 * - target_contract_uid: move the file to an existing contract.
 * - new_contract: create a new contract next to the source and move the file there.
 */
export type ContractManagementContractFileMoveUser = {
  /**
   * Target Contract Uid
   */
  target_contract_uid?: string | null;
  new_contract?: ContractManagementContractCreate | null;
};

/**
 * ContractManagementContractFileSearchResult
 */
export type ContractManagementContractFileSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementContractFileInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementContractFileType
 */
export enum ContractManagementContractFileType {
  CONTRACT = 'contract',
  AMENDMENT = 'amendment',
  APPENDIX = 'appendix',
}

/**
 * ContractManagementContractFileUpdateUser
 *
 * Schema for user updates to a ContractManagementContractFile (user-facing, uses UIDs).
 *
 * The vault file it holds cannot be swapped: a new version of the document is a new revision of
 * that vault file, a different document is removing this contract file and filing the other one.
 */
export type ContractManagementContractFileUpdateUser = {
  type?: ContractManagementContractFileType | null;
  /**
   * Description
   */
  description?: string | null;
  document_state?: DocumentState | null;
};

/**
 * ContractManagementContractInDB
 *
 * A contract with its matters.
 *
 * Attributes:
 * contract_file_count: How many of its files have finished uploading
 * permissions: The requesting user's effective permission bits on the contract (READ=1,
 * WRITE=2, ADMIN=4; an org admin holds 7), or None when not computed for this response
 * states: Its contract and amendment states for today, or None when not computed for this
 * response
 */
export type ContractManagementContractInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  contract_type?: ContractTypeRef | null;
  /**
   * Tags
   */
  tags: Array<string>;
  /**
   * Description
   */
  description: string | null;
  owner_user?: UserInDb | null;
  /**
   * Matters
   */
  matters: Array<ContractManagementMatterInDb>;
  /**
   * Contract File Count
   */
  contract_file_count: number;
  /**
   * Permissions
   */
  permissions?: number | null;
  states?: ContractManagementContractStates | null;
};

/**
 * ContractManagementContractInDBSlim
 *
 * A contract as a vault item: what a listing of the tree needs, without its matters.
 *
 * Attributes:
 * contract_file_count: How many of its files have finished uploading
 * permissions: The requesting user's effective permission bits on the contract (READ=1,
 * WRITE=2, ADMIN=4; an org admin holds 7), or None when not computed for this response
 * states: Its contract and amendment states for today, or None when not computed for this
 * response
 */
export type ContractManagementContractInDbSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  contract_type?: ContractTypeRef | null;
  owner_user?: UserInDb | null;
  /**
   * Contract File Count
   */
  contract_file_count: number;
  /**
   * Permissions
   */
  permissions?: number | null;
  states?: ContractManagementContractStates | null;
};

/**
 * ContractManagementContractSearchResult
 */
export type ContractManagementContractSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementContractInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementContractStates
 *
 * A contract's two roll-ups, each the worst effective state of its current revisions
 * (``ContractStateResolver.contract_states``).
 *
 * Attributes:
 * contract_state: Over its CONTRACT files; None when it has none
 * amendment_state: Over its AMENDMENT files; None when it has none
 */
export type ContractManagementContractStates = {
  contract_state: EffectiveState | null;
  amendment_state: EffectiveState | null;
};

/**
 * ContractManagementContractUpdate
 *
 * Schema for updating an existing ContractManagementContract.
 *
 * Args:
 * contract_type_uid: One of the organisation's contract types. Left out or null, the type is
 * kept.
 */
export type ContractManagementContractUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Contract Type Uid
   */
  contract_type_uid?: string | null;
  /**
   * Tags
   */
  tags?: Array<string> | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementContractValue
 *
 * One prompt of a contract (answered in one value type).
 *
 * Attributes:
 * prompt: The question asked of the contract's files.
 * value_type: The type it is answered in — one prompt answered in two types is two values.
 * extraction: The extraction whose value is the contract's (its ``human_overwrite`` in effect
 * when overwritten); None when no file the contract listens to has a value.
 * contract_file_uid: The contract file that extraction belongs to.
 */
export type ContractManagementContractValue = {
  /**
   * Prompt
   */
  prompt: string;
  value_type: ExtractionValueType;
  extraction: ContractManagementExtractionInDb | null;
  /**
   * Contract File Uid
   */
  contract_file_uid: string | null;
};

/**
 * ContractManagementContractValueMatrix
 *
 * A page of a contract's values with each file's own.
 *
 * Attributes:
 * contract_files: The matrix columns: the contract's files with a revision, by type (amendment,
 * appendix, contract, unclassified) then age. Unclassified files show their values but never
 * answer for the contract.
 */
export type ContractManagementContractValueMatrix = {
  /**
   * Results
   */
  results: Array<ContractManagementContractValueMatrixRow>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Contract Files
   */
  contract_files: Array<ContractManagementContractFileInDb>;
};

/**
 * ContractManagementContractValueMatrixRow
 *
 * :class:`ContractManagementContractValue` plus every file's own answer.
 *
 * Attributes:
 * files: Each contract file's extraction for the prompt, keyed by the file's uid — every file of
 * :attr:`ContractManagementContractValueMatrix.contract_files`, None for a file without one.
 */
export type ContractManagementContractValueMatrixRow = {
  /**
   * Prompt
   */
  prompt: string;
  value_type: ExtractionValueType;
  extraction: ContractManagementExtractionInDb | null;
  /**
   * Contract File Uid
   */
  contract_file_uid: string | null;
  /**
   * Files
   */
  files: {
    [key: string]: ContractManagementExtractionInDb | null;
  };
};

/**
 * ContractManagementContractValueQuery
 *
 * A page of a contract's values: they only sort by prompt, alphabetically by default.
 */
export type ContractManagementContractValueQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: 'prompt';
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
};

/**
 * ContractManagementContractValueSearchResult
 */
export type ContractManagementContractValueSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementContractValue>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractManagementContractualPartyKind
 *
 * Kinds of contractual parties; the polymorphic discriminator of the party hierarchy.
 */
export enum ContractManagementContractualPartyKind {
  NATURAL_PERSON = 'natural_person',
  LEGAL_ENTITY = 'legal_entity',
}

/**
 * ContractManagementContractualPartyLegalEntityCreate
 *
 * Schema for creating a legal entity party; representatives (and the default one) are linked separately.
 */
export type ContractManagementContractualPartyLegalEntityCreate = {
  /**
   * Kind
   */
  kind: 'legal_entity';
  /**
   * Legal Name
   */
  legal_name: string;
  /**
   * Street Address
   */
  street_address?: string | null;
  /**
   * Postcode
   */
  postcode?: string | null;
  /**
   * City
   */
  city?: string | null;
  /**
   * Iso 3166 Alpha 3
   */
  iso_3166_alpha_3?: string | null;
  /**
   * Legal Form
   */
  legal_form?: string | null;
  /**
   * Commercial Register
   */
  commercial_register?: string | null;
  /**
   * Registration Number
   */
  registration_number?: string | null;
};

/**
 * ContractManagementContractualPartyLegalEntityInDB
 *
 * A legal entity with the natural persons representing it.
 *
 * Attributes:
 * permissions: The requesting user's effective permission bits on the party (READ=1, WRITE=2, ADMIN=4;
 * an org admin holds 7), or None when not computed for this response; its representatives carry
 * their own
 */
export type ContractManagementContractualPartyLegalEntityInDb = {
  /**
   * Kind
   */
  kind?: 'legal_entity';
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Street Address
   */
  street_address?: string | null;
  /**
   * Postcode
   */
  postcode?: string | null;
  /**
   * City
   */
  city?: string | null;
  /**
   * Iso 3166 Alpha 3
   */
  iso_3166_alpha_3?: string | null;
  /**
   * Country Name
   */
  country_name?: string | null;
  /**
   * Legal Name
   */
  legal_name: string;
  /**
   * Legal Form
   */
  legal_form?: string | null;
  /**
   * Commercial Register
   */
  commercial_register?: string | null;
  /**
   * Registration Number
   */
  registration_number?: string | null;
  /**
   * Representatives
   */
  representatives?: Array<ContractManagementContractualPartyNaturalPersonInDb>;
  default_representative?: ContractManagementContractualPartyNaturalPersonInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
};

/**
 * ContractManagementContractualPartyLegalEntityUpdate
 *
 * Schema for updating a legal entity party; ``kind`` must match the stored party.
 */
export type ContractManagementContractualPartyLegalEntityUpdate = {
  /**
   * Kind
   */
  kind: 'legal_entity';
  /**
   * Legal Name
   */
  legal_name?: string | null;
  /**
   * Street Address
   */
  street_address?: string | null;
  /**
   * Postcode
   */
  postcode?: string | null;
  /**
   * City
   */
  city?: string | null;
  /**
   * Iso 3166 Alpha 3
   */
  iso_3166_alpha_3?: string | null;
  /**
   * Legal Form
   */
  legal_form?: string | null;
  /**
   * Commercial Register
   */
  commercial_register?: string | null;
  /**
   * Registration Number
   */
  registration_number?: string | null;
};

/**
 * ContractManagementContractualPartyNaturalPersonCreate
 *
 * Schema for creating a natural person party; ``name`` is composed from the name parts.
 */
export type ContractManagementContractualPartyNaturalPersonCreate = {
  /**
   * Kind
   */
  kind: 'natural_person';
  /**
   * Email
   */
  email?: string | null;
  /**
   * Street Address
   */
  street_address?: string | null;
  /**
   * Postcode
   */
  postcode?: string | null;
  /**
   * City
   */
  city?: string | null;
  /**
   * Iso 3166 Alpha 3
   */
  iso_3166_alpha_3?: string | null;
  /**
   * Title
   */
  title?: string | null;
  /**
   * First Name
   */
  first_name?: string | null;
  /**
   * Middle Name
   */
  middle_name?: string | null;
  /**
   * Last Name
   */
  last_name?: string | null;
  /**
   * Suffix
   */
  suffix?: string | null;
  /**
   * Artist Name
   */
  artist_name?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Anonymisation Id
   */
  anonymisation_id?: string | null;
  /**
   * Personal Id
   */
  personal_id?: string | null;
  /**
   * Issuing Authority
   */
  issuing_authority?: string | null;
};

/**
 * ContractManagementContractualPartyNaturalPersonInDB
 *
 * A natural person, of its own or as a legal entity's representative. The legal entities it represents are
 * listed separately (``/contractual-party/{uid}/represented-legal-entities``), filtered to those the caller may
 * read.
 *
 * Attributes:
 * permissions: The requesting user's effective permission bits on the party (READ=1, WRITE=2, ADMIN=4;
 * an org admin holds 7), or None when not computed for this response
 */
export type ContractManagementContractualPartyNaturalPersonInDb = {
  /**
   * Kind
   */
  kind?: 'natural_person';
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Email
   */
  email?: string | null;
  /**
   * Street Address
   */
  street_address?: string | null;
  /**
   * Postcode
   */
  postcode?: string | null;
  /**
   * City
   */
  city?: string | null;
  /**
   * Iso 3166 Alpha 3
   */
  iso_3166_alpha_3?: string | null;
  /**
   * Country Name
   */
  country_name?: string | null;
  /**
   * Title
   */
  title?: string | null;
  /**
   * First Name
   */
  first_name?: string | null;
  /**
   * Middle Name
   */
  middle_name?: string | null;
  /**
   * Last Name
   */
  last_name?: string | null;
  /**
   * Suffix
   */
  suffix?: string | null;
  /**
   * Artist Name
   */
  artist_name?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Anonymisation Id
   */
  anonymisation_id?: string | null;
  /**
   * Personal Id
   */
  personal_id?: string | null;
  /**
   * Issuing Authority
   */
  issuing_authority?: string | null;
  /**
   * Permissions
   */
  permissions?: number | null;
};

/**
 * ContractManagementContractualPartyNaturalPersonUpdate
 *
 * Schema for updating a natural person party; ``kind`` must match the stored party.
 */
export type ContractManagementContractualPartyNaturalPersonUpdate = {
  /**
   * Kind
   */
  kind: 'natural_person';
  /**
   * Email
   */
  email?: string | null;
  /**
   * Street Address
   */
  street_address?: string | null;
  /**
   * Postcode
   */
  postcode?: string | null;
  /**
   * City
   */
  city?: string | null;
  /**
   * Iso 3166 Alpha 3
   */
  iso_3166_alpha_3?: string | null;
  /**
   * Title
   */
  title?: string | null;
  /**
   * First Name
   */
  first_name?: string | null;
  /**
   * Middle Name
   */
  middle_name?: string | null;
  /**
   * Last Name
   */
  last_name?: string | null;
  /**
   * Suffix
   */
  suffix?: string | null;
  /**
   * Artist Name
   */
  artist_name?: string | null;
  /**
   * Date Of Birth
   */
  date_of_birth?: string | null;
  /**
   * Anonymisation Id
   */
  anonymisation_id?: string | null;
  /**
   * Personal Id
   */
  personal_id?: string | null;
  /**
   * Issuing Authority
   */
  issuing_authority?: string | null;
};

/**
 * ContractManagementContractualPartySearchResult
 */
export type ContractManagementContractualPartySearchResult = {
  /**
   * Results
   */
  results: Array<
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb)
  >;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementDateCreate
 *
 * Schema for creating a new ContractManagementDate. A revision may hold several dates of one type;
 * how they combine is checked against its other dates (``ContractStateResolver.check_effective_dates``).
 */
export type ContractManagementDateCreate = {
  /**
   * Recurrence Type
   *
   * How the date repeats: 'never' (a single date), 'count' (a fixed number of occurrences, recurrence_count), 'until_date' (until recurrence_until_date) or 'infinite' (without end).
   */
  recurrence_type: 'count' | 'until_date' | 'infinite' | 'never';
  /**
   * Recurrence Count
   *
   * Sets the total number of occurrences for the recurring date
   */
  recurrence_count?: number | null;
  /**
   * Recurrence Until Date
   *
   * The date until which the date repeats. It MUST be in ISO format.
   */
  recurrence_until_date?: string | null;
  /**
   * Recurrence Frequency
   *
   * In what interval the date repeats (e.g. every day, week or month.)
   */
  recurrence_frequency?: 'day' | 'week' | 'month' | 'quarter' | 'year' | null;
  /**
   * Recurrence Interval
   *
   * Specifies the interval between each occurrence (e.g., INTERVAL=2 for every two weeks).
   */
  recurrence_interval?: number | null;
  type: ContractManagementDateType;
  /**
   * Date
   */
  date?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementDateInDB
 */
export type ContractManagementDateInDb = {
  /**
   * Recurrence Type
   *
   * How the date repeats: 'never' (a single date), 'count' (a fixed number of occurrences, recurrence_count), 'until_date' (until recurrence_until_date) or 'infinite' (without end).
   */
  recurrence_type: 'count' | 'until_date' | 'infinite' | 'never';
  /**
   * Recurrence Count
   *
   * Sets the total number of occurrences for the recurring date
   */
  recurrence_count?: number | null;
  /**
   * Recurrence Until Date
   *
   * The date until which the date repeats. It MUST be in ISO format.
   */
  recurrence_until_date?: string | null;
  /**
   * Recurrence Frequency
   *
   * In what interval the date repeats (e.g. every day, week or month.)
   */
  recurrence_frequency?: 'day' | 'week' | 'month' | 'quarter' | 'year' | null;
  /**
   * Recurrence Interval
   *
   * Specifies the interval between each occurrence (e.g., INTERVAL=2 for every two weeks).
   */
  recurrence_interval?: number | null;
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: ContractManagementDateType;
  /**
   * Date
   */
  date?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementDateType
 *
 * The fixed date types extracted per contract file revision (at most one date of each).
 */
export enum ContractManagementDateType {
  EFFECTIVE_FROM = 'effective_from',
  EFFECTIVE_TO = 'effective_to',
}

/**
 * ContractManagementDateUpdate
 *
 * Schema for updating an existing ContractManagementDate. Its ``type`` stays — delete it and create
 * one of the other type instead.
 */
export type ContractManagementDateUpdate = {
  /**
   * Date
   */
  date?: string | null;
  /**
   * Recurrence Type
   */
  recurrence_type?: 'count' | 'until_date' | 'infinite' | 'never' | null;
  /**
   * Recurrence Count
   */
  recurrence_count?: number | null;
  /**
   * Recurrence Until Date
   */
  recurrence_until_date?: string | null;
  /**
   * Recurrence Frequency
   */
  recurrence_frequency?: 'day' | 'week' | 'month' | 'quarter' | 'year' | null;
  /**
   * Recurrence Interval
   */
  recurrence_interval?: number | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementExtractionInDB
 */
export type ContractManagementExtractionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  processing_state: TaskStatus;
  /**
   * Error Text
   */
  error_text?: string | null;
  /**
   * Approved
   */
  approved: boolean;
  /**
   * Prompt
   */
  prompt: string;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  value_type: ExtractionValueType | null;
  value: ExtractionValue | null;
  /**
   * Reason
   */
  reason: string | null;
  /**
   * Page
   */
  page: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text: Array<string> | null;
  human_overwrite: ContractManagementExtractionOverwriteInDb | null;
};

/**
 * ContractManagementExtractionOverwriteCreate
 */
export type ContractManagementExtractionOverwriteCreate = {
  /**
   * Value
   */
  value?: ExtractionValue | GaiaJsonValue | null;
  /**
   * Reason
   */
  reason?: string | null;
  /**
   * Page
   */
  page?: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  /**
   * Overwrite Reason
   */
  overwrite_reason?: string | null;
};

/**
 * ContractManagementExtractionOverwriteInDB
 */
export type ContractManagementExtractionOverwriteInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  value_type: ExtractionValueType | null;
  value: ExtractionValue | null;
  /**
   * Is Overwritten
   */
  is_overwritten: boolean;
  /**
   * Reason
   */
  reason: string | null;
  /**
   * Page
   */
  page: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text: Array<string> | null;
  user?: UserInDb | null;
  /**
   * Overwrite Reason
   */
  overwrite_reason: string | null;
};

/**
 * ContractManagementExtractionOverwriteUpdate
 */
export type ContractManagementExtractionOverwriteUpdate = {
  /**
   * Value
   */
  value?: ExtractionValue | GaiaJsonValue | null;
  /**
   * Reason
   */
  reason?: string | null;
  /**
   * Page
   */
  page?: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  /**
   * Overwrite Reason
   */
  overwrite_reason?: string | null;
};

/**
 * ContractManagementExtractionSearchResult
 */
export type ContractManagementExtractionSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementExtractionInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractManagementMatterCreate
 *
 * Schema for creating a new ContractManagementMatter.
 */
export type ContractManagementMatterCreate = {
  /**
   * Name
   */
  name: string;
  type: ContractManagementMatterType;
  /**
   * Tags
   */
  tags?: Array<string>;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementMatterInDB
 */
export type ContractManagementMatterInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  type: ContractManagementMatterType;
  /**
   * Tags
   */
  tags: Array<string>;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementMatterSearchResult
 */
export type ContractManagementMatterSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementMatterInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementMatterType
 *
 * Types of legal matters.
 */
export enum ContractManagementMatterType {
  'M&A' = 'M&A',
  EMPLOYMENT_DISPUTE = 'Employment Dispute',
  INTELLECTUAL_PROPERTY = 'Intellectual Property',
  CONTRACT_DISPUTE = 'Contract Dispute',
  REGULATORY = 'Regulatory',
  LITIGATION = 'Litigation',
  COMPLIANCE = 'Compliance',
  CORPORATE = 'Corporate',
  REAL_ESTATE = 'Real Estate',
  FINANCE = 'Finance',
  TAX = 'Tax',
  OTHER = 'Other',
}

/**
 * ContractManagementMatterUpdate
 *
 * Schema for updating an existing ContractManagementMatter.
 */
export type ContractManagementMatterUpdate = {
  /**
   * Name
   */
  name?: string | null;
  type?: ContractManagementMatterType | null;
  /**
   * Tags
   */
  tags?: Array<string> | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementMissingExtractions
 *
 * How many calendar and view-column questions were never asked of a contract file's current
 * revision — typically because ``contract_management`` was off when the file arrived.
 *
 * Each missing extraction costs one insights credit when it runs. A question shared by a
 * calendar and a column on the same revision is counted under both kinds but only once in
 * ``total``, which is what back-filling dispatches.
 *
 * Attributes:
 * contract_management_enabled: Whether the feature is on; back-filling refuses while it is off.
 * calendars: Missing calendar extractions.
 * view_columns: Missing extractions of each view column's latest question.
 * total: Distinct missing extractions — what back-filling would dispatch.
 * remaining_insights_credits: The organisation's credits left in its current period.
 */
export type ContractManagementMissingExtractions = {
  /**
   * Contract Management Enabled
   */
  contract_management_enabled: boolean;
  /**
   * Calendars
   */
  calendars: number;
  /**
   * View Columns
   */
  view_columns: number;
  /**
   * Total
   */
  total: number;
  /**
   * Remaining Insights Credits
   */
  remaining_insights_credits: number;
};

/**
 * ContractManagementObligationCreate
 *
 * Schema for creating a new ContractManagementObligation.
 */
export type ContractManagementObligationCreate = {
  /**
   * Name
   */
  name: string;
  type: ContractManagementObligationType;
  /**
   * Trigger Event
   */
  trigger_event?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementObligationInDB
 */
export type ContractManagementObligationInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  type: ContractManagementObligationType;
  /**
   * Trigger Event
   */
  trigger_event?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementObligationSearchResult
 */
export type ContractManagementObligationSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementObligationInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementObligationType
 *
 * Types of obligations.
 */
export enum ContractManagementObligationType {
  DUTY = 'Duty',
  RIGHT = 'Right',
  CONDITION = 'Condition',
  WARRANTY = 'Warranty',
  REPRESENTATION = 'Representation',
  COVENANT = 'Covenant',
  OTHER = 'Other',
}

/**
 * ContractManagementObligationUpdate
 *
 * Schema for updating an existing ContractManagementObligation.
 */
export type ContractManagementObligationUpdate = {
  /**
   * Name
   */
  name?: string | null;
  type?: ContractManagementObligationType | null;
  /**
   * Trigger Event
   */
  trigger_event?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractManagementViewColumnBulkAddResult
 *
 * The outcome of adding a contract type's extraction prompts to a view as columns.
 *
 * Args:
 * created: The columns added, one per prompt whose question the view did not ask yet.
 * existing: The view's columns already asking the question of one of the prompts (those
 * prompts were skipped).
 */
export type ContractManagementViewColumnBulkAddResult = {
  /**
   * Created
   */
  created: Array<ContractManagementViewColumnInDb>;
  /**
   * Existing
   */
  existing: Array<ContractManagementViewColumnInDb>;
};

/**
 * ContractManagementViewColumnCreate
 *
 * Schema for creating a new ContractManagementViewColumn.
 *
 * Args:
 * name: The column's own name; None (or its prompt's display name): the column shows its
 * prompt's display name and follows prompt renames.
 */
export type ContractManagementViewColumnCreate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Order Index
   */
  order_index: number;
  filters?: AndCriterionInput;
};

/**
 * ContractManagementViewColumnInDB
 *
 * A view column.
 *
 * Args:
 * name: The column's own name; None: it follows its prompt's display name.
 * display_name: What users see: ``name``, else the prompt's display name.
 */
export type ContractManagementViewColumnInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Order Index
   */
  order_index: number;
  filters: AndCriterionOutput;
  extraction_prompt: ExtractionPromptInDb;
};

/**
 * ContractManagementViewColumnQuestionCreate
 *
 * The question a view column asks: a new one, or an existing extraction prompt.
 *
 * Exactly one form:
 * - ``prompt`` + ``response_type`` (+ ``example_data``): creates an extraction prompt named
 * ``name`` (None: after the column; one of them is required), linked to ``contract_type_uids``
 * (none: a standalone prompt);
 * - ``extraction_prompt_uid``: links an existing prompt of the view's organisation.
 */
export type ContractManagementViewColumnQuestionCreate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Prompt
   */
  prompt?: string | null;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type?:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType
    | null;
  /**
   * Contract Type Uids
   */
  contract_type_uids?: Array<string>;
  /**
   * Extraction Prompt Uid
   */
  extraction_prompt_uid?: string | null;
};

/**
 * ContractManagementViewColumnUpdate
 *
 * Schema for updating an existing ContractManagementViewColumn. ``name`` sent as null (or as
 * the prompt's display name) drops the column's own name.
 */
export type ContractManagementViewColumnUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Visible
   */
  visible?: boolean | null;
  /**
   * Order Index
   */
  order_index?: number | null;
  filters?: AndCriterionInput | null;
};

/**
 * ContractManagementViewCreate
 *
 * Schema for creating a new ContractManagementView.
 */
export type ContractManagementViewCreate = {
  /**
   * Contractual Party Column Name
   */
  contractual_party_column_name?: string;
  /**
   * Contractual Party Column Visible
   */
  contractual_party_column_visible?: boolean;
  /**
   * Contractual Party Column Order Index
   */
  contractual_party_column_order_index?: number;
  contractual_party_filters?: AndCriterionInput;
  /**
   * Matter Column Name
   */
  matter_column_name?: string;
  /**
   * Matter Column Visible
   */
  matter_column_visible?: boolean;
  /**
   * Matter Column Order Index
   */
  matter_column_order_index?: number;
  matter_filters?: AndCriterionInput;
  /**
   * Created At Column Name
   */
  created_at_column_name?: string;
  /**
   * Created At Column Visible
   */
  created_at_column_visible?: boolean;
  /**
   * Created At Column Order Index
   */
  created_at_column_order_index?: number;
  created_at_filters?: AndCriterionInput;
  /**
   * Document Type Column Name
   */
  document_type_column_name?: string;
  /**
   * Document Type Column Visible
   */
  document_type_column_visible?: boolean;
  /**
   * Document Type Column Order Index
   */
  document_type_column_order_index?: number;
  document_type_filters?: AndCriterionInput;
  /**
   * Contract Tags Column Name
   */
  contract_tags_column_name?: string;
  /**
   * Contract Tags Column Visible
   */
  contract_tags_column_visible?: boolean;
  /**
   * Contract Tags Column Order Index
   */
  contract_tags_column_order_index?: number;
  contract_tags_filters?: AndCriterionInput;
  /**
   * Owner Column Name
   */
  owner_column_name?: string;
  /**
   * Owner Column Visible
   */
  owner_column_visible?: boolean;
  /**
   * Owner Column Order Index
   */
  owner_column_order_index?: number;
  owner_filters?: AndCriterionInput;
  /**
   * Effective From Column Name
   */
  effective_from_column_name?: string;
  /**
   * Effective From Column Visible
   */
  effective_from_column_visible?: boolean;
  /**
   * Effective From Column Order Index
   */
  effective_from_column_order_index?: number;
  effective_from_filters?: AndCriterionInput;
  /**
   * Effective To Column Name
   */
  effective_to_column_name?: string;
  /**
   * Effective To Column Visible
   */
  effective_to_column_visible?: boolean;
  /**
   * Effective To Column Order Index
   */
  effective_to_column_order_index?: number;
  effective_to_filters?: AndCriterionInput;
  /**
   * State Column Name
   */
  state_column_name?: string;
  /**
   * State Column Visible
   */
  state_column_visible?: boolean;
  /**
   * State Column Order Index
   */
  state_column_order_index?: number;
  contract_state_filters?: AndCriterionInput;
  amendment_state_filters?: AndCriterionInput;
  name_filters?: AndCriterionInput;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  visibility?: AccessVisibility;
};

/**
 * ContractManagementViewCreateFromPreview
 *
 * Request body for creating a view from preview configuration.
 *
 * Extends ContractManagementViewCreate with the scope picks and dynamic columns.
 * This schema captures all the configuration from the preview wizard
 * and creates the actual view with all its columns, questions, and filters.
 *
 * Static column settings are inherited from ContractManagementViewCreate.
 */
export type ContractManagementViewCreateFromPreview = {
  /**
   * Contractual Party Column Name
   */
  contractual_party_column_name?: string;
  /**
   * Contractual Party Column Visible
   */
  contractual_party_column_visible?: boolean;
  /**
   * Contractual Party Column Order Index
   */
  contractual_party_column_order_index?: number;
  contractual_party_filters?: AndCriterionInput;
  /**
   * Matter Column Name
   */
  matter_column_name?: string;
  /**
   * Matter Column Visible
   */
  matter_column_visible?: boolean;
  /**
   * Matter Column Order Index
   */
  matter_column_order_index?: number;
  matter_filters?: AndCriterionInput;
  /**
   * Created At Column Name
   */
  created_at_column_name?: string;
  /**
   * Created At Column Visible
   */
  created_at_column_visible?: boolean;
  /**
   * Created At Column Order Index
   */
  created_at_column_order_index?: number;
  created_at_filters?: AndCriterionInput;
  /**
   * Document Type Column Name
   */
  document_type_column_name?: string;
  /**
   * Document Type Column Visible
   */
  document_type_column_visible?: boolean;
  /**
   * Document Type Column Order Index
   */
  document_type_column_order_index?: number;
  document_type_filters?: AndCriterionInput;
  /**
   * Contract Tags Column Name
   */
  contract_tags_column_name?: string;
  /**
   * Contract Tags Column Visible
   */
  contract_tags_column_visible?: boolean;
  /**
   * Contract Tags Column Order Index
   */
  contract_tags_column_order_index?: number;
  contract_tags_filters?: AndCriterionInput;
  /**
   * Owner Column Name
   */
  owner_column_name?: string;
  /**
   * Owner Column Visible
   */
  owner_column_visible?: boolean;
  /**
   * Owner Column Order Index
   */
  owner_column_order_index?: number;
  owner_filters?: AndCriterionInput;
  /**
   * Effective From Column Name
   */
  effective_from_column_name?: string;
  /**
   * Effective From Column Visible
   */
  effective_from_column_visible?: boolean;
  /**
   * Effective From Column Order Index
   */
  effective_from_column_order_index?: number;
  effective_from_filters?: AndCriterionInput;
  /**
   * Effective To Column Name
   */
  effective_to_column_name?: string;
  /**
   * Effective To Column Visible
   */
  effective_to_column_visible?: boolean;
  /**
   * Effective To Column Order Index
   */
  effective_to_column_order_index?: number;
  effective_to_filters?: AndCriterionInput;
  /**
   * State Column Name
   */
  state_column_name?: string;
  /**
   * State Column Visible
   */
  state_column_visible?: boolean;
  /**
   * State Column Order Index
   */
  state_column_order_index?: number;
  contract_state_filters?: AndCriterionInput;
  amendment_state_filters?: AndCriterionInput;
  name_filters?: AndCriterionInput;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  visibility?: AccessVisibility;
  /**
   * Source Directory Uids
   */
  source_directory_uids?: Array<string>;
  /**
   * Source Contract Uids
   */
  source_contract_uids?: Array<string>;
  /**
   * Columns
   */
  columns?: Array<ContractManagementViewPreviewColumn>;
};

/**
 * ContractManagementViewInDB
 */
export type ContractManagementViewInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  visibility: AccessVisibility;
  /**
   * Contractual Party Column Name
   */
  contractual_party_column_name: string;
  /**
   * Contractual Party Column Visible
   */
  contractual_party_column_visible: boolean;
  /**
   * Contractual Party Column Order Index
   */
  contractual_party_column_order_index: number;
  contractual_party_filters: AndCriterionOutput;
  /**
   * Matter Column Name
   */
  matter_column_name: string;
  /**
   * Matter Column Visible
   */
  matter_column_visible: boolean;
  /**
   * Matter Column Order Index
   */
  matter_column_order_index: number;
  matter_filters: AndCriterionOutput;
  /**
   * Created At Column Name
   */
  created_at_column_name: string;
  /**
   * Created At Column Visible
   */
  created_at_column_visible: boolean;
  /**
   * Created At Column Order Index
   */
  created_at_column_order_index: number;
  created_at_filters: AndCriterionOutput;
  /**
   * Document Type Column Name
   */
  document_type_column_name: string;
  /**
   * Document Type Column Visible
   */
  document_type_column_visible: boolean;
  /**
   * Document Type Column Order Index
   */
  document_type_column_order_index: number;
  document_type_filters: AndCriterionOutput;
  /**
   * Contract Tags Column Name
   */
  contract_tags_column_name: string;
  /**
   * Contract Tags Column Visible
   */
  contract_tags_column_visible: boolean;
  /**
   * Contract Tags Column Order Index
   */
  contract_tags_column_order_index: number;
  contract_tags_filters: AndCriterionOutput;
  /**
   * Owner Column Name
   */
  owner_column_name: string;
  /**
   * Owner Column Visible
   */
  owner_column_visible: boolean;
  /**
   * Owner Column Order Index
   */
  owner_column_order_index: number;
  owner_filters: AndCriterionOutput;
  /**
   * Effective From Column Name
   */
  effective_from_column_name: string;
  /**
   * Effective From Column Visible
   */
  effective_from_column_visible: boolean;
  /**
   * Effective From Column Order Index
   */
  effective_from_column_order_index: number;
  effective_from_filters: AndCriterionOutput;
  /**
   * Effective To Column Name
   */
  effective_to_column_name: string;
  /**
   * Effective To Column Visible
   */
  effective_to_column_visible: boolean;
  /**
   * Effective To Column Order Index
   */
  effective_to_column_order_index: number;
  effective_to_filters: AndCriterionOutput;
  /**
   * State Column Name
   */
  state_column_name: string;
  /**
   * State Column Visible
   */
  state_column_visible: boolean;
  /**
   * State Column Order Index
   */
  state_column_order_index: number;
  contract_state_filters: AndCriterionOutput;
  amendment_state_filters: AndCriterionOutput;
  name_filters: AndCriterionOutput;
  /**
   * Columns
   */
  columns: Array<ContractManagementViewColumnInDb>;
  owner_user?: UserInDb | null;
};

/**
 * ContractManagementViewPreviewColumn
 *
 * Dynamic column configuration for preview.
 *
 * Extends ContractManagementViewColumnCreate to add question configuration.
 * This represents a custom extraction column that the user wants to add
 * during the view creation wizard. No database records are created -
 * this is purely for preview purposes.
 *
 * Note: Static columns (contractual parties, matters, …) are ALWAYS
 * returned by default and configured separately via static_columns.
 */
export type ContractManagementViewPreviewColumn = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Order Index
   */
  order_index: number;
  filters?: AndCriterionInput;
  question: ContractManagementViewColumnQuestionCreate;
};

/**
 * ContractManagementViewPreviewColumnInfo
 *
 * Information about a column in the preview response.
 *
 * This provides metadata about each column so the frontend knows
 * how to render the table headers and handle column data.
 *
 * Static columns (contractual_party, matter, created_at, type, tags, owner) are ALWAYS
 * included in the response by default.
 */
export type ContractManagementViewPreviewColumnInfo = {
  /**
   * Name
   */
  name: string;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Order Index
   */
  order_index: number;
  /**
   * Kind
   */
  kind:
    | 'dynamic'
    | 'contractual_party'
    | 'matter'
    | 'created_at'
    | 'type'
    | 'tags'
    | 'owner'
    | 'effective_from'
    | 'effective_to'
    | 'state';
  question?: ExtractionPromptQuestion | null;
  filters?: AndCriterionOutput;
  amendment_state_filters?: AndCriterionOutput | null;
};

/**
 * ContractManagementViewPreviewRequest
 *
 * Request body for the view preview endpoint.
 *
 * This schema is used during the step-by-step view creation wizard to
 * preview how the view table will look. NO database records are created.
 *
 * Workflow:
 * - Step 2: User picks the scope - source_directory_uids (each with its whole subtree) and
 * source_contract_uids; picking nothing means every contract in the main vault
 * - Step 3: User can add dynamic columns and/or configure static columns
 *
 * Static columns (contractual parties, matters, …) are ALWAYS returned
 * by default. Users can modify their settings via static_columns, or
 * add new dynamic columns via the columns field.
 */
export type ContractManagementViewPreviewRequest = {
  /**
   * Source Directory Uids
   */
  source_directory_uids?: Array<string>;
  /**
   * Source Contract Uids
   */
  source_contract_uids?: Array<string>;
  /**
   * Columns
   */
  columns?: Array<ContractManagementViewPreviewColumn>;
  static_columns?: ContractManagementViewStaticColumnsBase | null;
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
};

/**
 * ContractManagementViewPreviewTableResponse
 *
 * Response for the view preview endpoint.
 *
 * This matches the structure of ContractManagementViewTable but is used
 * in the preview context. Since no actual extractions exist for preview
 * columns, dynamic column values will be None.
 *
 * The response structure allows the frontend to render the same grid table
 * component used for actual views.
 */
export type ContractManagementViewPreviewTableResponse = {
  /**
   * Results
   */
  results: Array<
    [
      ContractManagementContractInDb,
      Array<
        | ContractManagementExtractionInDb
        | Array<
            | ({
                kind: 'natural_person';
              } & ContractManagementContractualPartyNaturalPersonInDb)
            | ({
                kind: 'legal_entity';
              } & ContractManagementContractualPartyLegalEntityInDb)
          >
        | Array<ContractManagementMatterInDb>
        | Array<ContractManagementDateInDb>
        | string
        | string
        | Array<string>
        | UserInDb
        | ContractManagementContractStates
        | null
      >,
    ]
  >;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
  /**
   * Columns
   */
  columns: Array<ContractManagementViewPreviewColumnInfo>;
};

/**
 * ContractManagementViewSearchResult
 */
export type ContractManagementViewSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementViewInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementViewStaticColumnsBase
 *
 * Base schema for static column configuration.
 *
 * Static columns (contractual parties, matters, created at, …) are built-in columns
 * that are always present in views. This base class defines their configuration
 * and is reused by both view creation and preview schemas.
 */
export type ContractManagementViewStaticColumnsBase = {
  /**
   * Contractual Party Column Name
   */
  contractual_party_column_name?: string;
  /**
   * Contractual Party Column Visible
   */
  contractual_party_column_visible?: boolean;
  /**
   * Contractual Party Column Order Index
   */
  contractual_party_column_order_index?: number;
  contractual_party_filters?: AndCriterionInput;
  /**
   * Matter Column Name
   */
  matter_column_name?: string;
  /**
   * Matter Column Visible
   */
  matter_column_visible?: boolean;
  /**
   * Matter Column Order Index
   */
  matter_column_order_index?: number;
  matter_filters?: AndCriterionInput;
  /**
   * Created At Column Name
   */
  created_at_column_name?: string;
  /**
   * Created At Column Visible
   */
  created_at_column_visible?: boolean;
  /**
   * Created At Column Order Index
   */
  created_at_column_order_index?: number;
  created_at_filters?: AndCriterionInput;
  /**
   * Document Type Column Name
   */
  document_type_column_name?: string;
  /**
   * Document Type Column Visible
   */
  document_type_column_visible?: boolean;
  /**
   * Document Type Column Order Index
   */
  document_type_column_order_index?: number;
  document_type_filters?: AndCriterionInput;
  /**
   * Contract Tags Column Name
   */
  contract_tags_column_name?: string;
  /**
   * Contract Tags Column Visible
   */
  contract_tags_column_visible?: boolean;
  /**
   * Contract Tags Column Order Index
   */
  contract_tags_column_order_index?: number;
  contract_tags_filters?: AndCriterionInput;
  /**
   * Owner Column Name
   */
  owner_column_name?: string;
  /**
   * Owner Column Visible
   */
  owner_column_visible?: boolean;
  /**
   * Owner Column Order Index
   */
  owner_column_order_index?: number;
  owner_filters?: AndCriterionInput;
  /**
   * Effective From Column Name
   */
  effective_from_column_name?: string;
  /**
   * Effective From Column Visible
   */
  effective_from_column_visible?: boolean;
  /**
   * Effective From Column Order Index
   */
  effective_from_column_order_index?: number;
  effective_from_filters?: AndCriterionInput;
  /**
   * Effective To Column Name
   */
  effective_to_column_name?: string;
  /**
   * Effective To Column Visible
   */
  effective_to_column_visible?: boolean;
  /**
   * Effective To Column Order Index
   */
  effective_to_column_order_index?: number;
  effective_to_filters?: AndCriterionInput;
  /**
   * State Column Name
   */
  state_column_name?: string;
  /**
   * State Column Visible
   */
  state_column_visible?: boolean;
  /**
   * State Column Order Index
   */
  state_column_order_index?: number;
  contract_state_filters?: AndCriterionInput;
  amendment_state_filters?: AndCriterionInput;
  name_filters?: AndCriterionInput;
};

/**
 * ContractManagementViewTable
 */
export type ContractManagementViewTable = {
  /**
   * Results
   */
  results: Array<
    [
      ContractManagementContractInDb,
      Array<
        | ContractManagementExtractionInDb
        | Array<
            | ({
                kind: 'natural_person';
              } & ContractManagementContractualPartyNaturalPersonInDb)
            | ({
                kind: 'legal_entity';
              } & ContractManagementContractualPartyLegalEntityInDb)
          >
        | Array<ContractManagementMatterInDb>
        | Array<ContractManagementDateInDb>
        | string
        | string
        | Array<string>
        | UserInDb
        | ContractManagementContractStates
        | null
      >,
    ]
  >;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractManagementViewTableContractFiles
 */
export type ContractManagementViewTableContractFiles = {
  /**
   * Results
   */
  results: Array<
    [
      ContractManagementContractFileInDb,
      Array<
        | ContractManagementExtractionInDb
        | Array<
            | ({
                kind: 'natural_person';
              } & ContractManagementContractualPartyNaturalPersonInDb)
            | ({
                kind: 'legal_entity';
              } & ContractManagementContractualPartyLegalEntityInDb)
          >
        | Array<ContractManagementDateInDb>
        | string
        | ContractManagementContractFileType
        | EffectiveState
        | null
      >,
    ]
  >;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractManagementViewTableFilterOptions
 *
 * The distinct filter values per column of a view's table (dynamic columns keyed by uid):
 * independent of the page, sort and filters, so fetched once per view, not with every page.
 */
export type ContractManagementViewTableFilterOptions = {
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementViewUpdate
 *
 * Schema for updating an existing ContractManagementView.
 */
export type ContractManagementViewUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  visibility?: AccessVisibility | null;
  /**
   * Contractual Party Column Name
   */
  contractual_party_column_name?: string | null;
  /**
   * Contractual Party Column Visible
   */
  contractual_party_column_visible?: boolean | null;
  /**
   * Contractual Party Column Order Index
   */
  contractual_party_column_order_index?: number | null;
  contractual_party_filters?: AndCriterionInput | null;
  /**
   * Matter Column Name
   */
  matter_column_name?: string | null;
  /**
   * Matter Column Visible
   */
  matter_column_visible?: boolean | null;
  /**
   * Matter Column Order Index
   */
  matter_column_order_index?: number | null;
  matter_filters?: AndCriterionInput | null;
  /**
   * Created At Column Name
   */
  created_at_column_name?: string | null;
  /**
   * Created At Column Visible
   */
  created_at_column_visible?: boolean | null;
  /**
   * Created At Column Order Index
   */
  created_at_column_order_index?: number | null;
  created_at_filters?: AndCriterionInput | null;
  /**
   * Document Type Column Name
   */
  document_type_column_name?: string | null;
  /**
   * Document Type Column Visible
   */
  document_type_column_visible?: boolean | null;
  /**
   * Document Type Column Order Index
   */
  document_type_column_order_index?: number | null;
  document_type_filters?: AndCriterionInput | null;
  /**
   * Contract Tags Column Name
   */
  contract_tags_column_name?: string | null;
  /**
   * Contract Tags Column Visible
   */
  contract_tags_column_visible?: boolean | null;
  /**
   * Contract Tags Column Order Index
   */
  contract_tags_column_order_index?: number | null;
  contract_tags_filters?: AndCriterionInput | null;
  /**
   * Owner Column Name
   */
  owner_column_name?: string | null;
  /**
   * Owner Column Visible
   */
  owner_column_visible?: boolean | null;
  /**
   * Owner Column Order Index
   */
  owner_column_order_index?: number | null;
  owner_filters?: AndCriterionInput | null;
  /**
   * Effective From Column Name
   */
  effective_from_column_name?: string | null;
  /**
   * Effective From Column Visible
   */
  effective_from_column_visible?: boolean | null;
  /**
   * Effective From Column Order Index
   */
  effective_from_column_order_index?: number | null;
  effective_from_filters?: AndCriterionInput | null;
  /**
   * Effective To Column Name
   */
  effective_to_column_name?: string | null;
  /**
   * Effective To Column Visible
   */
  effective_to_column_visible?: boolean | null;
  /**
   * Effective To Column Order Index
   */
  effective_to_column_order_index?: number | null;
  effective_to_filters?: AndCriterionInput | null;
  /**
   * State Column Name
   */
  state_column_name?: string | null;
  /**
   * State Column Visible
   */
  state_column_visible?: boolean | null;
  /**
   * State Column Order Index
   */
  state_column_order_index?: number | null;
  contract_state_filters?: AndCriterionInput | null;
  amendment_state_filters?: AndCriterionInput | null;
  name_filters?: AndCriterionInput | null;
};

/**
 * ContractRoleShareInDB
 *
 * Schema for contract role share data as stored in database.
 *
 * Represents a share relationship between a contract and a role with specific permissions.
 *
 * Attributes:
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * permissions: Access permission level for this share
 * contract: The shared contract
 * role: The role that has access to the contract
 */
export type ContractRoleShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  contract: ContractManagementContractInDb;
  role: PermissionRoleInDb;
};

/**
 * ContractRoleShareInDBContract
 *
 * Schema for contract role share when querying by contract.
 *
 * Includes the role data for each share.
 *
 * Attributes:
 * role: The role that has access to the contract
 */
export type ContractRoleShareInDbContract = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  role: PermissionRoleInDb;
};

/**
 * ContractRoleShareInDBRole
 *
 * Schema for contract role share when querying by role.
 *
 * Includes the contract data for each share.
 *
 * Attributes:
 * contract: The shared contract
 */
export type ContractRoleShareInDbRole = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  contract: ContractManagementContractInDb;
};

/**
 * ContractRoleShareSearchResultContract
 */
export type ContractRoleShareSearchResultContract = {
  /**
   * Results
   */
  results: Array<ContractRoleShareInDbContract>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractRoleShareSearchResultRole
 */
export type ContractRoleShareSearchResultRole = {
  /**
   * Results
   */
  results: Array<ContractRoleShareInDbRole>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractRoleShareUpdate
 *
 * Schema for updating an existing contract role share.
 *
 * All fields are optional to allow partial updates.
 */
export type ContractRoleShareUpdate = {
  permissions?: AccessPermission | null;
};

/**
 * ContractStatistics
 */
export type ContractStatistics = {
  /**
   * Total Contracts
   */
  total_contracts: number;
  /**
   * Average Contract File Count Per Contract
   */
  average_contract_file_count_per_contract: number;
  /**
   * Average Extraction Count Per Contract File
   */
  average_extraction_count_per_contract_file: number;
  /**
   * Total Matters
   */
  total_matters: number;
  /**
   * Average Contract Count Per Matter
   */
  average_contract_count_per_matter: number;
  /**
   * Total User Shares
   */
  total_user_shares: number;
  /**
   * Total Role Shares
   */
  total_role_shares: number;
};

/**
 * ContractTypeClauseSearchResult
 */
export type ContractTypeClauseSearchResult = {
  /**
   * Results
   */
  results: Array<ClauseSlim>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractTypeContractSearchResult
 */
export type ContractTypeContractSearchResult = {
  /**
   * Results
   */
  results: Array<ContractManagementContractInDbSlim>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractTypeCreate
 *
 * An organisation-created contract type.
 *
 * Args:
 * name: Unique within the organisation; saved view and calendar filters match on it.
 * description: What documents the type covers; the contract classifier reads it.
 */
export type ContractTypeCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string;
};

/**
 * ContractTypeExtractionEntry
 *
 * One extraction prompt with a contract file's answer to it — of the contract's type, or a
 * standalone one (``standalone-extractions``, always answered).
 *
 * Args:
 * extraction: The revision's extraction asking the prompt's question; None while it was
 * not asked (yet).
 */
export type ContractTypeExtractionEntry = {
  extraction_prompt: ExtractionPromptInDb;
  extraction?: ContractManagementExtractionInDb | null;
};

/**
 * ContractTypeExtractions
 *
 * A contract file's answers to its contract type's extraction prompts.
 *
 * Args:
 * contract_type: The contract's type; None for an untyped contract (no results).
 */
export type ContractTypeExtractions = {
  contract_type?: ContractTypeRef | null;
  /**
   * Results
   */
  results?: Array<ContractTypeExtractionEntry>;
};

/**
 * ContractTypeInDB
 *
 * A contract type.
 *
 * Args:
 * extraction_prompt_count: Extraction prompts linked to the type, or None when not computed
 * for this response (only populated by the list and search endpoints).
 */
export type ContractTypeInDb = {
  /**
   * Uid
   */
  uid: string;
  key?: ContractTypeTemplateKey | null;
  /**
   * Name
   */
  name: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Description
   */
  description: string;
  /**
   * Extraction Prompt Count
   */
  extraction_prompt_count?: number | null;
};

/**
 * ContractTypePlaybookSearchResult
 */
export type ContractTypePlaybookSearchResult = {
  /**
   * Results
   */
  results: Array<PlaybookInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractTypeRef
 *
 * A contract type as embedded in contracts, playbooks and clauses.
 *
 * Args:
 * key: The code template the type was copied from; None for organisation-created types.
 */
export type ContractTypeRef = {
  /**
   * Uid
   */
  uid: string;
  key?: ContractTypeTemplateKey | null;
  /**
   * Name
   */
  name: string;
};

/**
 * ContractTypeSearchResult
 */
export type ContractTypeSearchResult = {
  /**
   * Results
   */
  results: Array<ContractTypeInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractTypeTemplateKey
 *
 * Keys of the code-defined default contract types (``core/contract_types/templates.py``).
 *
 * They identify a template, never an organisation's row: an org's copy carries the key in
 * ``cm_contract_type.key``, and global Gaia clauses reference contract types by key.
 */
export enum ContractTypeTemplateKey {
  MSA = 'msa',
  NDA = 'nda',
  MUTUAL_NDA = 'mutual_nda',
  UNILATERAL_NDA = 'unilateral_nda',
  EMPLOYMENT = 'employment',
  OFFER_LETTER = 'offer_letter',
  TERMINATION_AGREEMENT = 'termination_agreement',
  CONTRACTOR = 'contractor',
  LEASE = 'lease',
  PURCHASE = 'purchase',
  TERMS_AND_CONDITIONS = 'terms_and_conditions',
  SERVICE = 'service',
  OUTSOURCING_AND_AGENCY_AGREEMENT = 'outsourcing_and_agency_agreement',
  MARKETING_AND_SPONSORSHIP_AGREEMENT = 'marketing_and_sponsorship_agreement',
  DIGITAL_SERVICES_AGREEMENT = 'digital_services_agreement',
  E_COMMERCE_AGREEMENT = 'e_commerce_agreement',
  LICENSING = 'licensing',
  SOFTWARE_AND_IP_LICENSE = 'software_and_ip_license',
  DISTRIBUTION = 'distribution',
  DPA = 'dpa',
  DATA_SECURITY = 'data_security',
  POWER_OF_ATTORNEY = 'power_of_attorney',
  JOINT_VENTURE = 'joint_venture',
  PRIVATE_EQUITY = 'private_equity',
  INCORPORATION_DOCUMENT = 'incorporation_document',
  BOARD_RESOLUTION = 'board_resolution',
  PARTNERSHIP = 'partnership',
  ACQUISITION = 'acquisition',
  MERGER = 'merger',
  OTHER = 'other',
}

/**
 * ContractTypeUpdate
 *
 * Partial update of a contract type; renaming it re-syncs the calendars.
 */
export type ContractTypeUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * ContractTypeUsage
 *
 * What references a contract type, so the frontend can warn before deleting it.
 *
 * Args:
 * sole_type_playbook_count: Playbooks linked to this type only; they are left without a
 * type (i.e. unrestricted) when it is deleted without a replacement.
 * sole_type_clause_count: The same for clauses.
 * extraction_prompt_count: Extraction prompts linked to the type; they stay (standalone, or
 * with their other types) when it is deleted without a replacement.
 */
export type ContractTypeUsage = {
  /**
   * Contract Count
   */
  contract_count: number;
  /**
   * Playbook Count
   */
  playbook_count: number;
  /**
   * Clause Count
   */
  clause_count: number;
  /**
   * Sole Type Playbook Count
   */
  sole_type_playbook_count: number;
  /**
   * Sole Type Clause Count
   */
  sole_type_clause_count: number;
  /**
   * Extraction Prompt Count
   */
  extraction_prompt_count: number;
};

/**
 * ContractUserShareInDB
 *
 * Schema for contract user share data as stored in database.
 *
 * Represents a share relationship between a contract and a user with specific permissions.
 *
 * Attributes:
 * permissions: Access permission level for this share
 * contract: The shared contract
 * user: The user who has access to the contract
 */
export type ContractUserShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
  contract: ContractManagementContractInDb;
};

/**
 * ContractUserShareInDBContract
 *
 * Schema for contract user share data as stored in database.
 *
 * Represents a share relationship between a contract and a user with specific permissions.
 *
 * Attributes:
 * permissions: Access permission level for this share
 * contract: The shared contract
 * user: The user who has access to the contract
 */
export type ContractUserShareInDbContract = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
};

/**
 * ContractUserShareInDBUser
 *
 * Schema for contract user share data as stored in database.
 *
 * Represents a share relationship between a contract and a user with specific permissions.
 *
 * Attributes:
 * permissions: Access permission level for this share
 * contract: The shared contract
 * user: The user who has access to the contract
 */
export type ContractUserShareInDbUser = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  contract: ContractManagementContractInDb;
};

/**
 * ContractUserShareSearchResultContract
 */
export type ContractUserShareSearchResultContract = {
  /**
   * Results
   */
  results: Array<ContractUserShareInDbContract>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractUserShareSearchResultUser
 */
export type ContractUserShareSearchResultUser = {
  /**
   * Results
   */
  results: Array<ContractUserShareInDbUser>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractUserShareUpdate
 *
 * Schema for updating an existing contract user share.
 *
 * All fields are optional to allow partial updates.
 */
export type ContractUserShareUpdate = {
  permissions?: AccessPermission | null;
};

/**
 * ContractualParty
 */
export type ContractualParty = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * ContractualPartyBulkShare
 *
 * Schema for bulk sharing contractual parties with users and roles.
 */
export type ContractualPartyBulkShare = {
  /**
   * Contractual Party Uids
   */
  contractual_party_uids?: Array<string>;
  /**
   * User Uids
   */
  user_uids?: Array<string>;
  /**
   * Role Uids
   */
  role_uids?: Array<string>;
  permissions: AccessPermission;
};

/**
 * ContractualPartyCriterion
 *
 * Filter by a contract's contractual parties, over the columns every party kind has (the subtype fields
 * would need a polymorphic join).
 */
export type ContractualPartyCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'contractual_party';
  name?: StringCriterion | null;
  kind?: StringCriterion | null;
  city?: StringCriterion | null;
  iso_3166_alpha_3?: StringCriterion | null;
};

/**
 * ContractualPartyRoleShareInDB
 *
 * Schema for contractual party role share data as stored in database.
 *
 * Represents a share relationship between a contractual party and a role with specific permissions.
 *
 * Attributes:
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * permissions: Access permission level for this share
 * contractual_party: The shared contractual party
 * role: The role that has access to the contractual party
 */
export type ContractualPartyRoleShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  /**
   * Contractual Party
   */
  contractual_party:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
  role: PermissionRoleInDb;
};

/**
 * ContractualPartyRoleShareInDBContractualParty
 *
 * Schema for contractual party role share when querying by contractual party.
 *
 * Includes the role data for each share.
 *
 * Attributes:
 * role: The role that has access to the contractual party
 */
export type ContractualPartyRoleShareInDbContractualParty = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  role: PermissionRoleInDb;
};

/**
 * ContractualPartyRoleShareInDBRole
 *
 * Schema for contractual party role share when querying by role.
 *
 * Includes the contractual party data for each share.
 *
 * Attributes:
 * contractual_party: The shared contractual party
 */
export type ContractualPartyRoleShareInDbRole = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  /**
   * Contractual Party
   */
  contractual_party:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

/**
 * ContractualPartyRoleShareSearchResultContractualParty
 */
export type ContractualPartyRoleShareSearchResultContractualParty = {
  /**
   * Results
   */
  results: Array<ContractualPartyRoleShareInDbContractualParty>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractualPartyRoleShareSearchResultRole
 */
export type ContractualPartyRoleShareSearchResultRole = {
  /**
   * Results
   */
  results: Array<ContractualPartyRoleShareInDbRole>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractualPartyRoleShareUpdate
 *
 * Schema for updating an existing contractual party role share.
 *
 * All fields are optional to allow partial updates.
 */
export type ContractualPartyRoleShareUpdate = {
  permissions?: AccessPermission | null;
};

/**
 * ContractualPartyUserShareInDB
 *
 * Schema for contractual party user share data as stored in database.
 *
 * Represents a share relationship between a contractual party and a user with specific permissions.
 *
 * Attributes:
 * permissions: Access permission level for this share
 * contractual_party: The shared contractual party
 * user: The user who has access to the contractual party
 */
export type ContractualPartyUserShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
  /**
   * Contractual Party
   */
  contractual_party:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

/**
 * ContractualPartyUserShareInDBContractualParty
 *
 * Schema for contractual party user share data as stored in database.
 *
 * Represents a share relationship between a contractual party and a user with specific permissions.
 *
 * Attributes:
 * permissions: Access permission level for this share
 * contractual_party: The shared contractual party
 * user: The user who has access to the contractual party
 */
export type ContractualPartyUserShareInDbContractualParty = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
};

/**
 * ContractualPartyUserShareInDBUser
 *
 * Schema for contractual party user share data as stored in database.
 *
 * Represents a share relationship between a contractual party and a user with specific permissions.
 *
 * Attributes:
 * permissions: Access permission level for this share
 * contractual_party: The shared contractual party
 * user: The user who has access to the contractual party
 */
export type ContractualPartyUserShareInDbUser = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  /**
   * Contractual Party
   */
  contractual_party:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

/**
 * ContractualPartyUserShareSearchResultContractualParty
 */
export type ContractualPartyUserShareSearchResultContractualParty = {
  /**
   * Results
   */
  results: Array<ContractualPartyUserShareInDbContractualParty>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractualPartyUserShareSearchResultUser
 */
export type ContractualPartyUserShareSearchResultUser = {
  /**
   * Results
   */
  results: Array<ContractualPartyUserShareInDbUser>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractualPartyUserShareUpdate
 *
 * Schema for updating an existing contractual party user share.
 *
 * All fields are optional to allow partial updates.
 */
export type ContractualPartyUserShareUpdate = {
  permissions?: AccessPermission | null;
};

/**
 * ConvertFileToContractAgentTaskInDB
 *
 * Read schema for the file → contract conversion subtype.
 */
export type ConvertFileToContractAgentTaskInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: OrganisationAgentTaskType;
  status: TaskStatus;
  /**
   * Kill Switch
   */
  kill_switch: boolean;
  /**
   * Started By
   */
  started_by?: string | null;
  /**
   * Error Message
   */
  error_message?: string | null;
  started_by_user?: UserInDb | null;
  /**
   * Started By User Uid
   */
  started_by_user_uid?: string | null;
  /**
   * Chat Thread Uid
   */
  chat_thread_uid?: string | null;
  chat_backend?: ChatBackend | null;
  /**
   * Vault File Uid
   */
  vault_file_uid?: string | null;
  /**
   * Contract Uid
   */
  contract_uid?: string | null;
};

/**
 * Country
 *
 * One selectable country: the stored ISO 3166 alpha-3 code and its English short name.
 */
export type Country = {
  /**
   * Iso 3166 Alpha 3
   */
  iso_3166_alpha_3: string;
  /**
   * Name
   */
  name: string;
};

/**
 * CreateAgentRunRequest
 *
 * Schema for creating a new agent run via API.
 *
 * This schema defines the request structure for triggering an agent workflow
 * programmatically through the API endpoint.
 */
export type CreateAgentRunRequest = {
  /**
   * File Urls
   */
  file_urls?: Array<string>;
  /**
   * From User Email
   */
  from_user_email: string;
  /**
   * Title
   */
  title: string;
};

/**
 * CreateParticipants
 */
export type CreateParticipants = {
  /**
   * Participants
   */
  participants: Array<ParticipantCreate>;
};

export type CriterionInput = LogicCriterionInput | ValueCriterion | ListCriterion;

export type CriterionOutput = LogicCriterionOutput | ValueCriterion | ListCriterion;

/**
 * CurrencyAmount
 */
export type CurrencyAmount = {
  /**
   * Amount
   */
  amount: number;
  /**
   * Iso 4217 Alpha 3
   */
  iso_4217_alpha_3:
    | 'AED'
    | 'AFN'
    | 'ALL'
    | 'AMD'
    | 'AOA'
    | 'ARS'
    | 'AUD'
    | 'AWG'
    | 'AZN'
    | 'BAM'
    | 'BBD'
    | 'BDT'
    | 'BHD'
    | 'BIF'
    | 'BMD'
    | 'BND'
    | 'BOB'
    | 'BOV'
    | 'BRL'
    | 'BSD'
    | 'BTN'
    | 'BWP'
    | 'BYN'
    | 'BZD'
    | 'CAD'
    | 'CDF'
    | 'CHE'
    | 'CHF'
    | 'CHW'
    | 'CLF'
    | 'CLP'
    | 'CNY'
    | 'COP'
    | 'COU'
    | 'CRC'
    | 'CUP'
    | 'CVE'
    | 'CZK'
    | 'DJF'
    | 'DKK'
    | 'DOP'
    | 'DZD'
    | 'EGP'
    | 'ERN'
    | 'ETB'
    | 'EUR'
    | 'FJD'
    | 'FKP'
    | 'GBP'
    | 'GEL'
    | 'GHS'
    | 'GIP'
    | 'GMD'
    | 'GNF'
    | 'GTQ'
    | 'GYD'
    | 'HKD'
    | 'HNL'
    | 'HTG'
    | 'HUF'
    | 'IDR'
    | 'ILS'
    | 'INR'
    | 'IQD'
    | 'IRR'
    | 'ISK'
    | 'JMD'
    | 'JOD'
    | 'JPY'
    | 'KES'
    | 'KGS'
    | 'KHR'
    | 'KMF'
    | 'KPW'
    | 'KRW'
    | 'KWD'
    | 'KYD'
    | 'KZT'
    | 'LAK'
    | 'LBP'
    | 'LKR'
    | 'LRD'
    | 'LSL'
    | 'LYD'
    | 'MAD'
    | 'MDL'
    | 'MGA'
    | 'MKD'
    | 'MMK'
    | 'MNT'
    | 'MOP'
    | 'MRU'
    | 'MUR'
    | 'MVR'
    | 'MWK'
    | 'MXN'
    | 'MXV'
    | 'MYR'
    | 'MZN'
    | 'NAD'
    | 'NGN'
    | 'NIO'
    | 'NOK'
    | 'NPR'
    | 'NZD'
    | 'OMR'
    | 'PAB'
    | 'PEN'
    | 'PGK'
    | 'PHP'
    | 'PKR'
    | 'PLN'
    | 'PYG'
    | 'QAR'
    | 'RON'
    | 'RSD'
    | 'RUB'
    | 'RWF'
    | 'SAR'
    | 'SBD'
    | 'SCR'
    | 'SDG'
    | 'SEK'
    | 'SGD'
    | 'SHP'
    | 'SLE'
    | 'SOS'
    | 'SRD'
    | 'SSP'
    | 'STN'
    | 'SVC'
    | 'SYP'
    | 'SZL'
    | 'THB'
    | 'TJS'
    | 'TMT'
    | 'TND'
    | 'TOP'
    | 'TRY'
    | 'TTD'
    | 'TWD'
    | 'TZS'
    | 'UAH'
    | 'UGX'
    | 'USD'
    | 'USN'
    | 'UYI'
    | 'UYU'
    | 'UYW'
    | 'UZS'
    | 'VED'
    | 'VES'
    | 'VND'
    | 'VUV'
    | 'WST'
    | 'XAD'
    | 'XAF'
    | 'XAG'
    | 'XAU'
    | 'XBA'
    | 'XBB'
    | 'XBC'
    | 'XBD'
    | 'XCD'
    | 'XCG'
    | 'XDR'
    | 'XOF'
    | 'XPD'
    | 'XPF'
    | 'XPT'
    | 'XSU'
    | 'XTS'
    | 'XUA'
    | 'XXX'
    | 'YER'
    | 'ZAR'
    | 'ZMW'
    | 'ZWG'
    | null;
};

/**
 * CurrencyAmountCriterion
 */
export type CurrencyAmountCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'currency_amount';
  amount?: NumberCriterion | null;
  iso_4217_alpha_3?: StringCriterion | null;
};

/**
 * DateCriterion
 *
 * Filter by a contract-file fixed-type date with field-level comparisons, like MatterCriterion.
 *
 * The date type is not part of the criterion: each view column (effective_from, effective_to) filters its own type.
 */
export type DateCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'date';
  date?: TimeCriterion | null;
  description?: StringCriterion | null;
};

/**
 * DemoDataArea
 *
 * App area an admin can seed demo data into.
 */
export enum DemoDataArea {
  SIGNING = 'signing',
  CONTRACT_MANAGEMENT = 'contract_management',
  REVIEWS = 'reviews',
  REVIEW_PROJECTS = 'review_projects',
  PLAYBOOKS = 'playbooks',
  CONTRACT_GENERATION = 'contract_generation',
  CHAT = 'chat',
  ROLES = 'roles',
  PROMPTS = 'prompts',
}

/**
 * DemoDataSeedPayload
 *
 * Self-describing progress / terminal payload for a DEMO_DATA_SEED_UPDATED event.
 *
 * `uid` is the synthetic `seed_uid` the client subscribes on. `areas` (the requested areas in
 * execution order) is carried on every event, so a client that reconnects mid-run — or a second
 * tab — can render full progress from any single event. While seeding, `status` is `"running"`
 * and `completed_areas` drives the progress bar. On success `status` is `"done"`; on failure
 * `status` is `"failed"` and `error` carries a short message.
 */
export type DemoDataSeedPayload = {
  /**
   * Uid
   */
  uid: string;
  status: TaskStatus;
  /**
   * Areas
   */
  areas: Array<DemoDataArea>;
  /**
   * Completed Areas
   */
  completed_areas: Array<DemoDataArea>;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * DemoDataSeedRequest
 *
 * Areas of the application to seed demo data into.
 */
export type DemoDataSeedRequest = {
  /**
   * Areas
   */
  areas: Array<DemoDataArea>;
};

/**
 * DemoDataSeedStartResponse
 *
 * The started seeding run: its synthetic uid to subscribe on and the areas in execution order.
 */
export type DemoDataSeedStartResponse = {
  /**
   * Seed Uid
   */
  seed_uid: string;
  /**
   * Areas
   */
  areas: Array<DemoDataArea>;
};

/**
 * DemoDataStatusResponse
 *
 * Which seedable areas of an organisation currently hold demo data.
 */
export type DemoDataStatusResponse = {
  /**
   * Organisation Uid
   */
  organisation_uid: string;
  /**
   * Areas
   */
  areas: {
    [key in DemoDataArea]?: boolean;
  };
};

/**
 * DocumentBlock
 *
 * One page-tagged text block of a document parsed by the frontend.
 *
 * Word-sourced runs arrive as a list of these instead of one flat blob, so the run's input can be
 * rendered into per-page texts (see ``document_blocks_to_pagetexts``) and findings can be grounded
 * on a page number. ``page_number`` is the frontend's own pagination of the document.
 */
export type DocumentBlock = {
  /**
   * Text
   */
  text: string;
  /**
   * Page Number
   */
  page_number: number;
};

/**
 * DocumentState
 *
 * Where a vault file revision stands as a document — stored on the revision.
 *
 * Moved by system events (upload, signature detection, signing requests) and by user overrides;
 * ``ContractStateResolver`` holds the rules. Once ``SIGNED``, the computed ``TimeState`` speaks for it.
 */
export enum DocumentState {
  NEEDS_ATTENTION = 'needs_attention',
  PROCESSING = 'processing',
  IN_DRAFT = 'in_draft',
  READY_TO_SEND_FOR_SIGNATURE = 'ready_to_send_for_signature',
  SENDING_FOR_SIGNATURE = 'sending_for_signature',
  WAITING_FOR_SIGNATURE = 'waiting_for_signature',
  SIGNATURE_FAILED = 'signature_failed',
  SIGNED = 'signed',
  SIGNATURE_REJECTED = 'signature_rejected',
  SIGNATURE_EXPIRED = 'signature_expired',
  TERMINATED = 'terminated',
  VOIDED = 'voided',
}

/**
 * DraftingStatistics
 *
 * Admin-level contract-drafting statistics.
 *
 * Template-side counts (templates, variants, questions) are scoped to rows created in the
 * org/time window; draft-side counts to drafts created in it. ``total_saved_drafts`` counts the drafts among
 * them that were saved into a stored document since.
 */
export type DraftingStatistics = {
  /**
   * Total Templates
   */
  total_templates: number;
  /**
   * Total Template Variants
   */
  total_template_variants: number;
  /**
   * Average Variants Per Template
   */
  average_variants_per_template: number;
  /**
   * Total Drafts
   */
  total_drafts: number;
  /**
   * Average Drafts Per Template
   */
  average_drafts_per_template: number;
  /**
   * Total Saved Drafts
   */
  total_saved_drafts: number;
  /**
   * Total Template Questions
   */
  total_template_questions: number;
  /**
   * Average Questions Per Template
   */
  average_questions_per_template: number;
};

/**
 * EffectiveState
 *
 * ``DocumentState`` with ``SIGNED`` replaced by the ``TimeState`` members — what a file or contract shows.
 *
 * Redeclares the members (Python enums with members cannot be subclassed); the string values are
 * shared, so members compare equal and hash identically across the three enums.
 */
export enum EffectiveState {
  NEEDS_ATTENTION = 'needs_attention',
  PROCESSING = 'processing',
  IN_DRAFT = 'in_draft',
  READY_TO_SEND_FOR_SIGNATURE = 'ready_to_send_for_signature',
  SENDING_FOR_SIGNATURE = 'sending_for_signature',
  WAITING_FOR_SIGNATURE = 'waiting_for_signature',
  SIGNATURE_FAILED = 'signature_failed',
  SIGNATURE_REJECTED = 'signature_rejected',
  SIGNATURE_EXPIRED = 'signature_expired',
  NOT_YET_ACTIVE = 'not_yet_active',
  ACTIVE = 'active',
  EXPIRED = 'expired',
  TERMINATED = 'terminated',
  VOIDED = 'voided',
}

/**
 * EmailSubmitRequest
 */
export type EmailSubmitRequest = {
  /**
   * Items
   */
  items: Array<{
    [key: string]: unknown;
  }>;
};

/**
 * EmailVerificationConfirm
 *
 * Request body for confirming an email verification token.
 */
export type EmailVerificationConfirm = {
  /**
   * Token
   */
  token: string;
};

/**
 * Entity
 */
export type Entity = {
  /**
   * Name
   *
   * Name of the contractual party without legal type like GmbH or Inc. (e.g. Gaia)
   */
  name: string;
  /**
   * Form Of Entity
   *
   * The legal type of the Entity (e.g, GmbH)
   */
  form_of_entity?: string | null;
  /**
   * Legal Name
   *
   * Name + Form of Entity (e.g. Gaia GmbH)
   */
  legal_name: string | null;
  /**
   * Address
   *
   * The address of the contractual party
   */
  address: string | null;
  /**
   * Email
   *
   * The email address of the contractual party
   */
  email: string | null;
  /**
   * Natural Person
   *
   * Whether the contractual party is a natural Person?
   */
  natural_person: boolean | null;
  /**
   * Commercial Register
   *
   * The commercial register name of the contractual party
   */
  commercial_register?: string | null;
  /**
   * Registration Number
   *
   * The register number of the contractual party
   */
  registration_number?: string | null;
  /**
   * Represent By
   *
   * The person that represents the legal entity
   */
  represent_by?: string | null;
  /**
   * Recipient
   *
   * Is the entity the receiving party in a contract?
   */
  recipient: boolean | null;
  /**
   * Signature Date
   *
   * The date this party signed the contract
   */
  signature_date: string | null;
  /**
   * Date Of Birth
   *
   * The date of birth of the contractual party
   */
  date_of_birth: string | null;
  /**
   * Country Code
   *
   * The country code for the legal entity (ISO 3166-1 alpha-2)
   */
  country_code?: string | null;
};

/**
 * EntityCriterion
 */
export type EntityCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'entity';
  name?: StringCriterion | null;
  form_of_entity?: StringCriterion | null;
  legal_name?: StringCriterion | null;
  address?: StringCriterion | null;
  email?: StringCriterion | null;
  natural_person?: EqualsCriterion | null;
  commercial_register?: StringCriterion | null;
  registration_number?: StringCriterion | null;
  represent_by?: StringCriterion | null;
  recipient?: EqualsCriterion | null;
  signature_date?: TimeCriterion | null;
  date_of_birth?: TimeCriterion | null;
  country_code?: StringCriterion | null;
};

/**
 * EnumResponseType
 */
export type EnumResponseType = {
  /**
   * Multiple Choices
   */
  multiple_choices: boolean;
  /**
   * Values
   */
  values: Array<EnumResponseTypeValue>;
};

/**
 * EnumResponseTypeValue
 */
export type EnumResponseTypeValue = {
  /**
   * Name
   */
  name: string;
  /**
   * Definition
   */
  definition: string;
  /**
   * Color
   */
  color?: string | null;
};

/**
 * EqualsCriterion
 */
export type EqualsCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: '==';
  /**
   * Value
   */
  value: string | string | string | string | boolean | number | number | null;
};

/**
 * EquitySectionCreateUser
 *
 * Schema for creating a new equity section.
 *
 * Args:
 * gaia_id: remote id in gaia
 */
export type EquitySectionCreateUser = {
  /**
   * Gaia Section Id
   */
  gaia_section_id: string;
  /**
   * Gaia Section Title
   */
  gaia_section_title: string;
  /**
   * Gaia Group Title
   */
  gaia_group_title: string | null;
};

/**
 * EquitySectionInDB
 *
 * Slim schema for equity sections stored in database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * owner_intake_agent_id: ID of the intake agent
 * gaia_id: remote id in gaia
 * fields:
 */
export type EquitySectionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Gaia Section Id
   */
  gaia_section_id: string;
  /**
   * Gaia Group Title
   */
  gaia_group_title: string | null;
  /**
   * Gaia Section Title
   */
  gaia_section_title: string | null;
};

/**
 * EquitySectionSearchResult
 */
export type EquitySectionSearchResult = {
  /**
   * Results
   */
  results: Array<EquitySectionInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * EsopPool
 */
export type EsopPool = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * EsopPoolIncrease
 */
export type EsopPoolIncrease = {
  /**
   * Esop Pool Id
   */
  esop_pool_id: string;
  esop_pool: EsopPool;
  /**
   * Target
   */
  target: number | string;
  method: EsopPoolMethodEnum;
};

/**
 * EsopPoolMethodEnum
 */
export enum EsopPoolMethodEnum {
  PREPRE = 'prepre',
  PREPOST = 'prepost',
  POSTPOST = 'postpost',
}

/**
 * EvaluationActionInDB
 */
export type EvaluationActionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Action Type
   */
  action_type?: 'evaluation';
  /**
   * Target Evaluation Uid
   */
  target_evaluation_uid: string;
  /**
   * Target Evaluation Name
   */
  target_evaluation_name?: string | null;
};

/**
 * EvaluationActionResultInDB
 */
export type EvaluationActionResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Action Uid
   */
  action_uid: string;
  /**
   * Action Trigger Uid
   */
  action_trigger_uid: string;
  /**
   * Selected Option Uid
   */
  selected_option_uid?: string | null;
  /**
   * Evaluation Result Uid
   */
  evaluation_result_uid: string;
  /**
   * Action Type
   */
  action_type?: 'evaluation';
  /**
   * Triggered Evaluation Result Uids
   */
  triggered_evaluation_result_uids?: Array<string>;
};

/**
 * EvaluationResponse
 */
export type EvaluationResponse = {
  /**
   * Reason
   */
  reason: string;
  /**
   * Answer
   */
  answer:
    | CalendarEvent
    | CurrencyAmount
    | Entity
    | string
    | string
    | number
    | number
    | boolean
    | string
    | Array<CalendarEvent>
    | Array<CurrencyAmount>
    | Array<Entity>
    | Array<string>
    | Array<string>
    | Array<number>
    | Array<number>
    | Array<boolean>
    | Array<string>
    | null;
};

/**
 * Event
 */
export type Event = {
  /**
   * Id
   */
  id?: string | null;
  /**
   * Start Date
   */
  start_date?: string | null;
  /**
   * End Date
   */
  end_date?: string | null;
  /**
   * Type
   */
  type: string;
  /**
   * Strategy
   */
  strategy?: string | null;
  /**
   * Params
   */
  params?: {
    [key: string]: unknown;
  };
  /**
   * Percentage
   */
  percentage?: number | null;
  /**
   * Bad Leaver
   */
  bad_leaver?: boolean | null;
  /**
   * Quantity
   */
  quantity?: number | null;
  /**
   * Note
   */
  note?: string | null;
  /**
   * Duration
   */
  duration?: number | null;
  /**
   * Final Vested Options
   */
  final_vested_options?: number | null;
};

/**
 * ExitSimulationData
 */
export type ExitSimulationData = {
  /**
   * Name
   */
  name: string;
  sell_price: Price;
  /**
   * Target Date
   */
  target_date: string;
  /**
   * Include Incentive Schemes
   */
  include_incentive_schemes?: boolean;
  /**
   * Include Nlp Prices
   */
  include_nlp_prices?: boolean;
  warrant_handling?: WarrantHandlingEnum;
  /**
   * Shares
   */
  shares: Array<ShareBlock>;
  /**
   * Shareholders
   */
  shareholders: Array<Shareholder>;
  /**
   * Share Classes
   */
  share_classes: Array<PythonServicesApisGatewayV1EndpointsFunctionsExitSimulationSchemasExitShareClass>;
  /**
   * Liquidation Preferences
   */
  liquidation_preferences?: Array<LiquidationPreferenceInput>;
  /**
   * Incentive Schemes
   */
  incentive_schemes: Array<IncentiveScheme>;
  /**
   * Convertible Loans
   */
  convertible_loans?: Array<PythonServicesApisGatewayV1EndpointsFunctionsExitSimulationSchemasExitConvertibleLoan>;
  /**
   * Shareholder Order
   */
  shareholder_order?: Array<string>;
};

/**
 * ExitSimulationResponse
 */
export type ExitSimulationResponse = {
  waterfall_proceeds: WaterfallProceeds;
  /**
   * Other Proceeds
   */
  other_proceeds: {
    [key: string]: string;
  };
  /**
   * Other Proceeds Total
   */
  other_proceeds_total: string;
  /**
   * Burdened Shares Used
   */
  burdened_shares_used: {
    [key: string]: number;
  };
  /**
   * Final Payout
   */
  final_payout: {
    [key: string]: PayoutDetails;
  };
};

/**
 * ExportFormat
 */
export enum ExportFormat {
  GAIA = 'gaia',
  KELLERHALS = 'kellerhals',
}

/**
 * ExternalAccessProjectMeta
 *
 * Basic display metadata of a review project for authenticated non-platform users.
 *
 * The capability flags carry the person's current (DB-recomputed) capabilities,
 * so the external UI can decide which features to show without decoding the JWT.
 */
export type ExternalAccessProjectMeta = {
  /**
   * Organisation Name
   */
  organisation_name: string;
  /**
   * Project Name
   */
  project_name: string;
  /**
   * Qa
   */
  qa: boolean;
  /**
   * Data Room Upload
   */
  data_room_upload: boolean;
};

/**
 * ExternalAccessRequest
 *
 * Request body for asking for an external access email.
 */
export type ExternalAccessRequest = {
  /**
   * Email
   */
  email: string;
};

/**
 * ExternalAccessToken
 *
 * Response of the token exchange and refresh endpoints.
 *
 * The capability flags are returned both here and as JWT claims, so the
 * frontend can use whichever is more convenient.
 */
export type ExternalAccessToken = {
  /**
   * Access Token
   */
  access_token: string;
  /**
   * Token Type
   */
  token_type?: string;
  non_platform_user: NonPlatformUserInDb;
  /**
   * Project External Uid
   */
  project_external_uid: string;
  /**
   * Qa
   */
  qa: boolean;
  /**
   * Data Room Upload
   */
  data_room_upload: boolean;
};

/**
 * ExternalAccessTokenExchange
 *
 * Request body for exchanging a one-time token for a JWT.
 */
export type ExternalAccessTokenExchange = {
  /**
   * Token
   */
  token: string;
};

/**
 * ExtractionEvaluationInDB
 */
export type ExtractionEvaluationInDb = {
  /**
   * Evaluation Type
   */
  evaluation_type?: 'extraction';
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Extraction Prompt
   */
  extraction_prompt: string;
  response_type: PlaybookExtractionResponseType;
  /**
   * Options
   */
  options?: Array<ClassificationOptionInDb>;
};

/**
 * ExtractionEvaluationResultInDB
 */
export type ExtractionEvaluationResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Reasoning
   */
  reasoning?: string | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  /**
   * Page Number
   */
  page_number?: number | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Evaluation Uid
   */
  evaluation_uid: string;
  /**
   * Matched Option Uids
   */
  matched_option_uids?: Array<string>;
  /**
   * Triggered By Action Result Uid
   */
  triggered_by_action_result_uid?: string | null;
  /**
   * Action Results
   */
  action_results?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionResultInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionResultInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionResultInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionResultInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionResultInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionResultInDb)
  >;
  /**
   * Evaluation Type
   */
  evaluation_type?: 'extraction';
  extracted_value?: GaiaJsonValue | null;
};

/**
 * ExtractionPromptCreate
 *
 * An organisation-created extraction prompt.
 *
 * Args:
 * display_name: What users see, to tell prompts of the same name apart; None: the name.
 * contract_type_uids: The organisation's contract types the prompt belongs to; empty for a
 * standalone prompt.
 */
export type ExtractionPromptCreate = {
  /**
   * Prompt
   */
  prompt: string;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Contract Type Uids
   */
  contract_type_uids?: Array<string>;
};

/**
 * ExtractionPromptInDB
 *
 * An extraction prompt.
 *
 * Args:
 * display_name: What users see; tells prompts of the same name apart (e.g. the default
 * variants "Change of Control (Sales & Purchase)" and "Change of Control (Services)").
 * is_default: Copied from Gaia's default prompts with the organisation's default contract
 * types; False for organisation-created prompts.
 * version: The current question's version number (1 on create, one more per question change).
 */
export type ExtractionPromptInDb = {
  /**
   * Prompt
   */
  prompt: string;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Is Default
   */
  is_default?: boolean;
  /**
   * Version
   */
  version: number;
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Contract Types
   */
  contract_types?: Array<ContractTypeRef>;
};

/**
 * ExtractionPromptQuestion
 *
 * The question an extraction prompt asks: always a valid custom-extraction question.
 *
 * Values are matched to it by these three fields, not by a foreign key.
 */
export type ExtractionPromptQuestion = {
  /**
   * Prompt
   */
  prompt: string;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
};

/**
 * ExtractionPromptSearchEntry
 *
 * An extraction prompt found by the picker.
 *
 * Args:
 * view_column_uids: The columns of the searched-for view whose active question is the prompt;
 * empty when no view was given.
 */
export type ExtractionPromptSearchEntry = {
  /**
   * Prompt
   */
  prompt: string;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Is Default
   */
  is_default?: boolean;
  /**
   * Version
   */
  version: number;
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Contract Types
   */
  contract_types?: Array<ContractTypeRef>;
  /**
   * View Column Uids
   */
  view_column_uids?: Array<string>;
};

/**
 * ExtractionPromptSearchResult
 */
export type ExtractionPromptSearchResult = {
  /**
   * Results
   */
  results: Array<ExtractionPromptSearchEntry>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ExtractionPromptUpdate
 *
 * Partial update of an extraction prompt. ``example_data`` is cleared only when sent as null.
 *
 * A changed question field re-extracts every view column whose newest question is the prompt.
 */
export type ExtractionPromptUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Prompt
   */
  prompt?: string | null;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type?:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType
    | null;
  /**
   * Contract Type Uids
   */
  contract_type_uids?: Array<string> | null;
};

/**
 * ExtractionPromptVersionInDB
 *
 * One question an extraction prompt has asked.
 *
 * Args:
 * version: 1 for the prompt's first question, one more per question change.
 * created_at: When the prompt started asking it.
 * is_current: The prompt's current question (its newest version).
 */
export type ExtractionPromptVersionInDb = {
  /**
   * Prompt
   */
  prompt: string;
  example_data?: JsonValue | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Version
   */
  version: number;
  /**
   * Is Current
   */
  is_current: boolean;
};

/**
 * ExtractionPromptVersionSearchResult
 *
 * The versions of an extraction prompt, newest first.
 */
export type ExtractionPromptVersionSearchResult = {
  /**
   * Results
   */
  results: Array<ExtractionPromptVersionInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ExtractionResult
 *
 * What `TemplateExtractor.extract*()` returns: the field schema plus any
 * validation issues found while walking the docx. Inherits `fields` from
 * `TemplateSchema` and adds `validation_errors`. Persistence strips the
 * error list so `extracted_schema` JSON columns carry only the field
 * structure — `apply_validation_result` handles the split.
 */
export type ExtractionResult = {
  /**
   * Fields
   */
  fields?: {
    [key: string]: TemplateField;
  };
  /**
   * Validation Errors
   */
  validation_errors?: Array<TemplateValidationError>;
};

export type ExtractionValue =
  | string
  | string
  | string
  | number
  | number
  | boolean
  | CurrencyAmount
  | Entity
  | Array<string>
  | Array<string>
  | Array<string>
  | Array<number>
  | Array<number>
  | Array<boolean>
  | Array<CurrencyAmount>
  | Array<Entity>
  | Array<CalendarEvent>;

/**
 * ExtractionValueType
 *
 * How a contract-management extraction stores its value, derived from the question's response type.
 *
 * Used as the polymorphic discriminator of the ``cm_extraction`` hierarchy. ``float`` and
 * ``number`` share ``FLOAT``; an enum response type is ``ENUM`` or ``LIST_ENUM`` by
 * ``multiple_choices``.
 */
export enum ExtractionValueType {
  STR = 'str',
  ENUM = 'enum',
  FLOAT = 'float',
  INT = 'int',
  BOOL = 'bool',
  DATE = 'date',
  DATETIME = 'datetime',
  LIST_STR = 'list_str',
  LIST_ENUM = 'list_enum',
  LIST_FLOAT = 'list_float',
  LIST_INT = 'list_int',
  LIST_BOOL = 'list_bool',
  LIST_DATE = 'list_date',
  LIST_DATETIME = 'list_datetime',
  CURRENCY_AMOUNT = 'currency_amount',
  LIST_CURRENCY_AMOUNT = 'list_currency_amount',
  ENTITY = 'entity',
  LIST_ENTITY = 'list_entity',
  CALENDAR_EVENTS = 'calendar_events',
}

/**
 * FieldCatalog
 */
export type FieldCatalog = {
  /**
   * Entities
   */
  entities: Array<FieldCatalogEntity>;
  /**
   * Primitive Types
   */
  primitive_types: Array<TemplateFieldType>;
  /**
   * Filters
   */
  filters: Array<FieldCatalogFilter>;
};

/**
 * FieldCatalogEntity
 */
export type FieldCatalogEntity = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  entity_field_type: TemplateFieldType;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Fields
   */
  fields: Array<FieldCatalogField>;
};

/**
 * FieldCatalogField
 */
export type FieldCatalogField = {
  /**
   * Name
   */
  name: string;
  type: TemplateFieldType;
  /**
   * Required
   */
  required: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Nested
   */
  nested?: Array<FieldCatalogField> | null;
};

/**
 * FieldCatalogFilter
 */
export type FieldCatalogFilter = {
  /**
   * Name
   */
  name: string;
  /**
   * Category
   */
  category: 'custom' | 'string' | 'number' | 'list' | 'date' | 'choice' | 'other';
  /**
   * Applicable Types
   */
  applicable_types: Array<TemplateFieldType>;
  /**
   * Description
   */
  description: string;
  /**
   * Args
   */
  args?: Array<FieldCatalogFilterArg>;
  /**
   * Examples
   */
  examples: Array<FieldCatalogFilterExample>;
};

/**
 * FieldCatalogFilterArg
 */
export type FieldCatalogFilterArg = {
  /**
   * Name
   */
  name: string;
  type: TemplateFieldType;
  /**
   * Default
   */
  default?: string | null;
  /**
   * Variadic
   */
  variadic?: boolean;
  /**
   * Choices
   */
  choices?: Array<FieldCatalogFilterArgChoice> | null;
};

/**
 * FieldCatalogFilterArgChoice
 *
 * One entry in a constrained-arg dropdown.
 *
 * ``value`` is the literal passed to the filter (`"long"`, `"en"`, `"EUR"`).
 * ``label`` is the human-readable display string the FE renders in the
 * picker (`"Long"`, `"English"`, `"Euro (€)"`). ``description`` carries
 * optional hover/help text — e.g. an example output like
 * *"May 7, 2026"* for ``format_date.format_style="long"``, or
 * ``"British English with 'and'"`` for an ``en`` locale.
 */
export type FieldCatalogFilterArgChoice = {
  /**
   * Value
   */
  value: string;
  /**
   * Label
   */
  label: string;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * FieldCatalogFilterExample
 *
 * A worked example for a filter — drives palette tooltips and popover preview rows.
 */
export type FieldCatalogFilterExample = {
  /**
   * Input
   */
  input: string;
  /**
   * Output
   */
  output: string;
  /**
   * Args
   */
  args?: Array<string>;
};

/**
 * FilePageInDB
 *
 * Schema for file chunks stored in database.
 */
export type FilePageInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Page
   */
  page: number;
};

/**
 * FileReviewCreateUser
 */
export type FileReviewCreateUser = {
  /**
   * Vault File Uids
   */
  vault_file_uids: Array<string>;
  /**
   * Vault Directory Uids
   */
  vault_directory_uids: Array<string>;
  /**
   * Ocr Provider
   */
  ocr_provider?: 'unstructured';
};

/**
 * FileReviewInDB
 */
export type FileReviewInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Markdown Summary
   */
  markdown_summary?: string | null;
  /**
   * Summary Prompt
   */
  summary_prompt: string | null;
  ocr_provider: OcrProvider;
  vault_file_revision: VaultFileRevisionInDb | null;
  static_vault_file_copy: VaultFileRevisionSnapshot;
};

/**
 * FileReviewSearchResult
 */
export type FileReviewSearchResult = {
  /**
   * Results
   */
  results: Array<FileReviewInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * FileReviewTableSearch
 */
export type FileReviewTableSearch = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Markdown Summary
   */
  markdown_summary?: string | null;
  /**
   * Summary Prompt
   */
  summary_prompt: string | null;
  ocr_provider: OcrProvider;
  vault_file_revision: VaultFileRevisionInDb | null;
  static_vault_file_copy: VaultFileRevisionSnapshot;
  /**
   * Columns
   */
  columns?: Array<ReviewExtractionInDb>;
};

/**
 * FileReviewUpdate
 */
export type FileReviewUpdate = {
  /**
   * Markdown Summary
   */
  markdown_summary?: string | null;
  /**
   * Summary Prompt
   */
  summary_prompt?: string | null;
  ocr_provider?: OcrProvider | null;
};

/**
 * FilenameDenyListItem
 *
 * Schema for filename deny list items for intake agents.
 *
 * Args:
 * match_string: The string to match against, eg a file type suffix
 * exact_match: Whether to use exact matching (true) or substring search (false)
 */
export type FilenameDenyListItem = {
  /**
   * Match String
   */
  match_string: string;
  /**
   * Exact Match
   */
  exact_match: boolean;
};

/**
 * FilterableSearchQuery
 */
export type FilterableSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
};

/**
 * FinancingSimulation
 */
export type FinancingSimulation = {
  /**
   * Pre Valuation
   */
  pre_valuation: number | string;
  /**
   * Total Target Investment
   */
  total_target_investment: number | string;
  /**
   * Closing Date
   */
  closing_date: string | null;
  /**
   * Nominal Value Per Share
   */
  nominal_value_per_share?: number | string | null;
  /**
   * Prorata Amounts
   */
  prorata_amounts?: {
    [key: string]: number | string;
  };
  /**
   * Investments
   */
  investments?: Array<Investment>;
  /**
   * Convertible Loans
   */
  convertible_loans?: Array<PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingConvertibleLoan>;
  /**
   * Esop Pool Increases
   */
  esop_pool_increases?: Array<EsopPoolIncrease>;
  /**
   * Cap Table
   */
  cap_table: Array<
    | PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingCapTableRowStakeholder
    | PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingCapTableRowEsopPoolInput
  >;
  /**
   * Share Classes
   */
  share_classes: Array<PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingShareClass>;
  /**
   * Export Spreadsheet
   */
  export_spreadsheet?: boolean | null;
  export_format?: ExportFormat | null;
};

/**
 * FinancingSimulationResponse
 */
export type FinancingSimulationResponse = {
  /**
   * New Share Price
   */
  new_share_price: string;
  /**
   * Actuals
   */
  actuals: {
    [key: string]: ActualAmountsAndShares;
  };
  /**
   * Actual Investments
   */
  actual_investments: {
    [key: string]: string;
  };
  /**
   * Actual Prorata
   */
  actual_prorata: {
    [key: string]: string;
  };
  /**
   * Actual Convertible
   */
  actual_convertible: {
    [key: string]: string;
  };
  /**
   * New Cap Table
   */
  new_cap_table: Array<CapTableRowStakeholderOutput | CapTableRowEsopPoolOutput>;
  /**
   * New Actual Shares
   */
  new_actual_shares: number;
  /**
   * New Virtual Shares
   */
  new_virtual_shares: number;
  /**
   * After Registered Shares
   */
  after_registered_shares: number;
  /**
   * After Fully Diluted Shares
   */
  after_fully_diluted_shares: string;
  /**
   * Spreadsheet
   */
  spreadsheet?: string | null;
};

/**
 * FindingThresholdTriggerInDB
 */
export type FindingThresholdTriggerInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Actions
   */
  actions?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb)
  >;
  /**
   * Trigger Type
   */
  trigger_type?: 'finding_threshold';
  mode: ActionTriggerMode;
  /**
   * Min Severity
   */
  min_severity: 'low' | 'medium' | 'high';
  /**
   * Min Count
   */
  min_count: number;
};

/**
 * GaiaClauseCreate
 *
 * Schema for creating a new gaia clause.
 *
 * Args:
 * name: Name of the    gaia clause
 * jurisdiction: Jurisdiction of the gaia clause
 * language: Language of the gaia clause
 * content: Content of the gaia clause
 * source: Source of the gaia clause
 */
export type GaiaClauseCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Jurisdiction
   */
  jurisdiction: string;
  /**
   * Contract Types
   */
  contract_types?: Array<ContractTypeTemplateKey>;
  clause_category: ClauseCategory;
  /**
   * Language
   */
  language: string;
  /**
   * Content
   */
  content: string;
  /**
   * Source
   */
  source?: string | null;
};

/**
 * GaiaClauseInDB
 */
export type GaiaClauseInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Jurisdiction
   */
  jurisdiction: string;
  /**
   * Language
   */
  language: string;
  /**
   * Content
   */
  content: string;
  /**
   * Source
   */
  source?: string | null;
  /**
   * Contract Types
   */
  contract_types?: Array<ContractTypeTemplateKey> | null;
  clause_category?: ClauseCategory | null;
};

/**
 * GaiaClauseSearchQuery
 */
export type GaiaClauseSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
};

/**
 * GaiaClauseSearchResult
 */
export type GaiaClauseSearchResult = {
  /**
   * Results
   */
  results: Array<GaiaClauseInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * GaiaClauseUpdate
 *
 * Schema for updating an existing gaia clause.
 *
 * Args:
 * name: New name of the gaia clause
 * jurisdiction: New jurisdiction of the gaia clause
 * language: New language of the gaia clause
 * content: New content of the gaia clause
 * source: New source of the gaia clause
 */
export type GaiaClauseUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Jurisdiction
   */
  jurisdiction?: string | null;
  /**
   * Language
   */
  language?: string | null;
  /**
   * Contract Types
   */
  contract_types?: Array<ContractTypeTemplateKey> | null;
  clause_category?: ClauseCategory | null;
  /**
   * Content
   */
  content?: string | null;
  /**
   * Source
   */
  source?: string | null;
};

/**
 * GaiaEvent
 */
export type GaiaEvent = {
  gaia_event_type: GaiaEventType;
  /**
   * Data
   */
  data:
    | DemoDataSeedPayload
    | VaultZipDownloadPayload
    | GaiaEventObjectPayload
    | GaiaEventUuidPayload
    | GaiaEventTextPayload;
  /**
   * Time Stamp
   */
  time_stamp?: string;
};

/**
 * GaiaEventObjectPayload
 */
export type GaiaEventObjectPayload = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Type
   */
  type: string;
  /**
   * Data
   */
  data: {
    [key: string]: unknown;
  };
};

/**
 * GaiaEventTextPayload
 */
export type GaiaEventTextPayload = {
  /**
   * Text
   */
  text: string;
};

/**
 * GaiaEventType
 */
export enum GaiaEventType {
  REVIEW_UPDATE = 'review_update',
  CONTRACT_MANAGEMENT_EXTRACTION_UPDATE = 'contract_management_extraction_update',
  CONTRACT_MANAGEMENT_MODEL_EXTRACTION_UPDATED = 'contract_management_model_extraction_updated',
  VAULT_FILE_UPDATED = 'vault_file_updated',
  VAULT_FILE_DELETED = 'vault_file_deleted',
  VAULT_DIRECTORY_UPDATED = 'vault_directory_updated',
  VAULT_DIRECTORY_DELETED = 'vault_directory_deleted',
  VAULT_ZIP_DOWNLOAD_UPDATED = 'vault_zip_download_updated',
  PLAYBOOK_UPDATED = 'playbook_updated',
  PLAYBOOK_EVALUATION_VALIDATED = 'playbook_evaluation_validated',
  PLAYBOOK_RUN_UPDATED = 'playbook_run_updated',
  PLAYBOOK_RUN_CREATED = 'playbook_run_created',
  SIGNING_REQUEST_UPDATED = 'signing_request_updated',
  SIGNING_REQUESTS_UPDATED = 'signing_requests_updated',
  REVIEW_PROJECT_FILE_UPDATED = 'review_project_file_updated',
  REVIEW_PROJECT_ANALYSIS_CREATED = 'review_project_analysis_created',
  REVIEW_PROJECT_ANALYSIS_UPDATED = 'review_project_analysis_updated',
  REVIEW_PROJECT_ANALYSIS_DELETED = 'review_project_analysis_deleted',
  REVIEW_PROJECT_FILE_SEEN_UPDATED = 'review_project_file_seen_updated',
  REVIEW_PROJECT_REPORT_UPDATED = 'review_project_report_updated',
  REVIEW_PROJECT_QUESTION_CREATED = 'review_project_question_created',
  REVIEW_PROJECT_QUESTION_UPDATED = 'review_project_question_updated',
  REVIEW_PROJECT_QUESTION_DELETED = 'review_project_question_deleted',
  DEMO_DATA_SEED_UPDATED = 'demo_data_seed_updated',
  USER_ATTENTION = 'user_attention',
  ACTIVE_ORGANISATION_CHANGED = 'active_organisation_changed',
  USER_ACCESS_REVOKED = 'user_access_revoked',
}

/**
 * GaiaEventUUIDPayload
 */
export type GaiaEventUuidPayload = {
  /**
   * Uid
   */
  uid: string;
};

/**
 * GaiaFileMeta
 */
export type GaiaFileMeta = {
  /**
   * Id
   */
  id: string | null;
  /**
   * Filename
   */
  filename: string | null;
  /**
   * Title
   */
  title: string | null;
  /**
   * Updated At
   */
  updated_at: string | null;
};

export type GaiaJsonValue = unknown;

/**
 * GaiaPaginatedSectionFiles
 */
export type GaiaPaginatedSectionFiles = {
  /**
   * Total
   */
  total: number | null;
  /**
   * Page
   */
  page: number | null;
  /**
   * Pages
   */
  pages: number | null;
  /**
   * Data
   */
  data: Array<GaiaFileMeta> | null;
};

/**
 * GaiaSection
 */
export type GaiaSection = {
  /**
   * Id
   */
  id: string | null;
  /**
   * Title
   */
  title: string | null;
  /**
   * Updated At
   */
  updated_at: string | null;
  /**
   * Fields
   */
  fields: Array<GaiaSectionField> | null;
};

/**
 * GaiaSectionField
 *
 * Attributes:
 * name (Union[Unset, str]): Name of the field Example: A custom string.
 * type_ (Union[None, Unset, str]): Type of the field Example: A custom string.
 */
export type GaiaSectionField = {
  /**
   * Name
   */
  name: string | null;
  /**
   * Type
   */
  type_: string | null;
};

/**
 * GaiaSectionGroupSlim
 */
export type GaiaSectionGroupSlim = {
  /**
   * Id
   */
  id: string;
  /**
   * Title
   */
  title: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Sections
   */
  sections: Array<GaiaSectionSlim>;
};

/**
 * GaiaSectionSlim
 */
export type GaiaSectionSlim = {
  /**
   * Id
   */
  id: string | null;
  /**
   * Title
   */
  title: string | null;
  /**
   * Updated At
   */
  updated_at: string | null;
};

/**
 * GaiaTokenCreate
 */
export type GaiaTokenCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Token
   */
  token: string;
};

/**
 * GaiaTokenInDB
 */
export type GaiaTokenInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Token
   */
  token: string;
};

/**
 * GaiaTokenUpdate
 */
export type GaiaTokenUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Token
   */
  token?: string | null;
};

/**
 * GeneralChatResumeRequest
 *
 * Resume request for the general chat backend.
 *
 * ``client_surface`` must be the surface of the turn being resumed, for the same reason
 * ``confirmation_mode`` must be: the tool belt is rebuilt for the resumed run, so a resume
 * claiming the web app would unbind the very live-document tool whose interrupt is being
 * answered. Defaults to the web app (fail-safe: no live-document tools).
 */
export type GeneralChatResumeRequest = {
  /**
   * Thread Uid
   */
  thread_uid: string;
  /**
   * Decisions
   */
  decisions?: Array<ChatResumeDecision> | null;
  /**
   * Resume Value
   */
  resume_value?:
    | GaiaJsonValue
    | {
        [key: string]: GaiaJsonValue;
      }
    | null;
  /**
   * Interrupt Id
   */
  interrupt_id?: string | null;
  confirmation_mode?: ChatConfirmationMode;
  client_surface?: ChatClientSurface;
};

/**
 * GeneralChatStreamRequest
 *
 * Request body for the general chat stream endpoint.
 *
 * The three ``selected_*_uids`` lists are what the user pointed this turn at: the items picked in
 * the app (the "Ask AI" action on a vault file, a contract or a contract file). Only their uids
 * travel — they are named in a ``<selected_documents>`` block on the message and looked up by the
 * assistant with ``inspect_vault_file`` / ``inspect_contract``, which check READ per call.
 *
 * They are a starting point, not a fence: the assistant may follow links out of them, to a parent
 * contract or a sibling file, on the same permission footing. Sent per message, never persisted.
 *
 * ``client_surface`` says which client sent the message. The general assistant also runs in the
 * chat panel of the GAIA Word add-in, where a document is open beside it; only that surface gets
 * the live-document tools, because only it can answer their ``document_tool`` interrupts.
 * Defaults to the web app: a client that does not send it gets no live-document tools, which
 * cannot hang.
 *
 * ``selection_content`` is the user's current selection in that document (the same field the
 * templating request carries), so "explain this clause" can anchor to it. It is ephemeral client
 * state with no tool to fetch it; the document body itself is never sent — the assistant reads the
 * live document on demand. Only the add-in has a document to select in, so the two fields are tied
 * together by ``validate_selection_comes_from_the_add_in``.
 */
export type GeneralChatStreamRequest = {
  /**
   * Thread Uid
   */
  thread_uid?: string | null;
  /**
   * Message
   */
  message: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Model
   */
  model?: string | null;
  llm_provider?: LlmProvider | null;
  confirmation_mode?: ChatConfirmationMode;
  /**
   * Selected Prompt Collection Uid
   */
  selected_prompt_collection_uid?: string | null;
  /**
   * Selected Vault File Uids
   */
  selected_vault_file_uids?: Array<string>;
  /**
   * Selected Contract Uids
   */
  selected_contract_uids?: Array<string>;
  /**
   * Selected Contract File Uids
   */
  selected_contract_file_uids?: Array<string>;
  client_surface?: ChatClientSurface;
  /**
   * Selection Content
   */
  selection_content?: string | null;
};

/**
 * GoogleDriveAuthUrlResponse
 */
export type GoogleDriveAuthUrlResponse = {
  /**
   * Url
   */
  url: string;
  /**
   * State
   */
  state?: string | null;
};

/**
 * GoogleDriveConnectRequest
 */
export type GoogleDriveConnectRequest = {
  /**
   * Code
   */
  code: string;
};

/**
 * GoogleDriveImportItem
 */
export type GoogleDriveImportItem = {
  /**
   * Id
   */
  id: string;
  /**
   * Is Folder
   */
  is_folder: boolean;
};

/**
 * GoogleDriveImportRequest
 */
export type GoogleDriveImportRequest = {
  /**
   * Items
   */
  items: Array<GoogleDriveImportItem>;
};

/**
 * GoogleDriveIntegrationStatus
 */
export type GoogleDriveIntegrationStatus = {
  state: IntegrationState;
  /**
   * Connected
   */
  connected: boolean;
  /**
   * Has Refresh Token
   */
  has_refresh_token: boolean;
  /**
   * Access Token Expires At
   */
  access_token_expires_at?: string | null;
  /**
   * Authorized At
   */
  authorized_at?: string | null;
  /**
   * Scope
   */
  scope?: string | null;
};

/**
 * GoogleDriveItem
 */
export type GoogleDriveItem = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Is Folder
   */
  is_folder: boolean;
  /**
   * Owner Name
   */
  owner_name?: string | null;
  /**
   * Modified Time
   */
  modified_time?: string | null;
};

/**
 * GoogleDriveListResponse
 */
export type GoogleDriveListResponse = {
  /**
   * Items
   */
  items: Array<GoogleDriveItem>;
  /**
   * Next Page Token
   */
  next_page_token?: string | null;
};

/**
 * GreaterEqualThanCriterion
 */
export type GreaterEqualThanCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: '>=';
  /**
   * Value
   */
  value: string | string | number | number;
};

/**
 * GreaterThanCriterion
 */
export type GreaterThanCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: '>';
  /**
   * Value
   */
  value: string | string | number | number;
};

/**
 * GroupTriggerCreateRequest
 *
 * API body for creating a group trigger; the group comes from the path.
 *
 * A member of the group *hits* when its latest result reached ``min_risk_level`` — any matched
 * option at/above it, or any uncommons finding at/above it. ``mode == at_least`` fires when at least
 * ``min_count`` members hit ("every member" is ``min_count == member count``); ``mode == less_than``
 * fires when fewer than ``min_count`` do; ``mode == none_found`` fires when none do, and ignores
 * ``min_count``; ``mode == always`` fires unconditionally.
 *
 * Only ``at_least`` needs the group to have members. ``always`` is the mode to use when the actions
 * must fire whatever the members did — or when the group has none at all.
 */
export type GroupTriggerCreateRequest = {
  mode?: ActionTriggerMode;
  min_risk_level?: PlaybookRiskLevel;
  /**
   * Min Count
   */
  min_count?: number;
};

/**
 * GroupTriggerInDB
 */
export type GroupTriggerInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Actions
   */
  actions?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb)
  >;
  /**
   * Trigger Type
   */
  trigger_type?: 'group';
  /**
   * Group Uid
   */
  group_uid: string;
  mode: ActionTriggerMode;
  min_risk_level: PlaybookRiskLevel;
  /**
   * Min Count
   */
  min_count: number;
};

/**
 * GroupTriggerUpdateRequest
 *
 * API body for updating a group trigger; omitted fields are left unchanged.
 */
export type GroupTriggerUpdateRequest = {
  mode?: ActionTriggerMode | null;
  min_risk_level?: PlaybookRiskLevel | null;
  /**
   * Min Count
   */
  min_count?: number | null;
};

/**
 * HTTPCallback
 *
 * Pydantic schema for describing a callback.
 */
export type HttpCallback = {
  /**
   * Method
   */
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  /**
   * Url
   */
  url: string;
  /**
   * Headers
   */
  headers?: {
    [key: string]: string;
  } | null;
  /**
   * Timeout S
   */
  timeout_s?: number;
};

/**
 * HTTPValidationError
 */
export type HttpValidationError = {
  /**
   * Detail
   */
  detail?: Array<ValidationError>;
};

/**
 * IncentiveAgreement
 */
export type IncentiveAgreement = {
  /**
   * Id
   */
  id: string;
  /**
   * Participant Id
   */
  participant_id: string;
  /**
   * Participant Name
   */
  participant_name: string;
  /**
   * Vested Options
   */
  vested_options: number | string;
  /**
   * Options
   */
  options: number | string;
  exercise_price_per_option: Price;
  state: AgreementState;
};

/**
 * IncentiveScheme
 */
export type IncentiveScheme = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Total Options
   */
  total_options: number | string;
  /**
   * Share Option Ratio
   */
  share_option_ratio: number;
  /**
   * Eip Pool
   */
  eip_pool: boolean;
  /**
   * Legal Entity Id
   */
  legal_entity_id?: string | null;
  /**
   * Agreements
   */
  agreements: Array<IncentiveAgreement>;
};

/**
 * IntakeAgentCreate
 *
 * Schema for creating a new intake agent.
 *
 * Args:
 * name: Name of the intake agent
 * email: Email of the intake agent
 * organisation_id: ID of the organisation
 */
export type IntakeAgentCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Allow All Senders
   */
  allow_all_senders: boolean;
  /**
   * Allow Api Requests
   */
  allow_api_requests: boolean;
  /**
   * Respond With Summary
   */
  respond_with_summary?: boolean;
  /**
   * Run Playbook
   */
  run_playbook?: boolean;
  /**
   * Owner Organisation Id
   */
  owner_organisation_id?: number | null;
  callback_webhook?: HttpCallback | null;
  /**
   * Filename Deny List
   */
  filename_deny_list?: Array<FilenameDenyListItem> | null;
  /**
   * Inbound Document Signing Status
   */
  inbound_document_signing_status: 'signed' | 'unknown' | 'unsigned';
  /**
   * Jira Webhook Enabled
   */
  jira_webhook_enabled?: boolean;
};

/**
 * IntakeAgentInDB
 *
 * Slim schema for intake agents stored in database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * name: Name of the intake agent
 * allow_all_senders: Allow all senders of the intake agent
 * allow_api_requests: Allow API requests of the intake agent
 * respond_with_summary: Respond with summary of the intake agent
 * owner_organisation_id: ID of the organisation
 */
export type IntakeAgentInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Short Uid
   */
  short_uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Allow All Senders
   */
  allow_all_senders: boolean;
  /**
   * Allow Api Requests
   */
  allow_api_requests: boolean;
  /**
   * Slack Enabled
   */
  slack_enabled?: boolean;
  /**
   * Respond With Summary
   */
  respond_with_summary: boolean;
  /**
   * Run Playbook
   */
  run_playbook: boolean;
  /**
   * Owner Organisation Id
   */
  owner_organisation_id: number;
  callback_webhook?: HttpCallback | null;
  /**
   * Prompt Collections
   */
  prompt_collections: Array<PromptCollectionInDb>;
  /**
   * Playbooks
   */
  playbooks: Array<PlaybookInDb>;
  /**
   * Allow List
   */
  allow_list: Array<AllowListInDb>;
  default_equity_section: EquitySectionInDb | null;
  /**
   * Equity Sections
   */
  equity_sections: Array<EquitySectionInDb>;
  /**
   * Jwt Token
   */
  jwt_token?: string | null;
  /**
   * Filename Deny List
   */
  filename_deny_list?: Array<FilenameDenyListItem> | null;
  /**
   * Inbound Document Signing Status
   */
  inbound_document_signing_status: 'signed' | 'unknown' | 'unsigned';
  /**
   * Jira Webhook Enabled
   */
  jira_webhook_enabled: boolean;
};

/**
 * IntakeAgentLogInDB
 */
export type IntakeAgentLogInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * From Email
   */
  from_email?: string | null;
  status: IntakeAgentLogStatus;
  /**
   * Status Message
   */
  status_message: string;
  /**
   * Intake Agent Id
   */
  intake_agent_id?: number | null;
  /**
   * Json Data
   */
  json_data?: {
    [key: string]: unknown;
  } | null;
  /**
   * Vault Files
   */
  vault_files: Array<VaultFileInDbSlim>;
  /**
   * Jira Issue Key
   */
  jira_issue_key?: string | null;
};

/**
 * IntakeAgentLogInDBSlim
 *
 * Slim schema for intake agent logs stored in database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * from_email: Sender of the intake, ``None`` when the source exposes none (Jira)
 * status: Status of the intake agent logs
 * status_message: Status message of the intake agent logs
 * json_data: JSON data of the intake agent logs
 * intake_agent_id: ID of the intake agent
 */
export type IntakeAgentLogInDbSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * From Email
   */
  from_email?: string | null;
  status: IntakeAgentLogStatus;
  /**
   * Status Message
   */
  status_message: string;
  /**
   * Intake Agent Id
   */
  intake_agent_id?: number | null;
};

/**
 * IntakeAgentLogSearchResult
 */
export type IntakeAgentLogSearchResult = {
  /**
   * Results
   */
  results: Array<IntakeAgentLogInDbSlim>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * IntakeAgentLogStatus
 *
 * ``TaskStatus`` + the email-intake stages, for ``IntakeAgentLog.status``.
 */
export enum IntakeAgentLogStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
  PARTIAL = 'partial',
  SKIPPED = 'skipped',
  CANCELLED = 'cancelled',
  WARNING = 'warning',
  RECEIVED = 'received',
  SCHEDULED = 'scheduled',
  NOT_RECIPIENT = 'not_recipient',
}

/**
 * IntakeAgentLogUpdate
 *
 * Schema for updating an existing intake agent logs.
 *
 * Args:
 * from_email: New from_email of the intake agent logs
 * status: New status of the intake agent logs
 * status_message: New status_message of the intake agent logs
 * json_data: New json_data of the intake agent logs
 */
export type IntakeAgentLogUpdate = {
  /**
   * From Email
   */
  from_email?: string | null;
  status?: IntakeAgentLogStatus | null;
  /**
   * Status Message
   */
  status_message?: string | null;
  /**
   * Json Data
   */
  json_data?: {
    [key: string]: unknown;
  } | null;
  /**
   * Intake Agent Id
   */
  intake_agent_id?: number | null;
  /**
   * Jira Cloud Id
   */
  jira_cloud_id?: string | null;
  /**
   * Jira Issue Id
   */
  jira_issue_id?: string | null;
  /**
   * Jira Issue Key
   */
  jira_issue_key?: string | null;
  /**
   * Slack Team Id
   */
  slack_team_id?: string | null;
  /**
   * Slack Channel Id
   */
  slack_channel_id?: string | null;
  /**
   * Slack Message Ts
   */
  slack_message_ts?: string | null;
  /**
   * Slack Thread Ts
   */
  slack_thread_ts?: string | null;
  /**
   * Slack User Id
   */
  slack_user_id?: string | null;
  /**
   * Requested Task Types
   */
  requested_task_types?: Array<OrganisationAgentTaskType> | null;
  /**
   * Requested Playbook Id
   */
  requested_playbook_id?: number | null;
};

/**
 * IntakeAgentSearchResult
 */
export type IntakeAgentSearchResult = {
  /**
   * Results
   */
  results: Array<IntakeAgentInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * IntakeAgentUpdate
 *
 * Schema for updating an existing intake agent.
 *
 * Args:
 * name: New name of the intake agent
 * allow_all_senders: New allow_all_senders of the intake agent
 * allow_api_requests: New allow_api_requests of the intake agent
 * respond_with_summary: New respond_with_summary of the intake agent
 */
export type IntakeAgentUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Allow All Senders
   */
  allow_all_senders?: boolean | null;
  /**
   * Allow Api Requests
   */
  allow_api_requests?: boolean | null;
  /**
   * Respond With Summary
   */
  respond_with_summary?: boolean | null;
  /**
   * Run Playbook
   */
  run_playbook?: boolean | null;
  callback_webhook?: HttpCallback | null;
  /**
   * Filename Deny List
   */
  filename_deny_list?: Array<FilenameDenyListItem> | null;
  /**
   * Inbound Document Signing Status
   */
  inbound_document_signing_status?: 'signed' | 'unknown' | 'unsigned' | null;
};

/**
 * IntakeAgentUpdatePlaybooks
 *
 * Schema for updating the playbooks of an existing intake agent.
 *
 * Args:
 * playbook_uids: UUIDs of the playbooks to link to the intake agent
 */
export type IntakeAgentUpdatePlaybooks = {
  /**
   * Playbook Uids
   */
  playbook_uids: Array<string>;
};

/**
 * IntakeAgentUpdatePromptCollections
 *
 * Schema for updating the prompt collections of an existing intake agent.
 *
 * Args:
 * prompt_collection_uids: UUIDs of the prompt collections to update
 */
export type IntakeAgentUpdatePromptCollections = {
  /**
   * Prompt Collection Uids
   */
  prompt_collection_uids: Array<string>;
};

/**
 * IntegrationState
 *
 * Possible states for third-party integrations (Google Drive, Slack, Youtrust, Jira).
 */
export enum IntegrationState {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  PENDING = 'pending',
}

/**
 * Interrupt
 */
export type Interrupt = {
  /**
   * Type
   */
  type?: 'interrupt';
  /**
   * Interrupts
   */
  interrupts: Array<InterruptItem>;
};

/**
 * InterruptItem
 */
export type InterruptItem = {
  /**
   * Id
   */
  id: string;
  /**
   * Value
   */
  value: unknown;
};

/**
 * Investment
 */
export type Investment = {
  /**
   * Amount
   */
  amount: number | string;
  /**
   * Tag
   */
  tag: string | null;
  /**
   * Contractual Party Id
   */
  contractual_party_id: string;
  contractual_party: ContractualParty;
};

/**
 * JiraAuthUrlResponse
 */
export type JiraAuthUrlResponse = {
  /**
   * Url
   */
  url: string;
  /**
   * State
   */
  state?: string | null;
};

/**
 * JiraConnectRequest
 */
export type JiraConnectRequest = {
  /**
   * Code
   */
  code: string;
};

/**
 * JiraIntegrationStatus
 *
 * API-facing projection. Never carries a token.
 */
export type JiraIntegrationStatus = {
  state: IntegrationState;
  /**
   * Connected
   */
  connected: boolean;
  /**
   * Has Refresh Token
   */
  has_refresh_token: boolean;
  /**
   * Needs Site Selection
   */
  needs_site_selection: boolean;
  /**
   * Cloud Id
   */
  cloud_id?: string | null;
  /**
   * Site Url
   */
  site_url?: string | null;
  /**
   * Site Name
   */
  site_name?: string | null;
  /**
   * Account Display Name
   */
  account_display_name?: string | null;
  /**
   * Access Token Expires At
   */
  access_token_expires_at?: string | null;
  /**
   * Authorized At
   */
  authorized_at?: string | null;
  /**
   * Scope
   */
  scope?: string | null;
};

/**
 * JiraNotificationActionInDB
 */
export type JiraNotificationActionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Action Type
   */
  action_type?: 'jira_notification';
  /**
   * Message
   */
  message?: string | null;
  /**
   * Include Playbook Summary
   */
  include_playbook_summary?: boolean;
  /**
   * Include Redlined Document
   */
  include_redlined_document?: boolean;
  /**
   * Include Run Initiator
   */
  include_run_initiator?: boolean;
};

/**
 * JiraNotificationActionResultInDB
 */
export type JiraNotificationActionResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Action Uid
   */
  action_uid: string;
  /**
   * Action Trigger Uid
   */
  action_trigger_uid: string;
  /**
   * Selected Option Uid
   */
  selected_option_uid?: string | null;
  /**
   * Evaluation Result Uid
   */
  evaluation_result_uid: string;
  /**
   * Action Type
   */
  action_type?: 'jira_notification';
  /**
   * Jira Comment Id
   */
  jira_comment_id?: string | null;
};

/**
 * JiraSelectSiteRequest
 */
export type JiraSelectSiteRequest = {
  /**
   * Cloud Id
   */
  cloud_id: string;
};

/**
 * JiraSite
 *
 * One entry of ``/oauth/token/accessible-resources`` -- a Jira site the consent covers.
 */
export type JiraSite = {
  /**
   * Cloud Id
   */
  cloud_id: string;
  /**
   * Url
   */
  url: string;
  /**
   * Name
   */
  name: string;
  /**
   * Avatar Url
   */
  avatar_url?: string | null;
  /**
   * Scopes
   */
  scopes: Array<string>;
};

/**
 * JiraSiteListResponse
 */
export type JiraSiteListResponse = {
  /**
   * Sites
   */
  sites: Array<JiraSite>;
};

/**
 * JiraWebhookConfig
 *
 * What an org admin needs in order to register an intake agent's webhook in Jira.
 */
export type JiraWebhookConfig = {
  /**
   * Enabled
   */
  enabled: boolean;
  /**
   * Webhook Url
   */
  webhook_url?: string | null;
  /**
   * Events
   */
  events: Array<string>;
  /**
   * Jql Hint
   */
  jql_hint: string;
};

export type JsonValue = unknown;

/**
 * KanbanCommentCreateUser
 *
 * Schema for creating a new kanban comment.
 *
 * Args:
 * body: Body of the kanban comment
 * author_user_email: Email of the author
 * review_id: ID of the review/kanban ticket
 */
export type KanbanCommentCreateUser = {
  /**
   * Body
   */
  body: string;
  /**
   * Review Uid
   */
  review_uid: string;
};

/**
 * KanbanCommentInDB
 *
 * Slim schema for kanban comments stored in database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * body: Body of the kanban comment
 * author_user_email: Email of the author
 * review_id: ID of the review/kanban ticket
 */
export type KanbanCommentInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Body
   */
  body: string;
  /**
   * Author User Email
   */
  author_user_email: string;
  /**
   * Review Id
   */
  review_id: number;
};

/**
 * KanbanCommentSearchResult
 */
export type KanbanCommentSearchResult = {
  /**
   * Results
   */
  results: Array<KanbanCommentInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * KanbanCommentUpdate
 *
 * Schema for updating an existing kanban comment.
 *
 * Args:
 * body: New body of the kanban comment
 * author_user_email: New author email of the kanban comment
 */
export type KanbanCommentUpdate = {
  /**
   * Body
   */
  body?: string | null;
};

/**
 * KanbanStateCreate
 *
 * Schema for creating a new kanban state.
 *
 * Args:
 * name: Name of the kanban state
 * position: Position of the kanban state
 * organisation_id: Owner organisation ID
 */
export type KanbanStateCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Position
   */
  position: number;
  /**
   * Organisation Id
   */
  organisation_id?: number | null;
  /**
   * Protected
   */
  protected?: boolean;
  /**
   * Hidden
   */
  hidden?: boolean;
  /**
   * Archive State
   */
  archive_state?: boolean;
};

/**
 * KanbanStateInDB
 *
 * Slim schema for vault files stored in database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * name: Name of the kanban state
 * position: Position of the kanban state
 * organisation_id: Owner organisation ID
 */
export type KanbanStateInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Position
   */
  position: number;
  /**
   * Organisation Id
   */
  organisation_id: number;
  /**
   * Protected
   */
  protected: boolean;
  /**
   * Hidden
   */
  hidden: boolean;
  /**
   * Archive State
   */
  archive_state: boolean;
};

/**
 * KanbanStateSearchResult
 */
export type KanbanStateSearchResult = {
  /**
   * Results
   */
  results: Array<KanbanStateInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * KanbanStateUpdate
 *
 * Schema for updating an existing kanban state.
 *
 * Args:
 * name: New name of the kanban state
 * position: New position of the kanban state
 */
export type KanbanStateUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Position
   */
  position?: number | null;
  /**
   * Hidden
   */
  hidden?: boolean | null;
  /**
   * Archive State
   */
  archive_state?: boolean | null;
};

/**
 * KanbanStateUpdateSortIndexes
 */
export type KanbanStateUpdateSortIndexes = {
  /**
   * Review Uids To Positions
   */
  review_uids_to_positions: {
    [key: string]: number;
  };
};

/**
 * KeyValue
 */
export type KeyValue = {
  /**
   * Key
   */
  key: string;
  /**
   * Value
   */
  value: number | number | string | string | null;
};

/**
 * LatestSigningRequestStatistic
 */
export type LatestSigningRequestStatistic = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Provider
   */
  provider: string;
  status?: SigningRequestStatus | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Organisation Uid
   */
  organisation_uid: string;
  /**
   * Organisation Name
   */
  organisation_name: string;
  /**
   * Participant Count
   */
  participant_count: number;
  /**
   * Signer Count
   */
  signer_count: number;
  /**
   * Approver Count
   */
  approver_count: number;
  /**
   * Follower Count
   */
  follower_count: number;
  /**
   * Document Count
   */
  document_count: number;
};

/**
 * LessEqualThanCriterion
 */
export type LessEqualThanCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: '<=';
  /**
   * Value
   */
  value: string | string | number | number;
};

/**
 * LessThanCriterion
 */
export type LessThanCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: '<';
  /**
   * Value
   */
  value: string | string | number | number;
};

/**
 * LimitableSearchableQuery
 *
 * Base model for HTTP error responses.
 *
 * Attributes:
 * text: The search string of the request.
 */
export type LimitableSearchableQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
};

/**
 * LinearVesting
 */
export type LinearVesting = {
  agreement: PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasLinearAgreement;
  /**
   * Events
   */
  events?: Array<Event> | null;
  /**
   * Valuations
   */
  valuations?: Array<PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasLinearValuation> | null;
  /**
   * Share Price
   */
  share_price?: number | null;
  /**
   * Total Number Of Shares
   */
  total_number_of_shares?: number | string | null;
  /**
   * Cap Table Totals
   */
  cap_table_totals?: Array<SharesOnDate> | null;
  /**
   * Precision
   */
  precision?: number | null;
};

/**
 * LinearVestingResponse
 */
export type LinearVestingResponse = {
  /**
   * Vesting Units
   */
  vesting_units: Array<PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasLinearVestingUnit>;
  /**
   * Estimated Value Today
   */
  estimated_value_today: number | null;
  /**
   * Estimated Value End Of Vesting
   */
  estimated_value_end_of_vesting: number | null;
};

/**
 * LiquidationPreference
 */
export type LiquidationPreferenceInput = {
  /**
   * Name
   */
  name: string;
  /**
   * Seniority
   */
  seniority: number;
  /**
   * Participating
   */
  participating?: boolean;
  /**
   * Liquidation Prices
   */
  liquidation_prices: Array<LiquidationPriceInput>;
};

/**
 * LiquidationPreference
 */
export type LiquidationPreferenceOutput = {
  /**
   * Name
   */
  name: string;
  /**
   * Seniority
   */
  seniority: number;
  /**
   * Participating
   */
  participating?: boolean;
  /**
   * Liquidation Prices
   */
  liquidation_prices: Array<LiquidationPriceOutput>;
};

/**
 * LiquidationPreferenceFulfilledEnum
 */
export enum LiquidationPreferenceFulfilledEnum {
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  EMPTY = 'empty',
}

/**
 * LiquidationPreferenceProceeds
 */
export type LiquidationPreferenceProceeds = {
  liquidation_preference: LiquidationPreferenceOutput;
  /**
   * Projected Proceeds
   */
  projected_proceeds: {
    [key: string]: PayoutDetails | null;
  };
  /**
   * Projected Proceeds Total
   */
  projected_proceeds_total: string;
  status: LiquidationPreferenceFulfilledEnum;
  /**
   * Money Out
   */
  money_out: string;
  /**
   * Expected Proceeds
   */
  expected_proceeds: {
    [key: string]: string | null;
  };
  /**
   * Expected Proceeds Total
   */
  expected_proceeds_total: string;
};

/**
 * LiquidationPrice
 */
export type LiquidationPriceInput = {
  /**
   * Share Class Id
   */
  share_class_id: string;
  liquidation_price_type: LiquidationPriceTypeEnum;
  /**
   * Multiplier
   */
  multiplier?: number | string | null;
  /**
   * Set Price
   */
  set_price?: number | string | null;
};

/**
 * LiquidationPrice
 */
export type LiquidationPriceOutput = {
  /**
   * Share Class Id
   */
  share_class_id: string;
  liquidation_price_type: LiquidationPriceTypeEnum;
  /**
   * Multiplier
   */
  multiplier?: string | null;
  /**
   * Set Price
   */
  set_price?: string | null;
};

/**
 * LiquidationPriceTypeEnum
 */
export enum LiquidationPriceTypeEnum {
  MULTIPLIER = 'multiplier',
  SET_PRICE = 'set_price',
}

/**
 * ListAllCriterion
 */
export type ListAllCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'list_all';
  criterion: ValueCriterion;
};

/**
 * ListAnyCriterion
 */
export type ListAnyCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'list_any';
  criterion: ValueCriterion;
};

export type ListCriterion = ListAllCriterion | ListAnyCriterion;

/**
 * LlmModelSpec
 */
export type LlmModelSpec = {
  /**
   * Display Name Model
   */
  display_name_model: string;
  /**
   * Display Name Provider
   */
  display_name_provider: string;
  /**
   * Max Context Window Token Count
   */
  max_context_window_token_count: number;
  /**
   * Max Response Token Count
   */
  max_response_token_count: number;
  /**
   * Credit Cost Multiplier
   */
  credit_cost_multiplier?: number;
  /**
   * Reasoning
   */
  reasoning?: boolean;
  /**
   * Region
   */
  region?: string | null;
  /**
   * Max Input Token Budget
   */
  max_input_token_budget?: number | null;
  /**
   * Chars Per Token
   */
  chars_per_token?: number | null;
};

/**
 * LlmProvider
 */
export enum LlmProvider {
  OPENAI = 'openai',
  OPENAI_EU = 'openai_eu',
  MISTRALAI = 'mistralai',
  VERTEX_AI = 'vertex_ai',
}

/**
 * LlmProviderSpec
 */
export type LlmProviderSpec = {
  /**
   * Llm Models
   */
  llm_models: {
    [key: string]: LlmModelSpec;
  };
};

/**
 * LoanInterest
 */
export type LoanInterest = {
  /**
   * Loan Amount
   */
  loan_amount: number | string;
  /**
   * Interest Rate
   */
  interest_rate: number | string;
  /**
   * Start Date
   */
  start_date: string;
  /**
   * End Date
   */
  end_date: string;
  /**
   * Days Per Year
   */
  days_per_year: number;
};

/**
 * LoanInterestResponse
 */
export type LoanInterestResponse = {
  /**
   * Interest
   */
  interest: string;
};

export type LogicCriterionInput = NotCriterionInput | AndCriterionInput | OrCriterionInput;

export type LogicCriterionOutput = NotCriterionOutput | AndCriterionOutput | OrCriterionOutput;

/**
 * MatterCriterion
 */
export type MatterCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'matter';
  name?: StringCriterion | null;
  description?: StringCriterion | null;
  type?: StringCriterion | null;
};

/**
 * MentionFieldCreate
 */
export type MentionFieldCreate = {
  /**
   * Font Family
   */
  font_family?: string;
  /**
   * Font Color
   */
  font_color?: string;
  /**
   * Font Size
   */
  font_size?: number;
  /**
   * Font Bold
   */
  font_bold?: boolean;
  /**
   * Font Italic
   */
  font_italic?: boolean;
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'mention';
  /**
   * Width
   */
  width: number;
  /**
   * Height
   */
  height: number;
  /**
   * Mention
   */
  mention: string;
  /**
   * Name
   */
  name?: string | null;
};

/**
 * ModelPricingCreate
 *
 * Schema for creating a new pricing row for a (provider, model) at a point in time.
 */
export type ModelPricingCreate = {
  provider: LlmProvider;
  /**
   * Model
   */
  model: string;
  /**
   * Effective From
   */
  effective_from: string;
  /**
   * Input Price Per Mtok
   */
  input_price_per_mtok: number | string;
  /**
   * Output Price Per Mtok
   */
  output_price_per_mtok: number | string;
  /**
   * Effective To
   */
  effective_to?: string | null;
  /**
   * Cached Input Price Per Mtok
   */
  cached_input_price_per_mtok?: number | string | null;
};

/**
 * ModelPricingInDB
 *
 * Schema representing a persisted ``model_pricing`` row.
 */
export type ModelPricingInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  provider: LlmProvider;
  /**
   * Model
   */
  model: string;
  /**
   * Effective From
   */
  effective_from: string;
  /**
   * Effective To
   */
  effective_to?: string | null;
  /**
   * Input Price Per Mtok
   */
  input_price_per_mtok: string;
  /**
   * Output Price Per Mtok
   */
  output_price_per_mtok: string;
  /**
   * Cached Input Price Per Mtok
   */
  cached_input_price_per_mtok?: string | null;
};

/**
 * ModelPricingUpdate
 *
 * Schema for updating a pricing row (typically only to close it out via ``effective_to``).
 */
export type ModelPricingUpdate = {
  provider?: LlmProvider | null;
  /**
   * Model
   */
  model?: string | null;
  /**
   * Effective From
   */
  effective_from?: string | null;
  /**
   * Effective To
   */
  effective_to?: string | null;
  /**
   * Input Price Per Mtok
   */
  input_price_per_mtok?: number | string | null;
  /**
   * Output Price Per Mtok
   */
  output_price_per_mtok?: number | string | null;
  /**
   * Cached Input Price Per Mtok
   */
  cached_input_price_per_mtok?: number | string | null;
};

/**
 * NeedsAttentionReason
 *
 * Why an effective state is ``NEEDS_ATTENTION``.
 */
export enum NeedsAttentionReason {
  MANUALLY_SET = 'manually_set',
  MISSING_EFFECTIVE_FROM = 'missing_effective_from',
  INVALID_EFFECTIVE_DATES = 'invalid_effective_dates',
}

/**
 * NonLinearPeriod
 */
export type NonLinearPeriod = {
  /**
   * Start
   */
  start: number;
  /**
   * Finish
   */
  finish: number;
  /**
   * Vesting Frequency
   */
  vesting_frequency: number;
  /**
   * Percentage
   */
  percentage: number;
};

/**
 * NonLinearVesting
 */
export type NonLinearVesting = {
  agreement: PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasNonLinearAgreement;
  /**
   * Events
   */
  events?: Array<Event> | null;
  /**
   * Non Linear Periods
   */
  non_linear_periods: Array<NonLinearPeriod>;
  /**
   * Valuations
   */
  valuations?: Array<PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasNonLinearValuation> | null;
  /**
   * Share Price
   */
  share_price?: number | null;
  /**
   * Total Number Of Shares
   */
  total_number_of_shares?: number | string | null;
  /**
   * Cap Table Totals
   */
  cap_table_totals?: Array<SharesOnDate> | null;
  /**
   * Precision
   */
  precision?: number | null;
};

/**
 * NonLinearVestingResponse
 */
export type NonLinearVestingResponse = {
  /**
   * Vesting Units
   */
  vesting_units: Array<PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasNonLinearVestingUnit>;
  /**
   * Estimated Value Today
   */
  estimated_value_today: number | null;
  /**
   * Estimated Value End Of Vesting
   */
  estimated_value_end_of_vesting: number | null;
};

/**
 * NonPlatformUserInDB
 *
 * Schema for non-platform user data as stored in database.
 */
export type NonPlatformUserInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name?: string | null;
};

/**
 * NotCriterion
 */
export type NotCriterionInput = {
  /**
   * Criterion Type
   */
  criterion_type?: 'not';
  criterion: CriterionInput;
};

/**
 * NotCriterion
 */
export type NotCriterionOutput = {
  /**
   * Criterion Type
   */
  criterion_type?: 'not';
  criterion: CriterionOutput;
};

/**
 * NotificationActionInDB
 */
export type NotificationActionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Action Type
   */
  action_type?: 'notification';
  /**
   * Recipient Email
   */
  recipient_email?: string | null;
  /**
   * Recipient Name
   */
  recipient_name?: string | null;
  /**
   * Notify Run Initiator
   */
  notify_run_initiator?: boolean;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Include Original Document
   */
  include_original_document?: boolean;
  /**
   * Include Redlined Document
   */
  include_redlined_document?: boolean;
  /**
   * Include Playbook Summary
   */
  include_playbook_summary?: boolean;
};

/**
 * NotificationActionResultInDB
 */
export type NotificationActionResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Action Uid
   */
  action_uid: string;
  /**
   * Action Trigger Uid
   */
  action_trigger_uid: string;
  /**
   * Selected Option Uid
   */
  selected_option_uid?: string | null;
  /**
   * Evaluation Result Uid
   */
  evaluation_result_uid: string;
  /**
   * Action Type
   */
  action_type?: 'notification';
  /**
   * Recipient Email
   */
  recipient_email?: string | null;
  /**
   * Provider Message Id
   */
  provider_message_id?: string | null;
};

export type NumberCriterion =
  | EqualsCriterion
  | GreaterEqualThanCriterion
  | GreaterThanCriterion
  | LessEqualThanCriterion
  | LessThanCriterion;

/**
 * OcrProvider
 */
export enum OcrProvider {
  GOOGLE = 'google',
  UNSTRUCTURED = 'unstructured',
}

/**
 * OcrProviderSpec
 */
export type OcrProviderSpec = {
  /**
   * Ocr Page Limit
   */
  ocr_page_limit: number;
};

/**
 * OptionTriggerInDB
 */
export type OptionTriggerInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Actions
   */
  actions?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb)
  >;
  /**
   * Trigger Type
   */
  trigger_type?: 'option';
  /**
   * Option Uid
   */
  option_uid: string;
};

/**
 * OrCriterion
 */
export type OrCriterionInput = {
  /**
   * Criterion Type
   */
  criterion_type?: 'or';
  /**
   * Criteria
   */
  criteria: Array<CriterionInput>;
};

/**
 * OrCriterion
 */
export type OrCriterionOutput = {
  /**
   * Criterion Type
   */
  criterion_type?: 'or';
  /**
   * Criteria
   */
  criteria: Array<CriterionOutput>;
};

/**
 * OrgFeature
 *
 * A product feature enabled for an organisation (``Organisation.features``), admins included.
 *
 * - ``CONTRACT_MANAGEMENT``: contract views and calendars (contracts themselves are ungated),
 * - ``CONTRACT_REVIEW``: playbooks, reviews, review projects and the kanban,
 * - ``CONTRACT_GENERATION``: templating and drafting,
 * - ``GAIA_SIGNING``: signing through GAIA's own Youtrust account — a signing *provider*, not a
 * surface: signing itself is gated by the role capability and works through a connected
 * Youtrust integration too.
 */
export enum OrgFeature {
  CONTRACT_MANAGEMENT = 'contract_management',
  CONTRACT_REVIEW = 'contract_review',
  CONTRACT_GENERATION = 'contract_generation',
  GAIA_SIGNING = 'gaia_signing',
}

/**
 * OrganisationAgentTaskInDB
 */
export type OrganisationAgentTaskInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: OrganisationAgentTaskType;
  status: TaskStatus;
  /**
   * Kill Switch
   */
  kill_switch: boolean;
  /**
   * Started By
   */
  started_by?: string | null;
  /**
   * Error Message
   */
  error_message?: string | null;
  started_by_user?: UserInDb | null;
  /**
   * Started By User Uid
   */
  started_by_user_uid?: string | null;
  /**
   * Chat Thread Uid
   */
  chat_thread_uid?: string | null;
  chat_backend?: ChatBackend | null;
};

/**
 * OrganisationAgentTaskSearchQuery
 *
 * Search query for organisation agent tasks.
 *
 * ``text`` matches ``started_by``; ``column_filters`` covers type/status/kill_switch/started_by;
 * ``started_by_user_uids`` filters by the initiating platform user.
 */
export type OrganisationAgentTaskSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
  /**
   * Started By User Uids
   */
  started_by_user_uids?: Array<string>;
};

/**
 * OrganisationAgentTaskSearchResult
 */
export type OrganisationAgentTaskSearchResult = {
  /**
   * Results
   */
  results: Array<OrganisationAgentTaskInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * OrganisationAgentTaskType
 *
 * Kind of work an organisation agent task performs.
 *
 * Used as the polymorphic discriminator for the organisation-agent-task hierarchy.
 */
export enum OrganisationAgentTaskType {
  CONTRACT_MANAGEMENT_REPOPULATE_CONTRACT = 'contract_management_repopulate_contract',
  CONTRACT_MANAGEMENT_CONVERT_FILE = 'contract_management_convert_file',
  INTAKE_AGENT_FILE_INTAKE = 'intake_agent_file_intake',
  REVIEW = 'review',
  PLAYBOOK = 'playbook',
  EQUITY_IMPORT = 'equity_import',
  REVIEW_PROJECT_TASK_GROUP = 'review_project_task_group',
}

/**
 * OrganisationCreate
 *
 * Schema for creating a new organisation.
 *
 * Attributes:
 * name: Organisation's display name
 * description: Optional description of organisation
 */
export type OrganisationCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Allowed Insights Credits
   */
  allowed_insights_credits?: number | null;
  allowed_insights_credits_period?: BillingPeriod | null;
  /**
   * Billing Start Date
   */
  billing_start_date?: string | null;
  /**
   * Features
   */
  features?: Array<OrgFeature>;
};

/**
 * OrganisationDailyActivity
 */
export type OrganisationDailyActivity = {
  /**
   * Organisation Uid
   */
  organisation_uid: string;
  /**
   * Organisation Name
   */
  organisation_name: string;
  /**
   * Daily Active Users
   */
  daily_active_users: {
    [key: string]: number;
  };
  /**
   * Daily Peak Points
   */
  daily_peak_points: {
    [key: string]: number;
  };
  /**
   * Daily Total Points
   */
  daily_total_points: {
    [key: string]: number;
  };
};

/**
 * OrganisationFeaturesUpdate
 *
 * Body of the platform-admin ``PUT /organisation/{uid}/features``: the full new set.
 */
export type OrganisationFeaturesUpdate = {
  /**
   * Features
   */
  features: Array<OrgFeature>;
};

/**
 * OrganisationInDB
 *
 * Schema for organisation data as stored in database.
 *
 * Attributes:
 * uid: Unique identifier
 * name: Organisation's display name
 * description: Optional description of organisation
 */
export type OrganisationInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Identifier
   */
  identifier: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Allowed Insights Credits
   */
  allowed_insights_credits: number;
  allowed_insights_credits_period: BillingPeriod;
  /**
   * Billing Start Date
   */
  billing_start_date: string | null;
  /**
   * Features
   */
  features: Array<OrgFeature>;
  default_signing_provider?: SigningProvider | null;
  /**
   * Signing Expiration Days
   */
  signing_expiration_days: number;
  /**
   * Is Admin
   */
  is_admin: boolean;
};

/**
 * OrganisationInvitationCreateRequest
 *
 * Request body for inviting an email address to the active organisation.
 *
 * ``permission_role_uids`` are extra roles (of the inviting organisation)
 * granted at accept time on top of the always-granted member role.
 */
export type OrganisationInvitationCreateRequest = {
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Permission Role Uids
   */
  permission_role_uids?: Array<string>;
};

/**
 * OrganisationInvitationInDB
 *
 * Invitation as shown to org admins in the management UI.
 */
export type OrganisationInvitationInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name: string;
  status: OrganisationInvitationStatus;
  /**
   * Expires At
   */
  expires_at: string;
  /**
   * Accepted At
   */
  accepted_at?: string | null;
  /**
   * Invited By Email
   */
  invited_by_email?: string | null;
  /**
   * Permission Roles
   */
  permission_roles: Array<PermissionRoleSlim>;
};

/**
 * OrganisationInvitationPublic
 *
 * What a plaintext invitation token publicly resolves to (accept page rendering only).
 */
export type OrganisationInvitationPublic = {
  /**
   * Organisation Name
   */
  organisation_name: string;
  /**
   * Invited Email
   */
  invited_email: string;
  /**
   * Full Name
   */
  full_name: string;
  status: OrganisationInvitationStatus;
};

/**
 * OrganisationInvitationRolesUpdateRequest
 *
 * Request body replacing the role set of a pending invitation (empty set clears it).
 */
export type OrganisationInvitationRolesUpdateRequest = {
  /**
   * Permission Role Uids
   */
  permission_role_uids: Array<string>;
};

/**
 * OrganisationInvitationStatus
 *
 * Lifecycle status of an organisation invitation.
 */
export enum OrganisationInvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REVOKED = 'revoked',
  EXPIRED = 'expired',
}

/**
 * OrganisationMemberAccess
 *
 * The organisation-wide access bits, chosen at creation and set on the protected member role.
 *
 * Every member holds that role, so these make e.g. "everyone may read every contractual party / contract"
 * the organisation's policy. Later changes go through the role update route.
 */
export type OrganisationMemberAccess = {
  all_contractual_parties_access?: AccessPermission;
  all_contracts_access?: AccessPermission;
};

/**
 * OrganisationMemoryCreate
 *
 * Schema for creating a new organisation memory.
 *
 * Args:
 * key: Unique (per organisation) handle of the memory
 * description: One-line summary of the memory, used as its index entry
 * content: Full markdown content of the memory
 * scope: Agent surface the memory is injected into
 */
export type OrganisationMemoryCreate = {
  /**
   * Key
   */
  key: string;
  /**
   * Description
   */
  description: string;
  /**
   * Content
   */
  content: string;
  scope?: OrganisationMemoryScope;
};

/**
 * OrganisationMemoryInDB
 *
 * Schema for organisation memories stored in the database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * scope: Agent surface the memory is injected into
 */
export type OrganisationMemoryInDb = {
  /**
   * Key
   */
  key: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Content
   */
  content: string;
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  scope: OrganisationMemoryScope;
};

/**
 * OrganisationMemoryScope
 *
 * Which agent surface an organisation memory is injected into.
 *
 * GENERAL memories are injected into every surface; the other scopes are only
 * injected into their own surface, always together with the GENERAL ones.
 */
export enum OrganisationMemoryScope {
  GENERAL = 'general',
  REVIEW = 'review',
  PLAYBOOKS = 'playbooks',
  CONTRACT_MANAGEMENT = 'contract_management',
  CHAT = 'chat',
}

/**
 * OrganisationMemorySearchResult
 *
 * Schema for organisation memory search results.
 */
export type OrganisationMemorySearchResult = {
  /**
   * Results
   */
  results: Array<OrganisationMemoryInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * OrganisationMemoryUpdate
 *
 * Schema for updating an existing organisation memory.
 *
 * Args:
 * key: New handle of the memory
 * description: New one-line summary of the memory
 * content: New markdown content of the memory
 * scope: New agent surface the memory is injected into
 */
export type OrganisationMemoryUpdate = {
  /**
   * Key
   */
  key?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Content
   */
  content?: string | null;
  scope?: OrganisationMemoryScope | null;
};

/**
 * OrganisationModelSettingsCreate
 */
export type OrganisationModelSettingsCreate = {
  simple_task_provider?: LlmProvider;
  /**
   * Simple Task Model
   */
  simple_task_model?: string;
  complex_task_provider?: LlmProvider;
  /**
   * Complex Task Model
   */
  complex_task_model?: string;
};

/**
 * OrganisationModelSettingsInDB
 */
export type OrganisationModelSettingsInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  simple_task_provider: LlmProvider;
  /**
   * Simple Task Model
   */
  simple_task_model: string;
  complex_task_provider: LlmProvider;
  /**
   * Complex Task Model
   */
  complex_task_model: string;
};

/**
 * OrganisationModelSettingsUpdate
 */
export type OrganisationModelSettingsUpdate = {
  simple_task_provider?: LlmProvider | null;
  /**
   * Simple Task Model
   */
  simple_task_model?: string | null;
  complex_task_provider?: LlmProvider | null;
  /**
   * Complex Task Model
   */
  complex_task_model?: string | null;
};

/**
 * OrganisationSearchResult
 *
 * A result schema for organisation searches.
 *
 * Attributes:
 * results: List of organisation search results
 * results_count: Total number of matching results
 */
export type OrganisationSearchResult = {
  /**
   * Results
   */
  results: Array<OrganisationInDb>;
  /**
   * Results Count
   */
  results_count: number;
};

/**
 * OrganisationSelfServiceCreate
 *
 * Body of ``POST /me/organisations`` (self-service organisation creation).
 *
 * Deliberately narrow: credits, billing period and features come from the sign-up recipe,
 * never from the caller. ``hosting_zone`` has the same semantics as ``SignupProfile.hosting_zone``
 * (``"us"`` selects the US model providers).
 */
export type OrganisationSelfServiceCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Hosting Zone
   */
  hosting_zone?: string | null;
  member_access?: OrganisationMemberAccess;
};

/**
 * OrganisationUpdate
 *
 * Schema for updating an existing organisation.
 *
 * All fields are optional to allow partial updates.
 *
 * Attributes:
 * name: Organisation's display name
 * description: Description of organisation
 */
export type OrganisationUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Allowed Insights Credits
   */
  allowed_insights_credits?: number | null;
  allowed_insights_credits_period?: BillingPeriod | null;
  /**
   * Billing Start Date
   */
  billing_start_date?: string | null;
  /**
   * Signing Expiration Days
   */
  signing_expiration_days?: number | null;
};

/**
 * OrganisationsStatistics
 */
export type OrganisationsStatistics = {
  /**
   * Newest Organisations
   */
  newest_organisations: Array<OrganisationInDb>;
  /**
   * Average Users Per Organisation
   */
  average_users_per_organisation: number;
};

/**
 * OwnerCriterion
 *
 * Filter by contract owner (User) with field-level equals/contains, like MatterCriterion.
 */
export type OwnerCriterion = {
  /**
   * Criterion Type
   */
  criterion_type?: 'owner';
  email?: StringCriterion | null;
  full_name?: StringCriterion | null;
};

/**
 * ParticipantCreate
 *
 * A contractual party taking part in a signing request. The signer's contact data is read from the party
 * itself, or for a legal entity from the representative signing for it.
 */
export type ParticipantCreate = {
  /**
   * Contractual Party Uid
   */
  contractual_party_uid: string;
  /**
   * Representative Uid
   */
  representative_uid?: string | null;
  role: SigningRequestSigneeRole;
  signature_type: SignatureType;
};

/**
 * ParticipantInDB
 *
 * Read schema for a Participant in a signing request.
 */
export type ParticipantInDb = {
  /**
   * Uid
   */
  uid: string;
  role: SigningRequestSigneeRole;
  signature_type?: SignatureType | null;
  /**
   * Contractual Party
   */
  contractual_party?:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb)
    | null;
  representative?: ContractManagementContractualPartyNaturalPersonInDb | null;
  status?: ParticipantStatus | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Last Manual Reminder Sent At
   */
  last_manual_reminder_sent_at?: string | null;
};

/**
 * ParticipantStatus
 *
 * Internal participant status, provider-agnostic.
 */
export enum ParticipantStatus {
  APPROVED = 'approved',
  DECLINED = 'declined',
  SIGNED = 'signed',
  ERROR = 'error',
  IDENTIFICATION_BLOCKED = 'identification_blocked',
  IDENTIFICATION_FAILED = 'identification_failed',
  IDENTIFICATION_SUCCEEDED = 'identification_succeeded',
  IDENTITY_SAVED = 'identity_saved',
  VIEWED = 'viewed',
  DELIVERY_FAILED = 'delivery_failed',
  NOTIFIED = 'notified',
  REJECTED = 'rejected',
  SENDER_CONTACTED = 'sender_contacted',
  INITIATED = 'initiated',
  PROCESSING = 'processing',
  ABORTED = 'aborted',
}

/**
 * PayoutDetails
 */
export type PayoutDetails = {
  /**
   * Number Of Shares
   */
  number_of_shares: number | string;
  /**
   * Investment
   */
  investment: string | null;
  /**
   * Projected Payout
   */
  projected_payout: string | null;
};

/**
 * PermissionRoleCreate
 *
 * Schema for creating a new role.
 */
export type PermissionRoleCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Can Create Review
   */
  can_create_review?: boolean;
  /**
   * Can Update Review
   */
  can_update_review?: boolean;
  /**
   * Can Create Playbooks
   */
  can_create_playbooks?: boolean;
  /**
   * Can Use Playbooks
   */
  can_use_playbooks?: boolean;
  /**
   * Can Use Signing
   */
  can_use_signing?: boolean;
  /**
   * Can Use Review Projects
   */
  can_use_review_projects?: boolean;
  all_contractual_parties_access?: AccessPermission;
  all_contracts_access?: AccessPermission;
};

/**
 * PermissionRoleInDB
 *
 * Schema for role data as stored in database.
 *
 * Basic role information for internal use.
 */
export type PermissionRoleInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Protected
   */
  protected: boolean;
  /**
   * Can Create Review
   */
  can_create_review: boolean;
  /**
   * Can Update Review
   */
  can_update_review: boolean;
  /**
   * Can Create Playbooks
   */
  can_create_playbooks: boolean;
  /**
   * Can Use Playbooks
   */
  can_use_playbooks: boolean;
  /**
   * Can Use Signing
   */
  can_use_signing: boolean;
  /**
   * Can Use Review Projects
   */
  can_use_review_projects: boolean;
  all_contractual_parties_access: AccessPermission;
  all_contracts_access: AccessPermission;
  /**
   * User Count
   */
  user_count: number;
  /**
   * Cm Contract Count
   */
  cm_contract_count: number;
};

/**
 * PermissionRoleSearchResult
 */
export type PermissionRoleSearchResult = {
  /**
   * Results
   */
  results: Array<PermissionRoleInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * PermissionRoleSlim
 *
 * Minimal role rendering (uid + name), e.g. on pending organisation invitations.
 *
 * Deliberately excludes the ``PermissionRoleInDB`` count properties, which
 * trigger an extra query per role.
 */
export type PermissionRoleSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name: string;
};

/**
 * PermissionRoleUpdate
 *
 * Schema for updating an existing role.
 *
 * All fields are optional to allow partial updates.
 */
export type PermissionRoleUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Can Create Review
   */
  can_create_review?: boolean | null;
  /**
   * Can Update Review
   */
  can_update_review?: boolean | null;
  /**
   * Can Create Playbooks
   */
  can_create_playbooks?: boolean | null;
  /**
   * Can Use Playbooks
   */
  can_use_playbooks?: boolean | null;
  /**
   * Can Use Signing
   */
  can_use_signing?: boolean | null;
  /**
   * Can Use Review Projects
   */
  can_use_review_projects?: boolean | null;
  all_contractual_parties_access?: AccessPermission | null;
  all_contracts_access?: AccessPermission | null;
};

/**
 * PlaybookActionCreateRequest
 *
 * API body for creating an action; the option comes from the path.
 *
 * The endpoint dispatches on ``action_type``: ``instruction`` applies to suggestion actions,
 * ``recipient_*`` / ``notify_run_initiator`` to notification actions, ``message`` to notification,
 * signature-preparation and slack-notification actions, ``channel_id`` to slack-notification actions,
 * ``include_playbook_summary`` to notification, slack-notification and jira-notification actions,
 * ``include_redlined_document`` to notification and jira-notification actions,
 * ``include_run_initiator`` to slack-notification and jira-notification actions, and
 * ``target_evaluation_uid`` to evaluation actions. A jira-notification action takes no target
 * field: its issue comes from the run.
 */
export type PlaybookActionCreateRequest = {
  action_type: PlaybookActionType;
  /**
   * Name
   */
  name: string;
  /**
   * Channel Id
   */
  channel_id?: string | null;
  /**
   * Instruction
   */
  instruction?: string | null;
  /**
   * Example Data
   */
  example_data?: string | null;
  /**
   * Use Web Search
   */
  use_web_search?: boolean;
  /**
   * Use Organisation Clause Library
   */
  use_organisation_clause_library?: boolean;
  /**
   * Use Gaia Clause Library
   */
  use_gaia_clause_library?: boolean;
  /**
   * Recipient Email
   */
  recipient_email?: string | null;
  /**
   * Recipient Name
   */
  recipient_name?: string | null;
  /**
   * Notify Run Initiator
   */
  notify_run_initiator?: boolean;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Include Original Document
   */
  include_original_document?: boolean;
  /**
   * Include Redlined Document
   */
  include_redlined_document?: boolean;
  /**
   * Include Playbook Summary
   */
  include_playbook_summary?: boolean;
  /**
   * Include Run Initiator
   */
  include_run_initiator?: boolean;
  /**
   * Target Evaluation Uid
   */
  target_evaluation_uid?: string | null;
};

/**
 * PlaybookActionType
 *
 * Type of action attached to a playbook option.
 *
 * Used as the polymorphic discriminator for the action hierarchy.
 */
export enum PlaybookActionType {
  SUGGESTION = 'suggestion',
  NOTIFICATION = 'notification',
  EVALUATION = 'evaluation',
  SIGNATURE_PREPARATION = 'signature_preparation',
  SLACK_NOTIFICATION = 'slack_notification',
  JIRA_NOTIFICATION = 'jira_notification',
}

/**
 * PlaybookActionUpdate
 *
 * Shared, updatable fields across all action subtypes.
 *
 * A partial update: omitted fields are left alone, while an explicitly provided ``null`` clears the
 * field. Only the nullable columns may be cleared that way — ``instruction``, ``example_data``,
 * ``recipient_email``, ``recipient_name`` and ``message``.
 */
export type PlaybookActionUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Channel Id
   */
  channel_id?: string | null;
  /**
   * Instruction
   */
  instruction?: string | null;
  /**
   * Example Data
   */
  example_data?: string | null;
  /**
   * Use Web Search
   */
  use_web_search?: boolean;
  /**
   * Use Organisation Clause Library
   */
  use_organisation_clause_library?: boolean;
  /**
   * Use Gaia Clause Library
   */
  use_gaia_clause_library?: boolean;
  /**
   * Recipient Email
   */
  recipient_email?: string | null;
  /**
   * Recipient Name
   */
  recipient_name?: string | null;
  /**
   * Notify Run Initiator
   */
  notify_run_initiator?: boolean | null;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Include Original Document
   */
  include_original_document?: boolean;
  /**
   * Include Redlined Document
   */
  include_redlined_document?: boolean;
  /**
   * Include Playbook Summary
   */
  include_playbook_summary?: boolean;
  /**
   * Include Run Initiator
   */
  include_run_initiator?: boolean;
};

/**
 * PlaybookCreateRequest
 *
 * API request schema for creating a playbook with optional task dispatch.
 *
 * Args:
 * contract_type_uids: The organisation's contract types the playbook is meant for; none
 * means any contract type.
 */
export type PlaybookCreateRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Contract Type Uids
   */
  contract_type_uids?: Array<string>;
  party_position: PlaybookPartyPosition;
  status?: PlaybookStatus;
  /**
   * Language
   */
  language: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Summary Prompt
   */
  summary_prompt?: string | null;
  /**
   * Add Clauses To Library
   */
  add_clauses_to_library?: boolean;
  /**
   * Generate With Ai
   */
  generate_with_ai?: boolean;
  user_input?: PlaybookUserInput | null;
  /**
   * Add Actions
   */
  add_actions?: boolean;
};

/**
 * PlaybookEvaluationCreateRequest
 *
 * API body for creating an evaluation; the playbook comes from the path.
 */
export type PlaybookEvaluationCreateRequest = {
  evaluation_type: PlaybookEvaluationType;
  /**
   * Name
   */
  name: string;
  /**
   * Extraction Prompt
   */
  extraction_prompt: string;
  /**
   * Multiple Choices
   */
  multiple_choices?: boolean;
  missing_clause_tier?: ClauseValidationOptionType;
  response_type?: PlaybookExtractionResponseType;
};

/**
 * PlaybookEvaluationGroupCreateRequest
 *
 * API body for creating an evaluation group; the playbook comes from the path.
 */
export type PlaybookEvaluationGroupCreateRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Evaluation Uids
   */
  evaluation_uids?: Array<string>;
};

/**
 * PlaybookEvaluationGroupInDB
 */
export type PlaybookEvaluationGroupInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Evaluation Uids
   */
  evaluation_uids?: Array<string>;
  /**
   * Triggers
   */
  triggers?: Array<GroupTriggerInDb>;
};

/**
 * PlaybookEvaluationGroupUpdateRequest
 *
 * API body for updating an evaluation group; omitted fields are left unchanged.
 */
export type PlaybookEvaluationGroupUpdateRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Evaluation Uids
   */
  evaluation_uids?: Array<string> | null;
};

/**
 * PlaybookEvaluationType
 *
 * Use case an evaluation (and its options) belongs to.
 *
 * Used as the polymorphic discriminator for the evaluation and option hierarchies.
 */
export enum PlaybookEvaluationType {
  CLASSIFICATION = 'classification',
  CLAUSE_VALIDATION = 'clause_validation',
  UNCOMMONS_CHECK = 'uncommons_check',
  EXTRACTION = 'extraction',
}

/**
 * PlaybookEvaluationUpdate
 *
 * Shared, updatable fields common to all evaluation subtypes.
 */
export type PlaybookEvaluationUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Extraction Prompt
   */
  extraction_prompt?: string | null;
  missing_clause_tier?: ClauseValidationOptionType | null;
};

/**
 * PlaybookExtractionResponseType
 *
 * Selectable answer type for an extraction evaluation (base types + their lists only).
 *
 * Every value is a key of ``INFORMATION_EXTRACTION_TYPE_MAP`` so the shared review extraction agents
 * resolve it unchanged. Deliberately excludes the richer review types (CalendarEvents, Entity,
 * CurrencyAmount) — playbook extraction only surfaces base scalars and their lists.
 */
export enum PlaybookExtractionResponseType {
  STR = 'str',
  INT = 'int',
  FLOAT = 'float',
  BOOL = 'bool',
  DATE = 'date',
  DATETIME = 'datetime',
  LIST_STR = 'list[str]',
  LIST_INT = 'list[int]',
  LIST_FLOAT = 'list[float]',
  LIST_BOOL = 'list[bool]',
  LIST_DATE = 'list[date]',
  LIST_DATETIME = 'list[datetime]',
}

/**
 * PlaybookInDB
 */
export type PlaybookInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Contract Types
   */
  contract_types: Array<ContractTypeRef>;
  party_position: PlaybookPartyPosition;
  status: PlaybookStatus;
  /**
   * Language
   */
  language: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Summary Prompt
   */
  summary_prompt?: string | null;
  /**
   * Add Clauses To Library
   */
  add_clauses_to_library: boolean;
  /**
   * Generate With Ai
   */
  generate_with_ai: boolean;
  /**
   * Generation Prompt
   */
  generation_prompt?: string | null;
  template_name?: PlaybookTemplateName | null;
  /**
   * Updated Since Template Import
   */
  updated_since_template_import?: boolean;
};

/**
 * PlaybookOptionCreateRequest
 *
 * API body for creating an option; the evaluation comes from the path.
 *
 * ``option_type`` and ``clause_uid`` only apply to clause-validation evaluations.
 */
export type PlaybookOptionCreateRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Definition
   */
  definition: string;
  /**
   * Color
   */
  color?: string | null;
  risk_level?: PlaybookRiskLevel | null;
  option_type?: ClauseValidationOptionType | null;
  /**
   * Clause Uid
   */
  clause_uid?: string | null;
};

/**
 * PlaybookOptionUpdateRequest
 *
 * API body for updating an option. ``clause_uid`` re-points a clause-validation option.
 */
export type PlaybookOptionUpdateRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Definition
   */
  definition?: string | null;
  /**
   * Color
   */
  color?: string | null;
  risk_level?: PlaybookRiskLevel | null;
  option_type?: ClauseValidationOptionType | null;
  /**
   * Clause Uid
   */
  clause_uid?: string | null;
};

/**
 * PlaybookPartyPosition
 *
 * The negotiating stance a playbook is written from.
 */
export enum PlaybookPartyPosition {
  COMPANY = 'company',
  THIRD_PARTY = 'third_party',
  NEUTRAL = 'neutral',
}

/**
 * PlaybookRiskLevel
 *
 * Risk level attached to a playbook option.
 */
export enum PlaybookRiskLevel {
  NONE = 'none',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

/**
 * PlaybookRunCreateRequest
 *
 * Public, uid-based body for starting a run; the endpoint resolves uids to the int-id
 * ``PlaybookRunCreate``. The playbook itself comes from the path, so only the input is carried here.
 */
export type PlaybookRunCreateRequest = {
  /**
   * Input Vault File Revision Uid
   */
  input_vault_file_revision_uid?: string | null;
  /**
   * Raw Input
   */
  raw_input?: Array<DocumentBlock> | null;
  /**
   * Review Project Uid
   */
  review_project_uid?: string | null;
  /**
   * Skip Action Types
   *
   * Action types this run must not execute. Actions of these types are recorded as skipped. Pass every action type to start the run without executing any action; pass an empty list to execute all of them.
   */
  skip_action_types: Array<PlaybookActionType>;
};

/**
 * PlaybookRunEvaluationExportFormat
 *
 * Spreadsheet format a playbook run's evaluation results can be downloaded in.
 */
export enum PlaybookRunEvaluationExportFormat {
  CSV = 'csv',
  XLSX = 'xlsx',
}

/**
 * PlaybookRunEvaluationExportRequest
 *
 * Which of a run's evaluations to export, and in which spreadsheet format.
 */
export type PlaybookRunEvaluationExportRequest = {
  export_format: PlaybookRunEvaluationExportFormat;
  /**
   * Evaluation Uids
   */
  evaluation_uids?: Array<string> | null;
};

/**
 * PlaybookRunInDB
 *
 * Full run detail, including the executed tree of evaluation results.
 */
export type PlaybookRunInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: PlaybookRunStatus;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Started By Email
   */
  started_by_email?: string | null;
  started_by_user?: UserInDbSlim | null;
  /**
   * Playbook Uid
   */
  playbook_uid: string;
  input_vault_file_revision?: VaultFileRevisionInDb | null;
  output_vault_file_revision?: VaultFileRevisionInDb | null;
  review_project?: ReviewProjectInDb | null;
  /**
   * Raw Input
   */
  raw_input?: Array<string> | null;
  /**
   * Markdown Summary
   */
  markdown_summary?: string | null;
  /**
   * Skip Action Types
   */
  skip_action_types: Array<PlaybookActionType>;
  /**
   * Evaluation Results
   */
  evaluation_results?: Array<
    | ({
        evaluation_type: 'classification';
      } & ClassificationEvaluationResultInDb)
    | ({
        evaluation_type: 'clause_validation';
      } & ClauseValidationEvaluationResultInDb)
    | ({
        evaluation_type: 'uncommons_check';
      } & UncommonsCheckEvaluationResultInDb)
    | ({
        evaluation_type: 'extraction';
      } & ExtractionEvaluationResultInDb)
  >;
};

/**
 * PlaybookRunSearchQuery
 */
export type PlaybookRunSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
  /**
   * Input Type
   */
  input_type?: 'raw_input' | 'file' | null;
};

/**
 * PlaybookRunSearchResult
 */
export type PlaybookRunSearchResult = {
  /**
   * Results
   */
  results: Array<PlaybookRunInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * PlaybookRunStatus
 *
 * ``TaskStatus`` + ``SUMMARIZING``, for the run-level playbook aggregate status.
 */
export enum PlaybookRunStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
  PARTIAL = 'partial',
  SKIPPED = 'skipped',
  CANCELLED = 'cancelled',
  WARNING = 'warning',
  SUMMARIZING = 'summarizing',
}

/**
 * PlaybookRunSummary
 *
 * Slim run metadata without the nested results (e.g. the just-started run returned on create).
 */
export type PlaybookRunSummary = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: PlaybookRunStatus;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Started By Email
   */
  started_by_email?: string | null;
  started_by_user?: UserInDbSlim | null;
  /**
   * Playbook Uid
   */
  playbook_uid: string;
  input_vault_file_revision?: VaultFileRevisionInDb | null;
  output_vault_file_revision?: VaultFileRevisionInDb | null;
  review_project?: ReviewProjectInDb | null;
  /**
   * Raw Input
   */
  raw_input?: Array<string> | null;
  /**
   * Markdown Summary
   */
  markdown_summary?: string | null;
  /**
   * Skip Action Types
   */
  skip_action_types: Array<PlaybookActionType>;
};

/**
 * PlaybookRunSummaryExportFormat
 *
 * File format a playbook run's markdown summary can be downloaded in.
 */
export enum PlaybookRunSummaryExportFormat {
  MD = 'md',
  PDF = 'pdf',
}

/**
 * PlaybookSearchQuery
 */
export type PlaybookSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
};

/**
 * PlaybookSearchResult
 */
export type PlaybookSearchResult = {
  /**
   * Results
   */
  results: Array<PlaybookInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * PlaybookStatistics
 *
 * Admin-level playbook statistics.
 *
 * Covers both the *configured* structure (definition rows the playbooks are built from) and the
 * *executed* activity (result rows produced during runs). Configured metrics are scoped to
 * playbooks in the org/time window; executed metrics are scoped to runs in the window and count
 * latest attempts only. Type breakdowns are keyed by the relevant enums; the action cross-tabs are
 * nested ``evaluation_type -> action_type -> count``.
 */
export type PlaybookStatistics = {
  /**
   * Total Playbooks
   */
  total_playbooks: number;
  /**
   * Total Runs
   */
  total_runs: number;
  /**
   * Average Runs Per Playbook
   */
  average_runs_per_playbook: number;
  /**
   * Total Configured Evaluations
   */
  total_configured_evaluations: number;
  /**
   * Configured Evaluations By Type
   */
  configured_evaluations_by_type?: {
    [key in PlaybookEvaluationType]?: number;
  };
  /**
   * Average Configured Evaluations Per Playbook
   */
  average_configured_evaluations_per_playbook: number;
  /**
   * Configured Actions By Eval And Action Type
   */
  configured_actions_by_eval_and_action_type?: {
    [key in PlaybookEvaluationType]?: {
      [key in PlaybookActionType]?: number;
    };
  };
  /**
   * Average Configured Actions Per Evaluation
   */
  average_configured_actions_per_evaluation: number;
  /**
   * Total Evaluation Results
   */
  total_evaluation_results: number;
  /**
   * Evaluation Results By Type
   */
  evaluation_results_by_type?: {
    [key in PlaybookEvaluationType]?: number;
  };
  /**
   * Average Evaluation Results Per Run
   */
  average_evaluation_results_per_run: number;
  /**
   * Action Results By Eval And Action Type
   */
  action_results_by_eval_and_action_type?: {
    [key in PlaybookEvaluationType]?: {
      [key in PlaybookActionType]?: number;
    };
  };
  /**
   * Average Action Results Per Evaluation Result
   */
  average_action_results_per_evaluation_result: number;
};

/**
 * PlaybookStatus
 *
 * Lifecycle status of a playbook.
 */
export enum PlaybookStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

/**
 * PlaybookTemplateName
 *
 * Identity of a code-defined playbook template (core.playbooks.templates).
 *
 * Stamped on playbooks created from a template so template updates can locate them later.
 */
export enum PlaybookTemplateName {
  NDA_CONFIDENTIALITY_RISK = 'nda-confidentiality-risk',
  DPA_DATA_PROCESSING = 'dpa-data-processing',
  SALES_AND_PURCHASE_AGREEMENTS = 'sales-and-purchase-agreements',
  LICENSING_AND_TECHNOLOGY_AGREEMENTS = 'licensing-and-technology-agreements',
  DISTRIBUTION_AND_SUPPLY_AGREEMENTS = 'distribution-and-supply-agreements',
  MARKETING_SPONSORSHIP_AND_EVENT_AGREEMENTS = 'marketing-sponsorship-and-event-agreements',
  OUTSOURCING_AND_AGENCY_AGREEMENTS = 'outsourcing-and-agency-agreements',
  CONSULTING_AND_SERVICE_AGREEMENTS = 'consulting-and-service-agreements',
  INCORPORATION_AND_FOUNDATIONAL_DOCS = 'incorporation-and-foundational-docs',
  BOARD_RESOLUTIONS_AND_MEETING_MINUTES = 'board-resolutions-and-meeting-minutes',
  EMPLOYMENT_CONTRACTS = 'employment-contracts',
  OFFER_LETTERS_AND_ONBOARDING_DOCS = 'offer-letters-and-onboarding-docs',
  FREELANCER_AND_CONTRACTOR_AGREEMENTS = 'freelancer-and-contractor-agreements',
  TERMINATIONS_AND_SEPARATIONS = 'terminations-and-separations',
  IP_AND_SOFTWARE_LICENSES = 'ip-and-software-licenses',
  DIGITAL_SERVICES = 'digital-services',
  POWER_OF_ATTORNEY = 'power-of-attorney',
  E_COMMERCE = 'e-commerce',
  COMMERCIAL_LEASE_AGREEMENTS = 'commercial-lease-agreements',
}

/**
 * PlaybookTemplateSummary
 *
 * Display metadata for one code-defined playbook template.
 *
 * Built at the call sites from the template registry (core.playbooks.templates) — this schema
 * must not import from there, as core.playbooks itself imports core.schemas.
 */
export type PlaybookTemplateSummary = {
  key: PlaybookTemplateName;
  /**
   * Name
   */
  name: string;
  /**
   * Contract Type Keys
   */
  contract_type_keys: Array<ContractTypeTemplateKey>;
  party_position: PlaybookPartyPosition;
  /**
   * Language
   */
  language: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Group
   */
  group: string;
  /**
   * Evaluation Count
   */
  evaluation_count: number;
  /**
   * Action Count
   */
  action_count: number;
};

/**
 * PlaybookTriggerCreateRequest
 *
 * API body for creating a trigger; the evaluation comes from the path.
 *
 * Uncommons checks take a ``finding_threshold`` trigger built from ``mode`` / ``min_severity`` /
 * ``min_count`` (``min_severity`` / ``min_count`` apply to ``at_least`` and ``less_than``;
 * ``none_found`` and ``always`` ignore them). Clause-validation and
 * classification evaluations are option-based, so an ``option`` trigger is created (or returned, if
 * one already exists) for the option named by ``option_uid``.
 */
export type PlaybookTriggerCreateRequest = {
  trigger_type: PlaybookTriggerType;
  /**
   * Option Uid
   */
  option_uid?: string | null;
  mode?: ActionTriggerMode;
  /**
   * Min Severity
   */
  min_severity?: 'low' | 'medium' | 'high';
  /**
   * Min Count
   */
  min_count?: number;
};

/**
 * PlaybookTriggerType
 *
 * What a trigger evaluates to decide whether its actions fire.
 *
 * Used as the polymorphic discriminator for the action-trigger hierarchy. A trigger owns the
 * actions and answers one question: "am I satisfied by this evaluation's result?".
 */
export enum PlaybookTriggerType {
  OPTION = 'option',
  FINDING_THRESHOLD = 'finding_threshold',
  GROUP = 'group',
}

/**
 * PlaybookTriggerUpdate
 *
 * API body for updating a finding-threshold trigger.
 */
export type PlaybookTriggerUpdate = {
  mode?: ActionTriggerMode | null;
  /**
   * Min Severity
   */
  min_severity?: 'low' | 'medium' | 'high' | null;
  /**
   * Min Count
   */
  min_count?: number | null;
};

/**
 * PlaybookUpdate
 *
 * Partial update of a playbook.
 *
 * Args:
 * contract_type_uids: None leaves the contract type links unchanged; a list replaces them
 * (an empty list means any contract type). Not a column: ``playbook_repo.update`` applies
 * it through the link table.
 */
export type PlaybookUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Contract Type Uids
   */
  contract_type_uids?: Array<string> | null;
  party_position?: PlaybookPartyPosition | null;
  status?: PlaybookStatus | null;
  /**
   * Language
   */
  language?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Summary Prompt
   */
  summary_prompt?: string | null;
  /**
   * Add Clauses To Library
   */
  add_clauses_to_library?: boolean | null;
};

/**
 * PlaybookUserInput
 *
 * User input for playbook creation: either a vault file UID, raw text, or both.
 */
export type PlaybookUserInput = {
  /**
   * Vault File Uid
   */
  vault_file_uid?: string | null;
  /**
   * Generation Prompt
   */
  generation_prompt?: string | null;
};

/**
 * PotentialValuation
 */
export type PotentialValuation = {
  /**
   * Share To Option Ratio
   */
  share_to_option_ratio: number | string;
  /**
   * Total Number Of Shares
   */
  total_number_of_shares?: number | string | null;
  /**
   * Cap Table Totals
   */
  cap_table_totals?: Array<SharesOnDate> | null;
  /**
   * Exercise Price
   */
  exercise_price?: number | string;
  /**
   * Valuations
   */
  valuations: Array<PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasPotentialValuation>;
  /**
   * Vesting Units
   */
  vesting_units: Array<VestingUnitInput>;
};

/**
 * PotentialValuationResponseItem
 */
export type PotentialValuationResponseItem = {
  /**
   * Year
   */
  year: number;
  /**
   * Vested Options
   */
  vested_options: string;
  /**
   * Valuation
   */
  valuation: string;
};

/**
 * PreprocessingTaskStatus
 *
 * ``TaskStatus`` + ``ARCHIVE``, for the vault-file preprocessing status columns.
 */
export enum PreprocessingTaskStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
  PARTIAL = 'partial',
  SKIPPED = 'skipped',
  CANCELLED = 'cancelled',
  WARNING = 'warning',
  ARCHIVE = 'archive',
}

/**
 * Price
 */
export type Price = {
  /**
   * Amount
   */
  amount: number | string;
  /**
   * Currency Code
   */
  currency_code: string;
};

/**
 * PromptCollectionCreate
 *
 * Schema for creating a new vault file.
 *
 * Args:
 * name: Name of the result
 */
export type PromptCollectionCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Summary Prompt
   */
  summary_prompt?: string | null;
};

/**
 * PromptCollectionCreateAI
 *
 * Schema for creating a new vault file.
 *
 * Args:
 * name: Name of the result
 */
export type PromptCollectionCreateAi = {
  /**
   * Name
   */
  name: string;
  /**
   * User Instructions
   */
  user_instructions: string;
  /**
   * Context
   */
  context: string;
  /**
   * Summary Prompt
   */
  summary_prompt: string;
};

/**
 * PromptCollectionInDB
 *
 * Schema for updating an existing vault file.
 *
 * Args:
 * name: New name of the result
 * prompt_count: Number of prompts in the collection, or None when not computed for
 * this response (only populated by the search endpoint)
 */
export type PromptCollectionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Summary Prompt
   */
  summary_prompt?: string | null;
  /**
   * Prompt Count
   */
  prompt_count?: number | null;
};

/**
 * PromptCollectionSearchResult
 */
export type PromptCollectionSearchResult = {
  /**
   * Results
   */
  results: Array<PromptCollectionInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * PromptCollectionUpdate
 *
 * Schema for updating an existing vault file.
 *
 * Args:
 * name: New name of the result
 */
export type PromptCollectionUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Summary Prompt
   */
  summary_prompt?: string | null;
};

/**
 * PromptCreate
 *
 * Schema for creating a new vault file.
 *
 * Args:
 * name: Name of the result
 */
export type PromptCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Example Data
   */
  example_data?: Array<string> | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  /**
   * Evaluation Prompt
   */
  evaluation_prompt: string | null;
  /**
   * Evaluation Response Type
   */
  evaluation_response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType
    | null;
  /**
   * Suggestion Prompt
   */
  suggestion_prompt?: string | null;
  /**
   * Suggestion Example Data
   */
  suggestion_example_data?: Array<string> | null;
  /**
   * Suggestion Tool Selection
   */
  suggestion_tool_selection?: Array<SuggestionTools> | null;
};

/**
 * PromptInDB
 *
 * Schema for updating an existing vault file.
 *
 * Args:
 * name: New name of the result
 */
export type PromptInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  /**
   * Example Data
   */
  example_data?: Array<string> | null;
  /**
   * Evaluation Prompt
   */
  evaluation_prompt: string | null;
  /**
   * Evaluation Response Type
   */
  evaluation_response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType
    | null;
  /**
   * Suggestion Prompt
   */
  suggestion_prompt?: string | null;
  /**
   * Suggestion Example Data
   */
  suggestion_example_data?: Array<string> | null;
  /**
   * Suggestion Tool Selection
   */
  suggestion_tool_selection?: Array<SuggestionTools> | null;
};

/**
 * PromptResponse
 */
export type PromptResponse = {
  /**
   * Name
   */
  name: string;
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Index
   */
  index: number;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  /**
   * Evaluation Prompt
   */
  evaluation_prompt: string | null;
  /**
   * Evaluation Response Type
   */
  evaluation_response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType
    | null;
  /**
   * Suggestion Prompt
   */
  suggestion_prompt?: string | null;
  /**
   * Suggestion Tool Selection
   */
  suggestion_tool_selection?: Array<SuggestionTools> | null;
  /**
   * Suggestion Example Data
   */
  suggestion_example_data?: Array<string> | null;
  /**
   * Tags
   */
  tags: Array<string>;
};

/**
 * PromptUpdate
 *
 * Schema for updating an existing vault file.
 *
 * Args:
 * name: New name of the result
 */
export type PromptUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Example Data
   */
  example_data?: Array<string> | null;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType
    | null;
  /**
   * Evaluation Prompt
   */
  evaluation_prompt: string | null;
  /**
   * Evaluation Response Type
   */
  evaluation_response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType
    | null;
  /**
   * Suggestion Prompt
   */
  suggestion_prompt?: string | null;
  /**
   * Suggestion Example Data
   */
  suggestion_example_data?: Array<string> | null;
  /**
   * Suggestion Tool Selection
   */
  suggestion_tool_selection?: Array<SuggestionTools> | null;
};

/**
 * ProrataAmounts
 */
export type ProrataAmounts = {
  /**
   * Total Target Investment
   */
  total_target_investment: number | string;
  /**
   * Cap Table
   */
  cap_table: Array<
    | PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingCapTableRowStakeholder
    | PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingCapTableRowEsopPoolInput
  >;
  /**
   * Share Classes
   */
  share_classes: Array<PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingShareClass>;
};

/**
 * ProrataAmountsResponse
 */
export type ProrataAmountsResponse = {
  /**
   * Pro Rata Rights
   */
  pro_rata_rights: {
    [key: string]: string;
  };
};

/**
 * ProviderFileLimit
 */
export type ProviderFileLimit = {
  /**
   * Max Size B
   */
  max_size_b: number;
  /**
   * Allowed File Extensions
   */
  allowed_file_extensions: Array<string>;
};

/**
 * ReadOnlyTextFieldCreate
 */
export type ReadOnlyTextFieldCreate = {
  /**
   * Font Family
   */
  font_family?: string;
  /**
   * Font Color
   */
  font_color?: string;
  /**
   * Font Size
   */
  font_size?: number;
  /**
   * Font Bold
   */
  font_bold?: boolean;
  /**
   * Font Italic
   */
  font_italic?: boolean;
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'read_only_text';
  /**
   * Width
   */
  width: number;
  /**
   * Height
   */
  height: number;
  /**
   * Text
   */
  text: string;
};

/**
 * RelatedContractFilesResponse
 *
 * Response schema for an extracted model showing related contract files.
 *
 * This schema provides a paginated view of contract files that are connected to an extracted model
 * through file chunks. Each contract file includes its related contract and vault file, allowing
 * the frontend to display the full relationship chain in a single response.
 *
 * The pagination is unified - scrolling loads more contract files, not separate pagination for
 * each entity type. This matches the frontend UX where users see a list of contract files and
 * can click on each to view its vault file and navigate through pages.
 *
 * Attributes:
 * contract_files: Paginated list of contract files with their contracts and vault files
 */
export type RelatedContractFilesResponse = {
  contract_files: ContractFileWithRelationsSearchResult;
};

/**
 * RenderTagRequest
 *
 * Input to POST /render-tag — the popover sends this on every keystroke.
 */
export type RenderTagRequest = {
  /**
   * Kind
   */
  kind?: 'inline' | 'block_start' | 'block_middle' | 'block_end';
  /**
   * Field Name
   */
  field_name?: string | null;
  /**
   * Binding
   */
  binding?:
    | ({
        kind: 'primitive';
      } & AnnotationPrimitiveBinding)
    | ({
        kind: 'entity';
      } & AnnotationEntityBinding)
    | ({
        kind: 'object';
      } & AnnotationObjectBinding)
    | null;
  /**
   * Default Value
   */
  default_value?: string | null;
  /**
   * Selection Options
   */
  selection_options?: Array<string> | null;
  /**
   * Filters
   */
  filters?: Array<AnnotationFilter>;
  /**
   * Condition
   */
  condition?: string | null;
};

/**
 * RenderTagResponse
 */
export type RenderTagResponse = {
  /**
   * Jinja
   */
  jinja: string;
};

/**
 * ReplacementSuggestion
 *
 * Schema for creating new replacement suggestions.
 *
 * Attributes:
 * reason (str): The reason for the replacement
 * string_to_replace (str): The string to replace
 * replacement_string (str): The replacement string
 * page (int): The page number of the replacement
 */
export type ReplacementSuggestion = {
  /**
   * Reason
   */
  reason: string;
  /**
   * String To Replace
   */
  string_to_replace: string;
  /**
   * Replacement String
   */
  replacement_string: string;
  /**
   * Page
   */
  page: number;
};

/**
 * RepopulateContractAgentTaskInDB
 *
 * Read schema for the contract-repopulation subtype.
 */
export type RepopulateContractAgentTaskInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: OrganisationAgentTaskType;
  status: TaskStatus;
  /**
   * Kill Switch
   */
  kill_switch: boolean;
  /**
   * Started By
   */
  started_by?: string | null;
  /**
   * Error Message
   */
  error_message?: string | null;
  started_by_user?: UserInDb | null;
  /**
   * Started By User Uid
   */
  started_by_user_uid?: string | null;
  /**
   * Chat Thread Uid
   */
  chat_thread_uid?: string | null;
  chat_backend?: ChatBackend | null;
  /**
   * Contract Uid
   */
  contract_uid: string;
};

/**
 * ReviewCostPromptBreakdown
 */
export type ReviewCostPromptBreakdown = {
  /**
   * Name
   */
  name: string;
  /**
   * Cost
   */
  cost: number;
};

/**
 * ReviewCostResponse
 */
export type ReviewCostResponse = {
  /**
   * Total Cost
   */
  total_cost: number;
  /**
   * Cost Per File
   */
  cost_per_file: number;
  /**
   * Num Files
   */
  num_files: number;
  /**
   * Num Prompts
   */
  num_prompts: number;
  /**
   * Prompt Breakdown
   */
  prompt_breakdown: Array<ReviewCostPromptBreakdown>;
  /**
   * Remaining Org Credits
   */
  remaining_org_credits: number;
  /**
   * Allowed Org Credits
   */
  allowed_org_credits: number;
  /**
   * Existing Used Credits
   */
  existing_used_credits?: number | null;
};

/**
 * ReviewCreateUser
 */
export type ReviewCreateUser = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  ocr_provider?: OcrProvider;
  /**
   * Prompt Collection Uids
   */
  prompt_collection_uids: Array<string>;
  /**
   * Prompt Uids
   */
  prompt_uids: Array<string>;
  chunking_config?: ChunkingConfiguration;
  /**
   * Hidden
   */
  hidden?: boolean;
};

/**
 * ReviewExtractionInDB
 */
export type ReviewExtractionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Extraction Start Time
   */
  extraction_start_time: string;
  /**
   * Extraction End Time
   */
  extraction_end_time: string | null;
  processing_state: ReviewTaskStatus;
  /**
   * Approved
   */
  approved: boolean;
  value: GaiaJsonValue | null;
  /**
   * Reason
   */
  reason: string | null;
  /**
   * Page
   */
  page: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text: Array<string> | null;
  /**
   * Ground Truth
   */
  ground_truth: string | null;
  evaluation: GaiaJsonValue | null;
  suggestion?: SuggestionResponse | null;
  prompt: PromptInDb | null;
  static_prompt_copy: PromptInDb;
  human_overwrite: ReviewExtractionOverwriteInDb | null;
  /**
   * Status Messages
   */
  status_messages: Array<ReviewExtractionStatusMessageInDb>;
};

/**
 * ReviewExtractionOverwriteCreate
 */
export type ReviewExtractionOverwriteCreate = {
  value?: GaiaJsonValue | null;
  /**
   * Reason
   */
  reason?: string | null;
  /**
   * Page
   */
  page?: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  evaluation?: GaiaJsonValue | null;
  /**
   * Evaluation Reason
   */
  evaluation_reason?: string | null;
  /**
   * Overwrite Reason
   */
  overwrite_reason?: string | null;
};

/**
 * ReviewExtractionOverwriteInDB
 */
export type ReviewExtractionOverwriteInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  value: GaiaJsonValue | null;
  /**
   * Reason
   */
  reason: string | null;
  /**
   * Page
   */
  page: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text: Array<string> | null;
  evaluation: GaiaJsonValue | null;
  /**
   * Evaluation Reason
   */
  evaluation_reason: string | null;
  user?: UserInDb | null;
  /**
   * Overwrite Reason
   */
  overwrite_reason: string | null;
};

/**
 * ReviewExtractionOverwriteUpdate
 */
export type ReviewExtractionOverwriteUpdate = {
  value?: GaiaJsonValue | null;
  /**
   * Reason
   */
  reason?: string | null;
  /**
   * Page
   */
  page?: number | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  evaluation?: GaiaJsonValue | null;
  /**
   * Evaluation Reason
   */
  evaluation_reason?: string | null;
  /**
   * Overwrite Reason
   */
  overwrite_reason?: string | null;
};

/**
 * ReviewExtractionSearchResult
 */
export type ReviewExtractionSearchResult = {
  /**
   * Results
   */
  results: Array<ReviewExtractionInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ReviewExtractionStatusMessageInDB
 */
export type ReviewExtractionStatusMessageInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Level
   */
  level: string;
  audience: TaskErrorAudience;
  /**
   * Message
   */
  message: string;
};

/**
 * ReviewExtractionUpdateUser
 */
export type ReviewExtractionUpdateUser = {
  /**
   * Approved
   */
  approved?: boolean | null;
};

/**
 * ReviewInDB
 *
 * Schema for updating an existing review.
 *
 * Args:
 * name: New name of the result
 */
export type ReviewInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Kanban Column Sorting Position
   */
  kanban_column_sorting_position?: number | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Due Date
   */
  due_date?: string | null;
  kanban_state?: KanbanStateInDb | null;
  /**
   * Assignee User Email
   */
  assignee_user_email?: string | null;
  /**
   * Created By User Email
   */
  created_by_user_email?: string | null;
  /**
   * Reporter User Email
   */
  reporter_user_email?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Total Count
   */
  total_count?: number | null;
  /**
   * Finished Count
   */
  finished_count?: number | null;
  /**
   * Markdown Summary
   */
  markdown_summary?: string | null;
  /**
   * Prompts
   */
  prompts: Array<PromptInDb>;
  /**
   * Static Prompt Copies
   */
  static_prompt_copies: Array<PromptInDb>;
};

/**
 * ReviewProjectAnalysisCreate
 *
 * Schema for creating a new ReviewProjectAnalysis.
 */
export type ReviewProjectAnalysisCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Playbook Uid
   */
  playbook_uid?: string | null;
};

/**
 * ReviewProjectAnalysisInDB
 */
export type ReviewProjectAnalysisInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Playbook Uid
   */
  playbook_uid?: string | null;
};

/**
 * ReviewProjectAnalysisRunStatistics
 *
 * Playbook run statistics for one analysis of the project.
 *
 * Runs are grouped by playbook; when the project has an analysis pointing at
 * that playbook the group carries its uid/name, otherwise both are None and
 * the playbook name is the only label. ``total_run_count`` covers every run,
 * while status, error, and severity counts consider only the latest run per
 * input file (superseded re-runs are excluded).
 */
export type ReviewProjectAnalysisRunStatistics = {
  /**
   * Analysis Uid
   */
  analysis_uid?: string | null;
  /**
   * Analysis Name
   */
  analysis_name?: string | null;
  /**
   * Playbook Uid
   */
  playbook_uid: string;
  /**
   * Playbook Name
   */
  playbook_name: string;
  /**
   * Total Run Count
   */
  total_run_count: number;
  /**
   * Distinct File Count
   */
  distinct_file_count: number;
  /**
   * Run Count By Status
   */
  run_count_by_status?: {
    [key in PlaybookRunStatus]?: number;
  };
  /**
   * Runs With Error Count
   */
  runs_with_error_count: number;
  /**
   * Finding Count By Severity
   *
   * Uncommons finding counts keyed by severity (low/medium/high; unknown values kept raw).
   */
  finding_count_by_severity?: {
    [key: string]: number;
  };
};

/**
 * ReviewProjectAnalysisStatistics
 *
 * Playbook run statistics of a review project, grouped per analysis.
 */
export type ReviewProjectAnalysisStatistics = {
  /**
   * Analyses
   */
  analyses?: Array<ReviewProjectAnalysisRunStatistics>;
  /**
   * Total Run Count
   */
  total_run_count: number;
  /**
   * Total Finding Count By Severity
   */
  total_finding_count_by_severity?: {
    [key: string]: number;
  };
};

/**
 * ReviewProjectAnalysisUpdate
 *
 * Schema for updating an existing ReviewProjectAnalysis.
 *
 * An explicitly sent ``playbook_uid: null`` detaches the playbook; leaving the
 * field out keeps the current playbook (distinguished via ``model_fields_set``).
 */
export type ReviewProjectAnalysisUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Playbook Uid
   */
  playbook_uid?: string | null;
};

/**
 * ReviewProjectAutoTagOptionCreate
 *
 * One selectable option of an auto-tag task group.
 *
 * ``name`` doubles as the tag name: a ReviewProjectTag with that name is created in the
 * project if it does not exist yet (``color`` applies only then). ``definition`` is the LLM
 * guidance for when the option matches.
 */
export type ReviewProjectAutoTagOptionCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Definition
   */
  definition: string;
  /**
   * Color
   */
  color?: string;
};

/**
 * ReviewProjectAutoTagOptionInDB
 */
export type ReviewProjectAutoTagOptionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name: string;
  /**
   * Definition
   */
  definition: string;
  tag: ReviewProjectTagInDb;
};

/**
 * ReviewProjectAutoTagTaskGroupCreate
 *
 * Schema for creating (and immediately starting) an auto-tag task group.
 */
export type ReviewProjectAutoTagTaskGroupCreate = {
  /**
   * Multiple Choices
   */
  multiple_choices?: boolean;
  /**
   * Mandatory
   */
  mandatory?: boolean;
  /**
   * Options
   */
  options: Array<ReviewProjectAutoTagOptionCreate>;
  /**
   * File Selection
   */
  file_selection?:
    | ({
        selection_type: 'all_usable';
      } & AllUsableFilesSelection)
    | ({
        selection_type: 'selected';
      } & SelectedFilesSelection);
};

/**
 * ReviewProjectAutoTagTaskGroupInDB
 *
 * Detail view of an auto-tag task group, including its task rows.
 */
export type ReviewProjectAutoTagTaskGroupInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Task Type
   */
  task_type: 'auto_tag';
  status: TaskStatus;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Started By User Uid
   */
  started_by_user_uid?: string | null;
  /**
   * File Selection
   */
  file_selection:
    | ({
        selection_type: 'all_usable';
      } & AllUsableFilesSelection)
    | ({
        selection_type: 'selected';
      } & SelectedFilesSelection);
  /**
   * Task Status Counts
   */
  task_status_counts: {
    [key in TaskStatus]?: number;
  };
  /**
   * Multiple Choices
   */
  multiple_choices: boolean;
  /**
   * Mandatory
   */
  mandatory: boolean;
  /**
   * Options
   */
  options: Array<ReviewProjectAutoTagOptionInDb>;
  /**
   * Tasks
   */
  tasks: Array<ReviewProjectAutoTagTaskInDb>;
};

/**
 * ReviewProjectAutoTagTaskGroupSummary
 *
 * List view of an auto-tag task group: configuration and progress, without the task rows.
 */
export type ReviewProjectAutoTagTaskGroupSummary = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Task Type
   */
  task_type: 'auto_tag';
  status: TaskStatus;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Started By User Uid
   */
  started_by_user_uid?: string | null;
  /**
   * File Selection
   */
  file_selection:
    | ({
        selection_type: 'all_usable';
      } & AllUsableFilesSelection)
    | ({
        selection_type: 'selected';
      } & SelectedFilesSelection);
  /**
   * Task Status Counts
   */
  task_status_counts: {
    [key in TaskStatus]?: number;
  };
  /**
   * Multiple Choices
   */
  multiple_choices: boolean;
  /**
   * Mandatory
   */
  mandatory: boolean;
  /**
   * Options
   */
  options: Array<ReviewProjectAutoTagOptionInDb>;
};

/**
 * ReviewProjectAutoTagTaskInDB
 */
export type ReviewProjectAutoTagTaskInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Task Type
   */
  task_type: 'auto_tag';
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Review Project File Uid
   */
  review_project_file_uid: string;
  /**
   * Reasoning
   */
  reasoning?: string | null;
  /**
   * Matched Tags
   */
  matched_tags: Array<ReviewProjectTagInDb>;
};

/**
 * ReviewProjectChatResumeRequest
 *
 * Resume request for the review project chat backend.
 */
export type ReviewProjectChatResumeRequest = {
  /**
   * Thread Uid
   */
  thread_uid: string;
  /**
   * Decisions
   */
  decisions?: Array<ChatResumeDecision> | null;
  /**
   * Resume Value
   */
  resume_value?:
    | GaiaJsonValue
    | {
        [key: string]: GaiaJsonValue;
      }
    | null;
  /**
   * Interrupt Id
   */
  interrupt_id?: string | null;
  confirmation_mode?: ChatConfirmationMode;
};

/**
 * ReviewProjectChatStreamRequest
 *
 * Request body for the review project chat stream endpoint.
 *
 * ``review_project_uid`` identifies the review project whose data room (the current
 * revisions of the project's files) the chat is scoped to. Required on a new thread;
 * on a continue it is ignored, since the project is fixed on the thread at creation.
 *
 * ``selected_revision_uids`` is the user's per-message focus: file revisions of the data
 * room the assistant should concentrate on for this turn (a review-project file is now
 * pinned to a specific vault file revision, so selection addresses revisions directly).
 * Soft focus — the rest of the room stays reachable. Sent per message (not persisted);
 * uids outside the data room are ignored.
 */
export type ReviewProjectChatStreamRequest = {
  /**
   * Thread Uid
   */
  thread_uid?: string | null;
  /**
   * Message
   */
  message: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Model
   */
  model?: string | null;
  llm_provider?: LlmProvider | null;
  confirmation_mode?: ChatConfirmationMode;
  /**
   * Review Project Uid
   */
  review_project_uid?: string | null;
  /**
   * Selected Revision Uids
   */
  selected_revision_uids?: Array<string>;
};

/**
 * ReviewProjectCreateUser
 *
 * Schema for a user creating a ReviewProject: the project's data room is an existing
 * vault folder the user picks.
 */
export type ReviewProjectCreateUser = {
  /**
   * Name
   */
  name: string;
  /**
   * Allow Duplicate Runs
   */
  allow_duplicate_runs?: boolean;
  /**
   * Public Qa Enabled
   */
  public_qa_enabled?: boolean;
  /**
   * Public Data Room Upload Enabled
   */
  public_data_room_upload_enabled?: boolean;
  /**
   * Data Room Directory Uid
   */
  data_room_directory_uid: string;
};

/**
 * ReviewProjectFileInDB
 */
export type ReviewProjectFileInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Fully Reviewed
   */
  fully_reviewed: boolean;
  /**
   * Notes
   */
  notes?: string | null;
  /**
   * Tags
   */
  tags: Array<ReviewProjectTagInDb>;
  /**
   * Path
   */
  path: string;
  /**
   * Usable
   */
  usable: boolean;
  /**
   * Page Count
   */
  page_count: number;
  /**
   * Version Number
   */
  version_number: number;
};

/**
 * ReviewProjectFilePageSeenRequest
 *
 * Identifies the file whose page was seen.
 *
 * At least one of the two uids must be given. If both are given they must
 * reference the same review project file.
 */
export type ReviewProjectFilePageSeenRequest = {
  /**
   * Review Project File Uid
   */
  review_project_file_uid?: string | null;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
};

/**
 * ReviewProjectFilePageViewInDB
 */
export type ReviewProjectFilePageViewInDb = {
  /**
   * Page Number
   */
  page_number: number;
  /**
   * Seen At
   */
  seen_at: string;
  user: UserInDb;
};

/**
 * ReviewProjectFileSearchResult
 */
export type ReviewProjectFileSearchResult = {
  /**
   * Results
   */
  results: Array<ReviewProjectFileWithRelations>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ReviewProjectFileStatistics
 *
 * File composition of a review project's data room.
 */
export type ReviewProjectFileStatistics = {
  /**
   * Total File Count
   */
  total_file_count: number;
  /**
   * Usable File Count
   */
  usable_file_count: number;
  /**
   * Non Usable File Count
   */
  non_usable_file_count: number;
  /**
   * Fully Reviewed File Count
   */
  fully_reviewed_file_count: number;
  /**
   * Usable File Percent
   *
   * Fraction of usable files in [0, 1]; 0.0 for an empty project.
   */
  usable_file_percent: number;
  /**
   * First File Uploaded At
   */
  first_file_uploaded_at?: string | null;
  /**
   * Last File Uploaded At
   */
  last_file_uploaded_at?: string | null;
  /**
   * Count By Mime Type
   */
  count_by_mime_type?: {
    [key: string]: number;
  };
  /**
   * Count By File Category
   */
  count_by_file_category?: {
    [key: string]: number;
  };
};

/**
 * ReviewProjectFileUpdate
 *
 * Schema for updating an existing ReviewProjectFile.
 *
 * ``tag_uids`` replaces the file's tag set; the tags must belong to the same
 * review project. Fields absent from the payload stay untouched; ``notes``
 * set explicitly to None clears the notes.
 */
export type ReviewProjectFileUpdate = {
  /**
   * Tag Uids
   */
  tag_uids?: Array<string> | null;
  /**
   * Fully Reviewed
   */
  fully_reviewed?: boolean | null;
  /**
   * Notes
   */
  notes?: string | null;
};

/**
 * ReviewProjectFileWithRelations
 *
 * Review project file with its vault file revision and the requesting user's page views.
 *
 * Attributes:
 * review_project_file: The review project file information.
 * revision: The vault file revision the review project file links to.
 * pages_seen: Pages of the file the requesting user has seen, with the latest seen time.
 */
export type ReviewProjectFileWithRelations = {
  review_project_file: ReviewProjectFileInDb;
  revision: VaultFileRevisionInDb;
  /**
   * Pages Seen
   */
  pages_seen: Array<ReviewProjectFilePageViewInDb>;
};

/**
 * ReviewProjectInDB
 */
export type ReviewProjectInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Allow Duplicate Runs
   */
  allow_duplicate_runs: boolean;
  /**
   * Public Qa Enabled
   */
  public_qa_enabled: boolean;
  /**
   * Public Data Room Upload Enabled
   */
  public_data_room_upload_enabled: boolean;
  /**
   * External Access Uid
   */
  external_access_uid: string;
  owner_user?: UserInDb | null;
};

/**
 * ReviewProjectInDBDetail
 *
 * Single-fetch representation including the project's data room root.
 */
export type ReviewProjectInDbDetail = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Allow Duplicate Runs
   */
  allow_duplicate_runs: boolean;
  /**
   * Public Qa Enabled
   */
  public_qa_enabled: boolean;
  /**
   * Public Data Room Upload Enabled
   */
  public_data_room_upload_enabled: boolean;
  /**
   * External Access Uid
   */
  external_access_uid: string;
  owner_user?: UserInDb | null;
  data_room_root_directory: VaultDirectoryInDbSlim;
};

/**
 * ReviewProjectNonPlatformUserLinkInDB
 *
 * Schema for a review project's non-platform user connection.
 */
export type ReviewProjectNonPlatformUserLinkInDb = {
  non_platform_user: NonPlatformUserInDb;
  /**
   * Enable Qa Answering
   */
  enable_qa_answering: boolean;
  /**
   * Enable Data Room Upload
   */
  enable_data_room_upload: boolean;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
};

/**
 * ReviewProjectNonPlatformUserLinkUpsert
 *
 * Schema for creating or updating a non-platform user connection on a review project.
 *
 * Identifies the person by email (created on first use) and sets what the
 * connection allows.
 */
export type ReviewProjectNonPlatformUserLinkUpsert = {
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name?: string | null;
  /**
   * Enable Qa Answering
   */
  enable_qa_answering?: boolean;
  /**
   * Enable Data Room Upload
   */
  enable_data_room_upload?: boolean;
};

/**
 * ReviewProjectQuestionAnswer
 *
 * Schema for a non-platform user answering an (unanswered) question.
 */
export type ReviewProjectQuestionAnswer = {
  /**
   * Answer
   */
  answer: string;
};

/**
 * ReviewProjectQuestionCreate
 *
 * Schema for creating a new ReviewProjectQuestion.
 */
export type ReviewProjectQuestionCreate = {
  /**
   * Question
   */
  question: string;
  /**
   * Answer
   */
  answer?: string | null;
  /**
   * Asked Via Chat
   */
  asked_via_chat?: boolean;
};

/**
 * ReviewProjectQuestionExternalInDB
 *
 * Slim Q&A projection for non-platform users; exposes no internal user data.
 */
export type ReviewProjectQuestionExternalInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Question
   */
  question: string;
  /**
   * Answer
   */
  answer?: string | null;
  /**
   * Answered At
   */
  answered_at?: string | null;
  answered_by_non_platform_user?: NonPlatformUserInDb | null;
};

/**
 * ReviewProjectQuestionExternalSearchResult
 */
export type ReviewProjectQuestionExternalSearchResult = {
  /**
   * Results
   */
  results: Array<ReviewProjectQuestionExternalInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ReviewProjectQuestionInDB
 */
export type ReviewProjectQuestionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Question
   */
  question: string;
  /**
   * Answer
   */
  answer?: string | null;
  /**
   * Asked Via Chat
   */
  asked_via_chat: boolean;
  asked_by_user?: UserInDb | null;
  /**
   * Answered At
   */
  answered_at?: string | null;
  answered_by_user?: UserInDb | null;
  answered_by_non_platform_user?: NonPlatformUserInDb | null;
};

/**
 * ReviewProjectQuestionSearchResult
 */
export type ReviewProjectQuestionSearchResult = {
  /**
   * Results
   */
  results: Array<ReviewProjectQuestionInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ReviewProjectQuestionStatistics
 *
 * Q&A progress of a review project.
 */
export type ReviewProjectQuestionStatistics = {
  /**
   * Total Question Count
   */
  total_question_count: number;
  /**
   * Answered Question Count
   */
  answered_question_count: number;
  /**
   * Unanswered Question Count
   */
  unanswered_question_count: number;
  /**
   * Answered Question Percent
   *
   * Fraction of answered questions in [0, 1]; 0.0 when there are no questions.
   */
  answered_question_percent: number;
  /**
   * Average Time To Answer Seconds
   *
   * Mean seconds between created_at and answered_at over answered questions; None when none.
   */
  average_time_to_answer_seconds?: number | null;
};

/**
 * ReviewProjectQuestionUpdate
 *
 * Schema for updating an existing ReviewProjectQuestion.
 *
 * Setting ``answer`` to a value auto-sets ``answered_at`` (unless given
 * explicitly); setting ``answer`` to None clears the answered metadata.
 */
export type ReviewProjectQuestionUpdate = {
  /**
   * Question
   */
  question?: string | null;
  /**
   * Answer
   */
  answer?: string | null;
  /**
   * Answered At
   */
  answered_at?: string | null;
};

/**
 * ReviewProjectReportCreate
 *
 * Schema for creating (or fully replacing) a ReviewProjectReport.
 */
export type ReviewProjectReportCreate = {
  /**
   * Content
   */
  content: string;
};

/**
 * ReviewProjectReportGenerationMode
 *
 * How the report agent treats the existing report content.
 */
export enum ReviewProjectReportGenerationMode {
  FULL_WRITE = 'full_write',
  EDIT = 'edit',
}

/**
 * ReviewProjectReportInDB
 */
export type ReviewProjectReportInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Content
   */
  content: string;
};

/**
 * ReviewProjectReportTaskGroupCreate
 *
 * Schema for creating (and immediately starting) a report-generation task group.
 */
export type ReviewProjectReportTaskGroupCreate = {
  mode: ReviewProjectReportGenerationMode;
  report_type: ReviewProjectReportType;
  /**
   * Instruction
   */
  instruction: string;
};

/**
 * ReviewProjectReportTaskGroupInDB
 *
 * Detail view of a report task group, including its (single) task row.
 */
export type ReviewProjectReportTaskGroupInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Task Type
   */
  task_type: 'report';
  status: TaskStatus;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Started By User Uid
   */
  started_by_user_uid?: string | null;
  /**
   * File Selection
   */
  file_selection?:
    | ({
        selection_type: 'all_usable';
      } & AllUsableFilesSelection)
    | ({
        selection_type: 'selected';
      } & SelectedFilesSelection)
    | null;
  /**
   * Task Status Counts
   */
  task_status_counts: {
    [key in TaskStatus]?: number;
  };
  mode: ReviewProjectReportGenerationMode;
  report_type: ReviewProjectReportType;
  /**
   * Instruction
   */
  instruction: string;
  /**
   * Tasks
   */
  tasks: Array<ReviewProjectReportTaskInDb>;
};

/**
 * ReviewProjectReportTaskGroupSummary
 *
 * List view of a report task group: configuration and progress, without the task row.
 */
export type ReviewProjectReportTaskGroupSummary = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Task Type
   */
  task_type: 'report';
  status: TaskStatus;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Started By User Uid
   */
  started_by_user_uid?: string | null;
  /**
   * File Selection
   */
  file_selection?:
    | ({
        selection_type: 'all_usable';
      } & AllUsableFilesSelection)
    | ({
        selection_type: 'selected';
      } & SelectedFilesSelection)
    | null;
  /**
   * Task Status Counts
   */
  task_status_counts: {
    [key in TaskStatus]?: number;
  };
  mode: ReviewProjectReportGenerationMode;
  report_type: ReviewProjectReportType;
  /**
   * Instruction
   */
  instruction: string;
};

/**
 * ReviewProjectReportTaskInDB
 *
 * The single task of a report task group; no file link, no subtype result columns.
 */
export type ReviewProjectReportTaskInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Task Type
   */
  task_type: 'report';
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Review Project File Uid
   */
  review_project_file_uid?: string | null;
};

/**
 * ReviewProjectReportType
 *
 * Which instruction set the report agent follows.
 */
export enum ReviewProjectReportType {
  GENERIC = 'generic',
  DUE_DILIGENCE = 'due_diligence',
}

/**
 * ReviewProjectSearchResult
 */
export type ReviewProjectSearchResult = {
  /**
   * Results
   */
  results: Array<ReviewProjectInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ReviewProjectSeenStatistics
 *
 * Review coverage of a data room, aggregated across all users.
 *
 * A page counts as seen when ANY user viewed it. Non-usable files have no
 * processed pages, so they contribute 0 to the page totals but do count as
 * never seen.
 */
export type ReviewProjectSeenStatistics = {
  /**
   * Total File Count
   */
  total_file_count: number;
  /**
   * Total Page Count
   */
  total_page_count: number;
  /**
   * Seen Page Count
   *
   * Distinct pages seen by any user, capped per file at its page count.
   */
  seen_page_count: number;
  /**
   * Seen Page Percent
   *
   * Fraction of pages seen in [0, 1]; 0.0 when there are no pages.
   */
  seen_page_percent: number;
  /**
   * Fully Seen File Count
   *
   * Files with pages where every page was seen by at least one user.
   */
  fully_seen_file_count: number;
  /**
   * Never Seen File Count
   *
   * Files without a single page view.
   */
  never_seen_file_count: number;
  /**
   * Partially Seen File Count
   */
  partially_seen_file_count: number;
};

/**
 * ReviewProjectTagCreate
 *
 * Schema for creating a new ReviewProjectTag.
 */
export type ReviewProjectTagCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Color
   */
  color?: string;
};

/**
 * ReviewProjectTagFileCount
 *
 * One tag of the project with the number of files carrying it.
 */
export type ReviewProjectTagFileCount = {
  /**
   * Tag Uid
   */
  tag_uid: string;
  /**
   * Name
   */
  name: string;
  /**
   * Color
   */
  color: string;
  /**
   * File Count
   */
  file_count: number;
};

/**
 * ReviewProjectTagInDB
 */
export type ReviewProjectTagInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Color
   */
  color: string;
};

/**
 * ReviewProjectTagSearchResult
 */
export type ReviewProjectTagSearchResult = {
  /**
   * Results
   */
  results: Array<ReviewProjectTagInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ReviewProjectTagStatistics
 *
 * Tag usage across a review project's files (tags with zero files included).
 */
export type ReviewProjectTagStatistics = {
  /**
   * Tags
   */
  tags?: Array<ReviewProjectTagFileCount>;
  /**
   * Untagged File Count
   */
  untagged_file_count: number;
  /**
   * Total File Count
   */
  total_file_count: number;
};

/**
 * ReviewProjectTagUpdate
 *
 * Schema for updating an existing ReviewProjectTag.
 */
export type ReviewProjectTagUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Color
   */
  color?: string | null;
};

/**
 * ReviewProjectUpdate
 *
 * Schema for updating an existing ReviewProject.
 */
export type ReviewProjectUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Allow Duplicate Runs
   */
  allow_duplicate_runs?: boolean | null;
  /**
   * Public Qa Enabled
   */
  public_qa_enabled?: boolean | null;
  /**
   * Public Data Room Upload Enabled
   */
  public_data_room_upload_enabled?: boolean | null;
};

/**
 * ReviewSearchQuery
 *
 * Search query for reviews with filterable fields support.
 *
 * Extends FilterableSearchQuery to support column-based filtering.
 * Use column_filters to filter by assignee_user_email and kanban_state_uid.
 */
export type ReviewSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
  /**
   * Include Archived Tickets
   */
  include_archived_tickets?: boolean;
};

/**
 * ReviewSearchResult
 */
export type ReviewSearchResult = {
  /**
   * Results
   */
  results: Array<ReviewInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ReviewStatistics
 */
export type ReviewStatistics = {
  /**
   * Total Reviews
   */
  total_reviews: number;
  /**
   * Average Reviews Per Organisation
   */
  average_reviews_per_organisation: number;
  /**
   * Average Prompts Per Review
   */
  average_prompts_per_review: number;
  /**
   * Average Files Per Review
   */
  average_files_per_review: number;
};

/**
 * ReviewTableSearch
 */
export type ReviewTableSearch = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Kanban Column Sorting Position
   */
  kanban_column_sorting_position?: number | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Due Date
   */
  due_date?: string | null;
  kanban_state?: KanbanStateInDb | null;
  /**
   * Assignee User Email
   */
  assignee_user_email?: string | null;
  /**
   * Created By User Email
   */
  created_by_user_email?: string | null;
  /**
   * Reporter User Email
   */
  reporter_user_email?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Total Count
   */
  total_count?: number | null;
  /**
   * Finished Count
   */
  finished_count?: number | null;
  /**
   * Markdown Summary
   */
  markdown_summary?: string | null;
  /**
   * Prompts
   */
  prompts: Array<PromptInDb>;
  /**
   * Static Prompt Copies
   */
  static_prompt_copies: Array<PromptInDb>;
  /**
   * Rows
   */
  rows?: Array<FileReviewTableSearch>;
};

/**
 * ReviewTableSearchQuery
 */
export type ReviewTableSearchQuery = {
  /**
   * Row Limit
   */
  row_limit?: number;
  /**
   * Row Offset
   */
  row_offset?: number;
  /**
   * Row Sort Direction
   */
  row_sort_direction?: 'asc' | 'desc';
  /**
   * Column Limit
   */
  column_limit?: number;
  /**
   * Column Offset
   */
  column_offset?: number;
  /**
   * Column Filters
   */
  column_filters: {
    [key: string]: [
      'reason' | 'processing_state' | 'value' | 'page' | 'evaluation',
      Array<GaiaJsonValue>,
    ];
  };
  /**
   * Sorting Column
   */
  sorting_column?: string | null;
  /**
   * Column Sort Direction
   */
  column_sort_direction?: 'asc' | 'desc';
};

/**
 * ReviewTableSearchResult
 */
export type ReviewTableSearchResult = {
  /**
   * Row Count
   */
  row_count: number;
  /**
   * Column Count
   */
  column_count: number;
  review: ReviewTableSearch;
};

/**
 * ReviewTaskStatus
 *
 * ``TaskStatus`` + the review-extraction pipeline stages, for ``processing_state``.
 */
export enum ReviewTaskStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
  PARTIAL = 'partial',
  SKIPPED = 'skipped',
  CANCELLED = 'cancelled',
  WARNING = 'warning',
  OCR_IN_PROGRESS = 'ocr_in_progress',
  EXTRACTION_IN_PROGRESS = 'extraction_in_progress',
  MERGE_IN_PROGRESS = 'merge_in_progress',
  EVALUATION_IN_PROGRESS = 'evaluation_in_progress',
  SUGGESTION_IN_PROGRESS = 'suggestion_in_progress',
}

/**
 * ReviewUpdateUser
 *
 * Schema for updating an existing review.
 *
 * Args:
 * name: New name of the result
 */
export type ReviewUpdateUser = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Kanban Column Sorting Position
   */
  kanban_column_sorting_position?: number | null;
  /**
   * Assignee User Email
   */
  assignee_user_email?: string | null;
  /**
   * Created By User Email
   */
  created_by_user_email?: string | null;
  /**
   * Reporter User Email
   */
  reporter_user_email?: string | null;
  /**
   * Due Date
   */
  due_date?: string | null;
  /**
   * Kanban State Uid
   */
  kanban_state_uid?: string | null;
};

/**
 * RoleUserResultItem
 *
 * Outcome for one user in an add/remove role-users request.
 */
export type RoleUserResultItem = {
  /**
   * User Uid
   */
  user_uid: string;
  /**
   * Success
   */
  success: boolean;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * RoleUsersRequest
 *
 * Request body for adding or removing users to/from a role (one or more user UIDs).
 */
export type RoleUsersRequest = {
  /**
   * User Uids
   */
  user_uids: Array<string>;
};

/**
 * RoleUsersResult
 *
 * Response for add/remove users to/from a role: counts and per-user outcomes.
 */
export type RoleUsersResult = {
  /**
   * Total
   */
  total: number;
  /**
   * Successful
   */
  successful: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Results
   */
  results: Array<RoleUserResultItem>;
};

/**
 * SearchQuery
 *
 * Base model for HTTP error responses.
 *
 * Attributes:
 * text: The search string of the request.
 */
export type SearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
};

/**
 * SectionFilesQuery
 */
export type SectionFilesQuery = {
  /**
   * Page
   */
  page: number;
  /**
   * Per Page
   */
  per_page: number;
  /**
   * Sorting Order
   */
  sorting_order?: 'asc' | 'desc';
  /**
   * Sorting Key
   */
  sorting_key?: 'filename' | 'title' | 'updated_at';
};

/**
 * SelectedFilesSelection
 *
 * Target an explicit set of review project files.
 */
export type SelectedFilesSelection = {
  /**
   * Selection Type
   */
  selection_type?: 'selected';
  /**
   * File Uids
   */
  file_uids: Array<string>;
};

/**
 * SendYoutrustCancelRequest
 */
export type SendYoutrustCancelRequest = {
  /**
   * Reason
   */
  reason: 'contractualization_aborted' | 'errors_in_document' | 'other';
  /**
   * Custom Note
   */
  custom_note?: string | null;
};

/**
 * ServiceProviderBatchExtractInformationTask
 *
 * Pydantic model for starting a task.
 */
export type ServiceProviderBatchExtractInformationTask = {
  /**
   * Task Uid
   */
  task_uid: string;
  metadata?: TaskMetaData;
  /**
   * Input
   */
  input: TaskFileUrlInput | TaskRedisInput;
  /**
   * Callbacks
   */
  callbacks: HttpCallback | Array<HttpCallback>;
  priority?: TaskPriority;
  /**
   * Questions
   */
  questions: Array<ServiceProviderExtractInformationQuestion>;
  chunking_config?: ChunkingConfiguration;
};

/**
 * ServiceProviderExtractInformationQuestion
 */
export type ServiceProviderExtractInformationQuestion = {
  /**
   * Response Uid
   */
  response_uid: string;
  metadata?: TaskMetaData;
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  llm_provider?: LlmProvider;
  /**
   * Llm Model
   */
  llm_model?: string;
  /**
   * Callbacks
   */
  callbacks?: HttpCallback | Array<HttpCallback>;
};

/**
 * ServiceProviderExtractInformationTask
 *
 * Pydantic model for starting a task.
 */
export type ServiceProviderExtractInformationTask = {
  /**
   * Task Uid
   */
  task_uid: string;
  metadata?: TaskMetaData;
  /**
   * Input
   */
  input: TaskFileUrlInput | TaskRedisInput;
  /**
   * Callbacks
   */
  callbacks: HttpCallback | Array<HttpCallback>;
  priority?: TaskPriority;
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Response Type
   */
  response_type:
    | 'date'
    | 'datetime'
    | 'str'
    | 'float'
    | 'number'
    | 'int'
    | 'bool'
    | 'list[date]'
    | 'list[datetime]'
    | 'list[str]'
    | 'list[float]'
    | 'list[number]'
    | 'list[int]'
    | 'list[bool]'
    | 'CurrencyAmount'
    | 'list[CurrencyAmount]'
    | 'Entity'
    | 'list[Entity]'
    | 'CalendarEvents'
    | EnumResponseType;
  llm_provider?: LlmProvider;
  /**
   * Llm Model
   */
  llm_model?: string;
  chunking_config?: ChunkingConfiguration;
};

/**
 * ShareBlock
 */
export type ShareBlock = {
  /**
   * Share Block Start
   */
  share_block_start: number;
  /**
   * Share Block End
   */
  share_block_end: number;
  /**
   * Shareholder Id
   */
  shareholder_id: string;
  /**
   * Share Class Id
   */
  share_class_id: string;
  /**
   * Issuance Price
   */
  issuance_price: number | string;
  /**
   * Secondary Sale Price
   */
  secondary_sale_price?: number | string | null;
  /**
   * Nlp Price
   */
  nlp_price?: number | string | null;
  /**
   * Burdened
   */
  burdened: boolean;
  /**
   * Warrant
   */
  warrant?: boolean;
  /**
   * Owned By Company
   */
  owned_by_company: boolean;
};

/**
 * Shareholder
 */
export type Shareholder = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * SharesOnDate
 */
export type SharesOnDate = {
  /**
   * Number Of Shares
   */
  number_of_shares: number | string;
  /**
   * Date
   */
  date: string;
};

/**
 * SignatureDateFieldCreate
 */
export type SignatureDateFieldCreate = {
  /**
   * Font Family
   */
  font_family?: string;
  /**
   * Font Color
   */
  font_color?: string;
  /**
   * Font Size
   */
  font_size?: number;
  /**
   * Font Bold
   */
  font_bold?: boolean;
  /**
   * Font Italic
   */
  font_italic?: boolean;
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'signature_date';
  /**
   * Name
   */
  name?: string | null;
};

/**
 * SignatureFieldInDB
 *
 * Read schema for a persisted signature field.
 *
 * Relations are identified by uid, matching what ``SignatureFieldCreate`` and
 * ``SignatureFieldUpdate`` accept, so a field that is read back can be placed and re-saved without
 * a lookup by internal id.
 *
 * ``page_width_pts`` / ``page_height_pts`` are the page box the coordinates were measured against
 * when the field was created; a canvas needs them to scale ``x`` / ``y`` / ``width`` / ``height``
 * onto its own render. Null for fields not pinned to a specific revision.
 */
export type SignatureFieldInDb = {
  /**
   * Page
   */
  page: number;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Height
   */
  height?: number;
  /**
   * Width
   */
  width?: number;
  /**
   * Uid
   */
  uid: string;
  type: SignatureFieldType;
  /**
   * Reason
   */
  reason?: string | null;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * Participant Uid
   */
  participant_uid: string;
  /**
   * Page Width Pts
   */
  page_width_pts?: number | null;
  /**
   * Page Height Pts
   */
  page_height_pts?: number | null;
  /**
   * Font Size
   */
  font_size?: number | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
};

/**
 * SignatureFieldType
 */
export enum SignatureFieldType {
  SIGNATURE = 'signature',
  CHECKBOX = 'checkbox',
  MENTION = 'mention',
  TEXT = 'text',
  SIGNATURE_DATE = 'signature_date',
  SIGNER_NAME = 'signer_name',
  SIGNER_EMAIL = 'signer_email',
  READ_ONLY_TEXT = 'read_only_text',
}

/**
 * SignatureFieldUpdate
 *
 * Update schema for a signature field; all fields optional.
 */
export type SignatureFieldUpdate = {
  /**
   * Page
   */
  page?: number | null;
  /**
   * X
   */
  x?: number | null;
  /**
   * Y
   */
  y?: number | null;
  /**
   * Height
   */
  height?: number | null;
  /**
   * Width
   */
  width?: number | null;
  type?: SignatureFieldType | null;
  /**
   * Participant Uid
   */
  participant_uid?: string | null;
  /**
   * Font Size
   */
  font_size?: number | null;
};

/**
 * SignaturePreparationActionInDB
 */
export type SignaturePreparationActionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Action Type
   */
  action_type?: 'signature_preparation';
  /**
   * Message
   */
  message?: string | null;
};

/**
 * SignaturePreparationActionResultInDB
 */
export type SignaturePreparationActionResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Action Uid
   */
  action_uid: string;
  /**
   * Action Trigger Uid
   */
  action_trigger_uid: string;
  /**
   * Selected Option Uid
   */
  selected_option_uid?: string | null;
  /**
   * Evaluation Result Uid
   */
  evaluation_result_uid: string;
  /**
   * Action Type
   */
  action_type?: 'signature_preparation';
  /**
   * Signing Request Uid
   */
  signing_request_uid?: string | null;
};

/**
 * SignatureType
 */
export enum SignatureType {
  ELECTRONIC_SIGNATURE = 'electronic_signature',
  ADVANCED_ELECTRONIC_SIGNATURE = 'advanced_electronic_signature',
  QUALIFIED_ELECTRONIC_SIGNATURE = 'qualified_electronic_signature',
}

/**
 * SignedVaultFileRevisionInDB
 *
 * Signed output revision produced for a source document in a signing request.
 */
export type SignedVaultFileRevisionInDb = {
  source_vault_file_revision: VaultFileRevisionInDb;
  signed_vault_file_revision: VaultFileRevisionInDb;
  signing_file_type: SigningFileType;
};

/**
 * SignerEmailFieldCreate
 */
export type SignerEmailFieldCreate = {
  /**
   * Font Family
   */
  font_family?: string;
  /**
   * Font Color
   */
  font_color?: string;
  /**
   * Font Size
   */
  font_size?: number;
  /**
   * Font Bold
   */
  font_bold?: boolean;
  /**
   * Font Italic
   */
  font_italic?: boolean;
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'signer_email';
  /**
   * Name
   */
  name?: string | null;
};

/**
 * SignerNameFieldCreate
 */
export type SignerNameFieldCreate = {
  /**
   * Font Family
   */
  font_family?: string;
  /**
   * Font Color
   */
  font_color?: string;
  /**
   * Font Size
   */
  font_size?: number;
  /**
   * Font Bold
   */
  font_bold?: boolean;
  /**
   * Font Italic
   */
  font_italic?: boolean;
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'signer_name';
  /**
   * Name Format
   */
  name_format?: 'first_name' | 'last_name' | 'full_name';
  /**
   * Name
   */
  name?: string | null;
};

/**
 * SignerNamePattern
 */
export type SignerNamePattern = {
  /**
   * Pattern
   */
  pattern: string;
};

/**
 * SigningAttentionInDB
 */
export type SigningAttentionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  organisation: UserAttentionOrganisation | null;
  /**
   * Type
   */
  type?: 'signing';
  reason: SigningAttentionReason;
  signing_request: SigningAttentionRequest;
  /**
   * Participant Uid
   */
  participant_uid: string;
  role: SigningRequestSigneeRole;
};

/**
 * SigningAttentionLink
 *
 * The provider's link for the user to sign or approve with, fetched fresh — it expires.
 */
export type SigningAttentionLink = {
  /**
   * Url
   */
  url: string;
  /**
   * Expires At
   */
  expires_at?: string | null;
};

/**
 * SigningAttentionReason
 *
 * What the user is asked to do on a signing request it is their turn on.
 */
export enum SigningAttentionReason {
  SIGN = 'sign',
  APPROVE = 'approve',
}

/**
 * SigningAttentionRequest
 *
 * The signing request it is the user's turn on, as ``/document-signing/{uid}`` opens it.
 */
export type SigningAttentionRequest = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name?: string | null;
  status: SigningRequestStatus | null;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
};

/**
 * SigningFileType
 */
export enum SigningFileType {
  SIGNABLE_DOCUMENT = 'signable_document',
  ATTACHMENT = 'attachment',
}

/**
 * SigningProvider
 *
 * Supported electronic signature providers.
 */
export enum SigningProvider {
  YOUTRUST_INTEGRATION = 'youtrust_integration',
  GAIA_YOUTRUST = 'gaia_youtrust',
}

/**
 * SigningRequestCreate
 */
export type SigningRequestCreate = {
  provider: SigningProvider;
  /**
   * Name
   */
  name: string;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Delivery Mode
   */
  delivery_mode?: string;
  /**
   * Ordered Approvers
   */
  ordered_approvers?: boolean;
  /**
   * Ordered Signers
   */
  ordered_signers?: boolean;
  /**
   * Archived
   */
  archived?: boolean;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
  /**
   * Contract File Uid
   */
  contract_file_uid?: string | null;
  /**
   * Contract Uid
   */
  contract_uid?: string | null;
};

/**
 * SigningRequestInDB
 */
export type SigningRequestInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Provider
   */
  provider: string;
  status: SigningRequestStatus | null;
  /**
   * Status Message
   */
  status_message?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Delivery Mode
   */
  delivery_mode: string;
  /**
   * Ordered Approvers
   */
  ordered_approvers: boolean;
  /**
   * Ordered Signers
   */
  ordered_signers: boolean;
  /**
   * Archived
   */
  archived: boolean;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Contract Files
   */
  contract_files?: Array<ContractManagementContractFileInDb>;
  contract?: ContractManagementContractInDb | null;
};

/**
 * SigningRequestSearchResult
 */
export type SigningRequestSearchResult = {
  /**
   * Results
   */
  results: Array<SigningRequestWithRelationsInDb>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * SigningRequestSigneeRole
 */
export enum SigningRequestSigneeRole {
  SIGNER = 'signer',
  APPROVER = 'approver',
  FOLLOWER = 'follower',
}

/**
 * SigningRequestStatus
 *
 * Possible states for a signing request.
 */
export enum SigningRequestStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  WAITING_FOR_SIGNATURES = 'waiting_for_signatures',
  SIGNED = 'signed',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  ERROR = 'error',
  CANCELLED = 'cancelled',
}

/**
 * SigningRequestStatusEntry
 */
export type SigningRequestStatusEntry = {
  status: SigningRequestStatus | null;
  /**
   * Changed At
   */
  changed_at: string | null;
  changed_by_user?: UserInDbSlim | null;
};

/**
 * SigningRequestUpdate
 *
 * Partial update of a signing request.
 *
 * Everything here except ``archived`` is draft-only — the endpoint refuses the update once the
 * request has left ``DRAFT``, since none of it propagates to the provider. Archiving stays available
 * at any status so finished requests can be filed away.
 */
export type SigningRequestUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Delivery Mode
   */
  delivery_mode?: string | null;
  /**
   * Ordered Approvers
   */
  ordered_approvers?: boolean | null;
  /**
   * Ordered Signers
   */
  ordered_signers?: boolean | null;
  /**
   * Archived
   */
  archived?: boolean | null;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
};

/**
 * SigningRequestWithRelationsInDB
 */
export type SigningRequestWithRelationsInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Provider
   */
  provider: string;
  status: SigningRequestStatus | null;
  /**
   * Status Message
   */
  status_message?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Delivery Mode
   */
  delivery_mode: string;
  /**
   * Ordered Approvers
   */
  ordered_approvers: boolean;
  /**
   * Ordered Signers
   */
  ordered_signers: boolean;
  /**
   * Archived
   */
  archived: boolean;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Contract Files
   */
  contract_files?: Array<ContractManagementContractFileInDb>;
  contract?: ContractManagementContractInDb | null;
  /**
   * Participants
   */
  participants?: Array<ParticipantInDb>;
  /**
   * Vault File Revisions
   */
  vault_file_revisions?: Array<VaultFileRevisionSigningRequestInDb>;
};

/**
 * SigningStatistics
 */
export type SigningStatistics = {
  /**
   * Total Signing Requests
   */
  total_signing_requests: number;
  /**
   * Total Documents
   */
  total_documents: number;
  /**
   * Total Participants
   */
  total_participants: number;
  /**
   * Average Documents Per Request
   */
  average_documents_per_request: number;
  /**
   * Average Participants Per Request
   */
  average_participants_per_request: number;
  /**
   * Average Signers Per Request
   */
  average_signers_per_request: number;
  /**
   * Requests By Provider
   */
  requests_by_provider?: {
    [key: string]: number;
  };
  /**
   * Requests By Status
   */
  requests_by_status?: {
    [key: string]: number;
  };
  /**
   * Participants By Role
   */
  participants_by_role?: {
    [key: string]: number;
  };
  /**
   * Participants By Status
   */
  participants_by_status?: {
    [key: string]: number;
  };
  /**
   * Documents By Type
   */
  documents_by_type?: {
    [key: string]: number;
  };
  /**
   * Participants Per Request
   */
  participants_per_request?: {
    [key: string]: number;
  };
  /**
   * Signers Per Request
   */
  signers_per_request?: {
    [key: string]: number;
  };
  /**
   * Documents Per Request
   */
  documents_per_request?: {
    [key: string]: number;
  };
  /**
   * Latest Signing Requests
   */
  latest_signing_requests?: Array<LatestSigningRequestStatistic>;
};

/**
 * Signup
 */
export type Signup = {
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Company Name
   */
  company_name: string;
  /**
   * Contracts Used
   */
  contracts_used: Array<string>;
  /**
   * Products Interested In
   */
  products_interested_in: Array<string>;
  /**
   * Company Size
   */
  company_size: string;
  /**
   * Hosting Zone
   */
  hosting_zone?: string | null;
  /**
   * Referrer
   */
  referrer?: string | null;
};

/**
 * SignupProfile
 *
 * Profile payload for the authenticated ``/sign_up`` endpoint.
 *
 * Deliberately carries no email — identity comes exclusively from the verified
 * Firebase ID token. Recaptcha arrives via the ``X-Recaptcha-Token`` header
 * dependency, not the body.
 */
export type SignupProfile = {
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Company Name
   */
  company_name: string;
  /**
   * Contracts Used
   */
  contracts_used: Array<string>;
  /**
   * Products Interested In
   */
  products_interested_in: Array<string>;
  /**
   * Company Size
   */
  company_size: string;
  /**
   * Hosting Zone
   */
  hosting_zone?: string | null;
};

/**
 * SlackAgentConfig
 */
export type SlackAgentConfig = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * SlackAuthUrlResponse
 */
export type SlackAuthUrlResponse = {
  /**
   * Url
   */
  url: string;
  /**
   * State
   */
  state?: string | null;
};

/**
 * SlackChannel
 */
export type SlackChannel = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Is Private
   */
  is_private: boolean;
  /**
   * Is Member
   */
  is_member: boolean;
};

/**
 * SlackChannelSearchResult
 */
export type SlackChannelSearchResult = {
  /**
   * Results
   */
  results: Array<SlackChannel>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * SlackConnectRequest
 */
export type SlackConnectRequest = {
  /**
   * Code
   */
  code: string;
};

/**
 * SlackIntegrationStatus
 */
export type SlackIntegrationStatus = {
  state: IntegrationState;
  /**
   * Connected
   */
  connected: boolean;
  /**
   * Team Name
   */
  team_name?: string | null;
  /**
   * Authorized At
   */
  authorized_at?: string | null;
  /**
   * Scope
   */
  scope?: string | null;
  /**
   * Can Push To Channel
   */
  can_push_to_channel?: boolean;
  /**
   * Can Use Commands
   */
  can_use_commands?: boolean;
};

/**
 * SlackNotificationActionInDB
 */
export type SlackNotificationActionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Action Type
   */
  action_type?: 'slack_notification';
  /**
   * Channel Id
   */
  channel_id: string;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Include Playbook Summary
   */
  include_playbook_summary?: boolean;
  /**
   * Include Run Initiator
   */
  include_run_initiator?: boolean;
};

/**
 * SlackNotificationActionResultInDB
 */
export type SlackNotificationActionResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Action Uid
   */
  action_uid: string;
  /**
   * Action Trigger Uid
   */
  action_trigger_uid: string;
  /**
   * Selected Option Uid
   */
  selected_option_uid?: string | null;
  /**
   * Evaluation Result Uid
   */
  evaluation_result_uid: string;
  /**
   * Action Type
   */
  action_type?: 'slack_notification';
};

/**
 * SlackTestMessageRequest
 */
export type SlackTestMessageRequest = {
  /**
   * Channel Id
   */
  channel_id: string;
};

/**
 * SortFolderToEquitySectionCreate
 */
export type SortFolderToEquitySectionCreate = {
  /**
   * Equity Sections
   */
  equity_sections: Array<EquitySectionCreateUser>;
  default_equity_section: EquitySectionCreateUser | null;
  /**
   * Signing Status
   */
  signing_status: 'signed' | 'unknown' | 'unsigned';
};

export type StringCriterion = EqualsCriterion | ContainsCriterion;

/**
 * SuggestionActionInDB
 */
export type SuggestionActionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Action Type
   */
  action_type?: 'suggestion';
  /**
   * Instruction
   */
  instruction?: string | null;
  /**
   * Example Data
   */
  example_data?: string | null;
  /**
   * Use Web Search
   */
  use_web_search?: boolean;
  /**
   * Use Organisation Clause Library
   */
  use_organisation_clause_library?: boolean;
  /**
   * Use Gaia Clause Library
   */
  use_gaia_clause_library?: boolean;
};

/**
 * SuggestionActionResultInDB
 */
export type SuggestionActionResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Action Uid
   */
  action_uid: string;
  /**
   * Action Trigger Uid
   */
  action_trigger_uid: string;
  /**
   * Selected Option Uid
   */
  selected_option_uid?: string | null;
  /**
   * Evaluation Result Uid
   */
  evaluation_result_uid: string;
  /**
   * Action Type
   */
  action_type?: 'suggestion';
  /**
   * Suggestion
   */
  suggestion?: string | null;
};

/**
 * SuggestionResponse
 */
export type SuggestionResponse = {
  /**
   * Suggestions
   */
  suggestions: Array<ReplacementSuggestion>;
};

/**
 * SuggestionTools
 */
export enum SuggestionTools {
  WEB_SEARCH = 'web_search',
  READ_CLAUSE_LIBRARY = 'read_clause_library',
  READ_ORGANISATION_CLAUSE_LIBRARY = 'read_organisation_clause_library',
  READ_GAIA_CLAUSE_LIBRARY = 'read_gaia_clause_library',
}

/**
 * SupportedLogLevels
 *
 * An Enum representing supported log levels.
 */
export enum SupportedLogLevels {
  _50 = 50,
  _40 = 40,
  _30 = 30,
  _20 = 20,
  _10 = 10,
  _0 = 0,
}

/**
 * TaskErrorAudience
 */
export enum TaskErrorAudience {
  USER = 'user',
  SYSTEM = 'system',
}

/**
 * TaskFileURLInput
 */
export type TaskFileUrlInput = {
  /**
   * Authenticated Document Url
   */
  authenticated_document_url: string;
  /**
   * File Name
   */
  file_name: string;
  /**
   * File Mime Type
   */
  file_mime_type: string;
  ocr_provider?: OcrProvider;
};

/**
 * TaskMetaData
 */
export type TaskMetaData = {
  /**
   * Extraction Uid
   */
  extraction_uid?: string | null;
  /**
   * File Review Uid
   */
  file_review_uid?: string | null;
  /**
   * Review Uid
   */
  review_uid?: string | null;
  /**
   * Organisation Agent Task Uid
   */
  organisation_agent_task_uid?: string | null;
  /**
   * Vault File Uid
   */
  vault_file_uid?: string | null;
  /**
   * Prompt Uid
   */
  prompt_uid?: string | null;
  /**
   * User Uid
   */
  user_uid?: string | null;
  /**
   * Organisation Uid
   */
  organisation_uid?: string | null;
  evaluation_response?: EvaluationResponse | null;
  suggestion_response?: SuggestionResponse | null;
  /**
   * Free Form
   */
  free_form?: Array<KeyValue> | null;
  /**
   * Langfuse Tags
   */
  langfuse_tags?: Array<string> | null;
};

/**
 * TaskPriority
 */
export enum TaskPriority {
  LOW = 'low',
  NORMAL = 'normal',
}

/**
 * TaskRedisInput
 */
export type TaskRedisInput = {
  /**
   * Redis Id
   */
  redis_id: string;
  ocr_provider?: OcrProvider;
};

/**
 * TaskStatus
 *
 * Canonical status for background tasks / runs / jobs.
 *
 * Domains with extra states use one of the specialized enums below, which redeclare these
 * members (Python enums with members cannot be subclassed) plus their domain-specific ones.
 * The string values are shared, so members compare equal and hash identically across the
 * enums. Not every domain uses every member.
 */
export enum TaskStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
  PARTIAL = 'partial',
  SKIPPED = 'skipped',
  CANCELLED = 'cancelled',
  WARNING = 'warning',
}

/**
 * TemplateAnnotation
 *
 * User intent — a Jinja tag at a specific docx coordinate.
 *
 * `binding` is optional: `None` (or `kind="primitive"`) means the Jinja
 * variable name is `field_name` directly. Entity/object bindings derive
 * the variable name from `(entity_type+'_'+alias).sub_field` or
 * `alias.sub_field` respectively.
 *
 * `origin="extracted"` annotations were discovered by parsing the source —
 * they carry an `origin_signature` so the materializer can find and rewrite
 * or delete the original tag span if the user edits them.
 *
 * `deleted=True` marks an extracted annotation the user explicitly removed
 * in the UI. The materializer drops the original tag span. Setting `deleted`
 * on a user-authored annotation (no `origin_signature`) is a no-op.
 */
export type TemplateAnnotation = {
  /**
   * Kind
   */
  kind?: 'inline' | 'block_start' | 'block_middle' | 'block_end';
  /**
   * Block Id
   */
  block_id?: string | null;
  anchor: AnnotationCoordinate;
  /**
   * Field Name
   */
  field_name?: string | null;
  /**
   * Binding
   */
  binding?:
    | ({
        kind: 'primitive';
      } & AnnotationPrimitiveBinding)
    | ({
        kind: 'entity';
      } & AnnotationEntityBinding)
    | ({
        kind: 'object';
      } & AnnotationObjectBinding)
    | null;
  /**
   * Default Value
   */
  default_value?: string | null;
  /**
   * Selection Options
   */
  selection_options?: Array<string> | null;
  /**
   * Filters
   */
  filters?: Array<AnnotationFilter>;
  /**
   * Condition
   */
  condition?: string | null;
  /**
   * Required
   */
  required?: boolean;
  /**
   * Origin
   */
  origin?: 'user' | 'extracted';
  origin_signature?: AnnotationOriginSignature | null;
  /**
   * Deleted
   */
  deleted?: boolean;
};

/**
 * TemplateAnnotationWithPath
 *
 * A discovered annotation plus the schema field its leaf refers to.
 *
 * Server-owned and response-only: the path is derived from the loop structure of the whole
 * document (see ``core/contract_generation/annotation_paths``), so a client could not compute
 * it from one tag and is never asked to send it back.
 *
 * ``None`` means the tag names no field — a block tag, a bare loop variable, or a reference
 * scoped to a loop over something that is not a list of records.
 */
export type TemplateAnnotationWithPath = {
  /**
   * Kind
   */
  kind?: 'inline' | 'block_start' | 'block_middle' | 'block_end';
  /**
   * Block Id
   */
  block_id?: string | null;
  anchor: AnnotationCoordinate;
  /**
   * Field Name
   */
  field_name?: string | null;
  /**
   * Binding
   */
  binding?:
    | ({
        kind: 'primitive';
      } & AnnotationPrimitiveBinding)
    | ({
        kind: 'entity';
      } & AnnotationEntityBinding)
    | ({
        kind: 'object';
      } & AnnotationObjectBinding)
    | null;
  /**
   * Default Value
   */
  default_value?: string | null;
  /**
   * Selection Options
   */
  selection_options?: Array<string> | null;
  /**
   * Filters
   */
  filters?: Array<AnnotationFilter>;
  /**
   * Condition
   */
  condition?: string | null;
  /**
   * Required
   */
  required?: boolean;
  /**
   * Origin
   */
  origin?: 'user' | 'extracted';
  origin_signature?: AnnotationOriginSignature | null;
  /**
   * Deleted
   */
  deleted?: boolean;
  /**
   * Schema Path
   */
  schema_path?: string | null;
};

/**
 * TemplateField
 *
 * A single field extracted from a document template.
 *
 * `default_value` is populated when the source template wraps the field in a
 * leading `default(...)` filter. The form-data UI uses it as hint/placeholder
 * text and treats the field as optional — the user can leave it blank and
 * the template will substitute the default at render time.
 *
 * `condition_ast` is the structured expression that gates this field's
 * visibility. None when the field is unconditional. When non-None, the
 * frontend evaluates it against the current form data to decide whether
 * to render the field. `required` already encodes "field is referenced
 * unconditionally somewhere" — a condition_ast with required=False means
 * the field is gated, while required=True means the gating is non-blocking
 * (the field appears unconditionally in at least one place too).
 *
 * `signer` marks a top-level party slot the template places a signature for
 * (`{{ signature("legal_entity_buyer") }}`): sending the finalized draft for
 * signing makes the party filling it a signer.
 */
export type TemplateField = {
  type?: TemplateFieldType;
  /**
   * Required
   */
  required?: boolean;
  /**
   * Condition Ast
   */
  condition_ast?:
    | ({
        kind: 'path';
      } & ConditionPathNode)
    | ({
        kind: 'literal';
      } & ConditionLiteralNode)
    | ({
        kind: 'list';
      } & ConditionListNode)
    | ({
        kind: 'compare';
      } & ConditionCompareNode)
    | ({
        kind: 'logical';
      } & ConditionLogicalNode)
    | ({
        kind: 'not';
      } & ConditionNotNode)
    | null;
  /**
   * Default Value
   */
  default_value?: string | null;
  /**
   * Selection Options
   */
  selection_options?: Array<string> | null;
  /**
   * Filters
   */
  filters?: Array<TemplateFilter>;
  /**
   * Fields
   */
  fields?: {
    [key: string]: TemplateField;
  } | null;
  /**
   * Item Fields
   */
  item_fields?: {
    [key: string]: TemplateField;
  } | null;
  item_type?: TemplateFieldType | null;
  /**
   * Signer
   */
  signer?: boolean;
};

/**
 * TemplateFieldType
 *
 * Types of fields extracted from document templates.
 */
export enum TemplateFieldType {
  STRING = 'string',
  NUMBER = 'number',
  DATE = 'date',
  BOOLEAN = 'boolean',
  SELECTION = 'selection',
  OBJECT = 'object',
  LIST = 'list',
  LEGAL_ENTITY = 'legal_entity',
  NATURAL_PERSON = 'natural_person',
}

/**
 * TemplateFilter
 *
 * A Jinja2 filter applied to a template field.
 */
export type TemplateFilter = {
  /**
   * Name
   */
  name: string;
  /**
   * Args
   */
  args?: Array<string>;
};

/**
 * TemplateSchema
 *
 * The complete extracted schema from a document template — pure field
 * structure, no error state. Validation issues encountered while extracting
 * are carried separately on `ExtractionResult` (and persisted to the
 * localized-template-file row's `validation_errors` column, never inside
 * `extracted_schema`).
 */
export type TemplateSchema = {
  /**
   * Fields
   */
  fields?: {
    [key: string]: TemplateField;
  };
};

/**
 * TemplateValidationError
 *
 * A validation issue found in the template.
 */
export type TemplateValidationError = {
  /**
   * Message
   */
  message: string;
  /**
   * Severity
   */
  severity?: 'warning' | 'error';
};

/**
 * TemplatingChatResumeRequest
 *
 * Resume request for the templating chat backend.
 */
export type TemplatingChatResumeRequest = {
  /**
   * Thread Uid
   */
  thread_uid: string;
  /**
   * Decisions
   */
  decisions?: Array<ChatResumeDecision> | null;
  /**
   * Resume Value
   */
  resume_value?:
    | GaiaJsonValue
    | {
        [key: string]: GaiaJsonValue;
      }
    | null;
  /**
   * Interrupt Id
   */
  interrupt_id?: string | null;
  confirmation_mode?: ChatConfirmationMode;
};

/**
 * TemplatingChatStreamRequest
 *
 * Request body for the templating chat stream endpoint.
 *
 * ``selection_content`` is the user's current editor selection, so "wrap this …" can
 * anchor to it. The document body is not sent — the assistant reads the live document
 * on demand via the document tools.
 */
export type TemplatingChatStreamRequest = {
  /**
   * Thread Uid
   */
  thread_uid?: string | null;
  /**
   * Message
   */
  message: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Model
   */
  model?: string | null;
  llm_provider?: LlmProvider | null;
  confirmation_mode?: ChatConfirmationMode;
  /**
   * Selection Content
   */
  selection_content?: string | null;
};

/**
 * TextFieldCreate
 */
export type TextFieldCreate = {
  /**
   * Font Family
   */
  font_family?: string;
  /**
   * Font Color
   */
  font_color?: string;
  /**
   * Font Size
   */
  font_size?: number;
  /**
   * Font Bold
   */
  font_bold?: boolean;
  /**
   * Font Italic
   */
  font_italic?: boolean;
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'text';
  /**
   * Width
   */
  width: number;
  /**
   * Height
   */
  height: number;
  /**
   * Question
   */
  question: string;
  /**
   * Max Length
   */
  max_length: number;
  /**
   * Instruction
   */
  instruction?: string | null;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Default Value
   */
  default_value?: string | null;
  /**
   * Read Only
   */
  read_only?: boolean;
};

/**
 * ThreadInfo
 */
export type ThreadInfo = {
  /**
   * Type
   */
  type?: 'thread_info';
  /**
   * Thread Uid
   */
  thread_uid: string;
  /**
   * Title
   */
  title: string;
};

export type TimeCriterion =
  | EqualsCriterion
  | GreaterEqualThanCriterion
  | GreaterThanCriterion
  | LessEqualThanCriterion
  | LessThanCriterion;

/**
 * ToolConfirmationAnswer
 *
 * Standardized resume value for a ``ToolConfirmationInterrupt``.
 *
 * ``choice`` is one of the interrupt's offered ``choices[].value`` (validated against the live
 * choice set by ``GaiaTool._confirm``, not here — this schema doesn't know the set). ``message``
 * is free text, always allowed regardless of ``choice`` — it can stand alone ("talk about this":
 * no choice picked, just feedback) or ride along with a choice (e.g. ``choice="create"`` +
 * ``message="Contract Templates"`` supplying the new name in one step).
 */
export type ToolConfirmationAnswer = {
  /**
   * Choice
   */
  choice?: string | null;
  /**
   * Message
   */
  message?: string | null;
};

/**
 * ToolConfirmationInterrupt
 *
 * Standardized interrupt payload a GaiaTool sends via ``GaiaTool._confirm()``.
 */
export type ToolConfirmationInterrupt = {
  /**
   * Type
   */
  type?: 'tool_confirmation';
  /**
   * Tool Name
   */
  tool_name: string;
  /**
   * Message
   */
  message: string;
  /**
   * Data
   */
  data?: {
    [key: string]: unknown;
  } | null;
  /**
   * Choices
   */
  choices?: Array<ToolInterruptChoice>;
};

/**
 * ToolInterruptChoice
 *
 * One selectable option offered to the user, e.g. {"value": "confirm", "label": "Confirm"}.
 */
export type ToolInterruptChoice = {
  /**
   * Value
   */
  value: string;
  /**
   * Label
   */
  label: string;
};

/**
 * UncommonsCheckEvaluationInDB
 */
export type UncommonsCheckEvaluationInDb = {
  /**
   * Evaluation Type
   */
  evaluation_type?: 'uncommons_check';
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Extraction Prompt
   */
  extraction_prompt: string;
  /**
   * Options
   */
  options?: Array<ClassificationOptionInDb>;
  /**
   * Finding Threshold Triggers
   */
  finding_threshold_triggers?: Array<FindingThresholdTriggerInDb>;
};

/**
 * UncommonsCheckEvaluationResultInDB
 */
export type UncommonsCheckEvaluationResultInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  status: TaskStatus;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Reasoning
   */
  reasoning?: string | null;
  /**
   * Highlighted Text
   */
  highlighted_text?: Array<string> | null;
  /**
   * Page Number
   */
  page_number?: number | null;
  /**
   * Attempt Number
   */
  attempt_number: number;
  /**
   * Is Latest
   */
  is_latest: boolean;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Evaluation Uid
   */
  evaluation_uid: string;
  /**
   * Matched Option Uids
   */
  matched_option_uids?: Array<string>;
  /**
   * Triggered By Action Result Uid
   */
  triggered_by_action_result_uid?: string | null;
  /**
   * Action Results
   */
  action_results?: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionResultInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionResultInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionResultInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionResultInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionResultInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionResultInDb)
  >;
  /**
   * Evaluation Type
   */
  evaluation_type?: 'uncommons_check';
  /**
   * Findings
   */
  findings?: Array<UncommonsFinding>;
};

/**
 * UncommonsFinding
 *
 * A passage that does not belong in the document.
 *
 * ``block_id`` anchors the finding to a document block when one is identifiable (generally null for
 * playbook runs, which feed page text); ``text`` is the verbatim span the frontend highlights.
 *
 * ``text`` is the only required field: the verbatim passage *is* the finding, so a scan that returns
 * one without the prose justification still surfaces it rather than failing the whole chunk.
 */
export type UncommonsFinding = {
  /**
   * Reason
   */
  reason?: string;
  /**
   * Block Id
   */
  block_id?: string | null;
  /**
   * Text
   */
  text: string;
  /**
   * Severity
   */
  severity?: 'low' | 'medium' | 'high';
  /**
   * Page Number
   */
  page_number?: number | null;
};

/**
 * UsageCostRow
 *
 * One raw daily ``usage_bucket`` entry with cost and org/user data embedded.
 *
 * Self-contained so the frontend can aggregate freely (by day, org, user,
 * model, purpose, ...). ``org_name`` / ``user_email`` / ``user_full_name`` are
 * resolved from the DB for the ``insights`` platform; for ``equity_management``
 * they stay ``None`` and only the raw UIDs are available. Cost fields are
 * currency-agnostic numbers; the operator converts to a display currency at
 * consumption time.
 */
export type UsageCostRow = {
  /**
   * Bucket Day
   */
  bucket_day: string;
  platform: UsagePlatform;
  /**
   * Platform Org Uid
   */
  platform_org_uid?: string | null;
  /**
   * Org Name
   */
  org_name?: string | null;
  /**
   * Platform User Uid
   */
  platform_user_uid?: string | null;
  /**
   * User Email
   */
  user_email?: string | null;
  /**
   * User Full Name
   */
  user_full_name?: string | null;
  provider: LlmProvider;
  /**
   * Model
   */
  model: string;
  purpose?: UsagePurpose | null;
  /**
   * Purpose Detail
   */
  purpose_detail?: string | null;
  /**
   * Input Tokens
   */
  input_tokens: number;
  /**
   * Output Tokens
   */
  output_tokens: number;
  /**
   * Cached Input Tokens
   */
  cached_input_tokens: number;
  /**
   * Call Count
   */
  call_count: number;
  /**
   * Input Cost
   */
  input_cost: string;
  /**
   * Output Cost
   */
  output_cost: string;
  /**
   * Cached Input Cost
   */
  cached_input_cost: string;
};

/**
 * UsagePlatform
 *
 * Source platform for AI usage tracking.
 */
export enum UsagePlatform {
  INSIGHTS = 'insights',
  EQUITY_MANAGEMENT = 'equity_management',
}

/**
 * UsagePurpose
 *
 * Main app area that issued an LLM call, recorded on the usage bucket.
 *
 * Paired with a free-form ``purpose_detail`` sub-category so cost can be
 * attributed to a feature. Canonical ``purpose_detail`` values per purpose:
 *
 * - ``review``: ``extraction``, ``evaluation``, ``suggestion``, ``clause``,
 * ``oddity``, ``equity_section``, ``summary``. Equity-management /
 * service-provider traffic is also ``review`` / ``extraction``.
 * - ``contract_management``: ``matter``, ``creation``, ``clause``,
 * ``legal_entity``, ``natural_person``, ``obligation``, ``date``,
 * ``custom``.
 * - ``playbook``: ``decide_playbook``, ``playbook``, ``suggestion``,
 * ``playbook_suggestion``. Covers both playbook creation and execution.
 * - ``chat``: no detail by default.
 */
export enum UsagePurpose {
  CHAT = 'chat',
  REVIEW = 'review',
  CONTRACT_MANAGEMENT = 'contract_management',
  PLAYBOOK = 'playbook',
}

/**
 * UsageStatistics
 *
 * Response shape for the usage-cost statistics endpoint.
 *
 * ``rows`` is the raw, un-aggregated daily usage (one entry per
 * ``usage_bucket`` row) with cost and org/user data embedded — the frontend
 * does any roll-ups. The ``total_*`` fields summarise the whole filtered range
 * as a convenience.
 */
export type UsageStatistics = {
  /**
   * Rows
   */
  rows: Array<UsageCostRow>;
  /**
   * Total Input Cost
   */
  total_input_cost: string;
  /**
   * Total Output Cost
   */
  total_output_cost: string;
  /**
   * Total Cached Input Cost
   */
  total_cached_input_cost: string;
  /**
   * Total Call Count
   */
  total_call_count: number;
};

/**
 * UserActivityStatistics
 *
 * Admin-level user activity statistics.
 *
 * Every authenticated request earns activity points on a fixed 0-3 scale,
 * declared per app area (``core/fastapi/shared/activity_scoring.py``):
 * 0 = baseline/excluded (logged in), 1 = chores (user/org/role management,
 * agent setup, playbook authoring, prompt editing), 2 = looking at data
 * (reading contracts, files, reviews, runs), 3 = productive work (starting
 * runs, adding contracts, chatting, signing).
 *
 * ``daily_active_users`` counts distinct users per day; a user counts as
 * active once their highest-weighted request of the day reaches the requested
 * ``min_points`` threshold (0 counts any authenticated request).
 * ``daily_peak_points`` (highest single user peak per day) and
 * ``daily_total_points`` (all weights summed per day) are never thresholded —
 * they feed the peak/total activity heatmaps. ``daily_activity_by_org``
 * attributes activity to the organisation that was active at request time.
 */
export type UserActivityStatistics = {
  /**
   * Daily Active Users
   */
  daily_active_users: {
    [key: string]: number;
  };
  /**
   * Daily Peak Points
   */
  daily_peak_points: {
    [key: string]: number;
  };
  /**
   * Daily Total Points
   */
  daily_total_points: {
    [key: string]: number;
  };
  /**
   * Daily Activity By Org
   */
  daily_activity_by_org: Array<OrganisationDailyActivity>;
};

/**
 * UserAttentionOrganisation
 *
 * The organisation the item lives in — the one to switch into before following it.
 */
export type UserAttentionOrganisation = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name: string;
};

/**
 * UserAttentionSearchQuery
 *
 * Narrow the user's list. ``None`` means all; an organisation the user is not in matches nothing.
 */
export type UserAttentionSearchQuery = {
  /**
   * Organisation Uids
   */
  organisation_uids?: Array<string> | null;
  /**
   * Types
   */
  types?: Array<UserAttentionType> | null;
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
};

/**
 * UserAttentionSearchResult
 */
export type UserAttentionSearchResult = {
  /**
   * Results
   */
  results: Array<
    | ({
        type: 'chat_thread';
      } & ChatThreadAttentionInDb)
    | ({
        type: 'signing';
      } & SigningAttentionInDb)
  >;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * UserAttentionType
 *
 * Kind of thing a user attention item points at.
 *
 * Used as the polymorphic discriminator for the user-attention hierarchy.
 */
export enum UserAttentionType {
  CHAT_THREAD = 'chat_thread',
  SIGNING = 'signing',
}

/**
 * UserInDB
 *
 * Schema for user data as stored in database.
 *
 * Basic user information for internal use.
 *
 * Attributes:
 * uid: Unique identifier
 * email: User's email address
 */
export type UserInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Is Active
   */
  is_active: boolean;
  permissions: UserPermissions;
};

/**
 * UserInDBMe
 *
 * The caller's own profile: the switcher state in one response.
 *
 * ``active_organisation`` is ``None`` for an org-less user (a member of no organisation, or one
 * whose active pointer was cleared); ``organisations`` is what they can switch to. Only ``/me``
 * carries these — the auth chain eager-loads exactly this graph, and nobody else has a use for
 * another user's active organisation.
 */
export type UserInDbMe = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Is Active
   */
  is_active: boolean;
  permissions: UserPermissions;
  /**
   * Organisations
   */
  organisations: Array<OrganisationInDb>;
  active_organisation: OrganisationInDb | null;
};

/**
 * UserInDBSlim
 *
 * A user without ``permissions`` — for users nested inside another aggregate's response.
 *
 * ``UserInDB.permissions`` is a property walking the user's active organisation, feature flags,
 * home-screen items, roles and organisations, so every nesting parent has to carry
 * ``UserRepo.permission_graph_under``. These four fields are plain columns.
 */
export type UserInDbSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Is Active
   */
  is_active: boolean;
};

/**
 * UserIsActiveUpdateRequest
 *
 * Schema for org admins (de)activating a user account.
 *
 * Attributes:
 * is_active: The desired activation state. Deactivation is platform-wide,
 * not per-organisation — the flag lives on the user itself.
 */
export type UserIsActiveUpdateRequest = {
  /**
   * Is Active
   */
  is_active: boolean;
};

/**
 * UserMemoryCreate
 *
 * Schema for creating a new user memory.
 *
 * Args:
 * key: Unique (per user and organisation) handle of the memory
 * description: One-line summary of the memory, used as its index entry
 * content: Full markdown content of the memory
 */
export type UserMemoryCreate = {
  /**
   * Key
   */
  key: string;
  /**
   * Description
   */
  description: string;
  /**
   * Content
   */
  content: string;
};

/**
 * UserMemoryInDB
 *
 * Schema for user memories stored in the database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 */
export type UserMemoryInDb = {
  /**
   * Key
   */
  key: string;
  /**
   * Description
   */
  description: string;
  /**
   * Content
   */
  content: string;
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
};

/**
 * UserMemorySearchResult
 *
 * Schema for user memory search results.
 */
export type UserMemorySearchResult = {
  /**
   * Results
   */
  results: Array<UserMemoryInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * UserMemoryUpdate
 *
 * Schema for updating an existing user memory.
 *
 * Args:
 * key: New handle of the memory
 * description: New one-line summary of the memory
 * content: New markdown content of the memory
 */
export type UserMemoryUpdate = {
  /**
   * Key
   */
  key?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Content
   */
  content?: string | null;
};

/**
 * UserPermissions
 *
 * What the user may do in their active organisation — the definitive answer for clients.
 *
 * Every capability is already folded with the organisation feature that gates it (feature AND
 * (admin OR role capability)), so clients never combine it with ``OrganisationInDB.features``.
 */
export type UserPermissions = {
  /**
   * Can Create Reviews
   */
  can_create_reviews: boolean;
  /**
   * Can Update Reviews
   */
  can_update_reviews: boolean;
  /**
   * Can Create Playbooks
   */
  can_create_playbooks: boolean;
  /**
   * Can Use Playbooks
   */
  can_use_playbooks: boolean;
  /**
   * Can Use Signing
   */
  can_use_signing: boolean;
  /**
   * Can Use Review Projects
   */
  can_use_review_projects: boolean;
  all_contractual_parties_access: AccessPermission;
  all_contracts_access: AccessPermission;
  /**
   * Can Use Contract Management
   */
  can_use_contract_management: boolean;
  /**
   * Can Use Contract Generation
   */
  can_use_contract_generation: boolean;
  /**
   * Is Org Admin
   */
  is_org_admin: boolean;
  /**
   * Is Platform Admin
   */
  is_platform_admin: boolean;
  /**
   * Can Create Organisation
   */
  can_create_organisation?: boolean;
};

/**
 * UserQuestionAnswer
 *
 * Standardized resume value for a ``UserQuestionInterrupt``.
 *
 * ``choice`` is one of the interrupt's offered ``options[].value``, validated against the live
 * option set by the tool and not here (this schema doesn't know the set) — the same split as
 * ``ToolConfirmationAnswer``. ``message`` is free text: it can stand alone (the user typed an
 * answer instead of picking one) or ride along with a choice ("the second one, but cap it at 5%").
 */
export type UserQuestionAnswer = {
  /**
   * Choice
   */
  choice?: string | null;
  /**
   * Message
   */
  message?: string | null;
};

/**
 * UserQuestionInterrupt
 *
 * Interrupt payload for the ``ask_user`` tool: one question, 2-6 selectable options.
 *
 * A sibling of ``ToolConfirmationInterrupt`` rather than a reuse of it, for two reasons: the
 * frontend branches on ``interrupts[0].value.type`` to pick which card to render, and this pause
 * must happen under every ``ChatConfirmationMode`` — a confirmation-based design would be silently
 * skipped in ``AUTO`` mode, leaving the model to invent the answer to its own question.
 */
export type UserQuestionInterrupt = {
  /**
   * Type
   */
  type?: 'user_question';
  /**
   * Tool Name
   */
  tool_name: string;
  /**
   * Question
   */
  question: string;
  /**
   * Options
   */
  options: Array<UserQuestionOption>;
};

/**
 * UserQuestionOption
 *
 * One answer the user can pick for a ``UserQuestionInterrupt``.
 *
 * Extends the confirmation choice with ``data``: optional plain text the card previews under the
 * label — a sample clause, a suggested name, an example value — so the user can see what picking
 * this option would mean. Shown verbatim and never interpreted; the assistant wrote it.
 */
export type UserQuestionOption = {
  /**
   * Value
   */
  value: string;
  /**
   * Label
   */
  label: string;
  /**
   * Data
   */
  data?: string | null;
};

/**
 * UserSearchResult
 */
export type UserSearchResult = {
  /**
   * Results
   */
  results: Array<UserInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * UserStatistic
 */
export type UserStatistic = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Last Logged In
   */
  last_logged_in: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Organisation Name
   */
  organisation_name?: string | null;
};

/**
 * UserStatistics
 *
 * Platform-level user statistics.
 *
 * The organisation-count distribution buckets the same filtered user set as ``total_user_count``
 * by how many organisations each user is a member of. Memberships are counted across ALL
 * organisations, not only the included ones — so with ``included_org_uids`` the "without" bucket
 * is necessarily 0 (membership in an included organisation is what put the user in scope).
 */
export type UserStatistics = {
  /**
   * Total User Count
   */
  total_user_count: number;
  /**
   * Active User Count
   */
  active_user_count: number;
  /**
   * Users Without Organisation Count
   */
  users_without_organisation_count: number;
  /**
   * Users With One Organisation Count
   */
  users_with_one_organisation_count: number;
  /**
   * Users With Multiple Organisations Count
   */
  users_with_multiple_organisations_count: number;
  /**
   * Average Organisations Per User
   */
  average_organisations_per_user: number;
  /**
   * Newest Users
   */
  newest_users: Array<UserStatistic>;
  /**
   * Recent Log Ins
   */
  recent_log_ins: Array<UserStatistic>;
};

/**
 * UserUpdate
 *
 * Schema for updating an existing user.
 *
 * All fields are optional to allow partial updates.
 *
 * Deliberately carries no ``email``, for the same reason ``UserCreate`` carries no
 * ``firebase_uid``: the address is identity-bearing and must only ever come from a verified
 * Firebase token, at provisioning. Nothing may re-assign it from a request body — not even the
 * platform-admin route through ``UserUpdateSuperuser``.
 *
 * That immutability is load-bearing beyond identity: a playbook run started by an intake agent is
 * visible to the platform user whose address it was mailed from
 * (``repos/playbook/_permissions._started_it_filter``), so a self-assignable email would be a
 * way to read another member's runs.
 *
 * Attributes:
 * full_name: User's display name
 */
export type UserUpdate = {
  /**
   * Full Name
   */
  full_name?: string | null;
};

/**
 * UserUpdateSuperuser
 *
 * Schema for superuser updates to a user.
 *
 * Extends UserUpdate with additional fields that only superusers can modify.
 *
 * Attributes:
 * is_active: Whether the user account is active
 */
export type UserUpdateSuperuser = {
  /**
   * Full Name
   */
  full_name?: string | null;
  /**
   * Is Active
   */
  is_active?: boolean | null;
};

/**
 * ValidationError
 */
export type ValidationError = {
  /**
   * Location
   */
  loc: Array<string | number>;
  /**
   * Message
   */
  msg: string;
  /**
   * Error Type
   */
  type: string;
  /**
   * Input
   */
  input?: unknown;
  /**
   * Context
   */
  ctx?: {
    [key: string]: unknown;
  };
};

export type ValueCriterion =
  | StringCriterion
  | NumberCriterion
  | EqualsCriterion
  | TimeCriterion
  | CurrencyAmountCriterion
  | EntityCriterion
  | CalendarEventCriterion
  | ContractualPartyCriterion
  | MatterCriterion
  | OwnerCriterion
  | DateCriterion;

/**
 * VaultAncestor
 *
 * One step of a breadcrumb trail: a folder, or the contract a file belongs to.
 *
 * Args:
 * kind: ``directory`` or ``contract``
 * uid: The folder's or the contract's uid
 * name: Its display name
 */
export type VaultAncestor = {
  /**
   * Kind
   */
  kind: 'directory' | 'contract';
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name: string;
};

/**
 * VaultArchiveSkipped
 *
 * An archive found while unpacking that was left packed.
 *
 * Attributes:
 * vault_file_uid: The archive's vault file
 * file_name: The archive's file name
 * reason: Why it was not unpacked
 */
export type VaultArchiveSkipped = {
  /**
   * Vault File Uid
   */
  vault_file_uid: string;
  /**
   * File Name
   */
  file_name: string;
  reason: ArchiveSkipReason;
};

/**
 * VaultBulkMoveUser
 *
 * Schema for bulk moving vault files, contracts and directories to a target directory.
 *
 * Args:
 * file_uids: List of plain vault file UIDs to move (a contract's files move with it)
 * contract_uids: List of contract UIDs to move, each with all of its files
 * directory_uids: List of vault directory UIDs to move
 * target_directory_uid: UUID of the directory to move all items into
 */
export type VaultBulkMoveUser = {
  /**
   * File Uids
   */
  file_uids?: Array<string>;
  /**
   * Contract Uids
   */
  contract_uids?: Array<string>;
  /**
   * Directory Uids
   */
  directory_uids?: Array<string>;
  /**
   * Target Directory Uid
   */
  target_directory_uid: string;
};

/**
 * VaultBulkReassignOwnerUser
 *
 * Schema for reassigning (or unassigning) the owner of vault files, contracts and directories.
 *
 * Org admins only. ``owner_user_uid`` None unassigns every listed item (owned by nobody: org
 * admins only). A contract's files follow the contract — they take its new owner and cannot be
 * listed on their own. With ``recursive`` (the default) a listed directory brings its whole
 * subtree: every directory, plain file and contract below it.
 *
 * Args:
 * file_uids: Plain vault file UIDs to reassign (a contract's files follow their contract)
 * contract_uids: Contract UIDs to reassign, each with all of its files
 * directory_uids: Vault directory UIDs to reassign
 * owner_user_uid: UUID of the new owner, a member of the organisation; None to unassign
 * recursive: When True (default), directory_uids bring their subdirectories, plain files and
 * contracts too; when False, only the listed directories themselves change owner
 */
export type VaultBulkReassignOwnerUser = {
  /**
   * File Uids
   */
  file_uids?: Array<string>;
  /**
   * Contract Uids
   */
  contract_uids?: Array<string>;
  /**
   * Directory Uids
   */
  directory_uids?: Array<string>;
  /**
   * Owner User Uid
   */
  owner_user_uid?: string | null;
  /**
   * Recursive
   */
  recursive?: boolean;
};

/**
 * VaultBulkShare
 *
 * Share vault files and directories with users and roles in one request.
 */
export type VaultBulkShare = {
  /**
   * File Uids
   */
  file_uids?: Array<string>;
  /**
   * Directory Uids
   */
  directory_uids?: Array<string>;
  /**
   * User Uids
   */
  user_uids?: Array<string>;
  /**
   * Role Uids
   */
  role_uids?: Array<string>;
  permissions: AccessPermission;
};

/**
 * VaultContractExternalInDB
 *
 * A contract as visible to non-platform users: its name and its files.
 *
 * Attributes:
 * contract_file_count: How many of its files have finished uploading
 */
export type VaultContractExternalInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Name
   */
  name: string;
  /**
   * Files
   */
  files?: Array<VaultFileExternalInDb>;
  /**
   * Contract File Count
   */
  contract_file_count: number;
};

/**
 * VaultDirectoryCreateUser
 *
 * Schema for user creation of a vault directory.
 *
 * Attributes:
 * directory_name: Name of the directory
 * parent_directory_uid: UUID of the parent directory
 */
export type VaultDirectoryCreateUser = {
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * Parent Directory Uid
   */
  parent_directory_uid: string;
};

/**
 * VaultDirectoryDownloadStartResponse
 *
 * Response for kicking off an async folder-zip download.
 *
 * Attributes:
 * download_uid: Synthetic id the client subscribes to over the websocket to receive zip
 * progress and the final "ready" event carrying the artifact id
 * total_files: Recursive file count of the directory — the progress-bar denominator
 * file_name: Name the built zip will be downloaded as
 */
export type VaultDirectoryDownloadStartResponse = {
  /**
   * Download Uid
   */
  download_uid: string;
  /**
   * Total Files
   */
  total_files: number;
  /**
   * File Name
   */
  file_name: string;
};

/**
 * VaultDirectoryExternalCreate
 *
 * Schema for a non-platform user creating a subdirectory in the data room.
 */
export type VaultDirectoryExternalCreate = {
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * Parent Directory Uid
   */
  parent_directory_uid: string;
};

/**
 * VaultDirectoryExternalInDB
 *
 * A browsed data-room directory: one page of its content plus the breadcrumb chain.
 *
 * ``ancestors`` is ordered root-first and trimmed at the data room root (the
 * root itself is included, everything above it is not part of the external
 * surface); empty when the browsed directory is the data room root.
 *
 * Attributes:
 * items: One page of its child folders, contracts and plain files in the requested order,
 * kinds interleaved (a contract's files are reached through the contract)
 * items_count: How many such items the directory holds, over all pages
 */
export type VaultDirectoryExternalInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * File Count
   */
  file_count?: number | null;
  /**
   * Ancestors
   */
  ancestors?: Array<VaultDirectoryExternalInDbSlim>;
  /**
   * Items
   */
  items: Array<
    | ({
        kind: 'directory';
      } & VaultExternalListedDirectory)
    | ({
        kind: 'contract';
      } & VaultExternalListedContract)
    | ({
        kind: 'file';
      } & VaultExternalListedFile)
  >;
  /**
   * Items Count
   */
  items_count: number;
};

/**
 * VaultDirectoryExternalInDBSlim
 *
 * A vault directory as visible to non-platform users.
 *
 * ``file_count`` counts items below it: plain files and contracts, a contract once.
 */
export type VaultDirectoryExternalInDbSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * File Count
   */
  file_count?: number | null;
};

/**
 * VaultDirectoryInDB
 *
 * Schema for vault directories stored in database.
 *
 * Extends the slim schema with one page of the directory's contents. Built only by the gateway's
 * permission-filtered listing (``endpoints/app/repository/vault/_listing.py``), never validated
 * from the ORM: the relationships are unfiltered.
 *
 * Attributes:
 * parent_directory: Parent directory, if the caller may READ it
 * items: One page of the child folders, contracts and plain files the caller may READ, in
 * the requested order, kinds interleaved (a contract's files are reached through the
 * contract)
 * items_count: How many such items the directory holds, over all pages
 */
export type VaultDirectoryInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * File Count
   */
  file_count?: number | null;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
  parent_directory?: VaultDirectoryInDbSlim | null;
  /**
   * Items
   */
  items: Array<
    | ({
        kind: 'directory';
      } & VaultListedDirectory)
    | ({
        kind: 'contract';
      } & VaultListedContract)
    | ({
        kind: 'file';
      } & VaultListedFile)
  >;
  /**
   * Items Count
   */
  items_count: number;
};

/**
 * VaultDirectoryInDBSlim
 *
 * Slim schema for vault directories stored in database.
 *
 * Attributes:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * directory_name: Name of the directory
 * file_count: Number of items (plain files and contracts; a contract counts once, however
 * many files it holds) in the directory and all its subdirectories that are visible to
 * the requesting user, or None when not computed for this response (only populated by
 * the directory listing endpoint)
 * owner_user: User that owns the directory, if any (default None)
 * permissions: The requesting user's effective permission bits on the directory (READ=1,
 * WRITE=2, ADMIN=4; an org admin holds 7; every member holds at least 3 on the org root),
 * or None when not computed for this response
 */
export type VaultDirectoryInDbSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * File Count
   */
  file_count?: number | null;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
};

/**
 * VaultDirectoryRoleShareInDB
 *
 * A role's share on a vault directory, with both sides.
 */
export type VaultDirectoryRoleShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  vault_directory: VaultDirectoryInDbSlim;
  role: PermissionRoleInDb;
};

/**
 * VaultDirectoryRoleShareInDBRole
 *
 * A role's share on a vault directory, listed by role: embeds the directory.
 */
export type VaultDirectoryRoleShareInDbRole = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  vault_directory: VaultDirectoryInDbSlim;
};

/**
 * VaultDirectoryRoleShareInDBVaultDirectory
 *
 * A role's share on a vault directory, listed by directory: embeds the role.
 */
export type VaultDirectoryRoleShareInDbVaultDirectory = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  role: PermissionRoleInDb;
};

/**
 * VaultDirectoryRoleShareSearchResultRole
 */
export type VaultDirectoryRoleShareSearchResultRole = {
  /**
   * Results
   */
  results: Array<VaultDirectoryRoleShareInDbRole>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultDirectoryRoleShareSearchResultVaultDirectory
 */
export type VaultDirectoryRoleShareSearchResultVaultDirectory = {
  /**
   * Results
   */
  results: Array<VaultDirectoryRoleShareInDbVaultDirectory>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultDirectoryUnpackInDB
 *
 * The listing of a directory an archive was unpacked into.
 *
 * Attributes:
 * skipped_archives: The nested archives that were left packed, with the reason
 */
export type VaultDirectoryUnpackInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * File Count
   */
  file_count?: number | null;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
  parent_directory?: VaultDirectoryInDbSlim | null;
  /**
   * Items
   */
  items: Array<
    | ({
        kind: 'directory';
      } & VaultListedDirectory)
    | ({
        kind: 'contract';
      } & VaultListedContract)
    | ({
        kind: 'file';
      } & VaultListedFile)
  >;
  /**
   * Items Count
   */
  items_count: number;
  /**
   * Skipped Archives
   */
  skipped_archives: Array<VaultArchiveSkipped>;
};

/**
 * VaultDirectoryUpdateUser
 *
 * Schema for user updates to a vault directory.
 *
 * Attributes:
 * directory_name: New name of the directory
 */
export type VaultDirectoryUpdateUser = {
  /**
   * Directory Name
   */
  directory_name?: string | null;
};

/**
 * VaultDirectoryUserShareInDB
 *
 * A user's share on a vault directory, with both sides.
 */
export type VaultDirectoryUserShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
  vault_directory: VaultDirectoryInDbSlim;
};

/**
 * VaultDirectoryUserShareInDBUser
 *
 * A user's share on a vault directory, listed by user: embeds the directory.
 */
export type VaultDirectoryUserShareInDbUser = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  vault_directory: VaultDirectoryInDbSlim;
};

/**
 * VaultDirectoryUserShareInDBVaultDirectory
 *
 * A user's share on a vault directory, listed by directory: embeds the user.
 */
export type VaultDirectoryUserShareInDbVaultDirectory = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
};

/**
 * VaultDirectoryUserShareSearchResultUser
 */
export type VaultDirectoryUserShareSearchResultUser = {
  /**
   * Results
   */
  results: Array<VaultDirectoryUserShareInDbUser>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultDirectoryUserShareSearchResultVaultDirectory
 */
export type VaultDirectoryUserShareSearchResultVaultDirectory = {
  /**
   * Results
   */
  results: Array<VaultDirectoryUserShareInDbVaultDirectory>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultExternalListedContract
 *
 * A contract as an item of a data-room listing.
 *
 * Attributes:
 * kind: Discriminates the listing's items
 * type_label: What the listing sorts by type: the contract's type
 */
export type VaultExternalListedContract = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Name
   */
  name: string;
  /**
   * Files
   */
  files?: Array<VaultFileExternalInDb>;
  /**
   * Contract File Count
   */
  contract_file_count: number;
  /**
   * Kind
   */
  kind?: 'contract';
  /**
   * Type Label
   */
  type_label: string;
};

/**
 * VaultExternalListedDirectory
 *
 * A folder as an item of a data-room listing.
 *
 * Attributes:
 * kind: Discriminates the listing's items
 * type_label: What the listing sorts by type: always ``folder``
 */
export type VaultExternalListedDirectory = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * File Count
   */
  file_count?: number | null;
  /**
   * Kind
   */
  kind?: 'directory';
  /**
   * Type Label
   */
  type_label: string;
};

/**
 * VaultExternalListedFile
 *
 * A plain file as an item of a data-room listing.
 *
 * Attributes:
 * kind: Discriminates the listing's items
 * type_label: What the listing sorts by type: ``archive`` for a file the vault can unpack,
 * ``file`` otherwise
 */
export type VaultExternalListedFile = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  current_revision?: VaultFileRevisionExternalInDb | null;
  /**
   * Kind
   */
  kind?: 'file';
  /**
   * Type Label
   */
  type_label: string;
};

/**
 * VaultExternalSearchResult
 *
 * Search result over a data room's plain files, contracts and directories.
 *
 * A contract's files never show as file results: the contract does, matched on its own fields or
 * on the name of any of its files.
 */
export type VaultExternalSearchResult = {
  /**
   * File Results
   */
  file_results: Array<VaultFileExternalInDb>;
  /**
   * File Results Count
   */
  file_results_count: number;
  /**
   * Contract Results
   */
  contract_results: Array<VaultContractExternalInDb>;
  /**
   * Contract Results Count
   */
  contract_results_count: number;
  /**
   * Directory Results
   */
  directory_results: Array<VaultDirectoryExternalInDbSlim>;
  /**
   * Directory Results Count
   */
  directory_results_count: number;
};

/**
 * VaultFileCreateUser
 *
 * Schema for creating an empty vault file anchor, in a folder or straight in a contract.
 *
 * Exactly one target. A file created in a contract joins it as a new contract file whose type and
 * description the classification fills once the first revision is uploaded.
 *
 * Args:
 * parent_directory_uid: UUID of the parent directory
 * contract_uid: UUID of the contract the file joins
 */
export type VaultFileCreateUser = {
  /**
   * Parent Directory Uid
   */
  parent_directory_uid?: string | null;
  /**
   * Contract Uid
   */
  contract_uid?: string | null;
};

/**
 * VaultFileCreateUserInitializeResponse
 *
 * Schema to return the ID for sending chunks.
 *
 * Args:
 * upload_id: ID to use for uploading chunks
 */
export type VaultFileCreateUserInitializeResponse = {
  /**
   * Upload Id
   */
  upload_id: string;
};

/**
 * VaultFileDuplicateCheckRequest
 *
 * Schema for checking whether a file name already exists in a directory.
 */
export type VaultFileDuplicateCheckRequest = {
  /**
   * Parent Directory Uid
   */
  parent_directory_uid: string;
  /**
   * File Name
   */
  file_name: string;
};

/**
 * VaultFileDuplicateCheckResponse
 *
 * Response for the duplicate file name check.
 */
export type VaultFileDuplicateCheckResponse = {
  /**
   * Is Duplicate
   */
  is_duplicate: boolean;
  /**
   * Vault File Uids
   */
  vault_file_uids?: Array<string>;
};

/**
 * VaultFileExternalCreate
 *
 * Schema for a non-platform user creating an empty file anchor in the data room.
 */
export type VaultFileExternalCreate = {
  /**
   * Parent Directory Uid
   */
  parent_directory_uid: string;
};

/**
 * VaultFileExternalInDB
 *
 * A vault file as visible to non-platform users: only its current revision.
 */
export type VaultFileExternalInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  current_revision?: VaultFileRevisionExternalInDb | null;
};

/**
 * VaultFileInDB
 *
 * Schema for vault files stored in database.
 *
 * Args:
 * parent_directory: Parent directory of the file; None for a contract member, which lives
 * under its contract instead
 */
export type VaultFileInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  current_revision?: VaultFileRevisionInDb | null;
  /**
   * Converting
   */
  converting: boolean;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
  parent_directory: VaultDirectoryInDbSlim | null;
};

/**
 * VaultFileInDBOwner
 *
 * Schema for vault files with owner-specific information.
 *
 * Args:
 * organisation_links: Number of organisation links
 */
export type VaultFileInDbOwner = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  current_revision?: VaultFileRevisionInDb | null;
  /**
   * Converting
   */
  converting: boolean;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
  /**
   * Organisation Links
   */
  organisation_links: number;
};

/**
 * VaultFileInDBSlim
 *
 * Slim schema for vault files stored in database.
 *
 * Args:
 * uid: Unique identifier
 * created_at: Creation timestamp
 * updated_at: Last update timestamp
 * current_revision: The latest version of this file, if any
 * owner: User that owns the file, if any (default None)
 * converting: Whether the file is being converted into a contract; while true it cannot be
 * moved, edited, deleted or filed into a contract
 * permissions: The requesting user's effective permission bits on the file (READ=1, WRITE=2,
 * ADMIN=4; an org admin holds 7; a contract's file holds its contract's), or None when
 * not computed for this response
 */
export type VaultFileInDbSlim = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  current_revision?: VaultFileRevisionInDb | null;
  /**
   * Converting
   */
  converting: boolean;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
};

/**
 * VaultFileIntakeItem
 *
 * One uploaded file: where it went (directory, contract, status) and what was in it.
 *
 * Attributes:
 * vault_file: The file with its current revision; ``created_at`` is the upload time and a
 * ``current_revision.version_number`` above 1 means the file was updated
 * origin: Where the first revision came from; None for files uploaded before it was recorded
 * started_by: Who sent or uploaded it (an email address), if known
 * directory: The folder the file sits in, or its contract's for a contract's file; the full
 * trail comes from ``GET /vault/ancestors``
 * contract_file_type: The file's role in the contract (contract / amendment / appendix), None
 * until classified or outside a contract
 * converted_by_agent: Whether the conversion created the contract (False: uploaded into an existing one)
 * effective_from: The current revision's effective-from date, None until extracted
 * effective_to: The current revision's effective-to date, None until extracted; its ``date`` is
 * None when the contract runs indefinitely
 * status_message: Why the file did not convert, when it did not
 */
export type VaultFileIntakeItem = {
  vault_file: VaultFileInDbSlim;
  origin: VaultFileRevisionSource | null;
  /**
   * Started By
   */
  started_by: string | null;
  directory: VaultDirectoryInDbSlim | null;
  contract: ContractManagementContractInDbSlim | null;
  contract_file_type: ContractManagementContractFileType | null;
  /**
   * Converted By Agent
   */
  converted_by_agent: boolean;
  /**
   * Parties
   */
  parties: Array<VaultFileIntakeParty>;
  /**
   * Matters
   */
  matters: Array<ContractManagementMatterInDb>;
  effective_from: ContractManagementDateInDb | null;
  effective_to: ContractManagementDateInDb | null;
  status: VaultFileIntakeStatus;
  /**
   * Status Message
   */
  status_message: string | null;
  steps: VaultFileIntakeSteps;
};

/**
 * VaultFileIntakeParty
 *
 * A party of the file, slim on purpose: a party has its own READ permission, the intake only
 * checks the contract's, so the full ``ContractManagementContractualPartyInDB`` stays out.
 */
export type VaultFileIntakeParty = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Name
   */
  name: string;
  kind: ContractManagementContractualPartyKind;
};

/**
 * VaultFileIntakeSearchQuery
 *
 * The intake search, narrowed on top of ``text`` and the contract ``column_filters``.
 *
 * Empty lists and unset dates do not filter.
 *
 * Attributes:
 * sources: Keep files whose first revision came from one of these entry points (the row's ``origin``)
 * owner_user_uids: Keep files one of these platform users owns
 * created_by_user_uids: Keep files with a revision one of these platform users created
 * uploaded_from: Keep files uploaded on or after this day (UTC)
 * uploaded_to: Keep files uploaded on or before this day (UTC)
 * statuses: Keep files whose row ``status`` is one of these
 */
export type VaultFileIntakeSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Column Filters
   */
  column_filters?: {
    [key: string]: Array<string>;
  };
  /**
   * Sources
   */
  sources?: Array<VaultFileRevisionSource>;
  /**
   * Owner User Uids
   */
  owner_user_uids?: Array<string>;
  /**
   * Created By User Uids
   */
  created_by_user_uids?: Array<string>;
  /**
   * Uploaded From
   */
  uploaded_from?: string | null;
  /**
   * Uploaded To
   */
  uploaded_to?: string | null;
  /**
   * Statuses
   */
  statuses?: Array<VaultFileIntakeStatus>;
};

/**
 * VaultFileIntakeSearchResult
 *
 * ``filter_options`` are the contract's filterable fields (type, tags), ``status``, the
 * statuses that occur among the files the caller can read, ``source``, the origins (first-revision
 * sources) that occur among them, and ``owner_user`` / ``created_by_user``,
 * the users (``{"uid", "email", "full_name"}``) owning them / creating one of their revisions.
 */
export type VaultFileIntakeSearchResult = {
  /**
   * Results
   */
  results: Array<VaultFileIntakeItem>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * VaultFileIntakeStatus
 *
 * Where an uploaded file stands on its way into a contract, derived from the preprocessing,
 * conversion and party- and date-extraction rows (nothing stores it).
 */
export enum VaultFileIntakeStatus {
  PREPROCESSING = 'preprocessing',
  CONVERTING = 'converting',
  EXTRACTING = 'extracting',
  IN_CONTRACT = 'in_contract',
  ARCHIVE = 'archive',
  FAILED = 'failed',
  NOT_CONVERTED = 'not_converted',
}

/**
 * VaultFileIntakeSteps
 *
 * The status of each background step, None when the step has no row (yet).
 *
 * Attributes:
 * preprocessing: The current revision's OCR status
 * conversion: The latest file → contract conversion task's status
 * extraction: The current revision's contractual-party extraction status
 */
export type VaultFileIntakeSteps = {
  preprocessing: PreprocessingTaskStatus | null;
  conversion: TaskStatus | null;
  extraction: TaskStatus | null;
};

/**
 * VaultFileRevisionCreateUserInitialize
 *
 * Schema for user-initiated chunked upload of a new revision.
 */
export type VaultFileRevisionCreateUserInitialize = {
  /**
   * File Name
   */
  file_name: string;
  /**
   * Total Bytes
   */
  total_bytes: number;
  /**
   * Change Description
   */
  change_description?: string | null;
  document_state?: DocumentState | null;
};

/**
 * VaultFileRevisionDiff
 *
 * Diff between two vault file revisions.
 */
export type VaultFileRevisionDiff = {
  /**
   * Added Lines
   */
  added_lines: number;
  /**
   * Removed Lines
   */
  removed_lines: number;
  /**
   * Pages
   */
  pages: Array<VaultFileRevisionDiffPage>;
  /**
   * Compared To Revision Uid
   */
  compared_to_revision_uid: string;
};

/**
 * VaultFileRevisionDiffLine
 */
export type VaultFileRevisionDiffLine = {
  /**
   * Text
   */
  text: string;
  /**
   * Status
   */
  status: 'added' | 'removed' | 'unchanged';
};

/**
 * VaultFileRevisionDiffPage
 */
export type VaultFileRevisionDiffPage = {
  /**
   * Page
   */
  page: number;
  /**
   * Lines
   */
  lines: Array<VaultFileRevisionDiffLine>;
};

/**
 * VaultFileRevisionExternalInDB
 *
 * A file's current revision as visible to non-platform users.
 */
export type VaultFileRevisionExternalInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Mime Type
   */
  mime_type: string;
  /**
   * Size B
   */
  size_b: number;
};

/**
 * VaultFileRevisionInDB
 *
 * Schema for vault file versions stored in database.
 *
 * ``effective_state`` is what the revision shows today: its time state once signed, its document
 * state otherwise; ``needs_attention_reasons`` says why it needs attention, empty otherwise.
 * ``signature_failure_reason`` says why sending it for signature failed, when the signing request did.
 */
export type VaultFileRevisionInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Version Number
   */
  version_number: number;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Mime Type
   */
  mime_type: string;
  /**
   * Size B
   */
  size_b?: number | null;
  /**
   * Preview File Size B
   */
  preview_file_size_b?: number | null;
  preview_status?: PreprocessingTaskStatus | null;
  ocr_status?: PreprocessingTaskStatus | null;
  signature_detection_status?: PreprocessingTaskStatus | null;
  conversion_status?: PreprocessingTaskStatus | null;
  /**
   * Signature Infos
   */
  signature_infos?: Array<VaultFileSignatureInfoInDb>;
  source?: VaultFileRevisionSource | null;
  /**
   * Change Description
   */
  change_description?: string | null;
  kind: VaultFileRevisionKind;
  created_by_user?: UserInDb | null;
  document_state: DocumentState;
  effective_state: EffectiveState;
  /**
   * Needs Attention Reasons
   */
  needs_attention_reasons: Array<NeedsAttentionReason>;
  /**
   * Signature Failure Reason
   */
  signature_failure_reason?: string | null;
};

/**
 * VaultFileRevisionKind
 *
 * Kinds of vault file revisions; the polymorphic discriminator of the revision hierarchy.
 *
 * Unlike ``VaultFileRevisionSource`` (the entry point, kept for every revision), the kind decides which data a
 * revision carries and where its bytes come from:
 *
 * - ``uploaded``: a stored file brought in from outside (upload, import, intake, derived copy).
 * - ``templated``: a contract generation template plus its input values. Nothing is stored but the preview; the
 * bytes are rendered whenever they are read.
 * - ``templated_uploaded``: a locked templated revision, rendered once and stored like an upload, still knowing
 * the inputs it was rendered from.
 */
export enum VaultFileRevisionKind {
  UPLOADED = 'uploaded',
  TEMPLATED = 'templated',
  TEMPLATED_UPLOADED = 'templated_uploaded',
}

/**
 * VaultFileRevisionListResponse
 *
 * Paginated list of vault file revisions.
 */
export type VaultFileRevisionListResponse = {
  /**
   * Results
   */
  results: Array<VaultFileRevisionInDb>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultFileRevisionSigningRequestCreate
 *
 * Reference to a vault file revision with its role in a signing request.
 */
export type VaultFileRevisionSigningRequestCreate = {
  vault_file_revision: VaultFileRevisionInDb;
  signing_file_type?: SigningFileType;
};

/**
 * VaultFileRevisionSigningRequestInDB
 *
 * A vault file linked to a signing request, with its role in the workflow.
 */
export type VaultFileRevisionSigningRequestInDb = {
  vault_file_revision: VaultFileRevisionInDb;
  signing_file_type: SigningFileType;
};

/**
 * VaultFileRevisionSnapshot
 *
 * A revision frozen into JSONB (``FileReview.static_vault_file_copy``).
 *
 * The creator is ``UserInDBSlim``: ``UserInDB.permissions`` is computed live, so a frozen copy
 * is meaningless and breaks every old row the moment ``UserPermissions`` gains a required field.
 * Rows written as a full ``VaultFileRevisionInDB`` still read — the stale ``permissions`` key is
 * ignored.
 */
export type VaultFileRevisionSnapshot = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Version Number
   */
  version_number: number;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Mime Type
   */
  mime_type: string;
  /**
   * Size B
   */
  size_b?: number | null;
  /**
   * Preview File Size B
   */
  preview_file_size_b?: number | null;
  preview_status?: PreprocessingTaskStatus | null;
  ocr_status?: PreprocessingTaskStatus | null;
  signature_detection_status?: PreprocessingTaskStatus | null;
  conversion_status?: PreprocessingTaskStatus | null;
  /**
   * Signature Infos
   */
  signature_infos?: Array<VaultFileSignatureInfoInDb>;
  source?: VaultFileRevisionSource | null;
  /**
   * Change Description
   */
  change_description?: string | null;
  created_by_user?: UserInDbSlim | null;
};

/**
 * VaultFileRevisionSource
 *
 * Where a vault file revision came from: the entry point that created it.
 *
 * A file's origin is its first revision's source. Archive members inherit the source of the
 * archive they were unpacked from.
 */
export enum VaultFileRevisionSource {
  USER_UPLOAD = 'user_upload',
  DATA_ROOM_UPLOAD = 'data_room_upload',
  INTAKE_EMAIL = 'intake_email',
  INTAKE_SLACK = 'intake_slack',
  INTAKE_JIRA = 'intake_jira',
  INTAKE_AGENT_API = 'intake_agent_api',
  GOOGLE_DRIVE_IMPORT = 'google_drive_import',
  GAIA_IMPORT = 'gaia_import',
  CONTRACT_DRAFT = 'contract_draft',
  TEMPLATE_AUTHORING = 'template_authoring',
  SIGNED_DOCUMENT = 'signed_document',
  SIGNING_CLEARED = 'signing_cleared',
  PDF_CONVERSION = 'pdf_conversion',
  PLAYBOOK_COMMENTS = 'playbook_comments',
  DEMO = 'demo',
}

/**
 * VaultFileRevisionUpdateUser
 *
 * Schema for user updates to a vault file revision.
 */
export type VaultFileRevisionUpdateUser = {
  /**
   * File Name
   */
  file_name?: string | null;
  /**
   * Change Description
   */
  change_description?: string | null;
  document_state?: DocumentState | null;
};

/**
 * VaultFileRoleShareInDB
 *
 * A role's share on a vault file, with both sides.
 */
export type VaultFileRoleShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  vault_file: VaultFileInDb;
  role: PermissionRoleInDb;
};

/**
 * VaultFileRoleShareInDBRole
 *
 * A role's share on a vault file, listed by role: embeds the file.
 */
export type VaultFileRoleShareInDbRole = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  vault_file: VaultFileInDb;
};

/**
 * VaultFileRoleShareInDBVaultFile
 *
 * A role's share on a vault file, listed by file: embeds the role.
 */
export type VaultFileRoleShareInDbVaultFile = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  role: PermissionRoleInDb;
};

/**
 * VaultFileRoleShareSearchResultRole
 */
export type VaultFileRoleShareSearchResultRole = {
  /**
   * Results
   */
  results: Array<VaultFileRoleShareInDbRole>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultFileRoleShareSearchResultVaultFile
 */
export type VaultFileRoleShareSearchResultVaultFile = {
  /**
   * Results
   */
  results: Array<VaultFileRoleShareInDbVaultFile>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultFileSignatureInfoInDB
 *
 * Read schema for a persisted signature info row.
 */
export type VaultFileSignatureInfoInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Type
   */
  type: 'simple' | 'advanced' | 'qualified';
  /**
   * Signer
   */
  signer?: string | null;
  /**
   * Authority
   */
  authority?: string | null;
  /**
   * Signing Date
   */
  signing_date?: string | null;
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Is Certification
   */
  is_certification?: boolean;
  /**
   * Docmdp Permission
   */
  docmdp_permission?: number | null;
};

/**
 * VaultFileSigningRequestInDB
 *
 * A vault file linked to a signing request, with its role in the workflow.
 *
 * The link is to a specific revision, not to whatever the file's current revision happens to be —
 * activation mints new revisions when it strips embedded signatures and re-points the links at them.
 * So ``vault_file_revision`` is carried alongside the file: it is what identifies the link (the
 * document endpoints key on its uid), and it lets a caller show which version is going out to sign.
 */
export type VaultFileSigningRequestInDb = {
  vault_file: VaultFileInDbSlim;
  vault_file_revision: VaultFileRevisionInDb;
  signing_file_type: SigningFileType;
};

/**
 * VaultFileUpdateUser
 *
 * Schema for user updates to a vault file.
 *
 * Args:
 * file_name: New file name (updates current revision)
 * owner_user_uid: UUID of new owner (reassign; omit to leave unchanged; unassign only via bulk)
 */
export type VaultFileUpdateUser = {
  /**
   * File Name
   */
  file_name?: string | null;
  /**
   * Owner User Uid
   */
  owner_user_uid?: string | null;
};

/**
 * VaultFileUserShareInDB
 *
 * A user's share on a vault file, with both sides.
 */
export type VaultFileUserShareInDb = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
  vault_file: VaultFileInDb;
};

/**
 * VaultFileUserShareInDBUser
 *
 * A user's share on a vault file, listed by user: embeds the file.
 */
export type VaultFileUserShareInDbUser = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  vault_file: VaultFileInDb;
};

/**
 * VaultFileUserShareInDBVaultFile
 *
 * A user's share on a vault file, listed by file: embeds the user.
 */
export type VaultFileUserShareInDbVaultFile = {
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  permissions: AccessPermission;
  user: UserInDb;
};

/**
 * VaultFileUserShareSearchResultUser
 */
export type VaultFileUserShareSearchResultUser = {
  /**
   * Results
   */
  results: Array<VaultFileUserShareInDbUser>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultFileUserShareSearchResultVaultFile
 */
export type VaultFileUserShareSearchResultVaultFile = {
  /**
   * Results
   */
  results: Array<VaultFileUserShareInDbVaultFile>;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * VaultKind
 *
 * The two vaults of an organisation.
 *
 * ``MAIN`` is the ordinary vault everyone works in; ``SYSTEM`` is the admin-only tree of
 * platform-managed content (Templates, Intake Agent), rooted at the organisation's
 * system root.
 */
export enum VaultKind {
  MAIN = 'main',
  SYSTEM = 'system',
}

/**
 * VaultListedContract
 *
 * A contract as an item of a vault listing.
 *
 * Attributes:
 * kind: Discriminates the listing's items
 * type_label: What the listing sorts by type: the contract's type
 * states: Its contract and amendment states for today — always computed for a listing
 */
export type VaultListedContract = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Name
   */
  name: string;
  contract_type?: ContractTypeRef | null;
  owner_user?: UserInDb | null;
  /**
   * Contract File Count
   */
  contract_file_count: number;
  /**
   * Permissions
   */
  permissions?: number | null;
  states: ContractManagementContractStates;
  /**
   * Kind
   */
  kind?: 'contract';
  /**
   * Type Label
   */
  type_label: string;
};

/**
 * VaultListedDirectory
 *
 * A folder as an item of a vault listing.
 *
 * Attributes:
 * kind: Discriminates the listing's items
 * type_label: What the listing sorts by type: always ``folder``
 */
export type VaultListedDirectory = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Directory Name
   */
  directory_name: string;
  /**
   * File Count
   */
  file_count?: number | null;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
  /**
   * Kind
   */
  kind?: 'directory';
  /**
   * Type Label
   */
  type_label: string;
};

/**
 * VaultListedFile
 *
 * A plain file as an item of a vault listing.
 *
 * Attributes:
 * kind: Discriminates the listing's items
 * type_label: What the listing sorts by type: ``archive`` for a file the vault can unpack,
 * ``file`` otherwise
 */
export type VaultListedFile = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  current_revision?: VaultFileRevisionInDb | null;
  /**
   * Converting
   */
  converting: boolean;
  owner_user?: UserInDb | null;
  /**
   * Permissions
   */
  permissions?: number | null;
  /**
   * Organisation Links
   */
  organisation_links: number;
  /**
   * Kind
   */
  kind?: 'file';
  /**
   * Type Label
   */
  type_label: string;
};

/**
 * VaultSearchQuery
 *
 * A free-text search over one of the organisation's two vaults.
 *
 * Attributes:
 * vault: Which vault to search; the system vault is org-admin only
 * contract_states: Only contracts whose contract state is one of these (any when empty)
 * amendment_states: Only contracts whose amendment state is one of these (any when empty);
 * with either set, the search returns contracts only — files and folders have no such state
 */
export type VaultSearchQuery = {
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Sort Key
   */
  sort_key?: string;
  /**
   * Sort Direction
   */
  sort_direction?: 'asc' | 'desc';
  /**
   * Text
   */
  text: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  vault?: VaultKind;
  /**
   * Contract States
   */
  contract_states?: Array<EffectiveState>;
  /**
   * Amendment States
   */
  amendment_states?: Array<EffectiveState>;
};

/**
 * VaultSearchResult
 *
 * A vault search: plain files, contracts and directories, each paginated on its own.
 *
 * A contract's files never show as file results: the contract does, matched on its own fields
 * or on the name of any of its files.
 *
 * Attributes:
 * file_results: Matching plain files
 * file_results_count: Total number of matching plain files
 * contract_results: Matching contracts, slim
 * contract_results_count: Total number of matching contracts
 * directory_results: Matching directories, slim: a folder's contents come from its listing,
 * which shows only what the caller may READ
 * directory_results_count: Total number of matching directories
 */
export type VaultSearchResult = {
  /**
   * File Results
   */
  file_results: Array<VaultFileInDb>;
  /**
   * File Results Count
   */
  file_results_count: number;
  /**
   * Contract Results
   */
  contract_results: Array<ContractManagementContractInDbSlim>;
  /**
   * Contract Results Count
   */
  contract_results_count: number;
  /**
   * Directory Results
   */
  directory_results: Array<VaultDirectoryInDbSlim>;
  /**
   * Directory Results Count
   */
  directory_results_count: number;
};

/**
 * VaultShareUpdate
 *
 * Schema for setting a vault share's permissions.
 *
 * ``permissions`` is optional for symmetry with the contract shares: ``None`` leaves an existing
 * share alone; creating a share through the upsert requires it.
 */
export type VaultShareUpdate = {
  permissions?: AccessPermission | null;
};

/**
 * VaultStatistics
 *
 * Admin-level vault statistics.
 *
 * ``files_by_source`` counts files by their origin (their first revision's source); files created
 * before the source was recorded are not counted, so its values may sum to less than ``total_files``.
 * ``files_by_source_per_day`` is the same count bucketed by the file's UTC creation day; days
 * without files are absent.
 */
export type VaultStatistics = {
  /**
   * Total Files
   */
  total_files: number;
  /**
   * File Type Counts
   */
  file_type_counts: {
    [key: string]: number;
  };
  /**
   * Average File Type Sizes
   */
  average_file_type_sizes: {
    [key: string]: number;
  };
  /**
   * Pages Per File
   */
  pages_per_file: {
    [key: string]: number;
  };
  /**
   * Files By Source
   */
  files_by_source?: {
    [key in VaultFileRevisionSource]?: number;
  };
  /**
   * Files By Source Per Day
   */
  files_by_source_per_day?: {
    [key: string]: {
      [key in VaultFileRevisionSource]?: number;
    };
  };
  /**
   * Failed Previews
   */
  failed_previews: number;
  /**
   * Failed Ocrs
   */
  failed_ocrs: number;
  /**
   * Average Files Per Organisation
   */
  average_files_per_organisation: number;
  /**
   * Average Directories Per Organisation
   */
  average_directories_per_organisation: number;
  /**
   * Average Files Per Directory
   */
  average_files_per_directory: number;
  /**
   * Average Revisions Per File
   */
  average_revisions_per_file: number;
};

/**
 * VaultZipDownloadPayload
 *
 * Self-describing progress / terminal payload for a VAULT_ZIP_DOWNLOAD_UPDATED event.
 *
 * `uid` is the synthetic `download_uid` the client subscribes on; `directory_uid` is the source vault
 * directory. `file_name` (the zip's download name) is carried on every event, so a client that reconnects
 * mid-build — or a second tab — can render a complete row from any single event. While zipping, `status`
 * is `"running"` and `processed_files`/`total_files` drive the progress bar. On success `status` is
 * `"done"` with `artifact_uid` (the stored zip's uid, fetched via the download endpoint) set; on failure
 * `status` is `"failed"` and `error` carries a short message.
 */
export type VaultZipDownloadPayload = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Directory Uid
   */
  directory_uid: string;
  status: TaskStatus;
  /**
   * Processed Files
   */
  processed_files: number;
  /**
   * Total Files
   */
  total_files: number;
  /**
   * File Name
   */
  file_name: string;
  /**
   * Artifact Uid
   */
  artifact_uid?: string | null;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * VestingUnit
 */
export type VestingUnitInput = {
  /**
   * Index
   */
  index: number;
  /**
   * Unit Start Date
   */
  unit_start_date: string;
  /**
   * Unit End Date
   */
  unit_end_date: string;
  /**
   * Vested Options
   */
  vested_options: number | string;
};

/**
 * WarrantHandlingEnum
 */
export enum WarrantHandlingEnum {
  ASSUME_EXERCISED = 'assume_exercised',
  HANDLE_INCENTIVE = 'handle_incentive',
  EXCLUDE = 'exclude',
}

/**
 * WaterfallProceeds
 */
export type WaterfallProceeds = {
  /**
   * All Liquidation Preference Proceeds
   */
  all_liquidation_preference_proceeds: Array<LiquidationPreferenceProceeds>;
  /**
   * Total Proceeds
   */
  total_proceeds: {
    [key: string]: PayoutDetails | null;
  };
};

/**
 * WebsocketChatCancelMessage
 */
export type WebsocketChatCancelMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'chat_cancel';
  /**
   * Run Id
   */
  run_id: string;
};

/**
 * WebsocketChatDoneMessage
 */
export type WebsocketChatDoneMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'chat_done';
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Stream Id
   */
  stream_id?: string | null;
};

/**
 * WebsocketChatErrorMessage
 */
export type WebsocketChatErrorMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'chat_error';
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Stream Id
   */
  stream_id?: string | null;
  level: SupportedLogLevels;
  /**
   * Message
   */
  message: string;
};

/**
 * WebsocketChatInterruptMessage
 */
export type WebsocketChatInterruptMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'chat_interrupt';
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Stream Id
   */
  stream_id?: string | null;
  data: Interrupt;
};

/**
 * WebsocketChatStatusMessage
 */
export type WebsocketChatStatusMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'chat_status';
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Stream Id
   */
  stream_id?: string | null;
  data: ChatStatusUpdate;
};

/**
 * WebsocketChatThreadInfoMessage
 */
export type WebsocketChatThreadInfoMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'chat_thread_info';
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Stream Id
   */
  stream_id?: string | null;
  data: ThreadInfo;
};

/**
 * WebsocketChatTokenMessage
 */
export type WebsocketChatTokenMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'chat_token';
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Stream Id
   */
  stream_id?: string | null;
  data: ChatToken;
};

/**
 * WebsocketEventMessage
 */
export type WebsocketEventMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'event';
  data: GaiaEvent;
};

/**
 * WebsocketGeneralChatResumeMessage
 */
export type WebsocketGeneralChatResumeMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'general_chat_resume';
  /**
   * Run Id
   */
  run_id: string;
  data: GeneralChatResumeRequest;
};

/**
 * WebsocketGeneralChatStartMessage
 */
export type WebsocketGeneralChatStartMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'general_chat_start';
  /**
   * Run Id
   */
  run_id: string;
  data: GeneralChatStreamRequest;
};

/**
 * WebsocketHeartbeatMessage
 */
export type WebsocketHeartbeatMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'heartbeat';
  /**
   * Data
   */
  data: 'ping' | 'pong';
};

/**
 * WebsocketListSubscriptionsMessage
 */
export type WebsocketListSubscriptionsMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'list_subscriptions';
  /**
   * Data
   */
  data: {
    [key in WebsocketSubscriptionTarget]?: Array<string>;
  };
};

/**
 * WebsocketReviewProjectChatResumeMessage
 */
export type WebsocketReviewProjectChatResumeMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'review_project_chat_resume';
  /**
   * Run Id
   */
  run_id: string;
  data: ReviewProjectChatResumeRequest;
};

/**
 * WebsocketReviewProjectChatStartMessage
 */
export type WebsocketReviewProjectChatStartMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'review_project_chat_start';
  /**
   * Run Id
   */
  run_id: string;
  data: ReviewProjectChatStreamRequest;
};

/**
 * WebsocketStatusMessage
 */
export type WebsocketStatusMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'status';
  level: SupportedLogLevels;
  /**
   * Message
   */
  message: string;
};

/**
 * WebsocketSubscriptionMessage
 *
 * Subscribe/unsubscribe to one resource.
 *
 * ``resume_from`` only applies to ``chat_run`` subscriptions: it is the last stream cursor the
 * client processed, and the run replays from just after it. Omitting it replays the run from its
 * first entry, which is what a fresh page load wants.
 */
export type WebsocketSubscriptionMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type: 'subscribe' | 'unsubscribe';
  target: WebsocketSubscriptionTarget;
  /**
   * Uid
   */
  uid: string;
  /**
   * Resume From
   */
  resume_from?: string | null;
};

/**
 * WebsocketSubscriptionTarget
 */
export enum WebsocketSubscriptionTarget {
  REVIEW = 'review',
  CONTRACT_MANAGEMENT_EXTRACTION = 'contract_management_extraction',
  VAULT_FILE = 'vault_file',
  VAULT_DIRECTORY = 'vault_directory',
  VAULT_ZIP_DOWNLOAD = 'vault_zip_download',
  PLAYBOOK = 'playbook',
  PLAYBOOK_RUN = 'playbook_run',
  SIGNING_REQUEST = 'signing_request',
  SIGNING_REQUESTS = 'signing_requests',
  REVIEW_PROJECT_FILE = 'review_project_file',
  REVIEW_PROJECT = 'review_project',
  DEMO_DATA_SEED = 'demo_data_seed',
  CHAT_RUN = 'chat_run',
  USER_ATTENTION = 'user_attention',
}

/**
 * WebsocketTemplatingChatResumeMessage
 */
export type WebsocketTemplatingChatResumeMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'templating_chat_resume';
  /**
   * Run Id
   */
  run_id: string;
  data: TemplatingChatResumeRequest;
};

/**
 * WebsocketTemplatingChatStartMessage
 */
export type WebsocketTemplatingChatStartMessage = {
  /**
   * Time Stamp
   */
  time_stamp?: string;
  /**
   * Type
   */
  type?: 'templating_chat_start';
  /**
   * Run Id
   */
  run_id: string;
  data: TemplatingChatStreamRequest;
};

/**
 * WebsocketTicket
 *
 * A single-use credential for opening the websocket, passed as its ``ticket`` query parameter.
 */
export type WebsocketTicket = {
  /**
   * Ticket
   */
  ticket: string;
};

/**
 * YoutrustIntegrationCreate
 */
export type YoutrustIntegrationCreate = {
  /**
   * Signing Api Key
   */
  signing_api_key: string;
  /**
   * Management Api Key
   */
  management_api_key: string;
  state?: IntegrationState;
  /**
   * Has Active Webhooks
   */
  has_active_webhooks?: boolean;
  /**
   * Webhook Secret
   */
  webhook_secret?: string;
};

/**
 * YoutrustIntegrationInDB
 */
export type YoutrustIntegrationInDb = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Signing Api Key
   */
  signing_api_key: string | null;
  /**
   * Management Api Key
   */
  management_api_key: string | null;
  state: IntegrationState;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Has Active Webhooks
   */
  has_active_webhooks: boolean;
};

/**
 * YoutrustIntegrationStatus
 */
export type YoutrustIntegrationStatus = {
  state: IntegrationState;
  /**
   * Connected
   */
  connected: boolean;
  /**
   * Has Active Webhooks
   */
  has_active_webhooks: boolean;
};

/**
 * YoutrustSignatureFieldCreate
 */
export type YoutrustSignatureFieldCreate = {
  /**
   * Page
   */
  page: number;
  /**
   * Vault File Revision Uid
   */
  vault_file_revision_uid?: string | null;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Type
   */
  type?: 'signature';
  /**
   * Width
   */
  width?: number;
  /**
   * Height
   */
  height?: number;
  /**
   * Reason
   */
  reason?: string | null;
};

/**
 * ConvertibleLoan
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsExitSimulationSchemasExitConvertibleLoan =
  {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Tag
     */
    tag?: string | null;
    /**
     * Lender Id
     */
    lender_id: string;
    /**
     * Principal Amount
     */
    principal_amount: number | string;
    /**
     * Disbursement Date
     */
    disbursement_date: string;
    /**
     * Maturity Date
     */
    maturity_date: string;
    /**
     * Interest Rate
     */
    interest_rate: number | string;
    /**
     * Days Per Year
     */
    days_per_year: number;
    /**
     * Convert Interest
     */
    convert_interest?: boolean;
    /**
     * Conversion Discount
     */
    conversion_discount: number | string;
    /**
     * Minimum Round Size
     */
    minimum_round_size?: number | string | null;
    /**
     * Cap
     */
    cap?: number | string | null;
  };

/**
 * ShareClass
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsExitSimulationSchemasExitShareClass = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * CapTableRowEsopPool
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsExportSchemasExportCapTableRowEsopPool = {
  /**
   * Id
   */
  id: string;
  type: CapTableRowTypeEnum;
  /**
   * Number Of Shares
   */
  number_of_shares: number | string;
  /**
   * Share Class Id
   */
  share_class_id: string;
  /**
   * Tag
   */
  tag: string | null;
  /**
   * Esop Pool Id
   */
  esop_pool_id: string;
  esop_pool: EsopPool;
};

/**
 * CapTableRowStakeholder
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsExportSchemasExportCapTableRowStakeholder =
  {
    /**
     * Id
     */
    id: string;
    type: CapTableRowTypeEnum;
    /**
     * Number Of Shares
     */
    number_of_shares: number;
    /**
     * Number Of Burdened Shares
     */
    number_of_burdened_shares?: number | null;
    /**
     * Share Class Id
     */
    share_class_id: string;
    /**
     * Tag
     */
    tag: string | null;
    /**
     * Contractual Party Id
     */
    contractual_party_id: string;
    contractual_party: ContractualParty;
  };

/**
 * ShareClass
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsExportSchemasExportShareClass = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Is Virtual
   */
  is_virtual: boolean;
};

/**
 * CapTableRowEsopPool
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingCapTableRowEsopPoolInput =
  {
    /**
     * Id
     */
    id: string;
    type: CapTableRowTypeEnum;
    /**
     * Number Of Shares
     */
    number_of_shares: number | string;
    /**
     * Share Class Id
     */
    share_class_id: string;
    /**
     * Tag
     */
    tag?: string | null;
    /**
     * Esop Pool Id
     */
    esop_pool_id: string;
    esop_pool: EsopPool;
  };

/**
 * CapTableRowStakeholder
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingCapTableRowStakeholder =
  {
    /**
     * Id
     */
    id: string;
    type: CapTableRowTypeEnum;
    /**
     * Number Of Shares
     */
    number_of_shares: number;
    /**
     * Number Of Burdened Shares
     */
    number_of_burdened_shares?: number | null;
    /**
     * Share Class Id
     */
    share_class_id: string;
    /**
     * Tag
     */
    tag?: string | null;
    /**
     * Contractual Party Id
     */
    contractual_party_id: string;
    contractual_party: ContractualParty;
  };

/**
 * ConvertibleLoan
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingConvertibleLoan =
  {
    /**
     * Id
     */
    id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Tag
     */
    tag?: string | null;
    /**
     * Lender Id
     */
    lender_id: string;
    lender: ContractualParty;
    /**
     * Principal Amount
     */
    principal_amount: number | string;
    /**
     * Disbursement Date
     */
    disbursement_date: string;
    /**
     * Maturity Date
     */
    maturity_date: string;
    /**
     * Interest Rate
     */
    interest_rate: number | string;
    /**
     * Days Per Year
     */
    days_per_year: number;
    /**
     * Convert Interest
     */
    convert_interest?: boolean;
    /**
     * Conversion Discount
     */
    conversion_discount: number | string;
    /**
     * Minimum Round Size
     */
    minimum_round_size?: number | string | null;
    /**
     * Cap
     */
    cap?: number | string | null;
  };

/**
 * ShareClass
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsFinancingSchemasFinancingShareClass = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Is Virtual
   */
  is_virtual: boolean;
};

/**
 * Agreement
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasLinearAgreement = {
  /**
   * Start Date
   */
  start_date: string;
  /**
   * Vesting Period
   */
  vesting_period: number;
  /**
   * Cliff Period
   */
  cliff_period?: number;
  /**
   * Vesting Frequency
   */
  vesting_frequency: number;
  /**
   * Number Of Options
   */
  number_of_options?: number;
  /**
   * Initially Vested Options
   */
  initially_vested_options?: number | null;
  /**
   * Share To Option Ratio
   */
  share_to_option_ratio?: number | null;
  /**
   * Exercise Price
   */
  exercise_price?: number | null;
};

/**
 * Valuation
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasLinearValuation = {
  /**
   * Date
   */
  date: string;
  /**
   * Valuation
   */
  valuation: number;
};

/**
 * VestingUnit
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasLinearVestingUnit = {
  /**
   * Index
   */
  index: number;
  /**
   * Unit Start Date
   */
  unit_start_date: string;
  /**
   * Unit End Date
   */
  unit_end_date: string;
  /**
   * Events
   */
  events: Array<Event>;
  /**
   * Added Options
   */
  added_options: number;
  /**
   * Vested Options
   */
  vested_options: number | number;
  /**
   * Vested Options Without Buyback
   */
  vested_options_without_buyback: number | number;
  /**
   * Estimated Value
   */
  estimated_value: number | null;
};

/**
 * Agreement
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasNonLinearAgreement = {
  /**
   * Start Date
   */
  start_date: string;
  /**
   * Cliff Period
   */
  cliff_period?: number;
  /**
   * Number Of Options
   */
  number_of_options?: number;
  /**
   * Initially Vested Options
   */
  initially_vested_options?: number | null;
  /**
   * Share To Option Ratio
   */
  share_to_option_ratio?: number | null;
  /**
   * Exercise Price
   */
  exercise_price?: number | null;
};

/**
 * Valuation
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasNonLinearValuation = {
  /**
   * Date
   */
  date: string;
  /**
   * Valuation
   */
  valuation: number;
};

/**
 * VestingUnit
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasNonLinearVestingUnit = {
  /**
   * Index
   */
  index: number;
  /**
   * Unit Start Date
   */
  unit_start_date: string;
  /**
   * Unit End Date
   */
  unit_end_date: string;
  /**
   * Last In Period
   */
  last_in_period: boolean;
  /**
   * Under Cliff
   */
  under_cliff: boolean;
  /**
   * Events
   */
  events: Array<Event>;
  /**
   * Added Options
   */
  added_options: number;
  /**
   * Vested Options
   */
  vested_options: number | number;
  /**
   * Vested Options Without Buyback
   */
  vested_options_without_buyback: number | number;
  /**
   * Estimated Value
   */
  estimated_value: number | null;
};

/**
 * Valuation
 */
export type PythonServicesApisGatewayV1EndpointsFunctionsVestingSchemasPotentialValuation = {
  /**
   * Date
   */
  date: string;
  /**
   * Valuation
   */
  valuation: number | string;
};

/**
 * CalendarPreviewResult
 */
export type CalendarPreviewResultWritable = {
  /**
   * Results
   */
  results: Array<
    [
      ContractManagementContractFileInDbExtendedWritable,
      ContractManagementExtractionInDb | null,
      Array<ContractManagementDateInDb>,
    ]
  >;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * ContractFileWithRelations
 *
 * Contract file with its related contract, vault file, revision, and pages.
 *
 * This schema represents a contract file in the detail view, including the contract it belongs to,
 * the vault file it references, the specific revision the entity was extracted from, and the pages
 * that contain chunks related to the extracted model.
 *
 * Attributes:
 * contract_file: The contract file information
 * contract: The contract this file belongs to
 * vault_file: The vault file referenced by this contract file
 * revision: The specific vault file revision the entity was extracted from
 * is_latest_revision: Whether the revision is the current (latest) revision of the vault file
 * pages: List of pages that contain chunks related to the extracted model for this contract file
 */
export type ContractFileWithRelationsWritable = {
  contract_file: ContractManagementContractFileInDbWritable;
  contract: ContractManagementContractInDb;
  vault_file?: VaultFileInDbSlim | null;
  revision?: VaultFileRevisionInDb | null;
  /**
   * Is Latest Revision
   */
  is_latest_revision?: boolean;
  /**
   * Pages
   */
  pages: Array<FilePageInDb>;
};

/**
 * ContractFileWithRelationsSearchResult
 *
 * Search result for contract files with relations.
 */
export type ContractFileWithRelationsSearchResultWritable = {
  /**
   * Results
   */
  results: Array<ContractFileWithRelationsWritable>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementContractFileInDB
 */
export type ContractManagementContractFileInDbWritable = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: ContractManagementContractFileType | null;
  /**
   * Description
   */
  description: string | null;
  vault_file?: VaultFileInDbSlim | null;
};

/**
 * ContractManagementContractFileInDBExtended
 */
export type ContractManagementContractFileInDbExtendedWritable = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  type: ContractManagementContractFileType | null;
  /**
   * Description
   */
  description: string | null;
  vault_file?: VaultFileInDbSlim | null;
  contract: ContractManagementContractInDb;
  /**
   * Dates
   */
  dates?: Array<ContractManagementDateInDb>;
};

/**
 * ContractManagementContractFileSearchResult
 */
export type ContractManagementContractFileSearchResultWritable = {
  /**
   * Results
   */
  results: Array<ContractManagementContractFileInDbWritable>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * ContractManagementContractValueMatrix
 *
 * A page of a contract's values with each file's own.
 *
 * Attributes:
 * contract_files: The matrix columns: the contract's files with a revision, by type (amendment,
 * appendix, contract, unclassified) then age. Unclassified files show their values but never
 * answer for the contract.
 */
export type ContractManagementContractValueMatrixWritable = {
  /**
   * Results
   */
  results: Array<ContractManagementContractValueMatrixRow>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Contract Files
   */
  contract_files: Array<ContractManagementContractFileInDbWritable>;
};

/**
 * ContractManagementViewTableContractFiles
 */
export type ContractManagementViewTableContractFilesWritable = {
  /**
   * Results
   */
  results: Array<
    [
      ContractManagementContractFileInDbWritable,
      Array<
        | ContractManagementExtractionInDb
        | Array<
            | ({
                kind: 'natural_person';
              } & ContractManagementContractualPartyNaturalPersonInDb)
            | ({
                kind: 'legal_entity';
              } & ContractManagementContractualPartyLegalEntityInDb)
          >
        | Array<ContractManagementDateInDb>
        | string
        | ContractManagementContractFileType
        | EffectiveState
        | null
      >,
    ]
  >;
  /**
   * Result Count
   */
  result_count: number;
};

/**
 * RelatedContractFilesResponse
 *
 * Response schema for an extracted model showing related contract files.
 *
 * This schema provides a paginated view of contract files that are connected to an extracted model
 * through file chunks. Each contract file includes its related contract and vault file, allowing
 * the frontend to display the full relationship chain in a single response.
 *
 * The pagination is unified - scrolling loads more contract files, not separate pagination for
 * each entity type. This matches the frontend UX where users see a list of contract files and
 * can click on each to view its vault file and navigate through pages.
 *
 * Attributes:
 * contract_files: Paginated list of contract files with their contracts and vault files
 */
export type RelatedContractFilesResponseWritable = {
  contract_files: ContractFileWithRelationsSearchResultWritable;
};

/**
 * SigningRequestInDB
 */
export type SigningRequestInDbWritable = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Provider
   */
  provider: string;
  status: SigningRequestStatus | null;
  /**
   * Status Message
   */
  status_message?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Delivery Mode
   */
  delivery_mode: string;
  /**
   * Ordered Approvers
   */
  ordered_approvers: boolean;
  /**
   * Ordered Signers
   */
  ordered_signers: boolean;
  /**
   * Archived
   */
  archived: boolean;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Contract Files
   */
  contract_files?: Array<ContractManagementContractFileInDbWritable>;
  contract?: ContractManagementContractInDb | null;
};

/**
 * SigningRequestSearchResult
 */
export type SigningRequestSearchResultWritable = {
  /**
   * Results
   */
  results: Array<SigningRequestWithRelationsInDbWritable>;
  /**
   * Result Count
   */
  result_count: number;
  /**
   * Filter Options
   */
  filter_options: {
    [key: string]: Array<GaiaJsonValue>;
  };
};

/**
 * SigningRequestWithRelationsInDB
 */
export type SigningRequestWithRelationsInDbWritable = {
  /**
   * Uid
   */
  uid: string;
  /**
   * Provider
   */
  provider: string;
  status: SigningRequestStatus | null;
  /**
   * Status Message
   */
  status_message?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Delivery Mode
   */
  delivery_mode: string;
  /**
   * Ordered Approvers
   */
  ordered_approvers: boolean;
  /**
   * Ordered Signers
   */
  ordered_signers: boolean;
  /**
   * Archived
   */
  archived: boolean;
  /**
   * Expiration Date
   */
  expiration_date?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at?: string | null;
  /**
   * Contract Files
   */
  contract_files?: Array<ContractManagementContractFileInDbWritable>;
  contract?: ContractManagementContractInDb | null;
  /**
   * Participants
   */
  participants?: Array<ParticipantInDb>;
  /**
   * Vault File Revisions
   */
  vault_file_revisions?: Array<VaultFileRevisionSigningRequestInDb>;
};

export type UpData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/up';
};

export type UpResponses = {
  /**
   * Response Up Up Get
   *
   * Successful Response
   */
  200: string;
};

export type UpResponse = UpResponses[keyof UpResponses];

export type ExampleTargetPostData = {
  /**
   * Data
   */
  body: {
    [key: string]: unknown;
  };
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/tasks/example-target/post';
};

export type ExampleTargetPostErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExampleTargetPostError = ExampleTargetPostErrors[keyof ExampleTargetPostErrors];

export type ExampleTargetPostResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ExtractInformationData = {
  body: ServiceProviderExtractInformationTask;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/tasks/extract_information';
};

export type ExtractInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExtractInformationError = ExtractInformationErrors[keyof ExtractInformationErrors];

export type ExtractInformationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ExtractInformationBatchData = {
  body: ServiceProviderBatchExtractInformationTask;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/tasks/extract_information_batch';
};

export type ExtractInformationBatchErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExtractInformationBatchError =
  ExtractInformationBatchErrors[keyof ExtractInformationBatchErrors];

export type ExtractInformationBatchResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ExportCapTableData = {
  body: CapTableExport;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/export/cap_table';
};

export type ExportCapTableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportCapTableError = ExportCapTableErrors[keyof ExportCapTableErrors];

export type ExportCapTableResponses = {
  /**
   * Successful Response
   */
  200: CapTableExportResponse;
};

export type ExportCapTableResponse = ExportCapTableResponses[keyof ExportCapTableResponses];

export type FinancingSimulationProrataAmountsData = {
  body: ProrataAmounts;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/financing_simulation/prorata_amounts';
};

export type FinancingSimulationProrataAmountsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type FinancingSimulationProrataAmountsError =
  FinancingSimulationProrataAmountsErrors[keyof FinancingSimulationProrataAmountsErrors];

export type FinancingSimulationProrataAmountsResponses = {
  /**
   * Successful Response
   */
  200: ProrataAmountsResponse;
};

export type FinancingSimulationProrataAmountsResponse =
  FinancingSimulationProrataAmountsResponses[keyof FinancingSimulationProrataAmountsResponses];

export type FinancingSimulationFinancingSimulationData = {
  body: FinancingSimulation;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/financing_simulation/financing_simulation';
};

export type FinancingSimulationFinancingSimulationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type FinancingSimulationFinancingSimulationError =
  FinancingSimulationFinancingSimulationErrors[keyof FinancingSimulationFinancingSimulationErrors];

export type FinancingSimulationFinancingSimulationResponses = {
  /**
   * Successful Response
   */
  200: FinancingSimulationResponse;
};

export type FinancingSimulationFinancingSimulationResponse =
  FinancingSimulationFinancingSimulationResponses[keyof FinancingSimulationFinancingSimulationResponses];

export type HelperFunctionsLoanInterestData = {
  body: LoanInterest;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/helper_functions/loan_interest';
};

export type HelperFunctionsLoanInterestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type HelperFunctionsLoanInterestError =
  HelperFunctionsLoanInterestErrors[keyof HelperFunctionsLoanInterestErrors];

export type HelperFunctionsLoanInterestResponses = {
  /**
   * Successful Response
   */
  200: LoanInterestResponse;
};

export type HelperFunctionsLoanInterestResponse =
  HelperFunctionsLoanInterestResponses[keyof HelperFunctionsLoanInterestResponses];

export type ExitSimulationData2 = {
  body: ExitSimulationData;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/exit_simulation';
};

export type ExitSimulationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExitSimulationError = ExitSimulationErrors[keyof ExitSimulationErrors];

export type ExitSimulationResponses = {
  /**
   * Successful Response
   */
  200: ExitSimulationResponse;
};

export type ExitSimulationResponse2 = ExitSimulationResponses[keyof ExitSimulationResponses];

export type ExitSimulationSpreadsheetData = {
  body: ExitSimulationData;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/exit_simulation/spreadsheet';
};

export type ExitSimulationSpreadsheetErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExitSimulationSpreadsheetError =
  ExitSimulationSpreadsheetErrors[keyof ExitSimulationSpreadsheetErrors];

export type ExitSimulationSpreadsheetResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type VestingCalculationLinearVestingData = {
  body: LinearVesting;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/vesting_calculation/linear_vesting';
};

export type VestingCalculationLinearVestingErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type VestingCalculationLinearVestingError =
  VestingCalculationLinearVestingErrors[keyof VestingCalculationLinearVestingErrors];

export type VestingCalculationLinearVestingResponses = {
  /**
   * Successful Response
   */
  200: LinearVestingResponse;
};

export type VestingCalculationLinearVestingResponse =
  VestingCalculationLinearVestingResponses[keyof VestingCalculationLinearVestingResponses];

export type VestingCalculationNonLinearVestingData = {
  body: NonLinearVesting;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/vesting_calculation/non_linear_vesting';
};

export type VestingCalculationNonLinearVestingErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type VestingCalculationNonLinearVestingError =
  VestingCalculationNonLinearVestingErrors[keyof VestingCalculationNonLinearVestingErrors];

export type VestingCalculationNonLinearVestingResponses = {
  /**
   * Successful Response
   */
  200: NonLinearVestingResponse;
};

export type VestingCalculationNonLinearVestingResponse =
  VestingCalculationNonLinearVestingResponses[keyof VestingCalculationNonLinearVestingResponses];

export type VestingCalculationPotentialValuationData = {
  body: PotentialValuation;
  headers: {
    /**
     * X-Auth
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/functions/vesting_calculation/potential_valuation';
};

export type VestingCalculationPotentialValuationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type VestingCalculationPotentialValuationError =
  VestingCalculationPotentialValuationErrors[keyof VestingCalculationPotentialValuationErrors];

export type VestingCalculationPotentialValuationResponses = {
  /**
   * Response Vesting Calculation Potential Valuation V1 Functions Vesting Calculation Potential Valuation Post
   *
   * Successful Response
   */
  200: Array<PotentialValuationResponseItem>;
};

export type VestingCalculationPotentialValuationResponse =
  VestingCalculationPotentialValuationResponses[keyof VestingCalculationPotentialValuationResponses];

export type DeleteGaiaTokenData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia';
};

export type DeleteGaiaTokenResponses = {
  /**
   * Response Delete Gaia Token V1 App Account User Me Active Organisation Integrations Gaia Delete
   *
   * Successful Response
   */
  200: GaiaTokenInDb | null;
};

export type DeleteGaiaTokenResponse = DeleteGaiaTokenResponses[keyof DeleteGaiaTokenResponses];

export type GetGaiaTokenData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia';
};

export type GetGaiaTokenResponses = {
  /**
   * Response Get Gaia Token V1 App Account User Me Active Organisation Integrations Gaia Get
   *
   * Successful Response
   */
  200: GaiaTokenInDb | null;
};

export type GetGaiaTokenResponse = GetGaiaTokenResponses[keyof GetGaiaTokenResponses];

export type CreateGaiaTokenData = {
  body: GaiaTokenCreate;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia';
};

export type CreateGaiaTokenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateGaiaTokenError = CreateGaiaTokenErrors[keyof CreateGaiaTokenErrors];

export type CreateGaiaTokenResponses = {
  /**
   * Successful Response
   */
  200: GaiaTokenInDb;
};

export type CreateGaiaTokenResponse = CreateGaiaTokenResponses[keyof CreateGaiaTokenResponses];

export type UpdateGaiaTokenData = {
  body: GaiaTokenUpdate;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia';
};

export type UpdateGaiaTokenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateGaiaTokenError = UpdateGaiaTokenErrors[keyof UpdateGaiaTokenErrors];

export type UpdateGaiaTokenResponses = {
  /**
   * Response Update Gaia Token V1 App Account User Me Active Organisation Integrations Gaia Put
   *
   * Successful Response
   */
  200: GaiaTokenInDb | null;
};

export type UpdateGaiaTokenResponse = UpdateGaiaTokenResponses[keyof UpdateGaiaTokenResponses];

export type GetSectionGroupsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Scope
     */
    scope?: 'read' | 'write';
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia/section-groups';
};

export type GetSectionGroupsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSectionGroupsError = GetSectionGroupsErrors[keyof GetSectionGroupsErrors];

export type GetSectionGroupsResponses = {
  /**
   * Response Get Section Groups V1 App Account User Me Active Organisation Integrations Gaia Section Groups Get
   *
   * Successful Response
   */
  200: Array<GaiaSectionGroupSlim>;
};

export type GetSectionGroupsResponse = GetSectionGroupsResponses[keyof GetSectionGroupsResponses];

export type GetSectionData = {
  body?: never;
  path: {
    /**
     * Section Id
     */
    section_id: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia/sections/{section_id}';
};

export type GetSectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSectionError = GetSectionErrors[keyof GetSectionErrors];

export type GetSectionResponses = {
  /**
   * Successful Response
   */
  200: GaiaSection;
};

export type GetSectionResponse = GetSectionResponses[keyof GetSectionResponses];

export type GetSectionFilesMetaData = {
  body: SectionFilesQuery;
  path: {
    /**
     * Section Id
     */
    section_id: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia/sections/{section_id}/files';
};

export type GetSectionFilesMetaErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSectionFilesMetaError = GetSectionFilesMetaErrors[keyof GetSectionFilesMetaErrors];

export type GetSectionFilesMetaResponses = {
  /**
   * Successful Response
   */
  200: GaiaPaginatedSectionFiles;
};

export type GetSectionFilesMetaResponse =
  GetSectionFilesMetaResponses[keyof GetSectionFilesMetaResponses];

export type ExportVaultFileToGaiaData = {
  body?: never;
  path: {
    /**
     * Gaia Section Id
     */
    gaia_section_id: string;
  };
  query: {
    /**
     * Vault File Uid
     */
    vault_file_uid: string;
    /**
     * Signing Status
     */
    signing_status?: 'signed' | 'unknown' | 'unsigned';
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia/sections/{gaia_section_id}/export';
};

export type ExportVaultFileToGaiaErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportVaultFileToGaiaError =
  ExportVaultFileToGaiaErrors[keyof ExportVaultFileToGaiaErrors];

export type ExportVaultFileToGaiaResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ImportGaiaFileData = {
  body?: never;
  path: {
    /**
     * File Id
     */
    file_id: string;
  };
  query?: {
    /**
     * Parent Directory Uid
     */
    parent_directory_uid?: string | null;
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/gaia/files/{file_id}/import';
};

export type ImportGaiaFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportGaiaFileError = ImportGaiaFileErrors[keyof ImportGaiaFileErrors];

export type ImportGaiaFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDb;
};

export type ImportGaiaFileResponse = ImportGaiaFileResponses[keyof ImportGaiaFileResponses];

export type GetOrganisationGoogleDriveAuthUrlData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * State
     */
    state?: string | null;
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/google-drive/auth-url';
};

export type GetOrganisationGoogleDriveAuthUrlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrganisationGoogleDriveAuthUrlError =
  GetOrganisationGoogleDriveAuthUrlErrors[keyof GetOrganisationGoogleDriveAuthUrlErrors];

export type GetOrganisationGoogleDriveAuthUrlResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveAuthUrlResponse;
};

export type GetOrganisationGoogleDriveAuthUrlResponse =
  GetOrganisationGoogleDriveAuthUrlResponses[keyof GetOrganisationGoogleDriveAuthUrlResponses];

export type ListOrganisationGoogleDriveData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Parent Id
     */
    parent_id?: string | null;
    /**
     * Page Size
     */
    page_size?: number;
    /**
     * Page Token
     */
    page_token?: string | null;
    /**
     * Search
     */
    search?: string | null;
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/google-drive/items';
};

export type ListOrganisationGoogleDriveErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListOrganisationGoogleDriveError =
  ListOrganisationGoogleDriveErrors[keyof ListOrganisationGoogleDriveErrors];

export type ListOrganisationGoogleDriveResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveListResponse;
};

export type ListOrganisationGoogleDriveResponse =
  ListOrganisationGoogleDriveResponses[keyof ListOrganisationGoogleDriveResponses];

export type ImportGoogleDriveItemsData = {
  body: GoogleDriveImportRequest;
  path?: never;
  query?: {
    /**
     * Scope
     */
    scope?: 'user' | 'organisation';
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/google-drive/import';
};

export type ImportGoogleDriveItemsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportGoogleDriveItemsError =
  ImportGoogleDriveItemsErrors[keyof ImportGoogleDriveItemsErrors];

export type ImportGoogleDriveItemsResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type ImportGoogleDriveItemsResponse =
  ImportGoogleDriveItemsResponses[keyof ImportGoogleDriveItemsResponses];

export type ConnectOrganisationGoogleDriveData = {
  body: GoogleDriveConnectRequest;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/google-drive/connect';
};

export type ConnectOrganisationGoogleDriveErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConnectOrganisationGoogleDriveError =
  ConnectOrganisationGoogleDriveErrors[keyof ConnectOrganisationGoogleDriveErrors];

export type ConnectOrganisationGoogleDriveResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveIntegrationStatus;
};

export type ConnectOrganisationGoogleDriveResponse =
  ConnectOrganisationGoogleDriveResponses[keyof ConnectOrganisationGoogleDriveResponses];

export type GetOrganisationGoogleDriveStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/google-drive/status';
};

export type GetOrganisationGoogleDriveStatusResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveIntegrationStatus;
};

export type GetOrganisationGoogleDriveStatusResponse =
  GetOrganisationGoogleDriveStatusResponses[keyof GetOrganisationGoogleDriveStatusResponses];

export type DisconnectOrganisationGoogleDriveData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/google-drive/disconnect';
};

export type DisconnectOrganisationGoogleDriveResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveIntegrationStatus;
};

export type DisconnectOrganisationGoogleDriveResponse =
  DisconnectOrganisationGoogleDriveResponses[keyof DisconnectOrganisationGoogleDriveResponses];

export type GetJiraAuthUrlData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * State
     */
    state?: string | null;
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/jira/auth-url';
};

export type GetJiraAuthUrlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetJiraAuthUrlError = GetJiraAuthUrlErrors[keyof GetJiraAuthUrlErrors];

export type GetJiraAuthUrlResponses = {
  /**
   * Successful Response
   */
  200: JiraAuthUrlResponse;
};

export type GetJiraAuthUrlResponse = GetJiraAuthUrlResponses[keyof GetJiraAuthUrlResponses];

export type ConnectJiraData = {
  body: JiraConnectRequest;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/jira/connect';
};

export type ConnectJiraErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConnectJiraError = ConnectJiraErrors[keyof ConnectJiraErrors];

export type ConnectJiraResponses = {
  /**
   * Successful Response
   */
  200: JiraIntegrationStatus;
};

export type ConnectJiraResponse = ConnectJiraResponses[keyof ConnectJiraResponses];

export type ListJiraSitesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/jira/sites';
};

export type ListJiraSitesResponses = {
  /**
   * Successful Response
   */
  200: JiraSiteListResponse;
};

export type ListJiraSitesResponse = ListJiraSitesResponses[keyof ListJiraSitesResponses];

export type SelectJiraSiteData = {
  body: JiraSelectSiteRequest;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/jira/site';
};

export type SelectJiraSiteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SelectJiraSiteError = SelectJiraSiteErrors[keyof SelectJiraSiteErrors];

export type SelectJiraSiteResponses = {
  /**
   * Successful Response
   */
  200: JiraIntegrationStatus;
};

export type SelectJiraSiteResponse = SelectJiraSiteResponses[keyof SelectJiraSiteResponses];

export type GetJiraStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/jira/status';
};

export type GetJiraStatusResponses = {
  /**
   * Successful Response
   */
  200: JiraIntegrationStatus;
};

export type GetJiraStatusResponse = GetJiraStatusResponses[keyof GetJiraStatusResponses];

export type DisconnectJiraData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/jira/disconnect';
};

export type DisconnectJiraResponses = {
  /**
   * Successful Response
   */
  200: JiraIntegrationStatus;
};

export type DisconnectJiraResponse = DisconnectJiraResponses[keyof DisconnectJiraResponses];

export type GetSlackAuthUrlData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * State
     */
    state?: string | null;
  };
  url: '/v1/app/account/user/me/active-organisation/integrations/slack/auth-url';
};

export type GetSlackAuthUrlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSlackAuthUrlError = GetSlackAuthUrlErrors[keyof GetSlackAuthUrlErrors];

export type GetSlackAuthUrlResponses = {
  /**
   * Successful Response
   */
  200: SlackAuthUrlResponse;
};

export type GetSlackAuthUrlResponse = GetSlackAuthUrlResponses[keyof GetSlackAuthUrlResponses];

export type ConnectSlackData = {
  body: SlackConnectRequest;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/slack/connect';
};

export type ConnectSlackErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConnectSlackError = ConnectSlackErrors[keyof ConnectSlackErrors];

export type ConnectSlackResponses = {
  /**
   * Successful Response
   */
  200: SlackIntegrationStatus;
};

export type ConnectSlackResponse = ConnectSlackResponses[keyof ConnectSlackResponses];

export type SearchSlackChannelsData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/slack/channels/search';
};

export type SearchSlackChannelsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSlackChannelsError = SearchSlackChannelsErrors[keyof SearchSlackChannelsErrors];

export type SearchSlackChannelsResponses = {
  /**
   * Successful Response
   */
  200: SlackChannelSearchResult;
};

export type SearchSlackChannelsResponse =
  SearchSlackChannelsResponses[keyof SearchSlackChannelsResponses];

export type SendSlackTestMessageData = {
  body: SlackTestMessageRequest;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/slack/test-message';
};

export type SendSlackTestMessageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SendSlackTestMessageError =
  SendSlackTestMessageErrors[keyof SendSlackTestMessageErrors];

export type SendSlackTestMessageResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type SendSlackTestMessageResponse =
  SendSlackTestMessageResponses[keyof SendSlackTestMessageResponses];

export type GetSlackStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/slack/status';
};

export type GetSlackStatusResponses = {
  /**
   * Successful Response
   */
  200: SlackIntegrationStatus;
};

export type GetSlackStatusResponse = GetSlackStatusResponses[keyof GetSlackStatusResponses];

export type DisconnectSlackData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/slack/disconnect';
};

export type DisconnectSlackResponses = {
  /**
   * Successful Response
   */
  200: SlackIntegrationStatus;
};

export type DisconnectSlackResponse = DisconnectSlackResponses[keyof DisconnectSlackResponses];

export type DeleteYoutrustIntegrationData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/youtrust';
};

export type DeleteYoutrustIntegrationResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type DeleteYoutrustIntegrationResponse =
  DeleteYoutrustIntegrationResponses[keyof DeleteYoutrustIntegrationResponses];

export type CreateYoutrustIntegrationData = {
  body: YoutrustIntegrationCreate;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/youtrust';
};

export type CreateYoutrustIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateYoutrustIntegrationError =
  CreateYoutrustIntegrationErrors[keyof CreateYoutrustIntegrationErrors];

export type CreateYoutrustIntegrationResponses = {
  /**
   * Successful Response
   */
  200: YoutrustIntegrationInDb;
};

export type CreateYoutrustIntegrationResponse =
  CreateYoutrustIntegrationResponses[keyof CreateYoutrustIntegrationResponses];

export type GetYoutrustStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/integrations/youtrust/status';
};

export type GetYoutrustStatusResponses = {
  /**
   * Successful Response
   */
  200: YoutrustIntegrationStatus;
};

export type GetYoutrustStatusResponse =
  GetYoutrustStatusResponses[keyof GetYoutrustStatusResponses];

export type ListInvitationsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Status
     */
    status?: Array<OrganisationInvitationStatus> | null;
  };
  url: '/v1/app/account/user/me/active-organisation/users/invitations';
};

export type ListInvitationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListInvitationsError = ListInvitationsErrors[keyof ListInvitationsErrors];

export type ListInvitationsResponses = {
  /**
   * Response List Invitations V1 App Account User Me Active Organisation Users Invitations Get
   *
   * Successful Response
   */
  200: Array<OrganisationInvitationInDb>;
};

export type ListInvitationsResponse = ListInvitationsResponses[keyof ListInvitationsResponses];

export type CreateInvitationData = {
  body: OrganisationInvitationCreateRequest;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/invitations';
};

export type CreateInvitationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateInvitationError = CreateInvitationErrors[keyof CreateInvitationErrors];

export type CreateInvitationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInvitationInDb;
};

export type CreateInvitationResponse = CreateInvitationResponses[keyof CreateInvitationResponses];

export type UpdateInvitationRolesData = {
  body: OrganisationInvitationRolesUpdateRequest;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/invitations/{uid}/roles';
};

export type UpdateInvitationRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateInvitationRolesError =
  UpdateInvitationRolesErrors[keyof UpdateInvitationRolesErrors];

export type UpdateInvitationRolesResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInvitationInDb;
};

export type UpdateInvitationRolesResponse =
  UpdateInvitationRolesResponses[keyof UpdateInvitationRolesResponses];

export type ResendInvitationData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/invitations/{uid}/resend';
};

export type ResendInvitationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResendInvitationError = ResendInvitationErrors[keyof ResendInvitationErrors];

export type ResendInvitationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInvitationInDb;
};

export type ResendInvitationResponse = ResendInvitationResponses[keyof ResendInvitationResponses];

export type RevokeInvitationData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/invitations/{uid}';
};

export type RevokeInvitationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RevokeInvitationError = RevokeInvitationErrors[keyof RevokeInvitationErrors];

export type RevokeInvitationResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type RevokeInvitationResponse = RevokeInvitationResponses[keyof RevokeInvitationResponses];

export type SearchOrganisationAgentTasksData = {
  body: OrganisationAgentTaskSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/agent-tasks/search';
};

export type SearchOrganisationAgentTasksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchOrganisationAgentTasksError =
  SearchOrganisationAgentTasksErrors[keyof SearchOrganisationAgentTasksErrors];

export type SearchOrganisationAgentTasksResponses = {
  /**
   * Successful Response
   */
  200: OrganisationAgentTaskSearchResult;
};

export type SearchOrganisationAgentTasksResponse =
  SearchOrganisationAgentTasksResponses[keyof SearchOrganisationAgentTasksResponses];

export type RetryOrganisationAgentTaskData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/agent-tasks/{uid}/retry';
};

export type RetryOrganisationAgentTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RetryOrganisationAgentTaskError =
  RetryOrganisationAgentTaskErrors[keyof RetryOrganisationAgentTaskErrors];

export type RetryOrganisationAgentTaskResponses = {
  /**
   * Successful Response
   */
  200: OrganisationAgentTaskInDb;
};

export type RetryOrganisationAgentTaskResponse =
  RetryOrganisationAgentTaskResponses[keyof RetryOrganisationAgentTaskResponses];

export type CreateUserMemoryData = {
  body: UserMemoryCreate;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/memories/create';
};

export type CreateUserMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateUserMemoryError = CreateUserMemoryErrors[keyof CreateUserMemoryErrors];

export type CreateUserMemoryResponses = {
  /**
   * Successful Response
   */
  200: UserMemoryInDb;
};

export type CreateUserMemoryResponse = CreateUserMemoryResponses[keyof CreateUserMemoryResponses];

export type DeleteUserMemoryData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/memories/{uid}';
};

export type DeleteUserMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserMemoryError = DeleteUserMemoryErrors[keyof DeleteUserMemoryErrors];

export type DeleteUserMemoryResponses = {
  /**
   * Successful Response
   */
  200: UserMemoryInDb;
};

export type DeleteUserMemoryResponse = DeleteUserMemoryResponses[keyof DeleteUserMemoryResponses];

export type GetSpecificUserMemoryData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/memories/{uid}';
};

export type GetSpecificUserMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSpecificUserMemoryError =
  GetSpecificUserMemoryErrors[keyof GetSpecificUserMemoryErrors];

export type GetSpecificUserMemoryResponses = {
  /**
   * Successful Response
   */
  200: UserMemoryInDb;
};

export type GetSpecificUserMemoryResponse =
  GetSpecificUserMemoryResponses[keyof GetSpecificUserMemoryResponses];

export type UpdateUserMemoryData = {
  body: UserMemoryUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/memories/{uid}';
};

export type UpdateUserMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateUserMemoryError = UpdateUserMemoryErrors[keyof UpdateUserMemoryErrors];

export type UpdateUserMemoryResponses = {
  /**
   * Successful Response
   */
  200: UserMemoryInDb;
};

export type UpdateUserMemoryResponse = UpdateUserMemoryResponses[keyof UpdateUserMemoryResponses];

export type GetAllUserMemoriesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/memories/';
};

export type GetAllUserMemoriesResponses = {
  /**
   * Response Get All User Memories V1 App Account User Me Active Organisation Memories  Get
   *
   * Successful Response
   */
  200: Array<UserMemoryInDb>;
};

export type GetAllUserMemoriesResponse =
  GetAllUserMemoriesResponses[keyof GetAllUserMemoriesResponses];

export type SearchUserMemoriesData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/memories/search';
};

export type SearchUserMemoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchUserMemoriesError = SearchUserMemoriesErrors[keyof SearchUserMemoriesErrors];

export type SearchUserMemoriesResponses = {
  /**
   * Successful Response
   */
  200: UserMemorySearchResult;
};

export type SearchUserMemoriesResponse =
  SearchUserMemoriesResponses[keyof SearchUserMemoriesResponses];

export type GetOwnActiveOrganisationData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation';
};

export type GetOwnActiveOrganisationResponses = {
  /**
   * Successful Response
   */
  200: ActiveOrganisationInDb;
};

export type GetOwnActiveOrganisationResponse =
  GetOwnActiveOrganisationResponses[keyof GetOwnActiveOrganisationResponses];

export type UpdateOwnActiveOrganisationData = {
  body: ActiveOrganisationUpdate;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation';
};

export type UpdateOwnActiveOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateOwnActiveOrganisationError =
  UpdateOwnActiveOrganisationErrors[keyof UpdateOwnActiveOrganisationErrors];

export type UpdateOwnActiveOrganisationResponses = {
  /**
   * Successful Response
   */
  200: ActiveOrganisationInDb;
};

export type UpdateOwnActiveOrganisationResponse =
  UpdateOwnActiveOrganisationResponses[keyof UpdateOwnActiveOrganisationResponses];

export type GetOwnActiveOrganisationAllowanceUsageData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/allowance_usage';
};

export type GetOwnActiveOrganisationAllowanceUsageResponses = {
  /**
   * Response Get Own Active Organisation Allowance Usage V1 App Account User Me Active Organisation Allowance Usage Get
   *
   * Successful Response
   */
  200: number | null;
};

export type GetOwnActiveOrganisationAllowanceUsageResponse =
  GetOwnActiveOrganisationAllowanceUsageResponses[keyof GetOwnActiveOrganisationAllowanceUsageResponses];

export type SearchActiveOrganisationUsersData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/search';
};

export type SearchActiveOrganisationUsersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchActiveOrganisationUsersError =
  SearchActiveOrganisationUsersErrors[keyof SearchActiveOrganisationUsersErrors];

export type SearchActiveOrganisationUsersResponses = {
  /**
   * Successful Response
   */
  200: UserSearchResult;
};

export type SearchActiveOrganisationUsersResponse =
  SearchActiveOrganisationUsersResponses[keyof SearchActiveOrganisationUsersResponses];

export type RemoveUserFromActiveOrganisationData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/{uid}';
};

export type RemoveUserFromActiveOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveUserFromActiveOrganisationError =
  RemoveUserFromActiveOrganisationErrors[keyof RemoveUserFromActiveOrganisationErrors];

export type RemoveUserFromActiveOrganisationResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type RemoveUserFromActiveOrganisationResponse =
  RemoveUserFromActiveOrganisationResponses[keyof RemoveUserFromActiveOrganisationResponses];

export type ReadActiveOrganisationUserData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/{uid}';
};

export type ReadActiveOrganisationUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReadActiveOrganisationUserError =
  ReadActiveOrganisationUserErrors[keyof ReadActiveOrganisationUserErrors];

export type ReadActiveOrganisationUserResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type ReadActiveOrganisationUserResponse =
  ReadActiveOrganisationUserResponses[keyof ReadActiveOrganisationUserResponses];

export type SetActiveOrganisationUserIsActiveData = {
  body: UserIsActiveUpdateRequest;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/active-organisation/users/{uid}/is-active';
};

export type SetActiveOrganisationUserIsActiveErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetActiveOrganisationUserIsActiveError =
  SetActiveOrganisationUserIsActiveErrors[keyof SetActiveOrganisationUserIsActiveErrors];

export type SetActiveOrganisationUserIsActiveResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type SetActiveOrganisationUserIsActiveResponse =
  SetActiveOrganisationUserIsActiveResponses[keyof SetActiveOrganisationUserIsActiveResponses];

export type GetPersonalGoogleDriveAuthUrlData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * State
     */
    state?: string | null;
  };
  url: '/v1/app/account/user/me/integrations/google-drive/auth-url';
};

export type GetPersonalGoogleDriveAuthUrlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPersonalGoogleDriveAuthUrlError =
  GetPersonalGoogleDriveAuthUrlErrors[keyof GetPersonalGoogleDriveAuthUrlErrors];

export type GetPersonalGoogleDriveAuthUrlResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveAuthUrlResponse;
};

export type GetPersonalGoogleDriveAuthUrlResponse =
  GetPersonalGoogleDriveAuthUrlResponses[keyof GetPersonalGoogleDriveAuthUrlResponses];

export type ListPersonalGoogleDriveData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Parent Id
     */
    parent_id?: string | null;
    /**
     * Page Size
     */
    page_size?: number;
    /**
     * Page Token
     */
    page_token?: string | null;
    /**
     * Search
     */
    search?: string | null;
  };
  url: '/v1/app/account/user/me/integrations/google-drive/items';
};

export type ListPersonalGoogleDriveErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPersonalGoogleDriveError =
  ListPersonalGoogleDriveErrors[keyof ListPersonalGoogleDriveErrors];

export type ListPersonalGoogleDriveResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveListResponse;
};

export type ListPersonalGoogleDriveResponse =
  ListPersonalGoogleDriveResponses[keyof ListPersonalGoogleDriveResponses];

export type ConnectPersonalGoogleDriveData = {
  body: GoogleDriveConnectRequest;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/integrations/google-drive/connect';
};

export type ConnectPersonalGoogleDriveErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConnectPersonalGoogleDriveError =
  ConnectPersonalGoogleDriveErrors[keyof ConnectPersonalGoogleDriveErrors];

export type ConnectPersonalGoogleDriveResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveIntegrationStatus;
};

export type ConnectPersonalGoogleDriveResponse =
  ConnectPersonalGoogleDriveResponses[keyof ConnectPersonalGoogleDriveResponses];

export type GetPersonalGoogleDriveStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/integrations/google-drive/status';
};

export type GetPersonalGoogleDriveStatusResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveIntegrationStatus;
};

export type GetPersonalGoogleDriveStatusResponse =
  GetPersonalGoogleDriveStatusResponses[keyof GetPersonalGoogleDriveStatusResponses];

export type DisconnectPersonalGoogleDriveData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/integrations/google-drive/disconnect';
};

export type DisconnectPersonalGoogleDriveResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveIntegrationStatus;
};

export type DisconnectPersonalGoogleDriveResponse =
  DisconnectPersonalGoogleDriveResponses[keyof DisconnectPersonalGoogleDriveResponses];

export type SearchUserAttentionData = {
  body: UserAttentionSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/attention/search';
};

export type SearchUserAttentionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchUserAttentionError = SearchUserAttentionErrors[keyof SearchUserAttentionErrors];

export type SearchUserAttentionResponses = {
  /**
   * Successful Response
   */
  200: UserAttentionSearchResult;
};

export type SearchUserAttentionResponse =
  SearchUserAttentionResponses[keyof SearchUserAttentionResponses];

export type AcknowledgeUserAttentionData = {
  body?: never;
  path: {
    /**
     * Attention Uid
     */
    attention_uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/attention/{attention_uid}/acknowledge';
};

export type AcknowledgeUserAttentionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AcknowledgeUserAttentionError =
  AcknowledgeUserAttentionErrors[keyof AcknowledgeUserAttentionErrors];

export type AcknowledgeUserAttentionResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type AcknowledgeUserAttentionResponse =
  AcknowledgeUserAttentionResponses[keyof AcknowledgeUserAttentionResponses];

export type GetSigningAttentionLinkData = {
  body?: never;
  path: {
    /**
     * Attention Uid
     */
    attention_uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/attention/{attention_uid}/signing-link';
};

export type GetSigningAttentionLinkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSigningAttentionLinkError =
  GetSigningAttentionLinkErrors[keyof GetSigningAttentionLinkErrors];

export type GetSigningAttentionLinkResponses = {
  /**
   * Successful Response
   */
  200: SigningAttentionLink;
};

export type GetSigningAttentionLinkResponse =
  GetSigningAttentionLinkResponses[keyof GetSigningAttentionLinkResponses];

export type ReadUserMeData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me';
};

export type ReadUserMeResponses = {
  /**
   * Successful Response
   */
  200: UserInDbMe;
};

export type ReadUserMeResponse = ReadUserMeResponses[keyof ReadUserMeResponses];

export type UpdateUserMeData = {
  body: UserUpdate;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me';
};

export type UpdateUserMeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateUserMeError = UpdateUserMeErrors[keyof UpdateUserMeErrors];

export type UpdateUserMeResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type UpdateUserMeResponse = UpdateUserMeResponses[keyof UpdateUserMeResponses];

export type GetOwnOrganisationsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/organisations';
};

export type GetOwnOrganisationsResponses = {
  /**
   * Response Get Own Organisations V1 App Account User Me Organisations Get
   *
   * Successful Response
   */
  200: Array<OrganisationInDb>;
};

export type GetOwnOrganisationsResponse =
  GetOwnOrganisationsResponses[keyof GetOwnOrganisationsResponses];

export type CreateOwnOrganisationData = {
  body: OrganisationSelfServiceCreate;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/organisations';
};

export type CreateOwnOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateOwnOrganisationError =
  CreateOwnOrganisationErrors[keyof CreateOwnOrganisationErrors];

export type CreateOwnOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type CreateOwnOrganisationResponse =
  CreateOwnOrganisationResponses[keyof CreateOwnOrganisationResponses];

export type LeaveOwnOrganisationData = {
  body?: never;
  path: {
    /**
     * Organisation Uid
     */
    organisation_uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/me/organisations/{organisation_uid}';
};

export type LeaveOwnOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type LeaveOwnOrganisationError =
  LeaveOwnOrganisationErrors[keyof LeaveOwnOrganisationErrors];

export type LeaveOwnOrganisationResponses = {
  /**
   * Successful Response
   */
  200: UserInDbMe;
};

export type LeaveOwnOrganisationResponse =
  LeaveOwnOrganisationResponses[keyof LeaveOwnOrganisationResponses];

export type GetOwnRolesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/me/roles';
};

export type GetOwnRolesResponses = {
  /**
   * Response Get Own Roles V1 App Account User Me Roles Get
   *
   * Successful Response
   */
  200: Array<PermissionRoleInDb>;
};

export type GetOwnRolesResponse = GetOwnRolesResponses[keyof GetOwnRolesResponses];

export type UserSwitchOrganisationData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Organisation Uid
     */
    organisation_uid: string;
  };
  url: '/v1/app/account/user/me/switch-org';
};

export type UserSwitchOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UserSwitchOrganisationError =
  UserSwitchOrganisationErrors[keyof UserSwitchOrganisationErrors];

export type UserSwitchOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type UserSwitchOrganisationResponse =
  UserSwitchOrganisationResponses[keyof UserSwitchOrganisationResponses];

export type SignupInsightsData = {
  body: SignupProfile;
  headers?: {
    /**
     * X-Recaptcha-Token
     */
    'X-Recaptcha-Token'?: string | null;
  };
  path?: never;
  query?: never;
  url: '/v1/app/account/user/sign_up';
};

export type SignupInsightsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SignupInsightsError = SignupInsightsErrors[keyof SignupInsightsErrors];

export type SignupInsightsResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type SignupInsightsResponse = SignupInsightsResponses[keyof SignupInsightsResponses];

export type SignupCmData = {
  body: Signup;
  headers?: {
    /**
     * X-Recaptcha-Token
     */
    'X-Recaptcha-Token'?: string | null;
  };
  path?: never;
  query?: never;
  url: '/v1/app/account/user/sign_up/cm';
};

export type SignupCmErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SignupCmError = SignupCmErrors[keyof SignupCmErrors];

export type SignupCmResponses = {
  /**
   * Response Signup Cm V1 App Account User Sign Up Cm Post
   *
   * Successful Response
   */
  200: {
    [key: string]: string;
  };
};

export type SignupCmResponse = SignupCmResponses[keyof SignupCmResponses];

export type RequestEmailVerificationData = {
  body?: never;
  headers?: {
    /**
     * X-Recaptcha-Token
     */
    'X-Recaptcha-Token'?: string | null;
  };
  path?: never;
  query?: never;
  url: '/v1/app/account/user/sign_up/email-verification/request';
};

export type RequestEmailVerificationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RequestEmailVerificationError =
  RequestEmailVerificationErrors[keyof RequestEmailVerificationErrors];

export type RequestEmailVerificationResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type RequestEmailVerificationResponse =
  RequestEmailVerificationResponses[keyof RequestEmailVerificationResponses];

export type ConfirmEmailVerificationData = {
  body: EmailVerificationConfirm;
  headers?: {
    /**
     * X-Recaptcha-Token
     */
    'X-Recaptcha-Token'?: string | null;
  };
  path?: never;
  query?: never;
  url: '/v1/app/account/user/sign_up/email-verification/confirm';
};

export type ConfirmEmailVerificationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConfirmEmailVerificationError =
  ConfirmEmailVerificationErrors[keyof ConfirmEmailVerificationErrors];

export type ConfirmEmailVerificationResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type ConfirmEmailVerificationResponse =
  ConfirmEmailVerificationResponses[keyof ConfirmEmailVerificationResponses];

export type SearchUserData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/user/search';
};

export type SearchUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchUserError = SearchUserErrors[keyof SearchUserErrors];

export type SearchUserResponses = {
  /**
   * Successful Response
   */
  200: UserSearchResult;
};

export type SearchUserResponse = SearchUserResponses[keyof SearchUserResponses];

export type ReadUserData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/{uid}';
};

export type ReadUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReadUserError = ReadUserErrors[keyof ReadUserErrors];

export type ReadUserResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type ReadUserResponse = ReadUserResponses[keyof ReadUserResponses];

export type UpdateUserData = {
  body: UserUpdateSuperuser;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/{uid}';
};

export type UpdateUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateUserError = UpdateUserErrors[keyof UpdateUserErrors];

export type UpdateUserResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type UpdateUserResponse = UpdateUserResponses[keyof UpdateUserResponses];

export type DeleteUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/user/{user_uid}';
};

export type DeleteUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserError = DeleteUserErrors[keyof DeleteUserErrors];

export type DeleteUserResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type DeleteUserResponse = DeleteUserResponses[keyof DeleteUserResponses];

export type CreateOrganisationMemoryData = {
  body: OrganisationMemoryCreate;
  path?: never;
  query?: never;
  url: '/v1/app/account/organisation/memories/create';
};

export type CreateOrganisationMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateOrganisationMemoryError =
  CreateOrganisationMemoryErrors[keyof CreateOrganisationMemoryErrors];

export type CreateOrganisationMemoryResponses = {
  /**
   * Successful Response
   */
  200: OrganisationMemoryInDb;
};

export type CreateOrganisationMemoryResponse =
  CreateOrganisationMemoryResponses[keyof CreateOrganisationMemoryResponses];

export type DeleteOrganisationMemoryData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/memories/{uid}';
};

export type DeleteOrganisationMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteOrganisationMemoryError =
  DeleteOrganisationMemoryErrors[keyof DeleteOrganisationMemoryErrors];

export type DeleteOrganisationMemoryResponses = {
  /**
   * Successful Response
   */
  200: OrganisationMemoryInDb;
};

export type DeleteOrganisationMemoryResponse =
  DeleteOrganisationMemoryResponses[keyof DeleteOrganisationMemoryResponses];

export type GetSpecificOrganisationMemoryData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/memories/{uid}';
};

export type GetSpecificOrganisationMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSpecificOrganisationMemoryError =
  GetSpecificOrganisationMemoryErrors[keyof GetSpecificOrganisationMemoryErrors];

export type GetSpecificOrganisationMemoryResponses = {
  /**
   * Successful Response
   */
  200: OrganisationMemoryInDb;
};

export type GetSpecificOrganisationMemoryResponse =
  GetSpecificOrganisationMemoryResponses[keyof GetSpecificOrganisationMemoryResponses];

export type UpdateOrganisationMemoryData = {
  body: OrganisationMemoryUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/memories/{uid}';
};

export type UpdateOrganisationMemoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateOrganisationMemoryError =
  UpdateOrganisationMemoryErrors[keyof UpdateOrganisationMemoryErrors];

export type UpdateOrganisationMemoryResponses = {
  /**
   * Successful Response
   */
  200: OrganisationMemoryInDb;
};

export type UpdateOrganisationMemoryResponse =
  UpdateOrganisationMemoryResponses[keyof UpdateOrganisationMemoryResponses];

export type GetAllOrganisationMemoriesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/account/organisation/memories/';
};

export type GetAllOrganisationMemoriesResponses = {
  /**
   * Response Get All Organisation Memories V1 App Account Organisation Memories  Get
   *
   * Successful Response
   */
  200: Array<OrganisationMemoryInDb>;
};

export type GetAllOrganisationMemoriesResponse =
  GetAllOrganisationMemoriesResponses[keyof GetAllOrganisationMemoriesResponses];

export type SearchOrganisationMemoriesData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/organisation/memories/search';
};

export type SearchOrganisationMemoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchOrganisationMemoriesError =
  SearchOrganisationMemoriesErrors[keyof SearchOrganisationMemoriesErrors];

export type SearchOrganisationMemoriesResponses = {
  /**
   * Successful Response
   */
  200: OrganisationMemorySearchResult;
};

export type SearchOrganisationMemoriesResponse =
  SearchOrganisationMemoriesResponses[keyof SearchOrganisationMemoriesResponses];

export type CreateOrganisationData = {
  body: BodyCreateOrganisationV1AppAccountOrganisationPost;
  path?: never;
  query?: never;
  url: '/v1/app/account/organisation/';
};

export type CreateOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateOrganisationError = CreateOrganisationErrors[keyof CreateOrganisationErrors];

export type CreateOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type CreateOrganisationResponse =
  CreateOrganisationResponses[keyof CreateOrganisationResponses];

export type SearchOrganisationData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/organisation/search';
};

export type SearchOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchOrganisationError = SearchOrganisationErrors[keyof SearchOrganisationErrors];

export type SearchOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationSearchResult;
};

export type SearchOrganisationResponse =
  SearchOrganisationResponses[keyof SearchOrganisationResponses];

export type DeleteOrganisationEndpointData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}';
};

export type DeleteOrganisationEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteOrganisationEndpointError =
  DeleteOrganisationEndpointErrors[keyof DeleteOrganisationEndpointErrors];

export type DeleteOrganisationEndpointResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type DeleteOrganisationEndpointResponse =
  DeleteOrganisationEndpointResponses[keyof DeleteOrganisationEndpointResponses];

export type GetOrganisationData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}';
};

export type GetOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrganisationError = GetOrganisationErrors[keyof GetOrganisationErrors];

export type GetOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type GetOrganisationResponse = GetOrganisationResponses[keyof GetOrganisationResponses];

export type UpdateOrganisationData = {
  body: OrganisationUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}';
};

export type UpdateOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateOrganisationError = UpdateOrganisationErrors[keyof UpdateOrganisationErrors];

export type UpdateOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type UpdateOrganisationResponse =
  UpdateOrganisationResponses[keyof UpdateOrganisationResponses];

export type UpdateOrganisationFeaturesData = {
  body: OrganisationFeaturesUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}/features';
};

export type UpdateOrganisationFeaturesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateOrganisationFeaturesError =
  UpdateOrganisationFeaturesErrors[keyof UpdateOrganisationFeaturesErrors];

export type UpdateOrganisationFeaturesResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type UpdateOrganisationFeaturesResponse =
  UpdateOrganisationFeaturesResponses[keyof UpdateOrganisationFeaturesResponses];

export type GetOrganisationModelSettingsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}/model_settings';
};

export type GetOrganisationModelSettingsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrganisationModelSettingsError =
  GetOrganisationModelSettingsErrors[keyof GetOrganisationModelSettingsErrors];

export type GetOrganisationModelSettingsResponses = {
  /**
   * Successful Response
   */
  200: OrganisationModelSettingsInDb;
};

export type GetOrganisationModelSettingsResponse =
  GetOrganisationModelSettingsResponses[keyof GetOrganisationModelSettingsResponses];

export type UpdateOrganisationModelSettingsData = {
  body: OrganisationModelSettingsUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}/model_settings';
};

export type UpdateOrganisationModelSettingsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateOrganisationModelSettingsError =
  UpdateOrganisationModelSettingsErrors[keyof UpdateOrganisationModelSettingsErrors];

export type UpdateOrganisationModelSettingsResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type UpdateOrganisationModelSettingsResponse =
  UpdateOrganisationModelSettingsResponses[keyof UpdateOrganisationModelSettingsResponses];

export type GetOrganisationAllowanceUsageData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}/allowance_usage';
};

export type GetOrganisationAllowanceUsageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrganisationAllowanceUsageError =
  GetOrganisationAllowanceUsageErrors[keyof GetOrganisationAllowanceUsageErrors];

export type GetOrganisationAllowanceUsageResponses = {
  /**
   * Response Get Organisation Allowance Usage V1 App Account Organisation  Uid  Allowance Usage Get
   *
   * Successful Response
   */
  200: number | null;
};

export type GetOrganisationAllowanceUsageResponse =
  GetOrganisationAllowanceUsageResponses[keyof GetOrganisationAllowanceUsageResponses];

export type GetOrganisationMissingExtractionsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}/missing-extractions';
};

export type GetOrganisationMissingExtractionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrganisationMissingExtractionsError =
  GetOrganisationMissingExtractionsErrors[keyof GetOrganisationMissingExtractionsErrors];

export type GetOrganisationMissingExtractionsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMissingExtractions;
};

export type GetOrganisationMissingExtractionsResponse =
  GetOrganisationMissingExtractionsResponses[keyof GetOrganisationMissingExtractionsResponses];

export type ExtractOrganisationMissingExtractionsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/organisation/{uid}/missing-extractions/extract';
};

export type ExtractOrganisationMissingExtractionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExtractOrganisationMissingExtractionsError =
  ExtractOrganisationMissingExtractionsErrors[keyof ExtractOrganisationMissingExtractionsErrors];

export type ExtractOrganisationMissingExtractionsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMissingExtractions;
};

export type ExtractOrganisationMissingExtractionsResponse =
  ExtractOrganisationMissingExtractionsResponses[keyof ExtractOrganisationMissingExtractionsResponses];

export type RemoveAllSharesByContractData = {
  body?: never;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/contract/{contract_uid}';
};

export type RemoveAllSharesByContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByContractError =
  RemoveAllSharesByContractErrors[keyof RemoveAllSharesByContractErrors];

export type RemoveAllSharesByContractResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractInDb;
};

export type RemoveAllSharesByContractResponse =
  RemoveAllSharesByContractResponses[keyof RemoveAllSharesByContractResponses];

export type GetContractShareContractData = {
  body?: never;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/contract/{contract_uid}';
};

export type GetContractShareContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractShareContractError =
  GetContractShareContractErrors[keyof GetContractShareContractErrors];

export type GetContractShareContractResponses = {
  /**
   * Response Get Contract Share Contract V1 App Account Permissions Contract Share Contract  Contract Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractUserShareInDb>;
};

export type GetContractShareContractResponse =
  GetContractShareContractResponses[keyof GetContractShareContractResponses];

export type RemoveAllSharesByUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/user/{user_uid}';
};

export type RemoveAllSharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByUserError =
  RemoveAllSharesByUserErrors[keyof RemoveAllSharesByUserErrors];

export type RemoveAllSharesByUserResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type RemoveAllSharesByUserResponse =
  RemoveAllSharesByUserResponses[keyof RemoveAllSharesByUserResponses];

export type GetContractShareUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/user/{user_uid}';
};

export type GetContractShareUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractShareUserError =
  GetContractShareUserErrors[keyof GetContractShareUserErrors];

export type GetContractShareUserResponses = {
  /**
   * Response Get Contract Share User V1 App Account Permissions Contract Share User  User Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractUserShareInDb>;
};

export type GetContractShareUserResponse =
  GetContractShareUserResponses[keyof GetContractShareUserResponses];

export type SearchSharesByContractData = {
  body: SearchQuery;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/contract/{contract_uid}/search';
};

export type SearchSharesByContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByContractError =
  SearchSharesByContractErrors[keyof SearchSharesByContractErrors];

export type SearchSharesByContractResponses = {
  /**
   * Successful Response
   */
  200: ContractUserShareSearchResultContract;
};

export type SearchSharesByContractResponse =
  SearchSharesByContractResponses[keyof SearchSharesByContractResponses];

export type SearchSharesByUserData = {
  body: SearchQuery;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/user/{user_uid}/search';
};

export type SearchSharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByUserError = SearchSharesByUserErrors[keyof SearchSharesByUserErrors];

export type SearchSharesByUserResponses = {
  /**
   * Successful Response
   */
  200: ContractUserShareSearchResultUser;
};

export type SearchSharesByUserResponse =
  SearchSharesByUserResponses[keyof SearchSharesByUserResponses];

export type RemoveContractShareData = {
  body?: never;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/contract/{contract_uid}/user/{user_uid}';
};

export type RemoveContractShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveContractShareError = RemoveContractShareErrors[keyof RemoveContractShareErrors];

export type RemoveContractShareResponses = {
  /**
   * Successful Response
   */
  200: ContractUserShareInDb;
};

export type RemoveContractShareResponse =
  RemoveContractShareResponses[keyof RemoveContractShareResponses];

export type UpdateContractSharePermissionsData = {
  body: ContractUserShareUpdate;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-share/contract/{contract_uid}/user/{user_uid}';
};

export type UpdateContractSharePermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractSharePermissionsError =
  UpdateContractSharePermissionsErrors[keyof UpdateContractSharePermissionsErrors];

export type UpdateContractSharePermissionsResponses = {
  /**
   * Successful Response
   */
  200: ContractUserShareInDb;
};

export type UpdateContractSharePermissionsResponse =
  UpdateContractSharePermissionsResponses[keyof UpdateContractSharePermissionsResponses];

export type RemoveAllSharesByContract2Data = {
  body?: never;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/contract/{contract_uid}';
};

export type RemoveAllSharesByContract2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByContract2Error =
  RemoveAllSharesByContract2Errors[keyof RemoveAllSharesByContract2Errors];

export type RemoveAllSharesByContract2Responses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractInDb;
};

export type RemoveAllSharesByContract2Response =
  RemoveAllSharesByContract2Responses[keyof RemoveAllSharesByContract2Responses];

export type GetContractShareByContractData = {
  body?: never;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/contract/{contract_uid}';
};

export type GetContractShareByContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractShareByContractError =
  GetContractShareByContractErrors[keyof GetContractShareByContractErrors];

export type GetContractShareByContractResponses = {
  /**
   * Response Get Contract Share By Contract V1 App Account Permissions Contract Role Share Contract  Contract Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractRoleShareInDb>;
};

export type GetContractShareByContractResponse =
  GetContractShareByContractResponses[keyof GetContractShareByContractResponses];

export type RemoveAllSharesByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/role/{role_uid}';
};

export type RemoveAllSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByRoleError =
  RemoveAllSharesByRoleErrors[keyof RemoveAllSharesByRoleErrors];

export type RemoveAllSharesByRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type RemoveAllSharesByRoleResponse =
  RemoveAllSharesByRoleResponses[keyof RemoveAllSharesByRoleResponses];

export type GetContractShareByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/role/{role_uid}';
};

export type GetContractShareByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractShareByRoleError =
  GetContractShareByRoleErrors[keyof GetContractShareByRoleErrors];

export type GetContractShareByRoleResponses = {
  /**
   * Response Get Contract Share By Role V1 App Account Permissions Contract Role Share Role  Role Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractRoleShareInDb>;
};

export type GetContractShareByRoleResponse =
  GetContractShareByRoleResponses[keyof GetContractShareByRoleResponses];

export type SearchSharesByContract2Data = {
  body: SearchQuery;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/contract/{contract_uid}/search';
};

export type SearchSharesByContract2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByContract2Error =
  SearchSharesByContract2Errors[keyof SearchSharesByContract2Errors];

export type SearchSharesByContract2Responses = {
  /**
   * Successful Response
   */
  200: ContractRoleShareSearchResultContract;
};

export type SearchSharesByContract2Response =
  SearchSharesByContract2Responses[keyof SearchSharesByContract2Responses];

export type SearchSharesByRoleData = {
  body: SearchQuery;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/role/{role_uid}/search';
};

export type SearchSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByRoleError = SearchSharesByRoleErrors[keyof SearchSharesByRoleErrors];

export type SearchSharesByRoleResponses = {
  /**
   * Successful Response
   */
  200: ContractRoleShareSearchResultRole;
};

export type SearchSharesByRoleResponse =
  SearchSharesByRoleResponses[keyof SearchSharesByRoleResponses];

export type RemoveContractShare2Data = {
  body?: never;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/contract/{contract_uid}/role/{role_uid}';
};

export type RemoveContractShare2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveContractShare2Error =
  RemoveContractShare2Errors[keyof RemoveContractShare2Errors];

export type RemoveContractShare2Responses = {
  /**
   * Successful Response
   */
  200: ContractRoleShareInDb;
};

export type RemoveContractShare2Response =
  RemoveContractShare2Responses[keyof RemoveContractShare2Responses];

export type UpdateContractSharePermissions2Data = {
  body: ContractRoleShareUpdate;
  path: {
    /**
     * Contract Uid
     */
    contract_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contract-role-share/contract/{contract_uid}/role/{role_uid}';
};

export type UpdateContractSharePermissions2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractSharePermissions2Error =
  UpdateContractSharePermissions2Errors[keyof UpdateContractSharePermissions2Errors];

export type UpdateContractSharePermissions2Responses = {
  /**
   * Successful Response
   */
  200: ContractRoleShareInDb;
};

export type UpdateContractSharePermissions2Response =
  UpdateContractSharePermissions2Responses[keyof UpdateContractSharePermissions2Responses];

export type RemoveAllSharesByContractualPartyData = {
  body?: never;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/contractual-party/{contractual_party_uid}';
};

export type RemoveAllSharesByContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByContractualPartyError =
  RemoveAllSharesByContractualPartyErrors[keyof RemoveAllSharesByContractualPartyErrors];

export type RemoveAllSharesByContractualPartyResponses = {
  /**
   * Response Remove All Shares By Contractual Party V1 App Account Permissions Contractual Party Share Contractual Party  Contractual Party Uid  Delete
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type RemoveAllSharesByContractualPartyResponse =
  RemoveAllSharesByContractualPartyResponses[keyof RemoveAllSharesByContractualPartyResponses];

export type GetContractualPartyShareContractualPartyData = {
  body?: never;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/contractual-party/{contractual_party_uid}';
};

export type GetContractualPartyShareContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractualPartyShareContractualPartyError =
  GetContractualPartyShareContractualPartyErrors[keyof GetContractualPartyShareContractualPartyErrors];

export type GetContractualPartyShareContractualPartyResponses = {
  /**
   * Response Get Contractual Party Share Contractual Party V1 App Account Permissions Contractual Party Share Contractual Party  Contractual Party Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractualPartyUserShareInDb>;
};

export type GetContractualPartyShareContractualPartyResponse =
  GetContractualPartyShareContractualPartyResponses[keyof GetContractualPartyShareContractualPartyResponses];

export type RemoveAllSharesByUser2Data = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/user/{user_uid}';
};

export type RemoveAllSharesByUser2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByUser2Error =
  RemoveAllSharesByUser2Errors[keyof RemoveAllSharesByUser2Errors];

export type RemoveAllSharesByUser2Responses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type RemoveAllSharesByUser2Response =
  RemoveAllSharesByUser2Responses[keyof RemoveAllSharesByUser2Responses];

export type GetContractualPartyShareUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/user/{user_uid}';
};

export type GetContractualPartyShareUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractualPartyShareUserError =
  GetContractualPartyShareUserErrors[keyof GetContractualPartyShareUserErrors];

export type GetContractualPartyShareUserResponses = {
  /**
   * Response Get Contractual Party Share User V1 App Account Permissions Contractual Party Share User  User Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractualPartyUserShareInDb>;
};

export type GetContractualPartyShareUserResponse =
  GetContractualPartyShareUserResponses[keyof GetContractualPartyShareUserResponses];

export type SearchSharesByContractualPartyData = {
  body: SearchQuery;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/contractual-party/{contractual_party_uid}/search';
};

export type SearchSharesByContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByContractualPartyError =
  SearchSharesByContractualPartyErrors[keyof SearchSharesByContractualPartyErrors];

export type SearchSharesByContractualPartyResponses = {
  /**
   * Successful Response
   */
  200: ContractualPartyUserShareSearchResultContractualParty;
};

export type SearchSharesByContractualPartyResponse =
  SearchSharesByContractualPartyResponses[keyof SearchSharesByContractualPartyResponses];

export type SearchSharesByUser2Data = {
  body: SearchQuery;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/user/{user_uid}/search';
};

export type SearchSharesByUser2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByUser2Error = SearchSharesByUser2Errors[keyof SearchSharesByUser2Errors];

export type SearchSharesByUser2Responses = {
  /**
   * Successful Response
   */
  200: ContractualPartyUserShareSearchResultUser;
};

export type SearchSharesByUser2Response =
  SearchSharesByUser2Responses[keyof SearchSharesByUser2Responses];

export type RemoveContractualPartyShareData = {
  body?: never;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/contractual-party/{contractual_party_uid}/user/{user_uid}';
};

export type RemoveContractualPartyShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveContractualPartyShareError =
  RemoveContractualPartyShareErrors[keyof RemoveContractualPartyShareErrors];

export type RemoveContractualPartyShareResponses = {
  /**
   * Successful Response
   */
  200: ContractualPartyUserShareInDb;
};

export type RemoveContractualPartyShareResponse =
  RemoveContractualPartyShareResponses[keyof RemoveContractualPartyShareResponses];

export type UpdateContractualPartySharePermissionsData = {
  body: ContractualPartyUserShareUpdate;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-share/contractual-party/{contractual_party_uid}/user/{user_uid}';
};

export type UpdateContractualPartySharePermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractualPartySharePermissionsError =
  UpdateContractualPartySharePermissionsErrors[keyof UpdateContractualPartySharePermissionsErrors];

export type UpdateContractualPartySharePermissionsResponses = {
  /**
   * Successful Response
   */
  200: ContractualPartyUserShareInDb;
};

export type UpdateContractualPartySharePermissionsResponse =
  UpdateContractualPartySharePermissionsResponses[keyof UpdateContractualPartySharePermissionsResponses];

export type RemoveAllSharesByContractualParty2Data = {
  body?: never;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/contractual-party/{contractual_party_uid}';
};

export type RemoveAllSharesByContractualParty2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByContractualParty2Error =
  RemoveAllSharesByContractualParty2Errors[keyof RemoveAllSharesByContractualParty2Errors];

export type RemoveAllSharesByContractualParty2Responses = {
  /**
   * Response Remove All Shares By Contractual Party V1 App Account Permissions Contractual Party Role Share Contractual Party  Contractual Party Uid  Delete
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type RemoveAllSharesByContractualParty2Response =
  RemoveAllSharesByContractualParty2Responses[keyof RemoveAllSharesByContractualParty2Responses];

export type GetContractualPartyShareByContractualPartyData = {
  body?: never;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/contractual-party/{contractual_party_uid}';
};

export type GetContractualPartyShareByContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractualPartyShareByContractualPartyError =
  GetContractualPartyShareByContractualPartyErrors[keyof GetContractualPartyShareByContractualPartyErrors];

export type GetContractualPartyShareByContractualPartyResponses = {
  /**
   * Response Get Contractual Party Share By Contractual Party V1 App Account Permissions Contractual Party Role Share Contractual Party  Contractual Party Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractualPartyRoleShareInDb>;
};

export type GetContractualPartyShareByContractualPartyResponse =
  GetContractualPartyShareByContractualPartyResponses[keyof GetContractualPartyShareByContractualPartyResponses];

export type RemoveAllSharesByRole2Data = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/role/{role_uid}';
};

export type RemoveAllSharesByRole2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllSharesByRole2Error =
  RemoveAllSharesByRole2Errors[keyof RemoveAllSharesByRole2Errors];

export type RemoveAllSharesByRole2Responses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type RemoveAllSharesByRole2Response =
  RemoveAllSharesByRole2Responses[keyof RemoveAllSharesByRole2Responses];

export type GetContractualPartyShareByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/role/{role_uid}';
};

export type GetContractualPartyShareByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractualPartyShareByRoleError =
  GetContractualPartyShareByRoleErrors[keyof GetContractualPartyShareByRoleErrors];

export type GetContractualPartyShareByRoleResponses = {
  /**
   * Response Get Contractual Party Share By Role V1 App Account Permissions Contractual Party Role Share Role  Role Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractualPartyRoleShareInDb>;
};

export type GetContractualPartyShareByRoleResponse =
  GetContractualPartyShareByRoleResponses[keyof GetContractualPartyShareByRoleResponses];

export type SearchSharesByContractualParty2Data = {
  body: SearchQuery;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/contractual-party/{contractual_party_uid}/search';
};

export type SearchSharesByContractualParty2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByContractualParty2Error =
  SearchSharesByContractualParty2Errors[keyof SearchSharesByContractualParty2Errors];

export type SearchSharesByContractualParty2Responses = {
  /**
   * Successful Response
   */
  200: ContractualPartyRoleShareSearchResultContractualParty;
};

export type SearchSharesByContractualParty2Response =
  SearchSharesByContractualParty2Responses[keyof SearchSharesByContractualParty2Responses];

export type SearchSharesByRole2Data = {
  body: SearchQuery;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/role/{role_uid}/search';
};

export type SearchSharesByRole2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByRole2Error = SearchSharesByRole2Errors[keyof SearchSharesByRole2Errors];

export type SearchSharesByRole2Responses = {
  /**
   * Successful Response
   */
  200: ContractualPartyRoleShareSearchResultRole;
};

export type SearchSharesByRole2Response =
  SearchSharesByRole2Responses[keyof SearchSharesByRole2Responses];

export type RemoveContractualPartyShare2Data = {
  body?: never;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/contractual-party/{contractual_party_uid}/role/{role_uid}';
};

export type RemoveContractualPartyShare2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveContractualPartyShare2Error =
  RemoveContractualPartyShare2Errors[keyof RemoveContractualPartyShare2Errors];

export type RemoveContractualPartyShare2Responses = {
  /**
   * Successful Response
   */
  200: ContractualPartyRoleShareInDb;
};

export type RemoveContractualPartyShare2Response =
  RemoveContractualPartyShare2Responses[keyof RemoveContractualPartyShare2Responses];

export type UpdateContractualPartySharePermissions2Data = {
  body: ContractualPartyRoleShareUpdate;
  path: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/contractual-party-role-share/contractual-party/{contractual_party_uid}/role/{role_uid}';
};

export type UpdateContractualPartySharePermissions2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractualPartySharePermissions2Error =
  UpdateContractualPartySharePermissions2Errors[keyof UpdateContractualPartySharePermissions2Errors];

export type UpdateContractualPartySharePermissions2Responses = {
  /**
   * Successful Response
   */
  200: ContractualPartyRoleShareInDb;
};

export type UpdateContractualPartySharePermissions2Response =
  UpdateContractualPartySharePermissions2Responses[keyof UpdateContractualPartySharePermissions2Responses];

export type CreateRoleData = {
  body: PermissionRoleCreate;
  path?: never;
  query?: never;
  url: '/v1/app/account/permissions/role/new';
};

export type CreateRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateRoleError = CreateRoleErrors[keyof CreateRoleErrors];

export type CreateRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type CreateRoleResponse = CreateRoleResponses[keyof CreateRoleResponses];

export type SearchRolesData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/account/permissions/role/search';
};

export type SearchRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchRolesError = SearchRolesErrors[keyof SearchRolesErrors];

export type SearchRolesResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleSearchResult;
};

export type SearchRolesResponse = SearchRolesResponses[keyof SearchRolesResponses];

export type ListRolesForUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/role/user/{user_uid}';
};

export type ListRolesForUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListRolesForUserError = ListRolesForUserErrors[keyof ListRolesForUserErrors];

export type ListRolesForUserResponses = {
  /**
   * Response List Roles For User V1 App Account Permissions Role User  User Uid  Get
   *
   * Successful Response
   */
  200: Array<PermissionRoleInDb>;
};

export type ListRolesForUserResponse = ListRolesForUserResponses[keyof ListRolesForUserResponses];

export type DeleteRoleData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/role/{uid}';
};

export type DeleteRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteRoleError = DeleteRoleErrors[keyof DeleteRoleErrors];

export type DeleteRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type DeleteRoleResponse = DeleteRoleResponses[keyof DeleteRoleResponses];

export type GetRoleData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/role/{uid}';
};

export type GetRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRoleError = GetRoleErrors[keyof GetRoleErrors];

export type GetRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type GetRoleResponse = GetRoleResponses[keyof GetRoleResponses];

export type UpdateRoleData = {
  body: PermissionRoleUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/role/{uid}';
};

export type UpdateRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateRoleError = UpdateRoleErrors[keyof UpdateRoleErrors];

export type UpdateRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type UpdateRoleResponse = UpdateRoleResponses[keyof UpdateRoleResponses];

export type AssignUsersToRoleData = {
  body: RoleUsersRequest;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/role/{role_uid}/users';
};

export type AssignUsersToRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AssignUsersToRoleError = AssignUsersToRoleErrors[keyof AssignUsersToRoleErrors];

export type AssignUsersToRoleResponses = {
  /**
   * Successful Response
   */
  200: RoleUsersResult;
};

export type AssignUsersToRoleResponse =
  AssignUsersToRoleResponses[keyof AssignUsersToRoleResponses];

export type RemoveUsersFromRoleData = {
  body: RoleUsersRequest;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/role/{role_uid}/users/remove';
};

export type RemoveUsersFromRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveUsersFromRoleError = RemoveUsersFromRoleErrors[keyof RemoveUsersFromRoleErrors];

export type RemoveUsersFromRoleResponses = {
  /**
   * Successful Response
   */
  200: RoleUsersResult;
};

export type RemoveUsersFromRoleResponse =
  RemoveUsersFromRoleResponses[keyof RemoveUsersFromRoleResponses];

export type SearchUsersInRoleData = {
  body: SearchQuery;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/role/{role_uid}/users/search';
};

export type SearchUsersInRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchUsersInRoleError = SearchUsersInRoleErrors[keyof SearchUsersInRoleErrors];

export type SearchUsersInRoleResponses = {
  /**
   * Successful Response
   */
  200: UserSearchResult;
};

export type SearchUsersInRoleResponse =
  SearchUsersInRoleResponses[keyof SearchUsersInRoleResponses];

export type RemoveTemplateRoleShareData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/template/{template_uid}/role/{role_uid}';
};

export type RemoveTemplateRoleShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveTemplateRoleShareError =
  RemoveTemplateRoleShareErrors[keyof RemoveTemplateRoleShareErrors];

export type RemoveTemplateRoleShareResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateRoleShareInDb;
};

export type RemoveTemplateRoleShareResponse =
  RemoveTemplateRoleShareResponses[keyof RemoveTemplateRoleShareResponses];

export type UpsertTemplateRoleShareData = {
  body: ContractGenerationTemplateRoleShareCreate;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/template/{template_uid}/role/{role_uid}';
};

export type UpsertTemplateRoleShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpsertTemplateRoleShareError =
  UpsertTemplateRoleShareErrors[keyof UpsertTemplateRoleShareErrors];

export type UpsertTemplateRoleShareResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateRoleShareInDb;
};

export type UpsertTemplateRoleShareResponse =
  UpsertTemplateRoleShareResponses[keyof UpsertTemplateRoleShareResponses];

export type UpdateTemplateRoleSharePermissionsData = {
  body: ContractGenerationTemplateRoleShareUpdate;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/template/{template_uid}/role/{role_uid}';
};

export type UpdateTemplateRoleSharePermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTemplateRoleSharePermissionsError =
  UpdateTemplateRoleSharePermissionsErrors[keyof UpdateTemplateRoleSharePermissionsErrors];

export type UpdateTemplateRoleSharePermissionsResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateRoleShareInDb;
};

export type UpdateTemplateRoleSharePermissionsResponse =
  UpdateTemplateRoleSharePermissionsResponses[keyof UpdateTemplateRoleSharePermissionsResponses];

export type RemoveAllRoleSharesByTemplateData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/template/{template_uid}';
};

export type RemoveAllRoleSharesByTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllRoleSharesByTemplateError =
  RemoveAllRoleSharesByTemplateErrors[keyof RemoveAllRoleSharesByTemplateErrors];

export type RemoveAllRoleSharesByTemplateResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateInDb;
};

export type RemoveAllRoleSharesByTemplateResponse =
  RemoveAllRoleSharesByTemplateResponses[keyof RemoveAllRoleSharesByTemplateResponses];

export type GetRoleSharesByTemplateData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/template/{template_uid}';
};

export type GetRoleSharesByTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRoleSharesByTemplateError =
  GetRoleSharesByTemplateErrors[keyof GetRoleSharesByTemplateErrors];

export type GetRoleSharesByTemplateResponses = {
  /**
   * Response Get Role Shares By Template V1 App Account Permissions Template Role Share Template  Template Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractGenerationTemplateRoleShareInDb>;
};

export type GetRoleSharesByTemplateResponse =
  GetRoleSharesByTemplateResponses[keyof GetRoleSharesByTemplateResponses];

export type RemoveAllTemplateSharesByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/role/{role_uid}';
};

export type RemoveAllTemplateSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllTemplateSharesByRoleError =
  RemoveAllTemplateSharesByRoleErrors[keyof RemoveAllTemplateSharesByRoleErrors];

export type RemoveAllTemplateSharesByRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type RemoveAllTemplateSharesByRoleResponse =
  RemoveAllTemplateSharesByRoleResponses[keyof RemoveAllTemplateSharesByRoleResponses];

export type GetTemplateSharesByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/role/{role_uid}';
};

export type GetTemplateSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateSharesByRoleError =
  GetTemplateSharesByRoleErrors[keyof GetTemplateSharesByRoleErrors];

export type GetTemplateSharesByRoleResponses = {
  /**
   * Response Get Template Shares By Role V1 App Account Permissions Template Role Share Role  Role Uid  Get
   *
   * Successful Response
   */
  200: Array<ContractGenerationTemplateRoleShareInDb>;
};

export type GetTemplateSharesByRoleResponse =
  GetTemplateSharesByRoleResponses[keyof GetTemplateSharesByRoleResponses];

export type SearchSharesByTemplateData = {
  body: SearchQuery;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/template/{template_uid}/search';
};

export type SearchSharesByTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByTemplateError =
  SearchSharesByTemplateErrors[keyof SearchSharesByTemplateErrors];

export type SearchSharesByTemplateResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateRoleShareSearchResultTemplate;
};

export type SearchSharesByTemplateResponse =
  SearchSharesByTemplateResponses[keyof SearchSharesByTemplateResponses];

export type SearchSharesByRole3Data = {
  body: SearchQuery;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/template-role-share/role/{role_uid}/search';
};

export type SearchSharesByRole3Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSharesByRole3Error = SearchSharesByRole3Errors[keyof SearchSharesByRole3Errors];

export type SearchSharesByRole3Responses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateRoleShareSearchResultRole;
};

export type SearchSharesByRole3Response =
  SearchSharesByRole3Responses[keyof SearchSharesByRole3Responses];

export type RemoveAllFileSharesByFileData = {
  body?: never;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/file/{file_uid}';
};

export type RemoveAllFileSharesByFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllFileSharesByFileError =
  RemoveAllFileSharesByFileErrors[keyof RemoveAllFileSharesByFileErrors];

export type RemoveAllFileSharesByFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDb;
};

export type RemoveAllFileSharesByFileResponse =
  RemoveAllFileSharesByFileResponses[keyof RemoveAllFileSharesByFileResponses];

export type GetFileSharesByFileData = {
  body?: never;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/file/{file_uid}';
};

export type GetFileSharesByFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFileSharesByFileError = GetFileSharesByFileErrors[keyof GetFileSharesByFileErrors];

export type GetFileSharesByFileResponses = {
  /**
   * Response Get File Shares By File V1 App Account Permissions File Share File  File Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultFileUserShareInDb>;
};

export type GetFileSharesByFileResponse =
  GetFileSharesByFileResponses[keyof GetFileSharesByFileResponses];

export type RemoveAllFileSharesByUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/user/{user_uid}';
};

export type RemoveAllFileSharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllFileSharesByUserError =
  RemoveAllFileSharesByUserErrors[keyof RemoveAllFileSharesByUserErrors];

export type RemoveAllFileSharesByUserResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type RemoveAllFileSharesByUserResponse =
  RemoveAllFileSharesByUserResponses[keyof RemoveAllFileSharesByUserResponses];

export type GetFileSharesByUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/user/{user_uid}';
};

export type GetFileSharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFileSharesByUserError = GetFileSharesByUserErrors[keyof GetFileSharesByUserErrors];

export type GetFileSharesByUserResponses = {
  /**
   * Response Get File Shares By User V1 App Account Permissions File Share User  User Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultFileUserShareInDb>;
};

export type GetFileSharesByUserResponse =
  GetFileSharesByUserResponses[keyof GetFileSharesByUserResponses];

export type SearchFileSharesByFileData = {
  body: SearchQuery;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/file/{file_uid}/search';
};

export type SearchFileSharesByFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchFileSharesByFileError =
  SearchFileSharesByFileErrors[keyof SearchFileSharesByFileErrors];

export type SearchFileSharesByFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileUserShareSearchResultVaultFile;
};

export type SearchFileSharesByFileResponse =
  SearchFileSharesByFileResponses[keyof SearchFileSharesByFileResponses];

export type SearchFileSharesByUserData = {
  body: SearchQuery;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/user/{user_uid}/search';
};

export type SearchFileSharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchFileSharesByUserError =
  SearchFileSharesByUserErrors[keyof SearchFileSharesByUserErrors];

export type SearchFileSharesByUserResponses = {
  /**
   * Successful Response
   */
  200: VaultFileUserShareSearchResultUser;
};

export type SearchFileSharesByUserResponse =
  SearchFileSharesByUserResponses[keyof SearchFileSharesByUserResponses];

export type RemoveFileShareData = {
  body?: never;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/file/{file_uid}/user/{user_uid}';
};

export type RemoveFileShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveFileShareError = RemoveFileShareErrors[keyof RemoveFileShareErrors];

export type RemoveFileShareResponses = {
  /**
   * Successful Response
   */
  200: VaultFileUserShareInDb;
};

export type RemoveFileShareResponse = RemoveFileShareResponses[keyof RemoveFileShareResponses];

export type UpsertFileShareData = {
  body: VaultShareUpdate;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-share/file/{file_uid}/user/{user_uid}';
};

export type UpsertFileShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpsertFileShareError = UpsertFileShareErrors[keyof UpsertFileShareErrors];

export type UpsertFileShareResponses = {
  /**
   * Successful Response
   */
  200: VaultFileUserShareInDb;
};

export type UpsertFileShareResponse = UpsertFileShareResponses[keyof UpsertFileShareResponses];

export type RemoveAllFileRoleSharesByFileData = {
  body?: never;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/file/{file_uid}';
};

export type RemoveAllFileRoleSharesByFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllFileRoleSharesByFileError =
  RemoveAllFileRoleSharesByFileErrors[keyof RemoveAllFileRoleSharesByFileErrors];

export type RemoveAllFileRoleSharesByFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDb;
};

export type RemoveAllFileRoleSharesByFileResponse =
  RemoveAllFileRoleSharesByFileResponses[keyof RemoveAllFileRoleSharesByFileResponses];

export type GetFileRoleSharesByFileData = {
  body?: never;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/file/{file_uid}';
};

export type GetFileRoleSharesByFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFileRoleSharesByFileError =
  GetFileRoleSharesByFileErrors[keyof GetFileRoleSharesByFileErrors];

export type GetFileRoleSharesByFileResponses = {
  /**
   * Response Get File Role Shares By File V1 App Account Permissions File Role Share File  File Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultFileRoleShareInDb>;
};

export type GetFileRoleSharesByFileResponse =
  GetFileRoleSharesByFileResponses[keyof GetFileRoleSharesByFileResponses];

export type RemoveAllFileRoleSharesByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/role/{role_uid}';
};

export type RemoveAllFileRoleSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllFileRoleSharesByRoleError =
  RemoveAllFileRoleSharesByRoleErrors[keyof RemoveAllFileRoleSharesByRoleErrors];

export type RemoveAllFileRoleSharesByRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type RemoveAllFileRoleSharesByRoleResponse =
  RemoveAllFileRoleSharesByRoleResponses[keyof RemoveAllFileRoleSharesByRoleResponses];

export type GetFileRoleSharesByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/role/{role_uid}';
};

export type GetFileRoleSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFileRoleSharesByRoleError =
  GetFileRoleSharesByRoleErrors[keyof GetFileRoleSharesByRoleErrors];

export type GetFileRoleSharesByRoleResponses = {
  /**
   * Response Get File Role Shares By Role V1 App Account Permissions File Role Share Role  Role Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultFileRoleShareInDb>;
};

export type GetFileRoleSharesByRoleResponse =
  GetFileRoleSharesByRoleResponses[keyof GetFileRoleSharesByRoleResponses];

export type SearchFileRoleSharesByFileData = {
  body: SearchQuery;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/file/{file_uid}/search';
};

export type SearchFileRoleSharesByFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchFileRoleSharesByFileError =
  SearchFileRoleSharesByFileErrors[keyof SearchFileRoleSharesByFileErrors];

export type SearchFileRoleSharesByFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRoleShareSearchResultVaultFile;
};

export type SearchFileRoleSharesByFileResponse =
  SearchFileRoleSharesByFileResponses[keyof SearchFileRoleSharesByFileResponses];

export type SearchFileRoleSharesByRoleData = {
  body: SearchQuery;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/role/{role_uid}/search';
};

export type SearchFileRoleSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchFileRoleSharesByRoleError =
  SearchFileRoleSharesByRoleErrors[keyof SearchFileRoleSharesByRoleErrors];

export type SearchFileRoleSharesByRoleResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRoleShareSearchResultRole;
};

export type SearchFileRoleSharesByRoleResponse =
  SearchFileRoleSharesByRoleResponses[keyof SearchFileRoleSharesByRoleResponses];

export type RemoveFileRoleShareData = {
  body?: never;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/file/{file_uid}/role/{role_uid}';
};

export type RemoveFileRoleShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveFileRoleShareError = RemoveFileRoleShareErrors[keyof RemoveFileRoleShareErrors];

export type RemoveFileRoleShareResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRoleShareInDb;
};

export type RemoveFileRoleShareResponse =
  RemoveFileRoleShareResponses[keyof RemoveFileRoleShareResponses];

export type UpsertFileRoleShareData = {
  body: VaultShareUpdate;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/file-role-share/file/{file_uid}/role/{role_uid}';
};

export type UpsertFileRoleShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpsertFileRoleShareError = UpsertFileRoleShareErrors[keyof UpsertFileRoleShareErrors];

export type UpsertFileRoleShareResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRoleShareInDb;
};

export type UpsertFileRoleShareResponse =
  UpsertFileRoleShareResponses[keyof UpsertFileRoleShareResponses];

export type RemoveAllDirectorySharesByDirectoryData = {
  body?: never;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/directory/{directory_uid}';
};

export type RemoveAllDirectorySharesByDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllDirectorySharesByDirectoryError =
  RemoveAllDirectorySharesByDirectoryErrors[keyof RemoveAllDirectorySharesByDirectoryErrors];

export type RemoveAllDirectorySharesByDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDbSlim;
};

export type RemoveAllDirectorySharesByDirectoryResponse =
  RemoveAllDirectorySharesByDirectoryResponses[keyof RemoveAllDirectorySharesByDirectoryResponses];

export type GetDirectorySharesByDirectoryData = {
  body?: never;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/directory/{directory_uid}';
};

export type GetDirectorySharesByDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDirectorySharesByDirectoryError =
  GetDirectorySharesByDirectoryErrors[keyof GetDirectorySharesByDirectoryErrors];

export type GetDirectorySharesByDirectoryResponses = {
  /**
   * Response Get Directory Shares By Directory V1 App Account Permissions Directory Share Directory  Directory Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultDirectoryUserShareInDb>;
};

export type GetDirectorySharesByDirectoryResponse =
  GetDirectorySharesByDirectoryResponses[keyof GetDirectorySharesByDirectoryResponses];

export type RemoveAllDirectorySharesByUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/user/{user_uid}';
};

export type RemoveAllDirectorySharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllDirectorySharesByUserError =
  RemoveAllDirectorySharesByUserErrors[keyof RemoveAllDirectorySharesByUserErrors];

export type RemoveAllDirectorySharesByUserResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type RemoveAllDirectorySharesByUserResponse =
  RemoveAllDirectorySharesByUserResponses[keyof RemoveAllDirectorySharesByUserResponses];

export type GetDirectorySharesByUserData = {
  body?: never;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/user/{user_uid}';
};

export type GetDirectorySharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDirectorySharesByUserError =
  GetDirectorySharesByUserErrors[keyof GetDirectorySharesByUserErrors];

export type GetDirectorySharesByUserResponses = {
  /**
   * Response Get Directory Shares By User V1 App Account Permissions Directory Share User  User Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultDirectoryUserShareInDb>;
};

export type GetDirectorySharesByUserResponse =
  GetDirectorySharesByUserResponses[keyof GetDirectorySharesByUserResponses];

export type SearchDirectorySharesByDirectoryData = {
  body: SearchQuery;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/directory/{directory_uid}/search';
};

export type SearchDirectorySharesByDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchDirectorySharesByDirectoryError =
  SearchDirectorySharesByDirectoryErrors[keyof SearchDirectorySharesByDirectoryErrors];

export type SearchDirectorySharesByDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryUserShareSearchResultVaultDirectory;
};

export type SearchDirectorySharesByDirectoryResponse =
  SearchDirectorySharesByDirectoryResponses[keyof SearchDirectorySharesByDirectoryResponses];

export type SearchDirectorySharesByUserData = {
  body: SearchQuery;
  path: {
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/user/{user_uid}/search';
};

export type SearchDirectorySharesByUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchDirectorySharesByUserError =
  SearchDirectorySharesByUserErrors[keyof SearchDirectorySharesByUserErrors];

export type SearchDirectorySharesByUserResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryUserShareSearchResultUser;
};

export type SearchDirectorySharesByUserResponse =
  SearchDirectorySharesByUserResponses[keyof SearchDirectorySharesByUserResponses];

export type RemoveDirectoryShareData = {
  body?: never;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/directory/{directory_uid}/user/{user_uid}';
};

export type RemoveDirectoryShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveDirectoryShareError =
  RemoveDirectoryShareErrors[keyof RemoveDirectoryShareErrors];

export type RemoveDirectoryShareResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryUserShareInDb;
};

export type RemoveDirectoryShareResponse =
  RemoveDirectoryShareResponses[keyof RemoveDirectoryShareResponses];

export type UpsertDirectoryShareData = {
  body: VaultShareUpdate;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-share/directory/{directory_uid}/user/{user_uid}';
};

export type UpsertDirectoryShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpsertDirectoryShareError =
  UpsertDirectoryShareErrors[keyof UpsertDirectoryShareErrors];

export type UpsertDirectoryShareResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryUserShareInDb;
};

export type UpsertDirectoryShareResponse =
  UpsertDirectoryShareResponses[keyof UpsertDirectoryShareResponses];

export type RemoveAllDirectoryRoleSharesByDirectoryData = {
  body?: never;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/directory/{directory_uid}';
};

export type RemoveAllDirectoryRoleSharesByDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllDirectoryRoleSharesByDirectoryError =
  RemoveAllDirectoryRoleSharesByDirectoryErrors[keyof RemoveAllDirectoryRoleSharesByDirectoryErrors];

export type RemoveAllDirectoryRoleSharesByDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDbSlim;
};

export type RemoveAllDirectoryRoleSharesByDirectoryResponse =
  RemoveAllDirectoryRoleSharesByDirectoryResponses[keyof RemoveAllDirectoryRoleSharesByDirectoryResponses];

export type GetDirectoryRoleSharesByDirectoryData = {
  body?: never;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/directory/{directory_uid}';
};

export type GetDirectoryRoleSharesByDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDirectoryRoleSharesByDirectoryError =
  GetDirectoryRoleSharesByDirectoryErrors[keyof GetDirectoryRoleSharesByDirectoryErrors];

export type GetDirectoryRoleSharesByDirectoryResponses = {
  /**
   * Response Get Directory Role Shares By Directory V1 App Account Permissions Directory Role Share Directory  Directory Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultDirectoryRoleShareInDb>;
};

export type GetDirectoryRoleSharesByDirectoryResponse =
  GetDirectoryRoleSharesByDirectoryResponses[keyof GetDirectoryRoleSharesByDirectoryResponses];

export type RemoveAllDirectoryRoleSharesByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/role/{role_uid}';
};

export type RemoveAllDirectoryRoleSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveAllDirectoryRoleSharesByRoleError =
  RemoveAllDirectoryRoleSharesByRoleErrors[keyof RemoveAllDirectoryRoleSharesByRoleErrors];

export type RemoveAllDirectoryRoleSharesByRoleResponses = {
  /**
   * Successful Response
   */
  200: PermissionRoleInDb;
};

export type RemoveAllDirectoryRoleSharesByRoleResponse =
  RemoveAllDirectoryRoleSharesByRoleResponses[keyof RemoveAllDirectoryRoleSharesByRoleResponses];

export type GetDirectoryRoleSharesByRoleData = {
  body?: never;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/role/{role_uid}';
};

export type GetDirectoryRoleSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDirectoryRoleSharesByRoleError =
  GetDirectoryRoleSharesByRoleErrors[keyof GetDirectoryRoleSharesByRoleErrors];

export type GetDirectoryRoleSharesByRoleResponses = {
  /**
   * Response Get Directory Role Shares By Role V1 App Account Permissions Directory Role Share Role  Role Uid  Get
   *
   * Successful Response
   */
  200: Array<VaultDirectoryRoleShareInDb>;
};

export type GetDirectoryRoleSharesByRoleResponse =
  GetDirectoryRoleSharesByRoleResponses[keyof GetDirectoryRoleSharesByRoleResponses];

export type SearchDirectoryRoleSharesByDirectoryData = {
  body: SearchQuery;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/directory/{directory_uid}/search';
};

export type SearchDirectoryRoleSharesByDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchDirectoryRoleSharesByDirectoryError =
  SearchDirectoryRoleSharesByDirectoryErrors[keyof SearchDirectoryRoleSharesByDirectoryErrors];

export type SearchDirectoryRoleSharesByDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryRoleShareSearchResultVaultDirectory;
};

export type SearchDirectoryRoleSharesByDirectoryResponse =
  SearchDirectoryRoleSharesByDirectoryResponses[keyof SearchDirectoryRoleSharesByDirectoryResponses];

export type SearchDirectoryRoleSharesByRoleData = {
  body: SearchQuery;
  path: {
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/role/{role_uid}/search';
};

export type SearchDirectoryRoleSharesByRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchDirectoryRoleSharesByRoleError =
  SearchDirectoryRoleSharesByRoleErrors[keyof SearchDirectoryRoleSharesByRoleErrors];

export type SearchDirectoryRoleSharesByRoleResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryRoleShareSearchResultRole;
};

export type SearchDirectoryRoleSharesByRoleResponse =
  SearchDirectoryRoleSharesByRoleResponses[keyof SearchDirectoryRoleSharesByRoleResponses];

export type RemoveDirectoryRoleShareData = {
  body?: never;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/directory/{directory_uid}/role/{role_uid}';
};

export type RemoveDirectoryRoleShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveDirectoryRoleShareError =
  RemoveDirectoryRoleShareErrors[keyof RemoveDirectoryRoleShareErrors];

export type RemoveDirectoryRoleShareResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryRoleShareInDb;
};

export type RemoveDirectoryRoleShareResponse =
  RemoveDirectoryRoleShareResponses[keyof RemoveDirectoryRoleShareResponses];

export type UpsertDirectoryRoleShareData = {
  body: VaultShareUpdate;
  path: {
    /**
     * Directory Uid
     */
    directory_uid: string;
    /**
     * Role Uid
     */
    role_uid: string;
  };
  query?: never;
  url: '/v1/app/account/permissions/directory-role-share/directory/{directory_uid}/role/{role_uid}';
};

export type UpsertDirectoryRoleShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpsertDirectoryRoleShareError =
  UpsertDirectoryRoleShareErrors[keyof UpsertDirectoryRoleShareErrors];

export type UpsertDirectoryRoleShareResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryRoleShareInDb;
};

export type UpsertDirectoryRoleShareResponse =
  UpsertDirectoryRoleShareResponses[keyof UpsertDirectoryRoleShareResponses];

export type BulkContractShareData = {
  body: ContractBulkShareUser;
  path?: never;
  query?: never;
  url: '/v1/app/account/permissions/bulk-contract-share';
};

export type BulkContractShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkContractShareError = BulkContractShareErrors[keyof BulkContractShareErrors];

export type BulkContractShareResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type BulkContractShareResponse =
  BulkContractShareResponses[keyof BulkContractShareResponses];

export type BulkContractualPartyShareData = {
  body: ContractualPartyBulkShare;
  path?: never;
  query?: never;
  url: '/v1/app/account/permissions/bulk-contractual-party-share';
};

export type BulkContractualPartyShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkContractualPartyShareError =
  BulkContractualPartyShareErrors[keyof BulkContractualPartyShareErrors];

export type BulkContractualPartyShareResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type BulkContractualPartyShareResponse =
  BulkContractualPartyShareResponses[keyof BulkContractualPartyShareResponses];

export type BulkVaultShareData = {
  body: VaultBulkShare;
  path?: never;
  query?: never;
  url: '/v1/app/account/permissions/bulk-vault-share';
};

export type BulkVaultShareErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkVaultShareError = BulkVaultShareErrors[keyof BulkVaultShareErrors];

export type BulkVaultShareResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type BulkVaultShareResponse = BulkVaultShareResponses[keyof BulkVaultShareResponses];

export type ResolveInvitationData = {
  body?: never;
  headers?: {
    /**
     * X-Recaptcha-Token
     */
    'X-Recaptcha-Token'?: string | null;
  };
  path: {
    /**
     * Token
     */
    token: string;
  };
  query?: never;
  url: '/v1/app/account/invitations/{token}';
};

export type ResolveInvitationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResolveInvitationError = ResolveInvitationErrors[keyof ResolveInvitationErrors];

export type ResolveInvitationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInvitationPublic;
};

export type ResolveInvitationResponse =
  ResolveInvitationResponses[keyof ResolveInvitationResponses];

export type AcceptInvitationData = {
  body?: never;
  path: {
    /**
     * Token
     */
    token: string;
  };
  query?: never;
  url: '/v1/app/account/invitations/{token}/accept';
};

export type AcceptInvitationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AcceptInvitationError = AcceptInvitationErrors[keyof AcceptInvitationErrors];

export type AcceptInvitationResponses = {
  /**
   * Successful Response
   */
  200: UserInDb;
};

export type AcceptInvitationResponse = AcceptInvitationResponses[keyof AcceptInvitationResponses];

export type GetUserStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/user';
};

export type GetUserStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserStatisticsError = GetUserStatisticsErrors[keyof GetUserStatisticsErrors];

export type GetUserStatisticsResponses = {
  /**
   * Successful Response
   */
  200: UserStatistics;
};

export type GetUserStatisticsResponse =
  GetUserStatisticsResponses[keyof GetUserStatisticsResponses];

export type GetUserActivityStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
    /**
     * Min Points
     *
     * Minimum highest daily request weight for a user to count as active. Scale: 0 = logged in, 1 = chores (user/org/playbook/prompt management), 2 = looking at data, 3 = productive work (runs, contracts, chat).
     */
    min_points?: number;
  };
  url: '/v1/app/admin/statistics/user-activity';
};

export type GetUserActivityStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserActivityStatisticsError =
  GetUserActivityStatisticsErrors[keyof GetUserActivityStatisticsErrors];

export type GetUserActivityStatisticsResponses = {
  /**
   * Successful Response
   */
  200: UserActivityStatistics;
};

export type GetUserActivityStatisticsResponse =
  GetUserActivityStatisticsResponses[keyof GetUserActivityStatisticsResponses];

export type GetOrganisationStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/organisation';
};

export type GetOrganisationStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrganisationStatisticsError =
  GetOrganisationStatisticsErrors[keyof GetOrganisationStatisticsErrors];

export type GetOrganisationStatisticsResponses = {
  /**
   * Successful Response
   */
  200: OrganisationsStatistics;
};

export type GetOrganisationStatisticsResponse =
  GetOrganisationStatisticsResponses[keyof GetOrganisationStatisticsResponses];

export type GetVaultStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/vault';
};

export type GetVaultStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVaultStatisticsError = GetVaultStatisticsErrors[keyof GetVaultStatisticsErrors];

export type GetVaultStatisticsResponses = {
  /**
   * Successful Response
   */
  200: VaultStatistics;
};

export type GetVaultStatisticsResponse =
  GetVaultStatisticsResponses[keyof GetVaultStatisticsResponses];

export type GetReviewStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/review';
};

export type GetReviewStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewStatisticsError = GetReviewStatisticsErrors[keyof GetReviewStatisticsErrors];

export type GetReviewStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ReviewStatistics;
};

export type GetReviewStatisticsResponse =
  GetReviewStatisticsResponses[keyof GetReviewStatisticsResponses];

export type GetContractStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/contracts';
};

export type GetContractStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractStatisticsError =
  GetContractStatisticsErrors[keyof GetContractStatisticsErrors];

export type GetContractStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ContractStatistics;
};

export type GetContractStatisticsResponse =
  GetContractStatisticsResponses[keyof GetContractStatisticsResponses];

export type GetDraftingStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/drafting';
};

export type GetDraftingStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDraftingStatisticsError =
  GetDraftingStatisticsErrors[keyof GetDraftingStatisticsErrors];

export type GetDraftingStatisticsResponses = {
  /**
   * Successful Response
   */
  200: DraftingStatistics;
};

export type GetDraftingStatisticsResponse =
  GetDraftingStatisticsResponses[keyof GetDraftingStatisticsResponses];

export type GetPlaybookStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/playbooks';
};

export type GetPlaybookStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPlaybookStatisticsError =
  GetPlaybookStatisticsErrors[keyof GetPlaybookStatisticsErrors];

export type GetPlaybookStatisticsResponses = {
  /**
   * Successful Response
   */
  200: PlaybookStatistics;
};

export type GetPlaybookStatisticsResponse =
  GetPlaybookStatisticsResponses[keyof GetPlaybookStatisticsResponses];

export type GetChatStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/chat';
};

export type GetChatStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetChatStatisticsError = GetChatStatisticsErrors[keyof GetChatStatisticsErrors];

export type GetChatStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ChatStatistics;
};

export type GetChatStatisticsResponse =
  GetChatStatisticsResponses[keyof GetChatStatisticsResponses];

export type GetSigningStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Provider
     *
     * Optional signing provider filter.
     */
    provider?: SigningProvider | null;
    /**
     * Statuses
     *
     * Optional list of signing request statuses to include.
     */
    statuses?: Array<SigningRequestStatus> | null;
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include in statistics.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude from statistics.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
    /**
     * Latest Limit
     *
     * Number of latest signing requests to return.
     */
    latest_limit?: number;
  };
  url: '/v1/app/admin/statistics/signing';
};

export type GetSigningStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSigningStatisticsError =
  GetSigningStatisticsErrors[keyof GetSigningStatisticsErrors];

export type GetSigningStatisticsResponses = {
  /**
   * Successful Response
   */
  200: SigningStatistics;
};

export type GetSigningStatisticsResponse =
  GetSigningStatisticsResponses[keyof GetSigningStatisticsResponses];

export type GetLlmUsageStatisticsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Included Org Uids
     *
     * Optional list of organisation UIDs to include.
     */
    included_org_uids?: Array<string> | null;
    /**
     * Excluded Org Uids
     *
     * Optional list of organisation UIDs to exclude.
     */
    excluded_org_uids?: Array<string> | null;
    /**
     * Included User Uids
     *
     * Optional list of user UIDs to include.
     */
    included_user_uids?: Array<string> | null;
    /**
     * Excluded User Uids
     *
     * Optional list of user UIDs to exclude.
     */
    excluded_user_uids?: Array<string> | null;
    /**
     * Platform
     *
     * Filter to one platform.
     */
    platform?: UsagePlatform | null;
    /**
     * Provider
     *
     * Filter to one LLM provider.
     */
    provider?: LlmProvider | null;
    /**
     * Model
     *
     * Filter to one model name.
     */
    model?: string | null;
    /**
     * Start Date
     *
     * Optional inclusive start date (UTC) for statistics filtering.
     */
    start_date?: string | null;
    /**
     * End Date
     *
     * Optional inclusive end date (UTC) for statistics filtering.
     */
    end_date?: string | null;
  };
  url: '/v1/app/admin/statistics/llm-usage';
};

export type GetLlmUsageStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetLlmUsageStatisticsError =
  GetLlmUsageStatisticsErrors[keyof GetLlmUsageStatisticsErrors];

export type GetLlmUsageStatisticsResponses = {
  /**
   * Successful Response
   */
  200: UsageStatistics;
};

export type GetLlmUsageStatisticsResponse =
  GetLlmUsageStatisticsResponses[keyof GetLlmUsageStatisticsResponses];

export type ListModelPricingData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/admin/model-pricing';
};

export type ListModelPricingResponses = {
  /**
   * Response List Model Pricing V1 App Admin Model Pricing Get
   *
   * Successful Response
   */
  200: Array<ModelPricingInDb>;
};

export type ListModelPricingResponse = ListModelPricingResponses[keyof ListModelPricingResponses];

export type CreateModelPricingData = {
  body: ModelPricingCreate;
  path?: never;
  query?: never;
  url: '/v1/app/admin/model-pricing';
};

export type CreateModelPricingErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateModelPricingError = CreateModelPricingErrors[keyof CreateModelPricingErrors];

export type CreateModelPricingResponses = {
  /**
   * Successful Response
   */
  201: ModelPricingInDb;
};

export type CreateModelPricingResponse =
  CreateModelPricingResponses[keyof CreateModelPricingResponses];

export type DeleteModelPricingData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/admin/model-pricing/{uid}';
};

export type DeleteModelPricingErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteModelPricingError = DeleteModelPricingErrors[keyof DeleteModelPricingErrors];

export type DeleteModelPricingResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeleteModelPricingResponse =
  DeleteModelPricingResponses[keyof DeleteModelPricingResponses];

export type UpdateModelPricingData = {
  body: ModelPricingUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/admin/model-pricing/{uid}';
};

export type UpdateModelPricingErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateModelPricingError = UpdateModelPricingErrors[keyof UpdateModelPricingErrors];

export type UpdateModelPricingResponses = {
  /**
   * Successful Response
   */
  200: ModelPricingInDb;
};

export type UpdateModelPricingResponse =
  UpdateModelPricingResponses[keyof UpdateModelPricingResponses];

export type StartDemoDataRemovalData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Organisation Uid
     */
    organisation_uid: string;
  };
  url: '/v1/app/admin/demo-data';
};

export type StartDemoDataRemovalErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StartDemoDataRemovalError =
  StartDemoDataRemovalErrors[keyof StartDemoDataRemovalErrors];

export type StartDemoDataRemovalResponses = {
  /**
   * Successful Response
   */
  200: DemoDataSeedStartResponse;
};

export type StartDemoDataRemovalResponse =
  StartDemoDataRemovalResponses[keyof StartDemoDataRemovalResponses];

export type GetDemoDataStatusData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Organisation Uid
     */
    organisation_uid: string;
  };
  url: '/v1/app/admin/demo-data';
};

export type GetDemoDataStatusErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDemoDataStatusError = GetDemoDataStatusErrors[keyof GetDemoDataStatusErrors];

export type GetDemoDataStatusResponses = {
  /**
   * Successful Response
   */
  200: DemoDataStatusResponse;
};

export type GetDemoDataStatusResponse =
  GetDemoDataStatusResponses[keyof GetDemoDataStatusResponses];

export type StartDemoDataSeedingData = {
  body: DemoDataSeedRequest;
  path?: never;
  query: {
    /**
     * Organisation Uid
     */
    organisation_uid: string;
  };
  url: '/v1/app/admin/demo-data';
};

export type StartDemoDataSeedingErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StartDemoDataSeedingError =
  StartDemoDataSeedingErrors[keyof StartDemoDataSeedingErrors];

export type StartDemoDataSeedingResponses = {
  /**
   * Successful Response
   */
  200: DemoDataSeedStartResponse;
};

export type StartDemoDataSeedingResponse =
  StartDemoDataSeedingResponses[keyof StartDemoDataSeedingResponses];

export type AdminSwitchOrganisationData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Organisation Uid
     */
    organisation_uid: string;
  };
  url: '/v1/app/admin/switch-org';
};

export type AdminSwitchOrganisationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AdminSwitchOrganisationError =
  AdminSwitchOrganisationErrors[keyof AdminSwitchOrganisationErrors];

export type AdminSwitchOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type AdminSwitchOrganisationResponse =
  AdminSwitchOrganisationResponses[keyof AdminSwitchOrganisationResponses];

export type SwitchToOrganisationData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/admin/switch-to-admin-org';
};

export type SwitchToOrganisationResponses = {
  /**
   * Successful Response
   */
  200: OrganisationInDb;
};

export type SwitchToOrganisationResponse =
  SwitchToOrganisationResponses[keyof SwitchToOrganisationResponses];

export type ListChangelogData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Since
     */
    since?: string | null;
    /**
     * Limit
     */
    limit?: number;
    /**
     * Offset
     */
    offset?: number;
  };
  url: '/v1/app/changelog';
};

export type ListChangelogErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListChangelogError = ListChangelogErrors[keyof ListChangelogErrors];

export type ListChangelogResponses = {
  /**
   * Successful Response
   */
  200: ChangelogListResult;
};

export type ListChangelogResponse = ListChangelogResponses[keyof ListChangelogResponses];

export type CreateChangelogData = {
  body: ChangelogCreate;
  path?: never;
  query?: never;
  url: '/v1/app/changelog';
};

export type CreateChangelogErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateChangelogError = CreateChangelogErrors[keyof CreateChangelogErrors];

export type CreateChangelogResponses = {
  /**
   * Successful Response
   */
  201: ChangelogInDb;
};

export type CreateChangelogResponse = CreateChangelogResponses[keyof CreateChangelogResponses];

export type DeleteChangelogData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/changelog/{uid}';
};

export type DeleteChangelogErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteChangelogError = DeleteChangelogErrors[keyof DeleteChangelogErrors];

export type DeleteChangelogResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeleteChangelogResponse = DeleteChangelogResponses[keyof DeleteChangelogResponses];

export type UpdateChangelogData = {
  body: ChangelogUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/changelog/{uid}';
};

export type UpdateChangelogErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateChangelogError = UpdateChangelogErrors[keyof UpdateChangelogErrors];

export type UpdateChangelogResponses = {
  /**
   * Successful Response
   */
  200: ChangelogInDb;
};

export type UpdateChangelogResponse = UpdateChangelogResponses[keyof UpdateChangelogResponses];

export type CreateTemplateData = {
  body: ContractGenerationTemplateCreateRequest;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/template/new';
};

export type CreateTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTemplateError = CreateTemplateErrors[keyof CreateTemplateErrors];

export type CreateTemplateResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateInDb;
};

export type CreateTemplateResponse = CreateTemplateResponses[keyof CreateTemplateResponses];

export type DeleteTemplateData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{uid}';
};

export type DeleteTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTemplateError = DeleteTemplateErrors[keyof DeleteTemplateErrors];

export type DeleteTemplateResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateInDb;
};

export type DeleteTemplateResponse = DeleteTemplateResponses[keyof DeleteTemplateResponses];

export type GetTemplateData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{uid}';
};

export type GetTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateError = GetTemplateErrors[keyof GetTemplateErrors];

export type GetTemplateResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateInDb;
};

export type GetTemplateResponse = GetTemplateResponses[keyof GetTemplateResponses];

export type UpdateTemplateData = {
  body: ContractGenerationTemplateUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{uid}';
};

export type UpdateTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTemplateError = UpdateTemplateErrors[keyof UpdateTemplateErrors];

export type UpdateTemplateResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateInDb;
};

export type UpdateTemplateResponse = UpdateTemplateResponses[keyof UpdateTemplateResponses];

export type SearchTemplatesData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/template/search';
};

export type SearchTemplatesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchTemplatesError = SearchTemplatesErrors[keyof SearchTemplatesErrors];

export type SearchTemplatesResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateSearchResult;
};

export type SearchTemplatesResponse = SearchTemplatesResponses[keyof SearchTemplatesResponses];

export type CreateTemplateVariantData = {
  body: ContractGenerationTemplateVariantCreateRequest;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/variant';
};

export type CreateTemplateVariantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTemplateVariantError =
  CreateTemplateVariantErrors[keyof CreateTemplateVariantErrors];

export type CreateTemplateVariantResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateVariantInDb;
};

export type CreateTemplateVariantResponse =
  CreateTemplateVariantResponses[keyof CreateTemplateVariantResponses];

export type ReplaceTemplateVariantFileData = {
  body: BodyReplaceTemplateVariantFileV1AppContractGenerationTemplateTemplateUidVariantVariantUidFilePut;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/variant/{variant_uid}/file';
};

export type ReplaceTemplateVariantFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReplaceTemplateVariantFileError =
  ReplaceTemplateVariantFileErrors[keyof ReplaceTemplateVariantFileErrors];

export type ReplaceTemplateVariantFileResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateVariantInDb;
};

export type ReplaceTemplateVariantFileResponse =
  ReplaceTemplateVariantFileResponses[keyof ReplaceTemplateVariantFileResponses];

export type DeleteTemplateVariantData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/variant/{variant_uid}';
};

export type DeleteTemplateVariantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTemplateVariantError =
  DeleteTemplateVariantErrors[keyof DeleteTemplateVariantErrors];

export type DeleteTemplateVariantResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateVariantInDb;
};

export type DeleteTemplateVariantResponse =
  DeleteTemplateVariantResponses[keyof DeleteTemplateVariantResponses];

export type GetTemplateVariantData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/variant/{variant_uid}';
};

export type GetTemplateVariantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateVariantError = GetTemplateVariantErrors[keyof GetTemplateVariantErrors];

export type GetTemplateVariantResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateVariantInDb;
};

export type GetTemplateVariantResponse =
  GetTemplateVariantResponses[keyof GetTemplateVariantResponses];

export type UpdateTemplateVariantData = {
  body: ContractGenerationTemplateVariantUpdate;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/variant/{variant_uid}';
};

export type UpdateTemplateVariantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTemplateVariantError =
  UpdateTemplateVariantErrors[keyof UpdateTemplateVariantErrors];

export type UpdateTemplateVariantResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateVariantInDb;
};

export type UpdateTemplateVariantResponse =
  UpdateTemplateVariantResponses[keyof UpdateTemplateVariantResponses];

export type ListTemplateVariantsData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/variants';
};

export type ListTemplateVariantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTemplateVariantsError =
  ListTemplateVariantsErrors[keyof ListTemplateVariantsErrors];

export type ListTemplateVariantsResponses = {
  /**
   * Response List Template Variants V1 App Contract Generation Template  Template Uid  Variants Get
   *
   * Successful Response
   */
  200: Array<ContractGenerationTemplateVariantSummary>;
};

export type ListTemplateVariantsResponse =
  ListTemplateVariantsResponses[keyof ListTemplateVariantsResponses];

export type ListTemplateQuestionsData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/questions';
};

export type ListTemplateQuestionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTemplateQuestionsError =
  ListTemplateQuestionsErrors[keyof ListTemplateQuestionsErrors];

export type ListTemplateQuestionsResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateQuestionsResponse;
};

export type ListTemplateQuestionsResponse =
  ListTemplateQuestionsResponses[keyof ListTemplateQuestionsResponses];

export type CreateTemplateQuestionData = {
  body: ContractGenerationTemplateQuestionCreateRequest;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/questions';
};

export type CreateTemplateQuestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTemplateQuestionError =
  CreateTemplateQuestionErrors[keyof CreateTemplateQuestionErrors];

export type CreateTemplateQuestionResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateQuestionsResponse;
};

export type CreateTemplateQuestionResponse =
  CreateTemplateQuestionResponses[keyof CreateTemplateQuestionResponses];

export type DeleteTemplateQuestionData = {
  body?: never;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Question Uid
     */
    question_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/question/{question_uid}';
};

export type DeleteTemplateQuestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTemplateQuestionError =
  DeleteTemplateQuestionErrors[keyof DeleteTemplateQuestionErrors];

export type DeleteTemplateQuestionResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateQuestionsResponse;
};

export type DeleteTemplateQuestionResponse =
  DeleteTemplateQuestionResponses[keyof DeleteTemplateQuestionResponses];

export type UpdateTemplateQuestionData = {
  body: ContractGenerationTemplateQuestionUpdateRequest;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Question Uid
     */
    question_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/question/{question_uid}';
};

export type UpdateTemplateQuestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTemplateQuestionError =
  UpdateTemplateQuestionErrors[keyof UpdateTemplateQuestionErrors];

export type UpdateTemplateQuestionResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateQuestionsResponse;
};

export type UpdateTemplateQuestionResponse =
  UpdateTemplateQuestionResponses[keyof UpdateTemplateQuestionResponses];

export type SetVariantAnswersData = {
  body: ContractGenerationVariantAnswersUpdateRequest;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/variant/{variant_uid}/answers';
};

export type SetVariantAnswersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetVariantAnswersError = SetVariantAnswersErrors[keyof SetVariantAnswersErrors];

export type SetVariantAnswersResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateQuestionsResponse;
};

export type SetVariantAnswersResponse =
  SetVariantAnswersResponses[keyof SetVariantAnswersResponses];

export type MatchTemplateVariantsData = {
  body: ContractGenerationTemplateMatchRequest;
  path: {
    /**
     * Template Uid
     */
    template_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template/{template_uid}/match';
};

export type MatchTemplateVariantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MatchTemplateVariantsError =
  MatchTemplateVariantsErrors[keyof MatchTemplateVariantsErrors];

export type MatchTemplateVariantsResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateMatchResponse;
};

export type MatchTemplateVariantsResponse =
  MatchTemplateVariantsResponses[keyof MatchTemplateVariantsResponses];

export type RenderTemplatePreviewData = {
  body: ContractGenerationRenderRequest;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/render/template-preview';
};

export type RenderTemplatePreviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RenderTemplatePreviewError =
  RenderTemplatePreviewErrors[keyof RenderTemplatePreviewErrors];

export type RenderTemplatePreviewResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateDraftData = {
  body: ContractGenerationDraftCreateRequest;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/draft/new';
};

export type CreateDraftErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateDraftError = CreateDraftErrors[keyof CreateDraftErrors];

export type CreateDraftResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationDraftInDb;
};

export type CreateDraftResponse = CreateDraftResponses[keyof CreateDraftResponses];

export type GetDraftData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/draft/{uid}';
};

export type GetDraftErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDraftError = GetDraftErrors[keyof GetDraftErrors];

export type GetDraftResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationDraftInDb;
};

export type GetDraftResponse = GetDraftResponses[keyof GetDraftResponses];

export type UpdateDraftData = {
  body: ContractGenerationDraftUpdateRequest;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/draft/{uid}';
};

export type UpdateDraftErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateDraftError = UpdateDraftErrors[keyof UpdateDraftErrors];

export type UpdateDraftResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationDraftInDb;
};

export type UpdateDraftResponse = UpdateDraftResponses[keyof UpdateDraftResponses];

export type SearchDraftsData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/draft/search';
};

export type SearchDraftsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchDraftsError = SearchDraftsErrors[keyof SearchDraftsErrors];

export type SearchDraftsResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationDraftSearchResult;
};

export type SearchDraftsResponse = SearchDraftsResponses[keyof SearchDraftsResponses];

export type DuplicateDraftData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/draft/{uid}/duplicate';
};

export type DuplicateDraftErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DuplicateDraftError = DuplicateDraftErrors[keyof DuplicateDraftErrors];

export type DuplicateDraftResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationDraftInDb;
};

export type DuplicateDraftResponse = DuplicateDraftResponses[keyof DuplicateDraftResponses];

export type FinalizeDraftData = {
  /**
   * Request Data
   */
  body?: ContractGenerationFinalizeRequest | null;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/draft/{uid}/finalize';
};

export type FinalizeDraftErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type FinalizeDraftError = FinalizeDraftErrors[keyof FinalizeDraftErrors];

export type FinalizeDraftResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationFinalizeResponse;
};

export type FinalizeDraftResponse = FinalizeDraftResponses[keyof FinalizeDraftResponses];

export type GetFieldCatalogData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/field-catalog';
};

export type GetFieldCatalogResponses = {
  /**
   * Successful Response
   */
  200: FieldCatalog;
};

export type GetFieldCatalogResponse = GetFieldCatalogResponses[keyof GetFieldCatalogResponses];

export type RenderTagData = {
  body: RenderTagRequest;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/render-tag';
};

export type RenderTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RenderTagError = RenderTagErrors[keyof RenderTagErrors];

export type RenderTagResponses = {
  /**
   * Successful Response
   */
  200: RenderTagResponse;
};

export type RenderTagResponse2 = RenderTagResponses[keyof RenderTagResponses];

export type ValidateTemplateFileData = {
  body: BodyValidateTemplateFileV1AppContractGenerationTemplateAuthoringValidatePost;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/validate';
};

export type ValidateTemplateFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ValidateTemplateFileError =
  ValidateTemplateFileErrors[keyof ValidateTemplateFileErrors];

export type ValidateTemplateFileResponses = {
  /**
   * Successful Response
   */
  200: ExtractionResult;
};

export type ValidateTemplateFileResponse =
  ValidateTemplateFileResponses[keyof ValidateTemplateFileResponses];

export type PreviewWithSampleDataData = {
  body: BodyPreviewWithSampleDataV1AppContractGenerationTemplateAuthoringPreviewSamplePost;
  path?: never;
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/preview-sample';
};

export type PreviewWithSampleDataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PreviewWithSampleDataError =
  PreviewWithSampleDataErrors[keyof PreviewWithSampleDataErrors];

export type PreviewWithSampleDataResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetPreviewHtmlData = {
  body?: never;
  path: {
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/{variant_uid}/preview-html';
};

export type GetPreviewHtmlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPreviewHtmlError = GetPreviewHtmlErrors[keyof GetPreviewHtmlErrors];

export type GetPreviewHtmlResponses = {
  /**
   * Successful Response
   */
  200: string;
};

export type GetPreviewHtmlResponse = GetPreviewHtmlResponses[keyof GetPreviewHtmlResponses];

export type GetAnnotationsData = {
  body?: never;
  path: {
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/{variant_uid}/annotations';
};

export type GetAnnotationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAnnotationsError = GetAnnotationsErrors[keyof GetAnnotationsErrors];

export type GetAnnotationsResponses = {
  /**
   * Successful Response
   */
  200: AnnotationsResponse;
};

export type GetAnnotationsResponse = GetAnnotationsResponses[keyof GetAnnotationsResponses];

export type UpdateAnnotationsData = {
  body: AnnotationsUpdateRequest;
  path: {
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/{variant_uid}/annotations';
};

export type UpdateAnnotationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateAnnotationsError = UpdateAnnotationsErrors[keyof UpdateAnnotationsErrors];

export type UpdateAnnotationsResponses = {
  /**
   * Successful Response
   */
  200: AnnotationsResponse;
};

export type UpdateAnnotationsResponse =
  UpdateAnnotationsResponses[keyof UpdateAnnotationsResponses];

export type DownloadMaterializedData = {
  body?: never;
  path: {
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/{variant_uid}/materialize/download';
};

export type DownloadMaterializedErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DownloadMaterializedError =
  DownloadMaterializedErrors[keyof DownloadMaterializedErrors];

export type DownloadMaterializedResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetTemplateFieldsData = {
  body?: never;
  path: {
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/{variant_uid}/fields';
};

export type GetTemplateFieldsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateFieldsError = GetTemplateFieldsErrors[keyof GetTemplateFieldsErrors];

export type GetTemplateFieldsResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateFieldsResponse;
};

export type GetTemplateFieldsResponse =
  GetTemplateFieldsResponses[keyof GetTemplateFieldsResponses];

export type UpdateTemplateFieldsData = {
  body: ContractGenerationTemplateFieldsUpdateRequest;
  path: {
    /**
     * Variant Uid
     */
    variant_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/{variant_uid}/fields';
};

export type UpdateTemplateFieldsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTemplateFieldsError =
  UpdateTemplateFieldsErrors[keyof UpdateTemplateFieldsErrors];

export type UpdateTemplateFieldsResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateFieldsResponse;
};

export type UpdateTemplateFieldsResponse =
  UpdateTemplateFieldsResponses[keyof UpdateTemplateFieldsResponses];

export type DeleteTemplateFieldData = {
  body?: never;
  path: {
    /**
     * Variant Uid
     */
    variant_uid: string;
    /**
     * Path
     */
    path: string;
  };
  query?: never;
  url: '/v1/app/contract-generation/template-authoring/{variant_uid}/fields/{path}';
};

export type DeleteTemplateFieldErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTemplateFieldError = DeleteTemplateFieldErrors[keyof DeleteTemplateFieldErrors];

export type DeleteTemplateFieldResponses = {
  /**
   * Successful Response
   */
  200: ContractGenerationTemplateFieldsResponse;
};

export type DeleteTemplateFieldResponse =
  DeleteTemplateFieldResponses[keyof DeleteTemplateFieldResponses];

export type CreateContractManagementCalendarData = {
  body: ContractManagementCalendarCreate;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/calendar/new';
};

export type CreateContractManagementCalendarErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementCalendarError =
  CreateContractManagementCalendarErrors[keyof CreateContractManagementCalendarErrors];

export type CreateContractManagementCalendarResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarInDb;
};

export type CreateContractManagementCalendarResponse =
  CreateContractManagementCalendarResponses[keyof CreateContractManagementCalendarResponses];

export type DeleteContractManagementCalendarData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}';
};

export type DeleteContractManagementCalendarErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementCalendarError =
  DeleteContractManagementCalendarErrors[keyof DeleteContractManagementCalendarErrors];

export type DeleteContractManagementCalendarResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarInDb;
};

export type DeleteContractManagementCalendarResponse =
  DeleteContractManagementCalendarResponses[keyof DeleteContractManagementCalendarResponses];

export type GetContractManagementCalendarData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}';
};

export type GetContractManagementCalendarErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementCalendarError =
  GetContractManagementCalendarErrors[keyof GetContractManagementCalendarErrors];

export type GetContractManagementCalendarResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarInDb;
};

export type GetContractManagementCalendarResponse =
  GetContractManagementCalendarResponses[keyof GetContractManagementCalendarResponses];

export type UpdateContractManagementCalendarData = {
  body: ContractManagementCalendarUpdate;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}';
};

export type UpdateContractManagementCalendarErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementCalendarError =
  UpdateContractManagementCalendarErrors[keyof UpdateContractManagementCalendarErrors];

export type UpdateContractManagementCalendarResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarInDb;
};

export type UpdateContractManagementCalendarResponse =
  UpdateContractManagementCalendarResponses[keyof UpdateContractManagementCalendarResponses];

export type SearchContractManagementCalendarsData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/calendar/search';
};

export type SearchContractManagementCalendarsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementCalendarsError =
  SearchContractManagementCalendarsErrors[keyof SearchContractManagementCalendarsErrors];

export type SearchContractManagementCalendarsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarSearchResult;
};

export type SearchContractManagementCalendarsResponse =
  SearchContractManagementCalendarsResponses[keyof SearchContractManagementCalendarsResponses];

export type PreviewContractManagementCalendarData = {
  body: CalendarPreviewQuery;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/preview';
};

export type PreviewContractManagementCalendarErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PreviewContractManagementCalendarError =
  PreviewContractManagementCalendarErrors[keyof PreviewContractManagementCalendarErrors];

export type PreviewContractManagementCalendarResponses = {
  /**
   * Successful Response
   */
  200: CalendarPreviewResult;
};

export type PreviewContractManagementCalendarResponse =
  PreviewContractManagementCalendarResponses[keyof PreviewContractManagementCalendarResponses];

export type PreviewContractManagementCalendarRangeData = {
  body: CalendarRangeQuery;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/preview/range';
};

export type PreviewContractManagementCalendarRangeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PreviewContractManagementCalendarRangeError =
  PreviewContractManagementCalendarRangeErrors[keyof PreviewContractManagementCalendarRangeErrors];

export type PreviewContractManagementCalendarRangeResponses = {
  /**
   * Successful Response
   */
  200: CalendarPreviewResult;
};

export type PreviewContractManagementCalendarRangeResponse =
  PreviewContractManagementCalendarRangeResponses[keyof PreviewContractManagementCalendarRangeResponses];

export type GetContractManagementCalendarExportUrlData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/sync/url';
};

export type GetContractManagementCalendarExportUrlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementCalendarExportUrlError =
  GetContractManagementCalendarExportUrlErrors[keyof GetContractManagementCalendarExportUrlErrors];

export type GetContractManagementCalendarExportUrlResponses = {
  /**
   * Response Get Contract Management Calendar Export Url V1 App Contract Management Calendar  Calendar Uid  Sync Url Get
   *
   * Successful Response
   */
  200: string;
};

export type GetContractManagementCalendarExportUrlResponse =
  GetContractManagementCalendarExportUrlResponses[keyof GetContractManagementCalendarExportUrlResponses];

export type RegenerateContractManagementCalendarExportUrlData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/sync/url/regenerate';
};

export type RegenerateContractManagementCalendarExportUrlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RegenerateContractManagementCalendarExportUrlError =
  RegenerateContractManagementCalendarExportUrlErrors[keyof RegenerateContractManagementCalendarExportUrlErrors];

export type RegenerateContractManagementCalendarExportUrlResponses = {
  /**
   * Response Regenerate Contract Management Calendar Export Url V1 App Contract Management Calendar  Calendar Uid  Sync Url Regenerate Post
   *
   * Successful Response
   */
  200: string;
};

export type RegenerateContractManagementCalendarExportUrlResponse =
  RegenerateContractManagementCalendarExportUrlResponses[keyof RegenerateContractManagementCalendarExportUrlResponses];

export type AddCalendarReceiversData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/receivers/add';
};

export type AddCalendarReceiversErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddCalendarReceiversError =
  AddCalendarReceiversErrors[keyof AddCalendarReceiversErrors];

export type AddCalendarReceiversResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type AddCalendarReceiversResponse =
  AddCalendarReceiversResponses[keyof AddCalendarReceiversResponses];

export type RemoveCalendarReceiversData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/receivers/remove';
};

export type RemoveCalendarReceiversErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveCalendarReceiversError =
  RemoveCalendarReceiversErrors[keyof RemoveCalendarReceiversErrors];

export type RemoveCalendarReceiversResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type RemoveCalendarReceiversResponse =
  RemoveCalendarReceiversResponses[keyof RemoveCalendarReceiversResponses];

export type ListCalendarReceiversData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/receivers';
};

export type ListCalendarReceiversErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListCalendarReceiversError =
  ListCalendarReceiversErrors[keyof ListCalendarReceiversErrors];

export type ListCalendarReceiversResponses = {
  /**
   * Response List Calendar Receivers V1 App Contract Management Calendar  Calendar Uid  Receivers Get
   *
   * Successful Response
   */
  200: Array<UserInDb>;
};

export type ListCalendarReceiversResponse =
  ListCalendarReceiversResponses[keyof ListCalendarReceiversResponses];

export type AddCalendarPermissionUsersData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/permission-users/add';
};

export type AddCalendarPermissionUsersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddCalendarPermissionUsersError =
  AddCalendarPermissionUsersErrors[keyof AddCalendarPermissionUsersErrors];

export type AddCalendarPermissionUsersResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type AddCalendarPermissionUsersResponse =
  AddCalendarPermissionUsersResponses[keyof AddCalendarPermissionUsersResponses];

export type RemoveCalendarPermissionUsersData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/permission-users/remove';
};

export type RemoveCalendarPermissionUsersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveCalendarPermissionUsersError =
  RemoveCalendarPermissionUsersErrors[keyof RemoveCalendarPermissionUsersErrors];

export type RemoveCalendarPermissionUsersResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type RemoveCalendarPermissionUsersResponse =
  RemoveCalendarPermissionUsersResponses[keyof RemoveCalendarPermissionUsersResponses];

export type ListCalendarPermissionUsersData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/permission-users';
};

export type ListCalendarPermissionUsersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListCalendarPermissionUsersError =
  ListCalendarPermissionUsersErrors[keyof ListCalendarPermissionUsersErrors];

export type ListCalendarPermissionUsersResponses = {
  /**
   * Response List Calendar Permission Users V1 App Contract Management Calendar  Calendar Uid  Permission Users Get
   *
   * Successful Response
   */
  200: Array<UserInDb>;
};

export type ListCalendarPermissionUsersResponse =
  ListCalendarPermissionUsersResponses[keyof ListCalendarPermissionUsersResponses];

export type AddCalendarPermissionRolesData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/permission-roles/add';
};

export type AddCalendarPermissionRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddCalendarPermissionRolesError =
  AddCalendarPermissionRolesErrors[keyof AddCalendarPermissionRolesErrors];

export type AddCalendarPermissionRolesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type AddCalendarPermissionRolesResponse =
  AddCalendarPermissionRolesResponses[keyof AddCalendarPermissionRolesResponses];

export type RemoveCalendarPermissionRolesData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/permission-roles/remove';
};

export type RemoveCalendarPermissionRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveCalendarPermissionRolesError =
  RemoveCalendarPermissionRolesErrors[keyof RemoveCalendarPermissionRolesErrors];

export type RemoveCalendarPermissionRolesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type RemoveCalendarPermissionRolesResponse =
  RemoveCalendarPermissionRolesResponses[keyof RemoveCalendarPermissionRolesResponses];

export type ListCalendarPermissionRolesData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/permission-roles';
};

export type ListCalendarPermissionRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListCalendarPermissionRolesError =
  ListCalendarPermissionRolesErrors[keyof ListCalendarPermissionRolesErrors];

export type ListCalendarPermissionRolesResponses = {
  /**
   * Response List Calendar Permission Roles V1 App Contract Management Calendar  Calendar Uid  Permission Roles Get
   *
   * Successful Response
   */
  200: Array<PermissionRoleInDb>;
};

export type ListCalendarPermissionRolesResponse =
  ListCalendarPermissionRolesResponses[keyof ListCalendarPermissionRolesResponses];

export type SearchCalendarContractsData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/contracts';
};

export type SearchCalendarContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchCalendarContractsError =
  SearchCalendarContractsErrors[keyof SearchCalendarContractsErrors];

export type SearchCalendarContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractSearchResult;
};

export type SearchCalendarContractsResponse =
  SearchCalendarContractsResponses[keyof SearchCalendarContractsResponses];

export type AddCalendarSourceDirectoriesData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/source-directories/add';
};

export type AddCalendarSourceDirectoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddCalendarSourceDirectoriesError =
  AddCalendarSourceDirectoriesErrors[keyof AddCalendarSourceDirectoriesErrors];

export type AddCalendarSourceDirectoriesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type AddCalendarSourceDirectoriesResponse =
  AddCalendarSourceDirectoriesResponses[keyof AddCalendarSourceDirectoriesResponses];

export type RemoveCalendarSourceDirectoriesData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/source-directories/remove';
};

export type RemoveCalendarSourceDirectoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveCalendarSourceDirectoriesError =
  RemoveCalendarSourceDirectoriesErrors[keyof RemoveCalendarSourceDirectoriesErrors];

export type RemoveCalendarSourceDirectoriesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type RemoveCalendarSourceDirectoriesResponse =
  RemoveCalendarSourceDirectoriesResponses[keyof RemoveCalendarSourceDirectoriesResponses];

export type ListCalendarSourceDirectoriesData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/source-directories';
};

export type ListCalendarSourceDirectoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListCalendarSourceDirectoriesError =
  ListCalendarSourceDirectoriesErrors[keyof ListCalendarSourceDirectoriesErrors];

export type ListCalendarSourceDirectoriesResponses = {
  /**
   * Response List Calendar Source Directories V1 App Contract Management Calendar  Calendar Uid  Source Directories Get
   *
   * Successful Response
   */
  200: Array<VaultDirectoryInDbSlim>;
};

export type ListCalendarSourceDirectoriesResponse =
  ListCalendarSourceDirectoriesResponses[keyof ListCalendarSourceDirectoriesResponses];

export type AddCalendarSourceContractsData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/source-contracts/add';
};

export type AddCalendarSourceContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddCalendarSourceContractsError =
  AddCalendarSourceContractsErrors[keyof AddCalendarSourceContractsErrors];

export type AddCalendarSourceContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type AddCalendarSourceContractsResponse =
  AddCalendarSourceContractsResponses[keyof AddCalendarSourceContractsResponses];

export type RemoveCalendarSourceContractsData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/source-contracts/remove';
};

export type RemoveCalendarSourceContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveCalendarSourceContractsError =
  RemoveCalendarSourceContractsErrors[keyof RemoveCalendarSourceContractsErrors];

export type RemoveCalendarSourceContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type RemoveCalendarSourceContractsResponse =
  RemoveCalendarSourceContractsResponses[keyof RemoveCalendarSourceContractsResponses];

export type ListCalendarSourceContractsData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/source-contracts';
};

export type ListCalendarSourceContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListCalendarSourceContractsError =
  ListCalendarSourceContractsErrors[keyof ListCalendarSourceContractsErrors];

export type ListCalendarSourceContractsResponses = {
  /**
   * Response List Calendar Source Contracts V1 App Contract Management Calendar  Calendar Uid  Source Contracts Get
   *
   * Successful Response
   */
  200: Array<ContractManagementContractInDbSlim>;
};

export type ListCalendarSourceContractsResponse =
  ListCalendarSourceContractsResponses[keyof ListCalendarSourceContractsResponses];

export type GetContractManagementCalendarExportData = {
  body?: never;
  path: {
    /**
     * Calendar Uid
     */
    calendar_uid: string;
    /**
     * Token
     */
    token: string;
  };
  query?: never;
  url: '/v1/app/contract-management/calendar/{calendar_uid}/sync/{token}/export.ics';
};

export type GetContractManagementCalendarExportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementCalendarExportError =
  GetContractManagementCalendarExportErrors[keyof GetContractManagementCalendarExportErrors];

export type GetContractManagementCalendarExportResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateContractManagementClauseData = {
  body: ContractManagementClauseCreate;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/clause/new';
};

export type CreateContractManagementClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementClauseError =
  CreateContractManagementClauseErrors[keyof CreateContractManagementClauseErrors];

export type CreateContractManagementClauseResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseInDb;
};

export type CreateContractManagementClauseResponse =
  CreateContractManagementClauseResponses[keyof CreateContractManagementClauseResponses];

export type MergeContractManagementClausesData = {
  body: ContractManagementClauseCreate;
  path?: never;
  query: {
    /**
     * Left Model Uid
     */
    left_model_uid: string;
    /**
     * Right Model Uid
     */
    right_model_uid: string;
  };
  url: '/v1/app/contract-management/clause/merge';
};

export type MergeContractManagementClausesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MergeContractManagementClausesError =
  MergeContractManagementClausesErrors[keyof MergeContractManagementClausesErrors];

export type MergeContractManagementClausesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseInDb;
};

export type MergeContractManagementClausesResponse =
  MergeContractManagementClausesResponses[keyof MergeContractManagementClausesResponses];

export type DeleteContractManagementClauseData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/clause/{uid}';
};

export type DeleteContractManagementClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementClauseError =
  DeleteContractManagementClauseErrors[keyof DeleteContractManagementClauseErrors];

export type DeleteContractManagementClauseResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseInDb;
};

export type DeleteContractManagementClauseResponse =
  DeleteContractManagementClauseResponses[keyof DeleteContractManagementClauseResponses];

export type GetContractManagementClauseData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/clause/{uid}';
};

export type GetContractManagementClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementClauseError =
  GetContractManagementClauseErrors[keyof GetContractManagementClauseErrors];

export type GetContractManagementClauseResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseInDb;
};

export type GetContractManagementClauseResponse =
  GetContractManagementClauseResponses[keyof GetContractManagementClauseResponses];

export type UpdateContractManagementClauseData = {
  body: ContractManagementClauseUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/clause/{uid}';
};

export type UpdateContractManagementClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementClauseError =
  UpdateContractManagementClauseErrors[keyof UpdateContractManagementClauseErrors];

export type UpdateContractManagementClauseResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseInDb;
};

export type UpdateContractManagementClauseResponse =
  UpdateContractManagementClauseResponses[keyof UpdateContractManagementClauseResponses];

export type SearchContractManagementClausesData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: {
    /**
     * Latest Revision Only
     */
    latest_revision_only?: boolean;
  };
  url: '/v1/app/contract-management/clause/search';
};

export type SearchContractManagementClausesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementClausesError =
  SearchContractManagementClausesErrors[keyof SearchContractManagementClausesErrors];

export type SearchContractManagementClausesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseSearchResult;
};

export type SearchContractManagementClausesResponse =
  SearchContractManagementClausesResponses[keyof SearchContractManagementClausesResponses];

export type GetClauseContractFilesData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Latest Revision Only
     */
    latest_revision_only?: boolean;
  };
  url: '/v1/app/contract-management/clause/{uid}/contract-files';
};

export type GetClauseContractFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetClauseContractFilesError =
  GetClauseContractFilesErrors[keyof GetClauseContractFilesErrors];

export type GetClauseContractFilesResponses = {
  /**
   * Successful Response
   */
  200: RelatedContractFilesResponse;
};

export type GetClauseContractFilesResponse =
  GetClauseContractFilesResponses[keyof GetClauseContractFilesResponses];

export type AddClauseToContractFileData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Clause Uid
     */
    clause_uid: string;
    /**
     * Contract File Uid
     */
    contract_file_uid: string;
    /**
     * Page Number
     */
    page_number: number | null;
  };
  url: '/v1/app/contract-management/clause/add-to-contract-file';
};

export type AddClauseToContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddClauseToContractFileError =
  AddClauseToContractFileErrors[keyof AddClauseToContractFileErrors];

export type AddClauseToContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseInDb;
};

export type AddClauseToContractFileResponse =
  AddClauseToContractFileResponses[keyof AddClauseToContractFileResponses];

export type RemoveClauseFromContractFileData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Clause Uid
     */
    clause_uid: string;
    /**
     * Contract File Uid
     */
    contract_file_uid: string;
  };
  url: '/v1/app/contract-management/clause/remove-from-contract-file';
};

export type RemoveClauseFromContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveClauseFromContractFileError =
  RemoveClauseFromContractFileErrors[keyof RemoveClauseFromContractFileErrors];

export type RemoveClauseFromContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementClauseInDb;
};

export type RemoveClauseFromContractFileResponse =
  RemoveClauseFromContractFileResponses[keyof RemoveClauseFromContractFileResponses];

export type CreateContractManagementMatterData = {
  body: ContractManagementMatterCreate;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/matter/new';
};

export type CreateContractManagementMatterErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementMatterError =
  CreateContractManagementMatterErrors[keyof CreateContractManagementMatterErrors];

export type CreateContractManagementMatterResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterInDb;
};

export type CreateContractManagementMatterResponse =
  CreateContractManagementMatterResponses[keyof CreateContractManagementMatterResponses];

export type MergeContractManagementMattersData = {
  body: ContractManagementMatterCreate;
  path?: never;
  query: {
    /**
     * Left Model Uid
     */
    left_model_uid: string;
    /**
     * Right Model Uid
     */
    right_model_uid: string;
  };
  url: '/v1/app/contract-management/matter/merge';
};

export type MergeContractManagementMattersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MergeContractManagementMattersError =
  MergeContractManagementMattersErrors[keyof MergeContractManagementMattersErrors];

export type MergeContractManagementMattersResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterInDb;
};

export type MergeContractManagementMattersResponse =
  MergeContractManagementMattersResponses[keyof MergeContractManagementMattersResponses];

export type DeleteContractManagementMatterData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/matter/{uid}';
};

export type DeleteContractManagementMatterErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementMatterError =
  DeleteContractManagementMatterErrors[keyof DeleteContractManagementMatterErrors];

export type DeleteContractManagementMatterResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterInDb;
};

export type DeleteContractManagementMatterResponse =
  DeleteContractManagementMatterResponses[keyof DeleteContractManagementMatterResponses];

export type GetContractManagementMatterData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/matter/{uid}';
};

export type GetContractManagementMatterErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementMatterError =
  GetContractManagementMatterErrors[keyof GetContractManagementMatterErrors];

export type GetContractManagementMatterResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterInDb;
};

export type GetContractManagementMatterResponse =
  GetContractManagementMatterResponses[keyof GetContractManagementMatterResponses];

export type UpdateContractManagementMatterData = {
  body: ContractManagementMatterUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/matter/{uid}';
};

export type UpdateContractManagementMatterErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementMatterError =
  UpdateContractManagementMatterErrors[keyof UpdateContractManagementMatterErrors];

export type UpdateContractManagementMatterResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterInDb;
};

export type UpdateContractManagementMatterResponse =
  UpdateContractManagementMatterResponses[keyof UpdateContractManagementMatterResponses];

export type SearchContractManagementMattersData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: {
    /**
     * Exclude Contract Uids
     *
     * Optional list of contract UIDs. Matters already linked to any of these contracts will be excluded from results. Useful when assigning matters to a contract to avoid duplicates.
     */
    exclude_contract_uids?: Array<string> | null;
  };
  url: '/v1/app/contract-management/matter/search';
};

export type SearchContractManagementMattersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementMattersError =
  SearchContractManagementMattersErrors[keyof SearchContractManagementMattersErrors];

export type SearchContractManagementMattersResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterSearchResult;
};

export type SearchContractManagementMattersResponse =
  SearchContractManagementMattersResponses[keyof SearchContractManagementMattersResponses];

export type AddMatterToContractData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Matter Uid
     */
    matter_uid: string;
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  url: '/v1/app/contract-management/matter/add-to-contract';
};

export type AddMatterToContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddMatterToContractError = AddMatterToContractErrors[keyof AddMatterToContractErrors];

export type AddMatterToContractResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterInDb;
};

export type AddMatterToContractResponse =
  AddMatterToContractResponses[keyof AddMatterToContractResponses];

export type RemoveMatterFromContractData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Matter Uid
     */
    matter_uid: string;
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  url: '/v1/app/contract-management/matter/remove-from-contract';
};

export type RemoveMatterFromContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveMatterFromContractError =
  RemoveMatterFromContractErrors[keyof RemoveMatterFromContractErrors];

export type RemoveMatterFromContractResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementMatterInDb;
};

export type RemoveMatterFromContractResponse =
  RemoveMatterFromContractResponses[keyof RemoveMatterFromContractResponses];

export type GetMatterContractsData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/matter/{uid}/contracts';
};

export type GetMatterContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMatterContractsError = GetMatterContractsErrors[keyof GetMatterContractsErrors];

export type GetMatterContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractSearchResult;
};

export type GetMatterContractsResponse =
  GetMatterContractsResponses[keyof GetMatterContractsResponses];

export type CreateContractManagementObligationData = {
  body: ContractManagementObligationCreate;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/obligation/new';
};

export type CreateContractManagementObligationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementObligationError =
  CreateContractManagementObligationErrors[keyof CreateContractManagementObligationErrors];

export type CreateContractManagementObligationResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationInDb;
};

export type CreateContractManagementObligationResponse =
  CreateContractManagementObligationResponses[keyof CreateContractManagementObligationResponses];

export type MergeContractManagementObligationsData = {
  body: ContractManagementObligationCreate;
  path?: never;
  query: {
    /**
     * Left Model Uid
     */
    left_model_uid: string;
    /**
     * Right Model Uid
     */
    right_model_uid: string;
  };
  url: '/v1/app/contract-management/obligation/merge';
};

export type MergeContractManagementObligationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MergeContractManagementObligationsError =
  MergeContractManagementObligationsErrors[keyof MergeContractManagementObligationsErrors];

export type MergeContractManagementObligationsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationInDb;
};

export type MergeContractManagementObligationsResponse =
  MergeContractManagementObligationsResponses[keyof MergeContractManagementObligationsResponses];

export type DeleteContractManagementObligationData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/obligation/{uid}';
};

export type DeleteContractManagementObligationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementObligationError =
  DeleteContractManagementObligationErrors[keyof DeleteContractManagementObligationErrors];

export type DeleteContractManagementObligationResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationInDb;
};

export type DeleteContractManagementObligationResponse =
  DeleteContractManagementObligationResponses[keyof DeleteContractManagementObligationResponses];

export type GetContractManagementObligationData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/obligation/{uid}';
};

export type GetContractManagementObligationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementObligationError =
  GetContractManagementObligationErrors[keyof GetContractManagementObligationErrors];

export type GetContractManagementObligationResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationInDb;
};

export type GetContractManagementObligationResponse =
  GetContractManagementObligationResponses[keyof GetContractManagementObligationResponses];

export type UpdateContractManagementObligationData = {
  body: ContractManagementObligationUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/obligation/{uid}';
};

export type UpdateContractManagementObligationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementObligationError =
  UpdateContractManagementObligationErrors[keyof UpdateContractManagementObligationErrors];

export type UpdateContractManagementObligationResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationInDb;
};

export type UpdateContractManagementObligationResponse =
  UpdateContractManagementObligationResponses[keyof UpdateContractManagementObligationResponses];

export type SearchContractManagementObligationsData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: {
    /**
     * Latest Revision Only
     */
    latest_revision_only?: boolean;
  };
  url: '/v1/app/contract-management/obligation/search';
};

export type SearchContractManagementObligationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementObligationsError =
  SearchContractManagementObligationsErrors[keyof SearchContractManagementObligationsErrors];

export type SearchContractManagementObligationsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationSearchResult;
};

export type SearchContractManagementObligationsResponse =
  SearchContractManagementObligationsResponses[keyof SearchContractManagementObligationsResponses];

export type GetObligationContractFilesData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Latest Revision Only
     */
    latest_revision_only?: boolean;
  };
  url: '/v1/app/contract-management/obligation/{uid}/contract-files';
};

export type GetObligationContractFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetObligationContractFilesError =
  GetObligationContractFilesErrors[keyof GetObligationContractFilesErrors];

export type GetObligationContractFilesResponses = {
  /**
   * Successful Response
   */
  200: RelatedContractFilesResponse;
};

export type GetObligationContractFilesResponse =
  GetObligationContractFilesResponses[keyof GetObligationContractFilesResponses];

export type AddObligationToContractFileData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Obligation Uid
     */
    obligation_uid: string;
    /**
     * Contract File Uid
     */
    contract_file_uid: string;
    /**
     * Page Number
     */
    page_number: number | null;
  };
  url: '/v1/app/contract-management/obligation/add-to-contract-file';
};

export type AddObligationToContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddObligationToContractFileError =
  AddObligationToContractFileErrors[keyof AddObligationToContractFileErrors];

export type AddObligationToContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationInDb;
};

export type AddObligationToContractFileResponse =
  AddObligationToContractFileResponses[keyof AddObligationToContractFileResponses];

export type RemoveObligationFromContractFileData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Obligation Uid
     */
    obligation_uid: string;
    /**
     * Contract File Uid
     */
    contract_file_uid: string;
  };
  url: '/v1/app/contract-management/obligation/remove-from-contract-file';
};

export type RemoveObligationFromContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveObligationFromContractFileError =
  RemoveObligationFromContractFileErrors[keyof RemoveObligationFromContractFileErrors];

export type RemoveObligationFromContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementObligationInDb;
};

export type RemoveObligationFromContractFileResponse =
  RemoveObligationFromContractFileResponses[keyof RemoveObligationFromContractFileResponses];

export type DeleteContractManagementDateData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/date/{uid}';
};

export type DeleteContractManagementDateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementDateError =
  DeleteContractManagementDateErrors[keyof DeleteContractManagementDateErrors];

export type DeleteContractManagementDateResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementDateInDb;
};

export type DeleteContractManagementDateResponse =
  DeleteContractManagementDateResponses[keyof DeleteContractManagementDateResponses];

export type UpdateContractManagementDateData = {
  body: ContractManagementDateUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/date/{uid}';
};

export type UpdateContractManagementDateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementDateError =
  UpdateContractManagementDateErrors[keyof UpdateContractManagementDateErrors];

export type UpdateContractManagementDateResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementDateInDb;
};

export type UpdateContractManagementDateResponse =
  UpdateContractManagementDateResponses[keyof UpdateContractManagementDateResponses];

export type DeleteContractManagementContractData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}';
};

export type DeleteContractManagementContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementContractError =
  DeleteContractManagementContractErrors[keyof DeleteContractManagementContractErrors];

export type DeleteContractManagementContractResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractInDb;
};

export type DeleteContractManagementContractResponse =
  DeleteContractManagementContractResponses[keyof DeleteContractManagementContractResponses];

export type GetContractManagementContractData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}';
};

export type GetContractManagementContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractError =
  GetContractManagementContractErrors[keyof GetContractManagementContractErrors];

export type GetContractManagementContractResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractInDb;
};

export type GetContractManagementContractResponse =
  GetContractManagementContractResponses[keyof GetContractManagementContractResponses];

export type UpdateContractManagementContractData = {
  body: ContractManagementContractUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}';
};

export type UpdateContractManagementContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementContractError =
  UpdateContractManagementContractErrors[keyof UpdateContractManagementContractErrors];

export type UpdateContractManagementContractResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractInDb;
};

export type UpdateContractManagementContractResponse =
  UpdateContractManagementContractResponses[keyof UpdateContractManagementContractResponses];

export type SearchContractManagementContractsData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: {
    /**
     * Exclude Matter Uids
     *
     * Optional list of matter UIDs. Contracts already linked to any of these matters will be excluded from results. Useful when assigning contracts to a matter to avoid duplicates.
     */
    exclude_matter_uids?: Array<string> | null;
  };
  url: '/v1/app/contract-management/contract/search';
};

export type SearchContractManagementContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementContractsError =
  SearchContractManagementContractsErrors[keyof SearchContractManagementContractsErrors];

export type SearchContractManagementContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractSearchResult;
};

export type SearchContractManagementContractsResponse =
  SearchContractManagementContractsResponses[keyof SearchContractManagementContractsResponses];

export type GetContractContractualPartiesData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}/contractual-parties';
};

export type GetContractContractualPartiesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractContractualPartiesError =
  GetContractContractualPartiesErrors[keyof GetContractContractualPartiesErrors];

export type GetContractContractualPartiesResponses = {
  /**
   * Response Get Contract Contractual Parties V1 App Contract Management Contract  Uid  Contractual Parties Get
   *
   * Successful Response
   */
  200: Array<
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb)
  >;
};

export type GetContractContractualPartiesResponse =
  GetContractContractualPartiesResponses[keyof GetContractContractualPartiesResponses];

export type GetContractFilesForContractData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}/contract-files';
};

export type GetContractFilesForContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractFilesForContractError =
  GetContractFilesForContractErrors[keyof GetContractFilesForContractErrors];

export type GetContractFilesForContractResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractFileSearchResult;
};

export type GetContractFilesForContractResponse =
  GetContractFilesForContractResponses[keyof GetContractFilesForContractResponses];

export type GetContractValuesData = {
  body: ContractManagementContractValueQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}/values';
};

export type GetContractValuesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractValuesError = GetContractValuesErrors[keyof GetContractValuesErrors];

export type GetContractValuesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractValueSearchResult;
};

export type GetContractValuesResponse =
  GetContractValuesResponses[keyof GetContractValuesResponses];

export type GetContractValueMatrixData = {
  body: ContractManagementContractValueQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}/values/matrix';
};

export type GetContractValueMatrixErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractValueMatrixError =
  GetContractValueMatrixErrors[keyof GetContractValueMatrixErrors];

export type GetContractValueMatrixResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractValueMatrix;
};

export type GetContractValueMatrixResponse =
  GetContractValueMatrixResponses[keyof GetContractValueMatrixResponses];

export type RepopulateContractManagementContractData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}/repopulate';
};

export type RepopulateContractManagementContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RepopulateContractManagementContractError =
  RepopulateContractManagementContractErrors[keyof RepopulateContractManagementContractErrors];

export type RepopulateContractManagementContractResponses = {
  /**
   * Successful Response
   */
  200: RepopulateContractAgentTaskInDb;
};

export type RepopulateContractManagementContractResponse =
  RepopulateContractManagementContractResponses[keyof RepopulateContractManagementContractResponses];

export type ConvertContractToFileData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}/convert-to-file';
};

export type ConvertContractToFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConvertContractToFileError =
  ConvertContractToFileErrors[keyof ConvertContractToFileErrors];

export type ConvertContractToFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDb;
};

export type ConvertContractToFileResponse =
  ConvertContractToFileResponses[keyof ConvertContractToFileResponses];

export type ConvertContractToFolderData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract/{uid}/convert-to-folder';
};

export type ConvertContractToFolderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConvertContractToFolderError =
  ConvertContractToFolderErrors[keyof ConvertContractToFolderErrors];

export type ConvertContractToFolderResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDb;
};

export type ConvertContractToFolderResponse =
  ConvertContractToFolderResponses[keyof ConvertContractToFolderResponses];

export type SearchContractManagementContractFilesData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/contract-file/search';
};

export type SearchContractManagementContractFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementContractFilesError =
  SearchContractManagementContractFilesErrors[keyof SearchContractManagementContractFilesErrors];

export type SearchContractManagementContractFilesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractFileSearchResult;
};

export type SearchContractManagementContractFilesResponse =
  SearchContractManagementContractFilesResponses[keyof SearchContractManagementContractFilesResponses];

export type CreateContractManagementContractFileData = {
  body: ContractManagementContractFileCreateUser;
  path?: never;
  query: {
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  url: '/v1/app/contract-management/contract-file/new';
};

export type CreateContractManagementContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementContractFileError =
  CreateContractManagementContractFileErrors[keyof CreateContractManagementContractFileErrors];

export type CreateContractManagementContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractFileInDb;
};

export type CreateContractManagementContractFileResponse =
  CreateContractManagementContractFileResponses[keyof CreateContractManagementContractFileResponses];

export type DeleteContractManagementContractFileData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-file/{uid}';
};

export type DeleteContractManagementContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementContractFileError =
  DeleteContractManagementContractFileErrors[keyof DeleteContractManagementContractFileErrors];

export type DeleteContractManagementContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractFileInDb;
};

export type DeleteContractManagementContractFileResponse =
  DeleteContractManagementContractFileResponses[keyof DeleteContractManagementContractFileResponses];

export type GetContractManagementContractFileData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-file/{uid}';
};

export type GetContractManagementContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractFileError =
  GetContractManagementContractFileErrors[keyof GetContractManagementContractFileErrors];

export type GetContractManagementContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractFileInDbExtended;
};

export type GetContractManagementContractFileResponse =
  GetContractManagementContractFileResponses[keyof GetContractManagementContractFileResponses];

export type UpdateContractManagementContractFileData = {
  body: ContractManagementContractFileUpdateUser;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-file/{uid}';
};

export type UpdateContractManagementContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementContractFileError =
  UpdateContractManagementContractFileErrors[keyof UpdateContractManagementContractFileErrors];

export type UpdateContractManagementContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractFileInDb;
};

export type UpdateContractManagementContractFileResponse =
  UpdateContractManagementContractFileResponses[keyof UpdateContractManagementContractFileResponses];

export type MoveContractManagementContractFileData = {
  body: ContractManagementContractFileMoveUser;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-file/{uid}/move';
};

export type MoveContractManagementContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MoveContractManagementContractFileError =
  MoveContractManagementContractFileErrors[keyof MoveContractManagementContractFileErrors];

export type MoveContractManagementContractFileResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractFileInDb;
};

export type MoveContractManagementContractFileResponse =
  MoveContractManagementContractFileResponses[keyof MoveContractManagementContractFileResponses];

export type GetContractManagementContractFileClausesData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/clauses';
};

export type GetContractManagementContractFileClausesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractFileClausesError =
  GetContractManagementContractFileClausesErrors[keyof GetContractManagementContractFileClausesErrors];

export type GetContractManagementContractFileClausesResponses = {
  /**
   * Response Get Contract Management Contract File Clauses V1 App Contract Management Contract File  Uid  Clauses Get
   *
   * Successful Response
   */
  200: Array<ContractManagementClauseInDb>;
};

export type GetContractManagementContractFileClausesResponse =
  GetContractManagementContractFileClausesResponses[keyof GetContractManagementContractFileClausesResponses];

export type GetContractManagementContractFileObligationsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/obligations';
};

export type GetContractManagementContractFileObligationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractFileObligationsError =
  GetContractManagementContractFileObligationsErrors[keyof GetContractManagementContractFileObligationsErrors];

export type GetContractManagementContractFileObligationsResponses = {
  /**
   * Response Get Contract Management Contract File Obligations V1 App Contract Management Contract File  Uid  Obligations Get
   *
   * Successful Response
   */
  200: Array<ContractManagementObligationInDb>;
};

export type GetContractManagementContractFileObligationsResponse =
  GetContractManagementContractFileObligationsResponses[keyof GetContractManagementContractFileObligationsResponses];

export type GetContractManagementContractFileDatesData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/dates';
};

export type GetContractManagementContractFileDatesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractFileDatesError =
  GetContractManagementContractFileDatesErrors[keyof GetContractManagementContractFileDatesErrors];

export type GetContractManagementContractFileDatesResponses = {
  /**
   * Response Get Contract Management Contract File Dates V1 App Contract Management Contract File  Uid  Dates Get
   *
   * Successful Response
   */
  200: Array<ContractManagementDateInDb>;
};

export type GetContractManagementContractFileDatesResponse =
  GetContractManagementContractFileDatesResponses[keyof GetContractManagementContractFileDatesResponses];

export type CreateContractManagementContractFileDateData = {
  body: ContractManagementDateCreate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/dates';
};

export type CreateContractManagementContractFileDateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementContractFileDateError =
  CreateContractManagementContractFileDateErrors[keyof CreateContractManagementContractFileDateErrors];

export type CreateContractManagementContractFileDateResponses = {
  /**
   * Successful Response
   */
  201: ContractManagementDateInDb;
};

export type CreateContractManagementContractFileDateResponse =
  CreateContractManagementContractFileDateResponses[keyof CreateContractManagementContractFileDateResponses];

export type GetContractManagementContractFileContractualPartiesData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-file/{uid}/contractual_parties';
};

export type GetContractManagementContractFileContractualPartiesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractFileContractualPartiesError =
  GetContractManagementContractFileContractualPartiesErrors[keyof GetContractManagementContractFileContractualPartiesErrors];

export type GetContractManagementContractFileContractualPartiesResponses = {
  /**
   * Response Get Contract Management Contract File Contractual Parties V1 App Contract Management Contract File  Uid  Contractual Parties Get
   *
   * Successful Response
   */
  200: Array<
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb)
  >;
};

export type GetContractManagementContractFileContractualPartiesResponse =
  GetContractManagementContractFileContractualPartiesResponses[keyof GetContractManagementContractFileContractualPartiesResponses];

export type GetContractManagementContractFileSigningRequestsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/signing-requests';
};

export type GetContractManagementContractFileSigningRequestsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractFileSigningRequestsError =
  GetContractManagementContractFileSigningRequestsErrors[keyof GetContractManagementContractFileSigningRequestsErrors];

export type GetContractManagementContractFileSigningRequestsResponses = {
  /**
   * Response Get Contract Management Contract File Signing Requests V1 App Contract Management Contract File  Uid  Signing Requests Get
   *
   * Successful Response
   */
  200: Array<SigningRequestWithRelationsInDb>;
};

export type GetContractManagementContractFileSigningRequestsResponse =
  GetContractManagementContractFileSigningRequestsResponses[keyof GetContractManagementContractFileSigningRequestsResponses];

export type SearchContractFileExtractionsData = {
  body: SearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/extractions';
};

export type SearchContractFileExtractionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractFileExtractionsError =
  SearchContractFileExtractionsErrors[keyof SearchContractFileExtractionsErrors];

export type SearchContractFileExtractionsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementExtractionSearchResult;
};

export type SearchContractFileExtractionsResponse =
  SearchContractFileExtractionsResponses[keyof SearchContractFileExtractionsResponses];

export type RerunContractFileExtractionsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/extractions/rerun';
};

export type RerunContractFileExtractionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RerunContractFileExtractionsError =
  RerunContractFileExtractionsErrors[keyof RerunContractFileExtractionsErrors];

export type RerunContractFileExtractionsResponses = {
  /**
   * Successful Response
   */
  202: unknown;
};

export type RerunContractFileExtractionData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * Extraction Uid
     */
    extraction_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-file/{uid}/extractions/{extraction_uid}/rerun';
};

export type RerunContractFileExtractionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RerunContractFileExtractionError =
  RerunContractFileExtractionErrors[keyof RerunContractFileExtractionErrors];

export type RerunContractFileExtractionResponses = {
  /**
   * Successful Response
   */
  202: unknown;
};

export type GetContractFileContractTypeExtractionsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/contract-type-extractions';
};

export type GetContractFileContractTypeExtractionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractFileContractTypeExtractionsError =
  GetContractFileContractTypeExtractionsErrors[keyof GetContractFileContractTypeExtractionsErrors];

export type GetContractFileContractTypeExtractionsResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeExtractions;
};

export type GetContractFileContractTypeExtractionsResponse =
  GetContractFileContractTypeExtractionsResponses[keyof GetContractFileContractTypeExtractionsResponses];

export type GetContractFileStandaloneExtractionsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Revision Uid
     */
    revision_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-file/{uid}/standalone-extractions';
};

export type GetContractFileStandaloneExtractionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractFileStandaloneExtractionsError =
  GetContractFileStandaloneExtractionsErrors[keyof GetContractFileStandaloneExtractionsErrors];

export type GetContractFileStandaloneExtractionsResponses = {
  /**
   * Response Get Contract File Standalone Extractions V1 App Contract Management Contract File  Uid  Standalone Extractions Get
   *
   * Successful Response
   */
  200: Array<ContractTypeExtractionEntry>;
};

export type GetContractFileStandaloneExtractionsResponse =
  GetContractFileStandaloneExtractionsResponses[keyof GetContractFileStandaloneExtractionsResponses];

export type ListContractTypesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/contract-type/';
};

export type ListContractTypesResponses = {
  /**
   * Response List Contract Types V1 App Contract Management Contract Type  Get
   *
   * Successful Response
   */
  200: Array<ContractTypeInDb>;
};

export type ListContractTypesResponse =
  ListContractTypesResponses[keyof ListContractTypesResponses];

export type SearchContractTypesData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/contract-type/search';
};

export type SearchContractTypesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractTypesError = SearchContractTypesErrors[keyof SearchContractTypesErrors];

export type SearchContractTypesResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeSearchResult;
};

export type SearchContractTypesResponse =
  SearchContractTypesResponses[keyof SearchContractTypesResponses];

export type CreateContractTypeData = {
  body: ContractTypeCreate;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/contract-type/new';
};

export type CreateContractTypeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractTypeError = CreateContractTypeErrors[keyof CreateContractTypeErrors];

export type CreateContractTypeResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeInDb;
};

export type CreateContractTypeResponse =
  CreateContractTypeResponses[keyof CreateContractTypeResponses];

export type DeleteContractTypeData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Replacement Uid
     */
    replacement_uid?: string | null;
  };
  url: '/v1/app/contract-management/contract-type/{uid}';
};

export type DeleteContractTypeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractTypeError = DeleteContractTypeErrors[keyof DeleteContractTypeErrors];

export type DeleteContractTypeResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeInDb;
};

export type DeleteContractTypeResponse =
  DeleteContractTypeResponses[keyof DeleteContractTypeResponses];

export type UpdateContractTypeData = {
  body: ContractTypeUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-type/{uid}';
};

export type UpdateContractTypeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractTypeError = UpdateContractTypeErrors[keyof UpdateContractTypeErrors];

export type UpdateContractTypeResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeInDb;
};

export type UpdateContractTypeResponse =
  UpdateContractTypeResponses[keyof UpdateContractTypeResponses];

export type GetContractTypeUsageData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-type/{uid}/usage';
};

export type GetContractTypeUsageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractTypeUsageError =
  GetContractTypeUsageErrors[keyof GetContractTypeUsageErrors];

export type GetContractTypeUsageResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeUsage;
};

export type GetContractTypeUsageResponse =
  GetContractTypeUsageResponses[keyof GetContractTypeUsageResponses];

export type SearchContractTypeContractsData = {
  body: SearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-type/{uid}/contracts';
};

export type SearchContractTypeContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractTypeContractsError =
  SearchContractTypeContractsErrors[keyof SearchContractTypeContractsErrors];

export type SearchContractTypeContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeContractSearchResult;
};

export type SearchContractTypeContractsResponse =
  SearchContractTypeContractsResponses[keyof SearchContractTypeContractsResponses];

export type SearchContractTypePlaybooksData = {
  body: SearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-type/{uid}/playbooks';
};

export type SearchContractTypePlaybooksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractTypePlaybooksError =
  SearchContractTypePlaybooksErrors[keyof SearchContractTypePlaybooksErrors];

export type SearchContractTypePlaybooksResponses = {
  /**
   * Successful Response
   */
  200: ContractTypePlaybookSearchResult;
};

export type SearchContractTypePlaybooksResponse =
  SearchContractTypePlaybooksResponses[keyof SearchContractTypePlaybooksResponses];

export type SearchContractTypeClausesData = {
  body: SearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contract-type/{uid}/clauses';
};

export type SearchContractTypeClausesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractTypeClausesError =
  SearchContractTypeClausesErrors[keyof SearchContractTypeClausesErrors];

export type SearchContractTypeClausesResponses = {
  /**
   * Successful Response
   */
  200: ContractTypeClauseSearchResult;
};

export type SearchContractTypeClausesResponse =
  SearchContractTypeClausesResponses[keyof SearchContractTypeClausesResponses];

export type SearchExtractionPromptsData = {
  body: SearchQuery;
  path?: never;
  query?: {
    /**
     * Contract Type Uids
     */
    contract_type_uids?: Array<string> | null;
    /**
     * Without Contract Type
     */
    without_contract_type?: boolean;
    /**
     * View Uid
     */
    view_uid?: string | null;
  };
  url: '/v1/app/contract-management/extraction-prompt/search';
};

export type SearchExtractionPromptsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchExtractionPromptsError =
  SearchExtractionPromptsErrors[keyof SearchExtractionPromptsErrors];

export type SearchExtractionPromptsResponses = {
  /**
   * Successful Response
   */
  200: ExtractionPromptSearchResult;
};

export type SearchExtractionPromptsResponse =
  SearchExtractionPromptsResponses[keyof SearchExtractionPromptsResponses];

export type DeleteExtractionPromptData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/extraction-prompt/{uid}';
};

export type DeleteExtractionPromptErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteExtractionPromptError =
  DeleteExtractionPromptErrors[keyof DeleteExtractionPromptErrors];

export type DeleteExtractionPromptResponses = {
  /**
   * Successful Response
   */
  200: ExtractionPromptInDb;
};

export type DeleteExtractionPromptResponse =
  DeleteExtractionPromptResponses[keyof DeleteExtractionPromptResponses];

export type GetExtractionPromptData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/extraction-prompt/{uid}';
};

export type GetExtractionPromptErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetExtractionPromptError = GetExtractionPromptErrors[keyof GetExtractionPromptErrors];

export type GetExtractionPromptResponses = {
  /**
   * Successful Response
   */
  200: ExtractionPromptInDb;
};

export type GetExtractionPromptResponse =
  GetExtractionPromptResponses[keyof GetExtractionPromptResponses];

export type UpdateExtractionPromptData = {
  body: ExtractionPromptUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query: {
    extraction_scope: ContractManagementColumnExtractionScope;
  };
  url: '/v1/app/contract-management/extraction-prompt/{uid}';
};

export type UpdateExtractionPromptErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateExtractionPromptError =
  UpdateExtractionPromptErrors[keyof UpdateExtractionPromptErrors];

export type UpdateExtractionPromptResponses = {
  /**
   * Successful Response
   */
  200: ExtractionPromptInDb;
};

export type UpdateExtractionPromptResponse =
  UpdateExtractionPromptResponses[keyof UpdateExtractionPromptResponses];

export type CreateExtractionPromptData = {
  body: ExtractionPromptCreate;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/extraction-prompt/new';
};

export type CreateExtractionPromptErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateExtractionPromptError =
  CreateExtractionPromptErrors[keyof CreateExtractionPromptErrors];

export type CreateExtractionPromptResponses = {
  /**
   * Successful Response
   */
  200: ExtractionPromptInDb;
};

export type CreateExtractionPromptResponse =
  CreateExtractionPromptResponses[keyof CreateExtractionPromptResponses];

export type SearchExtractionPromptVersionsData = {
  body: SearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/extraction-prompt/{uid}/versions/search';
};

export type SearchExtractionPromptVersionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchExtractionPromptVersionsError =
  SearchExtractionPromptVersionsErrors[keyof SearchExtractionPromptVersionsErrors];

export type SearchExtractionPromptVersionsResponses = {
  /**
   * Successful Response
   */
  200: ExtractionPromptVersionSearchResult;
};

export type SearchExtractionPromptVersionsResponse =
  SearchExtractionPromptVersionsResponses[keyof SearchExtractionPromptVersionsResponses];

export type CreateContractManagementContractualPartyData = {
  /**
   * New Data
   */
  body:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonCreate)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityCreate);
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/contractual-party/new';
};

export type CreateContractManagementContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementContractualPartyError =
  CreateContractManagementContractualPartyErrors[keyof CreateContractManagementContractualPartyErrors];

export type CreateContractManagementContractualPartyResponses = {
  /**
   * Response Create Contract Management Contractual Party V1 App Contract Management Contractual Party New Post
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type CreateContractManagementContractualPartyResponse =
  CreateContractManagementContractualPartyResponses[keyof CreateContractManagementContractualPartyResponses];

export type MergeContractManagementContractualPartiesData = {
  /**
   * Merged Data
   */
  body:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonCreate)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityCreate);
  path?: never;
  query: {
    /**
     * Left Model Uid
     */
    left_model_uid: string;
    /**
     * Right Model Uid
     */
    right_model_uid: string;
  };
  url: '/v1/app/contract-management/contractual-party/merge';
};

export type MergeContractManagementContractualPartiesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MergeContractManagementContractualPartiesError =
  MergeContractManagementContractualPartiesErrors[keyof MergeContractManagementContractualPartiesErrors];

export type MergeContractManagementContractualPartiesResponses = {
  /**
   * Response Merge Contract Management Contractual Parties V1 App Contract Management Contractual Party Merge Post
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type MergeContractManagementContractualPartiesResponse =
  MergeContractManagementContractualPartiesResponses[keyof MergeContractManagementContractualPartiesResponses];

export type DeleteContractManagementContractualPartyData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contractual-party/{uid}';
};

export type DeleteContractManagementContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementContractualPartyError =
  DeleteContractManagementContractualPartyErrors[keyof DeleteContractManagementContractualPartyErrors];

export type DeleteContractManagementContractualPartyResponses = {
  /**
   * Response Delete Contract Management Contractual Party V1 App Contract Management Contractual Party  Uid  Delete
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type DeleteContractManagementContractualPartyResponse =
  DeleteContractManagementContractualPartyResponses[keyof DeleteContractManagementContractualPartyResponses];

export type GetContractManagementContractualPartyData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contractual-party/{uid}';
};

export type GetContractManagementContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementContractualPartyError =
  GetContractManagementContractualPartyErrors[keyof GetContractManagementContractualPartyErrors];

export type GetContractManagementContractualPartyResponses = {
  /**
   * Response Get Contract Management Contractual Party V1 App Contract Management Contractual Party  Uid  Get
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type GetContractManagementContractualPartyResponse =
  GetContractManagementContractualPartyResponses[keyof GetContractManagementContractualPartyResponses];

export type UpdateContractManagementContractualPartyData = {
  /**
   * New Data
   */
  body:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonUpdate)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityUpdate);
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contractual-party/{uid}';
};

export type UpdateContractManagementContractualPartyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementContractualPartyError =
  UpdateContractManagementContractualPartyErrors[keyof UpdateContractManagementContractualPartyErrors];

export type UpdateContractManagementContractualPartyResponses = {
  /**
   * Response Update Contract Management Contractual Party V1 App Contract Management Contractual Party  Uid  Put
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type UpdateContractManagementContractualPartyResponse =
  UpdateContractManagementContractualPartyResponses[keyof UpdateContractManagementContractualPartyResponses];

export type SearchContractManagementContractualPartiesData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: {
    /**
     * Exclude Contract File Uids
     *
     * Optional list of contract file UIDs. Parties already linked to any of these files will be excluded from results. Useful when adding parties to a file to avoid duplicates.
     */
    exclude_contract_file_uids?: Array<string> | null;
  };
  url: '/v1/app/contract-management/contractual-party/search';
};

export type SearchContractManagementContractualPartiesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementContractualPartiesError =
  SearchContractManagementContractualPartiesErrors[keyof SearchContractManagementContractualPartiesErrors];

export type SearchContractManagementContractualPartiesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractualPartySearchResult;
};

export type SearchContractManagementContractualPartiesResponse =
  SearchContractManagementContractualPartiesResponses[keyof SearchContractManagementContractualPartiesResponses];

export type AddContractualPartyToContractFileData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
    /**
     * Contract File Uid
     */
    contract_file_uid: string;
  };
  url: '/v1/app/contract-management/contractual-party/add-to-contract-file';
};

export type AddContractualPartyToContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddContractualPartyToContractFileError =
  AddContractualPartyToContractFileErrors[keyof AddContractualPartyToContractFileErrors];

export type AddContractualPartyToContractFileResponses = {
  /**
   * Response Add Contractual Party To Contract File V1 App Contract Management Contractual Party Add To Contract File Post
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type AddContractualPartyToContractFileResponse =
  AddContractualPartyToContractFileResponses[keyof AddContractualPartyToContractFileResponses];

export type RemoveContractualPartyFromContractFileData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Contractual Party Uid
     */
    contractual_party_uid: string;
    /**
     * Contract File Uid
     */
    contract_file_uid: string;
  };
  url: '/v1/app/contract-management/contractual-party/remove-from-contract-file';
};

export type RemoveContractualPartyFromContractFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveContractualPartyFromContractFileError =
  RemoveContractualPartyFromContractFileErrors[keyof RemoveContractualPartyFromContractFileErrors];

export type RemoveContractualPartyFromContractFileResponses = {
  /**
   * Response Remove Contractual Party From Contract File V1 App Contract Management Contractual Party Remove From Contract File Post
   *
   * Successful Response
   */
  200:
    | ({
        kind: 'natural_person';
      } & ContractManagementContractualPartyNaturalPersonInDb)
    | ({
        kind: 'legal_entity';
      } & ContractManagementContractualPartyLegalEntityInDb);
};

export type RemoveContractualPartyFromContractFileResponse =
  RemoveContractualPartyFromContractFileResponses[keyof RemoveContractualPartyFromContractFileResponses];

export type GetContractualPartyContractsData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contractual-party/{uid}/contracts';
};

export type GetContractualPartyContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractualPartyContractsError =
  GetContractualPartyContractsErrors[keyof GetContractualPartyContractsErrors];

export type GetContractualPartyContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractSearchResult;
};

export type GetContractualPartyContractsResponse =
  GetContractualPartyContractsResponses[keyof GetContractualPartyContractsResponses];

export type GetContractualPartyRepresentedLegalEntitiesData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/contractual-party/{uid}/represented-legal-entities';
};

export type GetContractualPartyRepresentedLegalEntitiesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractualPartyRepresentedLegalEntitiesError =
  GetContractualPartyRepresentedLegalEntitiesErrors[keyof GetContractualPartyRepresentedLegalEntitiesErrors];

export type GetContractualPartyRepresentedLegalEntitiesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractualPartySearchResult;
};

export type GetContractualPartyRepresentedLegalEntitiesResponse =
  GetContractualPartyRepresentedLegalEntitiesResponses[keyof GetContractualPartyRepresentedLegalEntitiesResponses];

export type AddContractualPartyRepresentativeData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Legal Entity Uid
     */
    legal_entity_uid: string;
    /**
     * Representative Uid
     */
    representative_uid: string;
  };
  url: '/v1/app/contract-management/contractual-party/add-representative';
};

export type AddContractualPartyRepresentativeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddContractualPartyRepresentativeError =
  AddContractualPartyRepresentativeErrors[keyof AddContractualPartyRepresentativeErrors];

export type AddContractualPartyRepresentativeResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractualPartyLegalEntityInDb;
};

export type AddContractualPartyRepresentativeResponse =
  AddContractualPartyRepresentativeResponses[keyof AddContractualPartyRepresentativeResponses];

export type RemoveContractualPartyRepresentativeData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Legal Entity Uid
     */
    legal_entity_uid: string;
    /**
     * Representative Uid
     */
    representative_uid: string;
  };
  url: '/v1/app/contract-management/contractual-party/remove-representative';
};

export type RemoveContractualPartyRepresentativeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveContractualPartyRepresentativeError =
  RemoveContractualPartyRepresentativeErrors[keyof RemoveContractualPartyRepresentativeErrors];

export type RemoveContractualPartyRepresentativeResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractualPartyLegalEntityInDb;
};

export type RemoveContractualPartyRepresentativeResponse =
  RemoveContractualPartyRepresentativeResponses[keyof RemoveContractualPartyRepresentativeResponses];

export type SetContractualPartyDefaultRepresentativeData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Legal Entity Uid
     */
    legal_entity_uid: string;
    /**
     * Representative Uid
     */
    representative_uid?: string | null;
  };
  url: '/v1/app/contract-management/contractual-party/set-default-representative';
};

export type SetContractualPartyDefaultRepresentativeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetContractualPartyDefaultRepresentativeError =
  SetContractualPartyDefaultRepresentativeErrors[keyof SetContractualPartyDefaultRepresentativeErrors];

export type SetContractualPartyDefaultRepresentativeResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementContractualPartyLegalEntityInDb;
};

export type SetContractualPartyDefaultRepresentativeResponse =
  SetContractualPartyDefaultRepresentativeResponses[keyof SetContractualPartyDefaultRepresentativeResponses];

export type GetContractManagementViewPreviewData = {
  body: ContractManagementViewPreviewRequest;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/views/preview';
};

export type GetContractManagementViewPreviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementViewPreviewError =
  GetContractManagementViewPreviewErrors[keyof GetContractManagementViewPreviewErrors];

export type GetContractManagementViewPreviewResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewPreviewTableResponse;
};

export type GetContractManagementViewPreviewResponse =
  GetContractManagementViewPreviewResponses[keyof GetContractManagementViewPreviewResponses];

export type CreateContractManagementViewFromPreviewData = {
  body: ContractManagementViewCreateFromPreview;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/views/preview/create';
};

export type CreateContractManagementViewFromPreviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementViewFromPreviewError =
  CreateContractManagementViewFromPreviewErrors[keyof CreateContractManagementViewFromPreviewErrors];

export type CreateContractManagementViewFromPreviewResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewInDb;
};

export type CreateContractManagementViewFromPreviewResponse =
  CreateContractManagementViewFromPreviewResponses[keyof CreateContractManagementViewFromPreviewResponses];

export type CreateContractManagementViewData = {
  body: ContractManagementViewCreate;
  path?: never;
  query?: {
    /**
     * Source Directory Uids
     */
    source_directory_uids?: Array<string> | null;
    /**
     * Source Contract Uids
     */
    source_contract_uids?: Array<string> | null;
  };
  url: '/v1/app/contract-management/views/new';
};

export type CreateContractManagementViewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementViewError =
  CreateContractManagementViewErrors[keyof CreateContractManagementViewErrors];

export type CreateContractManagementViewResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewInDb;
};

export type CreateContractManagementViewResponse =
  CreateContractManagementViewResponses[keyof CreateContractManagementViewResponses];

export type DeleteContractManagementViewData = {
  body?: never;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: {
    /**
     * Delete Unused Library Prompts
     */
    delete_unused_library_prompts?: boolean;
  };
  url: '/v1/app/contract-management/views/{view_uid}';
};

export type DeleteContractManagementViewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementViewError =
  DeleteContractManagementViewErrors[keyof DeleteContractManagementViewErrors];

export type DeleteContractManagementViewResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewInDb;
};

export type DeleteContractManagementViewResponse =
  DeleteContractManagementViewResponses[keyof DeleteContractManagementViewResponses];

export type GetContractManagementViewData = {
  body?: never;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}';
};

export type GetContractManagementViewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementViewError =
  GetContractManagementViewErrors[keyof GetContractManagementViewErrors];

export type GetContractManagementViewResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewInDb;
};

export type GetContractManagementViewResponse =
  GetContractManagementViewResponses[keyof GetContractManagementViewResponses];

export type UpdateContractManagementViewData = {
  body: ContractManagementViewUpdate;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}';
};

export type UpdateContractManagementViewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementViewError =
  UpdateContractManagementViewErrors[keyof UpdateContractManagementViewErrors];

export type UpdateContractManagementViewResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewInDb;
};

export type UpdateContractManagementViewResponse =
  UpdateContractManagementViewResponses[keyof UpdateContractManagementViewResponses];

export type GetContractManagementViewTableData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/table';
};

export type GetContractManagementViewTableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementViewTableError =
  GetContractManagementViewTableErrors[keyof GetContractManagementViewTableErrors];

export type GetContractManagementViewTableResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewTable;
};

export type GetContractManagementViewTableResponse =
  GetContractManagementViewTableResponses[keyof GetContractManagementViewTableResponses];

export type GetContractManagementViewTableFilterOptionsData = {
  body?: never;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/table/filter-options';
};

export type GetContractManagementViewTableFilterOptionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementViewTableFilterOptionsError =
  GetContractManagementViewTableFilterOptionsErrors[keyof GetContractManagementViewTableFilterOptionsErrors];

export type GetContractManagementViewTableFilterOptionsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewTableFilterOptions;
};

export type GetContractManagementViewTableFilterOptionsResponse =
  GetContractManagementViewTableFilterOptionsResponses[keyof GetContractManagementViewTableFilterOptionsResponses];

export type GetContractManagementViewTableContractFilesData = {
  body: LimitableSearchableQuery;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
    /**
     * Contract Uid
     */
    contract_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/table/{contract_uid}/contract-files';
};

export type GetContractManagementViewTableContractFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetContractManagementViewTableContractFilesError =
  GetContractManagementViewTableContractFilesErrors[keyof GetContractManagementViewTableContractFilesErrors];

export type GetContractManagementViewTableContractFilesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewTableContractFiles;
};

export type GetContractManagementViewTableContractFilesResponse =
  GetContractManagementViewTableContractFilesResponses[keyof GetContractManagementViewTableContractFilesResponses];

export type SearchContractManagementViewsData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/contract-management/views/search';
};

export type SearchContractManagementViewsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchContractManagementViewsError =
  SearchContractManagementViewsErrors[keyof SearchContractManagementViewsErrors];

export type SearchContractManagementViewsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewSearchResult;
};

export type SearchContractManagementViewsResponse =
  SearchContractManagementViewsResponses[keyof SearchContractManagementViewsResponses];

export type AddColumnData = {
  body: BodyAddColumnV1AppContractManagementViewsViewUidAddColumnPost;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/add_column';
};

export type AddColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddColumnError = AddColumnErrors[keyof AddColumnErrors];

export type AddColumnResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewColumnInDb;
};

export type AddColumnResponse = AddColumnResponses[keyof AddColumnResponses];

export type AddContractTypeColumnsData = {
  body?: never;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query: {
    /**
     * Contract Type Uid
     */
    contract_type_uid: string;
  };
  url: '/v1/app/contract-management/views/{view_uid}/add_contract_type_columns';
};

export type AddContractTypeColumnsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddContractTypeColumnsError =
  AddContractTypeColumnsErrors[keyof AddContractTypeColumnsErrors];

export type AddContractTypeColumnsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewColumnBulkAddResult;
};

export type AddContractTypeColumnsResponse =
  AddContractTypeColumnsResponses[keyof AddContractTypeColumnsResponses];

export type AddViewSourceDirectoriesData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/source-directories/add';
};

export type AddViewSourceDirectoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddViewSourceDirectoriesError =
  AddViewSourceDirectoriesErrors[keyof AddViewSourceDirectoriesErrors];

export type AddViewSourceDirectoriesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type AddViewSourceDirectoriesResponse =
  AddViewSourceDirectoriesResponses[keyof AddViewSourceDirectoriesResponses];

export type RemoveViewSourceDirectoriesData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/source-directories/remove';
};

export type RemoveViewSourceDirectoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveViewSourceDirectoriesError =
  RemoveViewSourceDirectoriesErrors[keyof RemoveViewSourceDirectoriesErrors];

export type RemoveViewSourceDirectoriesResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type RemoveViewSourceDirectoriesResponse =
  RemoveViewSourceDirectoriesResponses[keyof RemoveViewSourceDirectoriesResponses];

export type ListViewSourceDirectoriesData = {
  body?: never;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/source-directories';
};

export type ListViewSourceDirectoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListViewSourceDirectoriesError =
  ListViewSourceDirectoriesErrors[keyof ListViewSourceDirectoriesErrors];

export type ListViewSourceDirectoriesResponses = {
  /**
   * Response List View Source Directories V1 App Contract Management Views  View Uid  Source Directories Get
   *
   * Successful Response
   */
  200: Array<VaultDirectoryInDbSlim>;
};

export type ListViewSourceDirectoriesResponse =
  ListViewSourceDirectoriesResponses[keyof ListViewSourceDirectoriesResponses];

export type AddViewSourceContractsData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/source-contracts/add';
};

export type AddViewSourceContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddViewSourceContractsError =
  AddViewSourceContractsErrors[keyof AddViewSourceContractsErrors];

export type AddViewSourceContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type AddViewSourceContractsResponse =
  AddViewSourceContractsResponses[keyof AddViewSourceContractsResponses];

export type RemoveViewSourceContractsData = {
  body: ContractManagementCalendarLinkRequest;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/source-contracts/remove';
};

export type RemoveViewSourceContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveViewSourceContractsError =
  RemoveViewSourceContractsErrors[keyof RemoveViewSourceContractsErrors];

export type RemoveViewSourceContractsResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementCalendarLinkResult;
};

export type RemoveViewSourceContractsResponse =
  RemoveViewSourceContractsResponses[keyof RemoveViewSourceContractsResponses];

export type ListViewSourceContractsData = {
  body?: never;
  path: {
    /**
     * View Uid
     */
    view_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/{view_uid}/source-contracts';
};

export type ListViewSourceContractsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListViewSourceContractsError =
  ListViewSourceContractsErrors[keyof ListViewSourceContractsErrors];

export type ListViewSourceContractsResponses = {
  /**
   * Response List View Source Contracts V1 App Contract Management Views  View Uid  Source Contracts Get
   *
   * Successful Response
   */
  200: Array<ContractManagementContractInDbSlim>;
};

export type ListViewSourceContractsResponse =
  ListViewSourceContractsResponses[keyof ListViewSourceContractsResponses];

export type DeleteCmViewQuestionColumnData = {
  body?: never;
  path: {
    /**
     * View Column Uid
     */
    view_column_uid: string;
  };
  query?: {
    /**
     * Delete Unused Library Prompts
     */
    delete_unused_library_prompts?: boolean;
  };
  url: '/v1/app/contract-management/views/columns/{view_column_uid}';
};

export type DeleteCmViewQuestionColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteCmViewQuestionColumnError =
  DeleteCmViewQuestionColumnErrors[keyof DeleteCmViewQuestionColumnErrors];

export type DeleteCmViewQuestionColumnResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewColumnInDb;
};

export type DeleteCmViewQuestionColumnResponse =
  DeleteCmViewQuestionColumnResponses[keyof DeleteCmViewQuestionColumnResponses];

export type GetCmViewQuestionColumnData = {
  body?: never;
  path: {
    /**
     * View Column Uid
     */
    view_column_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/columns/{view_column_uid}';
};

export type GetCmViewQuestionColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCmViewQuestionColumnError =
  GetCmViewQuestionColumnErrors[keyof GetCmViewQuestionColumnErrors];

export type GetCmViewQuestionColumnResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewColumnInDb;
};

export type GetCmViewQuestionColumnResponse =
  GetCmViewQuestionColumnResponses[keyof GetCmViewQuestionColumnResponses];

export type UpdateCmViewQuestionColumnData = {
  body: ContractManagementViewColumnUpdate;
  path: {
    /**
     * View Column Uid
     */
    view_column_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/columns/{view_column_uid}';
};

export type UpdateCmViewQuestionColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateCmViewQuestionColumnError =
  UpdateCmViewQuestionColumnErrors[keyof UpdateCmViewQuestionColumnErrors];

export type UpdateCmViewQuestionColumnResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewColumnInDb;
};

export type UpdateCmViewQuestionColumnResponse =
  UpdateCmViewQuestionColumnResponses[keyof UpdateCmViewQuestionColumnResponses];

export type UpdateCmViewQuestionColumnQuestionData = {
  body: ContractManagementViewColumnQuestionCreate;
  path: {
    /**
     * View Column Uid
     */
    view_column_uid: string;
  };
  query: {
    extraction_scope: ContractManagementColumnExtractionScope;
  };
  url: '/v1/app/contract-management/views/columns/{view_column_uid}/question';
};

export type UpdateCmViewQuestionColumnQuestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateCmViewQuestionColumnQuestionError =
  UpdateCmViewQuestionColumnQuestionErrors[keyof UpdateCmViewQuestionColumnQuestionErrors];

export type UpdateCmViewQuestionColumnQuestionResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementViewColumnInDb;
};

export type UpdateCmViewQuestionColumnQuestionResponse =
  UpdateCmViewQuestionColumnQuestionResponses[keyof UpdateCmViewQuestionColumnQuestionResponses];

export type CreateContractManagementExtractionOverwriteData = {
  body: ContractManagementExtractionOverwriteCreate;
  path: {
    /**
     * Contract Management Extraction Uid
     */
    contract_management_extraction_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/overwrite/{contract_management_extraction_uid}/create';
};

export type CreateContractManagementExtractionOverwriteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateContractManagementExtractionOverwriteError =
  CreateContractManagementExtractionOverwriteErrors[keyof CreateContractManagementExtractionOverwriteErrors];

export type CreateContractManagementExtractionOverwriteResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementExtractionOverwriteInDb;
};

export type CreateContractManagementExtractionOverwriteResponse =
  CreateContractManagementExtractionOverwriteResponses[keyof CreateContractManagementExtractionOverwriteResponses];

export type DeleteContractManagementExtractionOverwriteData = {
  body?: never;
  path: {
    /**
     * Contract Management Extraction Uid
     */
    contract_management_extraction_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/overwrite/{contract_management_extraction_uid}';
};

export type DeleteContractManagementExtractionOverwriteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteContractManagementExtractionOverwriteError =
  DeleteContractManagementExtractionOverwriteErrors[keyof DeleteContractManagementExtractionOverwriteErrors];

export type DeleteContractManagementExtractionOverwriteResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementExtractionOverwriteInDb;
};

export type DeleteContractManagementExtractionOverwriteResponse =
  DeleteContractManagementExtractionOverwriteResponses[keyof DeleteContractManagementExtractionOverwriteResponses];

export type UpdateContractManagementExtractionOverwriteData = {
  body: ContractManagementExtractionOverwriteUpdate;
  path: {
    /**
     * Contract Management Extraction Uid
     */
    contract_management_extraction_uid: string;
  };
  query?: never;
  url: '/v1/app/contract-management/views/overwrite/{contract_management_extraction_uid}';
};

export type UpdateContractManagementExtractionOverwriteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateContractManagementExtractionOverwriteError =
  UpdateContractManagementExtractionOverwriteErrors[keyof UpdateContractManagementExtractionOverwriteErrors];

export type UpdateContractManagementExtractionOverwriteResponses = {
  /**
   * Successful Response
   */
  200: ContractManagementExtractionOverwriteInDb;
};

export type UpdateContractManagementExtractionOverwriteResponse =
  UpdateContractManagementExtractionOverwriteResponses[keyof UpdateContractManagementExtractionOverwriteResponses];

export type SearchClauseData = {
  body: ClauseSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/repository/clause/clause/search';
};

export type SearchClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchClauseError = SearchClauseErrors[keyof SearchClauseErrors];

export type SearchClauseResponses = {
  /**
   * Successful Response
   */
  200: ClauseSearchResult;
};

export type SearchClauseResponse = SearchClauseResponses[keyof SearchClauseResponses];

export type CreateClauseData = {
  body: ClauseCreate;
  path?: never;
  query?: never;
  url: '/v1/app/repository/clause/clause/new';
};

export type CreateClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateClauseError = CreateClauseErrors[keyof CreateClauseErrors];

export type CreateClauseResponses = {
  /**
   * Successful Response
   */
  200: ClauseInDb;
};

export type CreateClauseResponse = CreateClauseResponses[keyof CreateClauseResponses];

export type DeleteClauseData = {
  body?: never;
  path: {
    /**
     * Clause Uid
     */
    clause_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/clause/clause/{clause_uid}';
};

export type DeleteClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteClauseError = DeleteClauseErrors[keyof DeleteClauseErrors];

export type DeleteClauseResponses = {
  /**
   * Successful Response
   */
  200: ClauseInDb;
};

export type DeleteClauseResponse = DeleteClauseResponses[keyof DeleteClauseResponses];

export type GetClauseData = {
  body?: never;
  path: {
    /**
     * Clause Uid
     */
    clause_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/clause/clause/{clause_uid}';
};

export type GetClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetClauseError = GetClauseErrors[keyof GetClauseErrors];

export type GetClauseResponses = {
  /**
   * Successful Response
   */
  200: ClauseInDb;
};

export type GetClauseResponse = GetClauseResponses[keyof GetClauseResponses];

export type UpdateClauseData = {
  body: ClauseUpdate;
  path: {
    /**
     * Clause Uid
     */
    clause_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/clause/clause/{clause_uid}';
};

export type UpdateClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateClauseError = UpdateClauseErrors[keyof UpdateClauseErrors];

export type UpdateClauseResponses = {
  /**
   * Successful Response
   */
  200: ClauseInDb;
};

export type UpdateClauseResponse = UpdateClauseResponses[keyof UpdateClauseResponses];

export type SearchGaiaClauseData = {
  body: GaiaClauseSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/repository/clause/gaia-clause/search';
};

export type SearchGaiaClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchGaiaClauseError = SearchGaiaClauseErrors[keyof SearchGaiaClauseErrors];

export type SearchGaiaClauseResponses = {
  /**
   * Successful Response
   */
  200: GaiaClauseSearchResult;
};

export type SearchGaiaClauseResponse = SearchGaiaClauseResponses[keyof SearchGaiaClauseResponses];

export type CreateGaiaClauseData = {
  body: GaiaClauseCreate;
  path?: never;
  query?: never;
  url: '/v1/app/repository/clause/gaia-clause/new';
};

export type CreateGaiaClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateGaiaClauseError = CreateGaiaClauseErrors[keyof CreateGaiaClauseErrors];

export type CreateGaiaClauseResponses = {
  /**
   * Successful Response
   */
  200: GaiaClauseInDb;
};

export type CreateGaiaClauseResponse = CreateGaiaClauseResponses[keyof CreateGaiaClauseResponses];

export type DeleteGaiaClauseData = {
  body?: never;
  path: {
    /**
     * Gaia Clause Uid
     */
    gaia_clause_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/clause/gaia-clause/{gaia_clause_uid}';
};

export type DeleteGaiaClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteGaiaClauseError = DeleteGaiaClauseErrors[keyof DeleteGaiaClauseErrors];

export type DeleteGaiaClauseResponses = {
  /**
   * Successful Response
   */
  200: GaiaClauseInDb;
};

export type DeleteGaiaClauseResponse = DeleteGaiaClauseResponses[keyof DeleteGaiaClauseResponses];

export type GetGaiaClauseData = {
  body?: never;
  path: {
    /**
     * Gaia Clause Uid
     */
    gaia_clause_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/clause/gaia-clause/{gaia_clause_uid}';
};

export type GetGaiaClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetGaiaClauseError = GetGaiaClauseErrors[keyof GetGaiaClauseErrors];

export type GetGaiaClauseResponses = {
  /**
   * Successful Response
   */
  200: GaiaClauseInDb;
};

export type GetGaiaClauseResponse = GetGaiaClauseResponses[keyof GetGaiaClauseResponses];

export type UpdateGaiaClauseData = {
  body: GaiaClauseUpdate;
  path: {
    /**
     * Gaia Clause Uid
     */
    gaia_clause_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/clause/gaia-clause/{gaia_clause_uid}';
};

export type UpdateGaiaClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateGaiaClauseError = UpdateGaiaClauseErrors[keyof UpdateGaiaClauseErrors];

export type UpdateGaiaClauseResponses = {
  /**
   * Successful Response
   */
  200: GaiaClauseInDb;
};

export type UpdateGaiaClauseResponse = UpdateGaiaClauseResponses[keyof UpdateGaiaClauseResponses];

export type ReloadCacheData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/repository/prompt/data/reload-cache';
};

export type ReloadCacheResponses = {
  /**
   * Response Reload Cache V1 App Repository Prompt Data Reload Cache Post
   *
   * Successful Response
   */
  200: Array<PromptResponse>;
};

export type ReloadCacheResponse = ReloadCacheResponses[keyof ReloadCacheResponses];

export type GetSummaryFileLevelDefaultData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/repository/prompt/data/summary_file_level_default';
};

export type GetSummaryFileLevelDefaultResponses = {
  /**
   * Response Get Summary File Level Default V1 App Repository Prompt Data Summary File Level Default Get
   *
   * Successful Response
   */
  200: string;
};

export type GetSummaryFileLevelDefaultResponse =
  GetSummaryFileLevelDefaultResponses[keyof GetSummaryFileLevelDefaultResponses];

export type GetAllAvailablePromptsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/repository/prompt/data/all';
};

export type GetAllAvailablePromptsResponses = {
  /**
   * Response Get All Available Prompts V1 App Repository Prompt Data All Get
   *
   * Successful Response
   */
  200: Array<PromptResponse>;
};

export type GetAllAvailablePromptsResponse =
  GetAllAvailablePromptsResponses[keyof GetAllAvailablePromptsResponses];

export type UpdateLangfuseCollectionByPromptCollectionData = {
  /**
   * Environments
   */
  body: Array<'development' | 'staging' | 'production' | 'test'>;
  path: {
    /**
     * Collection Uid
     */
    collection_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/prompt/data/update-collection-by-prompt-collection/{collection_uid}';
};

export type UpdateLangfuseCollectionByPromptCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateLangfuseCollectionByPromptCollectionError =
  UpdateLangfuseCollectionByPromptCollectionErrors[keyof UpdateLangfuseCollectionByPromptCollectionErrors];

export type UpdateLangfuseCollectionByPromptCollectionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SearchPromptCollectionData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/search';
};

export type SearchPromptCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchPromptCollectionError =
  SearchPromptCollectionErrors[keyof SearchPromptCollectionErrors];

export type SearchPromptCollectionResponses = {
  /**
   * Successful Response
   */
  200: PromptCollectionSearchResult;
};

export type SearchPromptCollectionResponse =
  SearchPromptCollectionResponses[keyof SearchPromptCollectionResponses];

export type CreateCollectionData = {
  body: PromptCollectionCreate;
  path?: never;
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/new';
};

export type CreateCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateCollectionError = CreateCollectionErrors[keyof CreateCollectionErrors];

export type CreateCollectionResponses = {
  /**
   * Successful Response
   */
  200: PromptCollectionInDb;
};

export type CreateCollectionResponse = CreateCollectionResponses[keyof CreateCollectionResponses];

export type CreateCollectionAiData = {
  body: PromptCollectionCreateAi;
  path?: never;
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/new_ai';
};

export type CreateCollectionAiErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateCollectionAiError = CreateCollectionAiErrors[keyof CreateCollectionAiErrors];

export type CreateCollectionAiResponses = {
  /**
   * Successful Response
   */
  200: PromptCollectionInDb;
};

export type CreateCollectionAiResponse =
  CreateCollectionAiResponses[keyof CreateCollectionAiResponses];

export type DeleteCollectionData = {
  body?: never;
  path: {
    /**
     * Prompt Collection Uid
     */
    prompt_collection_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/{prompt_collection_uid}';
};

export type DeleteCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteCollectionError = DeleteCollectionErrors[keyof DeleteCollectionErrors];

export type DeleteCollectionResponses = {
  /**
   * Successful Response
   */
  200: PromptCollectionInDb;
};

export type DeleteCollectionResponse = DeleteCollectionResponses[keyof DeleteCollectionResponses];

export type UpdateCollectionData = {
  body: PromptCollectionUpdate;
  path: {
    /**
     * Prompt Collection Uid
     */
    prompt_collection_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/{prompt_collection_uid}';
};

export type UpdateCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateCollectionError = UpdateCollectionErrors[keyof UpdateCollectionErrors];

export type UpdateCollectionResponses = {
  /**
   * Successful Response
   */
  200: PromptCollectionInDb;
};

export type UpdateCollectionResponse = UpdateCollectionResponses[keyof UpdateCollectionResponses];

export type GetPromptCollectionPromptsData = {
  body?: never;
  path: {
    /**
     * Prompt Collection Uid
     */
    prompt_collection_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/{prompt_collection_uid}/prompts';
};

export type GetPromptCollectionPromptsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPromptCollectionPromptsError =
  GetPromptCollectionPromptsErrors[keyof GetPromptCollectionPromptsErrors];

export type GetPromptCollectionPromptsResponses = {
  /**
   * Response Get Prompt Collection Prompts V1 App Repository Prompt Prompt Collection  Prompt Collection Uid  Prompts Get
   *
   * Successful Response
   */
  200: Array<PromptInDb>;
};

export type GetPromptCollectionPromptsResponse =
  GetPromptCollectionPromptsResponses[keyof GetPromptCollectionPromptsResponses];

export type AddPromptToPromptCollectionData = {
  body: PromptCreate;
  path: {
    /**
     * Prompt Collection Uid
     */
    prompt_collection_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/{prompt_collection_uid}/prompts/add';
};

export type AddPromptToPromptCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddPromptToPromptCollectionError =
  AddPromptToPromptCollectionErrors[keyof AddPromptToPromptCollectionErrors];

export type AddPromptToPromptCollectionResponses = {
  /**
   * Successful Response
   */
  200: PromptInDb;
};

export type AddPromptToPromptCollectionResponse =
  AddPromptToPromptCollectionResponses[keyof AddPromptToPromptCollectionResponses];

export type DeletePromptFromPromptCollectionData = {
  body?: never;
  path: {
    /**
     * Prompt Collection Uid
     */
    prompt_collection_uid: string;
    /**
     * Prompt Uid
     */
    prompt_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/{prompt_collection_uid}/prompts/{prompt_uid}';
};

export type DeletePromptFromPromptCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePromptFromPromptCollectionError =
  DeletePromptFromPromptCollectionErrors[keyof DeletePromptFromPromptCollectionErrors];

export type DeletePromptFromPromptCollectionResponses = {
  /**
   * Successful Response
   */
  200: PromptInDb;
};

export type DeletePromptFromPromptCollectionResponse =
  DeletePromptFromPromptCollectionResponses[keyof DeletePromptFromPromptCollectionResponses];

export type UpdatePromptFromPromptCollectionData = {
  body: PromptUpdate;
  path: {
    /**
     * Prompt Collection Uid
     */
    prompt_collection_uid: string;
    /**
     * Prompt Uid
     */
    prompt_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/prompt/prompt-collection/{prompt_collection_uid}/prompts/{prompt_uid}';
};

export type UpdatePromptFromPromptCollectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePromptFromPromptCollectionError =
  UpdatePromptFromPromptCollectionErrors[keyof UpdatePromptFromPromptCollectionErrors];

export type UpdatePromptFromPromptCollectionResponses = {
  /**
   * Successful Response
   */
  200: PromptInDb;
};

export type UpdatePromptFromPromptCollectionResponse =
  UpdatePromptFromPromptCollectionResponses[keyof UpdatePromptFromPromptCollectionResponses];

export type GetVaultDirectoryData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Directory Uid
     */
    directory_uid?: string | null;
    /**
     * Sort Key
     */
    sort_key?:
      | 'name'
      | 'owner'
      | 'type'
      | 'created_at'
      | 'updated_at'
      | 'contract_state'
      | 'amendment_state';
    /**
     * Sort Direction
     */
    sort_direction?: 'asc' | 'desc';
    /**
     * Limit
     */
    limit?: number;
    /**
     * Offset
     */
    offset?: number;
    /**
     * Contract States
     */
    contract_states?: Array<EffectiveState> | null;
    /**
     * Amendment States
     */
    amendment_states?: Array<EffectiveState> | null;
  };
  url: '/v1/app/repository/vault/directory/directory';
};

export type GetVaultDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVaultDirectoryError = GetVaultDirectoryErrors[keyof GetVaultDirectoryErrors];

export type GetVaultDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDb;
};

export type GetVaultDirectoryResponse =
  GetVaultDirectoryResponses[keyof GetVaultDirectoryResponses];

export type CreateVaultDirectoryData = {
  body: VaultDirectoryCreateUser;
  path?: never;
  query?: never;
  url: '/v1/app/repository/vault/directory/directory';
};

export type CreateVaultDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateVaultDirectoryError =
  CreateVaultDirectoryErrors[keyof CreateVaultDirectoryErrors];

export type CreateVaultDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDb;
};

export type CreateVaultDirectoryResponse =
  CreateVaultDirectoryResponses[keyof CreateVaultDirectoryResponses];

export type GetSystemVaultRootData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Sort Key
     */
    sort_key?:
      | 'name'
      | 'owner'
      | 'type'
      | 'created_at'
      | 'updated_at'
      | 'contract_state'
      | 'amendment_state';
    /**
     * Sort Direction
     */
    sort_direction?: 'asc' | 'desc';
    /**
     * Limit
     */
    limit?: number;
    /**
     * Offset
     */
    offset?: number;
    /**
     * Contract States
     */
    contract_states?: Array<EffectiveState> | null;
    /**
     * Amendment States
     */
    amendment_states?: Array<EffectiveState> | null;
  };
  url: '/v1/app/repository/vault/directory/directory/system';
};

export type GetSystemVaultRootErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSystemVaultRootError = GetSystemVaultRootErrors[keyof GetSystemVaultRootErrors];

export type GetSystemVaultRootResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDb;
};

export type GetSystemVaultRootResponse =
  GetSystemVaultRootResponses[keyof GetSystemVaultRootResponses];

export type DeleteVaultDirectoryData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/directory/directory/{uid}';
};

export type DeleteVaultDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteVaultDirectoryError =
  DeleteVaultDirectoryErrors[keyof DeleteVaultDirectoryErrors];

export type DeleteVaultDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDbSlim;
};

export type DeleteVaultDirectoryResponse =
  DeleteVaultDirectoryResponses[keyof DeleteVaultDirectoryResponses];

export type UpdateVaultDirectoryData = {
  body: VaultDirectoryUpdateUser;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/directory/directory/{uid}';
};

export type UpdateVaultDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateVaultDirectoryError =
  UpdateVaultDirectoryErrors[keyof UpdateVaultDirectoryErrors];

export type UpdateVaultDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryInDb;
};

export type UpdateVaultDirectoryResponse =
  UpdateVaultDirectoryResponses[keyof UpdateVaultDirectoryResponses];

export type StartVaultDirectoryDownloadData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/directory/directory/{uid}/download';
};

export type StartVaultDirectoryDownloadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StartVaultDirectoryDownloadError =
  StartVaultDirectoryDownloadErrors[keyof StartVaultDirectoryDownloadErrors];

export type StartVaultDirectoryDownloadResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryDownloadStartResponse;
};

export type StartVaultDirectoryDownloadResponse =
  StartVaultDirectoryDownloadResponses[keyof StartVaultDirectoryDownloadResponses];

export type DownloadVaultDirectoryZipData = {
  body?: never;
  path: {
    /**
     * Artifact Uid
     */
    artifact_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/directory/download/{artifact_uid}';
};

export type DownloadVaultDirectoryZipErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DownloadVaultDirectoryZipError =
  DownloadVaultDirectoryZipErrors[keyof DownloadVaultDirectoryZipErrors];

export type DownloadVaultDirectoryZipResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateVaultFileData = {
  body: VaultFileCreateUser;
  path?: never;
  query?: never;
  url: '/v1/app/repository/vault/file';
};

export type CreateVaultFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateVaultFileError = CreateVaultFileErrors[keyof CreateVaultFileErrors];

export type CreateVaultFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDb;
};

export type CreateVaultFileResponse = CreateVaultFileResponses[keyof CreateVaultFileResponses];

export type DuplicateVaultFileNameData = {
  body: VaultFileDuplicateCheckRequest;
  path?: never;
  query?: never;
  url: '/v1/app/repository/vault/file/duplicate-vault-file-name';
};

export type DuplicateVaultFileNameErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DuplicateVaultFileNameError =
  DuplicateVaultFileNameErrors[keyof DuplicateVaultFileNameErrors];

export type DuplicateVaultFileNameResponses = {
  /**
   * Successful Response
   */
  200: VaultFileDuplicateCheckResponse;
};

export type DuplicateVaultFileNameResponse =
  DuplicateVaultFileNameResponses[keyof DuplicateVaultFileNameResponses];

export type GetVaultFileMetadataData = {
  body?: never;
  path: {
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{file_uid}/metadata';
};

export type GetVaultFileMetadataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVaultFileMetadataError =
  GetVaultFileMetadataErrors[keyof GetVaultFileMetadataErrors];

export type GetVaultFileMetadataResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDbOwner;
};

export type GetVaultFileMetadataResponse =
  GetVaultFileMetadataResponses[keyof GetVaultFileMetadataResponses];

export type DeleteVaultFileData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}';
};

export type DeleteVaultFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteVaultFileError = DeleteVaultFileErrors[keyof DeleteVaultFileErrors];

export type DeleteVaultFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDbOwner;
};

export type DeleteVaultFileResponse = DeleteVaultFileResponses[keyof DeleteVaultFileResponses];

export type UpdateVaultFileData = {
  body: VaultFileUpdateUser;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}';
};

export type UpdateVaultFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateVaultFileError = UpdateVaultFileErrors[keyof UpdateVaultFileErrors];

export type UpdateVaultFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDb;
};

export type UpdateVaultFileResponse = UpdateVaultFileResponses[keyof UpdateVaultFileResponses];

export type ConvertVaultFileToContractData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}/convert-to-contract';
};

export type ConvertVaultFileToContractErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConvertVaultFileToContractError =
  ConvertVaultFileToContractErrors[keyof ConvertVaultFileToContractErrors];

export type ConvertVaultFileToContractResponses = {
  /**
   * Successful Response
   */
  200: ConvertFileToContractAgentTaskInDb;
};

export type ConvertVaultFileToContractResponse =
  ConvertVaultFileToContractResponses[keyof ConvertVaultFileToContractResponses];

export type UnpackVaultFileData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}/unpack';
};

export type UnpackVaultFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UnpackVaultFileError = UnpackVaultFileErrors[keyof UnpackVaultFileErrors];

export type UnpackVaultFileResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryUnpackInDb;
};

export type UnpackVaultFileResponse = UnpackVaultFileResponses[keyof UnpackVaultFileResponses];

export type GetFileDataData = {
  body?: never;
  path: {
    /**
     * Revision Uid
     */
    revision_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/revision/{revision_uid}';
};

export type GetFileDataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFileDataError = GetFileDataErrors[keyof GetFileDataErrors];

export type GetFileDataResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetVaultFileByRevisionData = {
  body?: never;
  path: {
    /**
     * Revision Uid
     */
    revision_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/revision/{revision_uid}/file';
};

export type GetVaultFileByRevisionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVaultFileByRevisionError =
  GetVaultFileByRevisionErrors[keyof GetVaultFileByRevisionErrors];

export type GetVaultFileByRevisionResponses = {
  /**
   * Successful Response
   */
  200: VaultFileInDbSlim;
};

export type GetVaultFileByRevisionResponse =
  GetVaultFileByRevisionResponses[keyof GetVaultFileByRevisionResponses];

export type GetFilePreviewDataData = {
  body?: never;
  headers?: {
    /**
     * Range
     */
    Range?: string | null;
  };
  path: {
    /**
     * Revision Uid
     */
    revision_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/revision/{revision_uid}/preview';
};

export type GetFilePreviewDataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFilePreviewDataError = GetFilePreviewDataErrors[keyof GetFilePreviewDataErrors];

export type GetFilePreviewDataResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type HeadFilePreviewDataData = {
  body?: never;
  path: {
    /**
     * Revision Uid
     */
    revision_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/revision/{revision_uid}/preview';
};

export type HeadFilePreviewDataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type HeadFilePreviewDataError = HeadFilePreviewDataErrors[keyof HeadFilePreviewDataErrors];

export type HeadFilePreviewDataResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListVaultFileRevisionsData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: {
    /**
     * Limit
     */
    limit?: number;
    /**
     * Offset
     */
    offset?: number;
  };
  url: '/v1/app/repository/vault/file/{uid}/revisions';
};

export type ListVaultFileRevisionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListVaultFileRevisionsError =
  ListVaultFileRevisionsErrors[keyof ListVaultFileRevisionsErrors];

export type ListVaultFileRevisionsResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRevisionListResponse;
};

export type ListVaultFileRevisionsResponse =
  ListVaultFileRevisionsResponses[keyof ListVaultFileRevisionsResponses];

export type InitializeVaultFileRevisionUploadData = {
  body: VaultFileRevisionCreateUserInitialize;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}/revision/initialize';
};

export type InitializeVaultFileRevisionUploadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type InitializeVaultFileRevisionUploadError =
  InitializeVaultFileRevisionUploadErrors[keyof InitializeVaultFileRevisionUploadErrors];

export type InitializeVaultFileRevisionUploadResponses = {
  /**
   * Successful Response
   */
  200: VaultFileCreateUserInitializeResponse;
};

export type InitializeVaultFileRevisionUploadResponse =
  InitializeVaultFileRevisionUploadResponses[keyof InitializeVaultFileRevisionUploadResponses];

export type UploadVaultFileChunkData = {
  body: BodyUploadVaultFileChunkV1AppRepositoryVaultFileChunkUploadIdPost;
  path: {
    /**
     * Upload Id
     */
    upload_id: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/chunk/{upload_id}';
};

export type UploadVaultFileChunkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UploadVaultFileChunkError =
  UploadVaultFileChunkErrors[keyof UploadVaultFileChunkErrors];

export type UploadVaultFileChunkResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type UploadVaultFileChunkResponse =
  UploadVaultFileChunkResponses[keyof UploadVaultFileChunkResponses];

export type FinalizeVaultFileRevisionUploadData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * Upload Id
     */
    upload_id: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}/revision/finalize/{upload_id}';
};

export type FinalizeVaultFileRevisionUploadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type FinalizeVaultFileRevisionUploadError =
  FinalizeVaultFileRevisionUploadErrors[keyof FinalizeVaultFileRevisionUploadErrors];

export type FinalizeVaultFileRevisionUploadResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRevisionInDb;
};

export type FinalizeVaultFileRevisionUploadResponse =
  FinalizeVaultFileRevisionUploadResponses[keyof FinalizeVaultFileRevisionUploadResponses];

export type DeleteVaultFileRevisionData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * Revision Uid
     */
    revision_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}/revision/{revision_uid}';
};

export type DeleteVaultFileRevisionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteVaultFileRevisionError =
  DeleteVaultFileRevisionErrors[keyof DeleteVaultFileRevisionErrors];

export type DeleteVaultFileRevisionResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRevisionInDb;
};

export type DeleteVaultFileRevisionResponse =
  DeleteVaultFileRevisionResponses[keyof DeleteVaultFileRevisionResponses];

export type UpdateVaultFileRevisionData = {
  body: VaultFileRevisionUpdateUser;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * Revision Uid
     */
    revision_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}/revision/{revision_uid}';
};

export type UpdateVaultFileRevisionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateVaultFileRevisionError =
  UpdateVaultFileRevisionErrors[keyof UpdateVaultFileRevisionErrors];

export type UpdateVaultFileRevisionResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRevisionInDb;
};

export type UpdateVaultFileRevisionResponse =
  UpdateVaultFileRevisionResponses[keyof UpdateVaultFileRevisionResponses];

export type GetVaultFileRevisionDiffData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * Revision Uid
     */
    revision_uid: string;
  };
  query: {
    /**
     * Compare To Revision Uid
     */
    compare_to_revision_uid: string;
  };
  url: '/v1/app/repository/vault/file/{uid}/revision/{revision_uid}/diff';
};

export type GetVaultFileRevisionDiffErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVaultFileRevisionDiffError =
  GetVaultFileRevisionDiffErrors[keyof GetVaultFileRevisionDiffErrors];

export type GetVaultFileRevisionDiffResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRevisionDiff;
};

export type GetVaultFileRevisionDiffResponse =
  GetVaultFileRevisionDiffResponses[keyof GetVaultFileRevisionDiffResponses];

export type ConvertPdfToDocxData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/file/{uid}/convert-pdf-to-docx';
};

export type ConvertPdfToDocxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConvertPdfToDocxError = ConvertPdfToDocxErrors[keyof ConvertPdfToDocxErrors];

export type ConvertPdfToDocxResponses = {
  /**
   * Successful Response
   */
  202: ApplicationMessage;
};

export type ConvertPdfToDocxResponse = ConvertPdfToDocxResponses[keyof ConvertPdfToDocxResponses];

export type SearchVaultFileIntakeData = {
  body: VaultFileIntakeSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/repository/vault/intake/search';
};

export type SearchVaultFileIntakeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchVaultFileIntakeError =
  SearchVaultFileIntakeErrors[keyof SearchVaultFileIntakeErrors];

export type SearchVaultFileIntakeResponses = {
  /**
   * Successful Response
   */
  200: VaultFileIntakeSearchResult;
};

export type SearchVaultFileIntakeResponse =
  SearchVaultFileIntakeResponses[keyof SearchVaultFileIntakeResponses];

export type SortFolderToEquitySectionData = {
  body: SortFolderToEquitySectionCreate;
  path: {
    /**
     * Vault Directory Uid
     */
    vault_directory_uid: string;
  };
  query?: never;
  url: '/v1/app/repository/vault/sorting/sort_folder_to_equity_section/{vault_directory_uid}';
};

export type SortFolderToEquitySectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SortFolderToEquitySectionError =
  SortFolderToEquitySectionErrors[keyof SortFolderToEquitySectionErrors];

export type SortFolderToEquitySectionResponses = {
  /**
   * Response Sort Folder To Equity Section V1 App Repository Vault Sorting Sort Folder To Equity Section  Vault Directory Uid  Post
   *
   * Successful Response
   */
  200: {
    [key: string]: string;
  };
};

export type SortFolderToEquitySectionResponse =
  SortFolderToEquitySectionResponses[keyof SortFolderToEquitySectionResponses];

export type SearchVaultData = {
  body: VaultSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/repository/vault/search';
};

export type SearchVaultErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchVaultError = SearchVaultErrors[keyof SearchVaultErrors];

export type SearchVaultResponses = {
  /**
   * Successful Response
   */
  200: VaultSearchResult;
};

export type SearchVaultResponse = SearchVaultResponses[keyof SearchVaultResponses];

export type BulkMoveVaultItemsData = {
  body: VaultBulkMoveUser;
  path?: never;
  query?: never;
  url: '/v1/app/repository/vault/bulk-move';
};

export type BulkMoveVaultItemsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkMoveVaultItemsError = BulkMoveVaultItemsErrors[keyof BulkMoveVaultItemsErrors];

export type BulkMoveVaultItemsResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type BulkMoveVaultItemsResponse =
  BulkMoveVaultItemsResponses[keyof BulkMoveVaultItemsResponses];

export type BulkReassignVaultOwnerData = {
  body: VaultBulkReassignOwnerUser;
  path?: never;
  query?: never;
  url: '/v1/app/repository/vault/bulk-reassign-owner';
};

export type BulkReassignVaultOwnerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkReassignVaultOwnerError =
  BulkReassignVaultOwnerErrors[keyof BulkReassignVaultOwnerErrors];

export type BulkReassignVaultOwnerResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type BulkReassignVaultOwnerResponse =
  BulkReassignVaultOwnerResponses[keyof BulkReassignVaultOwnerResponses];

export type GetVaultAncestorsData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Kind
     */
    kind: 'directory' | 'contract' | 'file' | 'contract_file';
    /**
     * Uid
     */
    uid: string;
  };
  url: '/v1/app/repository/vault/ancestors';
};

export type GetVaultAncestorsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVaultAncestorsError = GetVaultAncestorsErrors[keyof GetVaultAncestorsErrors];

export type GetVaultAncestorsResponses = {
  /**
   * Response Get Vault Ancestors V1 App Repository Vault Ancestors Get
   *
   * Successful Response
   */
  200: Array<VaultAncestor>;
};

export type GetVaultAncestorsResponse =
  GetVaultAncestorsResponses[keyof GetVaultAncestorsResponses];

export type SearchAllowListData = {
  body: SearchQuery;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/allow-list/{intake_agent_uid}/search';
};

export type SearchAllowListErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchAllowListError = SearchAllowListErrors[keyof SearchAllowListErrors];

export type SearchAllowListResponses = {
  /**
   * Successful Response
   */
  200: AllowListSearchResult;
};

export type SearchAllowListResponse = SearchAllowListResponses[keyof SearchAllowListResponses];

export type CreateAllowListEntryData = {
  body: AllowListCreate;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/allow-list/{intake_agent_uid}/new';
};

export type CreateAllowListEntryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateAllowListEntryError =
  CreateAllowListEntryErrors[keyof CreateAllowListEntryErrors];

export type CreateAllowListEntryResponses = {
  /**
   * Successful Response
   */
  200: AllowListInDb;
};

export type CreateAllowListEntryResponse =
  CreateAllowListEntryResponses[keyof CreateAllowListEntryResponses];

export type DeleteAllowListEntryData = {
  body?: never;
  path: {
    /**
     * Allow List Entry Uid
     */
    allow_list_entry_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/allow-list/{allow_list_entry_uid}';
};

export type DeleteAllowListEntryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteAllowListEntryError =
  DeleteAllowListEntryErrors[keyof DeleteAllowListEntryErrors];

export type DeleteAllowListEntryResponses = {
  /**
   * Successful Response
   */
  200: AllowListInDb;
};

export type DeleteAllowListEntryResponse =
  DeleteAllowListEntryResponses[keyof DeleteAllowListEntryResponses];

export type UpdateAllowListEntryData = {
  body: AllowListUpdate;
  path: {
    /**
     * Allow List Entry Uid
     */
    allow_list_entry_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/allow-list/{allow_list_entry_uid}';
};

export type UpdateAllowListEntryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateAllowListEntryError =
  UpdateAllowListEntryErrors[keyof UpdateAllowListEntryErrors];

export type UpdateAllowListEntryResponses = {
  /**
   * Successful Response
   */
  200: AllowListInDb;
};

export type UpdateAllowListEntryResponse =
  UpdateAllowListEntryResponses[keyof UpdateAllowListEntryResponses];

export type SearchIntakeAgentData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/review/intake-agent/agent/search';
};

export type SearchIntakeAgentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchIntakeAgentError = SearchIntakeAgentErrors[keyof SearchIntakeAgentErrors];

export type SearchIntakeAgentResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentSearchResult;
};

export type SearchIntakeAgentResponse =
  SearchIntakeAgentResponses[keyof SearchIntakeAgentResponses];

export type CreateIntakeAgentData = {
  body: IntakeAgentCreate;
  path?: never;
  query?: never;
  url: '/v1/app/review/intake-agent/agent/new';
};

export type CreateIntakeAgentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateIntakeAgentError = CreateIntakeAgentErrors[keyof CreateIntakeAgentErrors];

export type CreateIntakeAgentResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentInDb;
};

export type CreateIntakeAgentResponse =
  CreateIntakeAgentResponses[keyof CreateIntakeAgentResponses];

export type DeleteIntakeAgentData = {
  body?: never;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}';
};

export type DeleteIntakeAgentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteIntakeAgentError = DeleteIntakeAgentErrors[keyof DeleteIntakeAgentErrors];

export type DeleteIntakeAgentResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentInDb;
};

export type DeleteIntakeAgentResponse =
  DeleteIntakeAgentResponses[keyof DeleteIntakeAgentResponses];

export type UpdateIntakeAgentData = {
  body: IntakeAgentUpdate;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}';
};

export type UpdateIntakeAgentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateIntakeAgentError = UpdateIntakeAgentErrors[keyof UpdateIntakeAgentErrors];

export type UpdateIntakeAgentResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentInDb;
};

export type UpdateIntakeAgentResponse =
  UpdateIntakeAgentResponses[keyof UpdateIntakeAgentResponses];

export type ConfigureSlackAgentData = {
  body: SlackAgentConfig;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}/slack';
};

export type ConfigureSlackAgentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConfigureSlackAgentError = ConfigureSlackAgentErrors[keyof ConfigureSlackAgentErrors];

export type ConfigureSlackAgentResponses = {
  /**
   * Successful Response
   */
  200: SlackAgentConfig;
};

export type ConfigureSlackAgentResponse =
  ConfigureSlackAgentResponses[keyof ConfigureSlackAgentResponses];

export type UpdateIntakeAgentPromptCollectionsData = {
  body: IntakeAgentUpdatePromptCollections;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}/prompt-collections';
};

export type UpdateIntakeAgentPromptCollectionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateIntakeAgentPromptCollectionsError =
  UpdateIntakeAgentPromptCollectionsErrors[keyof UpdateIntakeAgentPromptCollectionsErrors];

export type UpdateIntakeAgentPromptCollectionsResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentInDb;
};

export type UpdateIntakeAgentPromptCollectionsResponse =
  UpdateIntakeAgentPromptCollectionsResponses[keyof UpdateIntakeAgentPromptCollectionsResponses];

export type UpdateIntakeAgentPlaybooksData = {
  body: IntakeAgentUpdatePlaybooks;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}/playbooks';
};

export type UpdateIntakeAgentPlaybooksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateIntakeAgentPlaybooksError =
  UpdateIntakeAgentPlaybooksErrors[keyof UpdateIntakeAgentPlaybooksErrors];

export type UpdateIntakeAgentPlaybooksResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentInDb;
};

export type UpdateIntakeAgentPlaybooksResponse =
  UpdateIntakeAgentPlaybooksResponses[keyof UpdateIntakeAgentPlaybooksResponses];

export type GetJiraWebhookData = {
  body?: never;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}/jira-webhook';
};

export type GetJiraWebhookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetJiraWebhookError = GetJiraWebhookErrors[keyof GetJiraWebhookErrors];

export type GetJiraWebhookResponses = {
  /**
   * Successful Response
   */
  200: JiraWebhookConfig;
};

export type GetJiraWebhookResponse = GetJiraWebhookResponses[keyof GetJiraWebhookResponses];

export type EnableJiraWebhookData = {
  body?: never;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}/jira-webhook/enable';
};

export type EnableJiraWebhookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EnableJiraWebhookError = EnableJiraWebhookErrors[keyof EnableJiraWebhookErrors];

export type EnableJiraWebhookResponses = {
  /**
   * Successful Response
   */
  200: JiraWebhookConfig;
};

export type EnableJiraWebhookResponse =
  EnableJiraWebhookResponses[keyof EnableJiraWebhookResponses];

export type RotateJiraWebhookData = {
  body?: never;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}/jira-webhook/rotate';
};

export type RotateJiraWebhookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RotateJiraWebhookError = RotateJiraWebhookErrors[keyof RotateJiraWebhookErrors];

export type RotateJiraWebhookResponses = {
  /**
   * Successful Response
   */
  200: JiraWebhookConfig;
};

export type RotateJiraWebhookResponse =
  RotateJiraWebhookResponses[keyof RotateJiraWebhookResponses];

export type DisableJiraWebhookData = {
  body?: never;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/agent/{intake_agent_uid}/jira-webhook/disable';
};

export type DisableJiraWebhookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisableJiraWebhookError = DisableJiraWebhookErrors[keyof DisableJiraWebhookErrors];

export type DisableJiraWebhookResponses = {
  /**
   * Successful Response
   */
  200: JiraWebhookConfig;
};

export type DisableJiraWebhookResponse =
  DisableJiraWebhookResponses[keyof DisableJiraWebhookResponses];

export type SearchIntakeAgentLogByAgentUidData = {
  body: SearchQuery;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/logs/search/agent/{intake_agent_uid}';
};

export type SearchIntakeAgentLogByAgentUidErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchIntakeAgentLogByAgentUidError =
  SearchIntakeAgentLogByAgentUidErrors[keyof SearchIntakeAgentLogByAgentUidErrors];

export type SearchIntakeAgentLogByAgentUidResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentLogSearchResult;
};

export type SearchIntakeAgentLogByAgentUidResponse =
  SearchIntakeAgentLogByAgentUidResponses[keyof SearchIntakeAgentLogByAgentUidResponses];

export type SearchIntakeAgentLogAdminData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/review/intake-agent/logs/search/admin';
};

export type SearchIntakeAgentLogAdminErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchIntakeAgentLogAdminError =
  SearchIntakeAgentLogAdminErrors[keyof SearchIntakeAgentLogAdminErrors];

export type SearchIntakeAgentLogAdminResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentLogSearchResult;
};

export type SearchIntakeAgentLogAdminResponse =
  SearchIntakeAgentLogAdminResponses[keyof SearchIntakeAgentLogAdminResponses];

export type GetIntakeAgentLogDetailsData = {
  body?: never;
  path: {
    /**
     * Intake Agent Log Uid
     */
    intake_agent_log_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/logs/{intake_agent_log_uid}';
};

export type GetIntakeAgentLogDetailsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIntakeAgentLogDetailsError =
  GetIntakeAgentLogDetailsErrors[keyof GetIntakeAgentLogDetailsErrors];

export type GetIntakeAgentLogDetailsResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentLogInDb;
};

export type GetIntakeAgentLogDetailsResponse =
  GetIntakeAgentLogDetailsResponses[keyof GetIntakeAgentLogDetailsResponses];

export type UpdateIntakeAgentLogAdminData = {
  body: IntakeAgentLogUpdate;
  path: {
    /**
     * Intake Agent Log Uid
     */
    intake_agent_log_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/logs/{intake_agent_log_uid}';
};

export type UpdateIntakeAgentLogAdminErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateIntakeAgentLogAdminError =
  UpdateIntakeAgentLogAdminErrors[keyof UpdateIntakeAgentLogAdminErrors];

export type UpdateIntakeAgentLogAdminResponses = {
  /**
   * Successful Response
   */
  200: IntakeAgentLogInDb;
};

export type UpdateIntakeAgentLogAdminResponse =
  UpdateIntakeAgentLogAdminResponses[keyof UpdateIntakeAgentLogAdminResponses];

export type RestartIntakeAgentLogAdminData = {
  body?: never;
  path: {
    /**
     * Intake Agent Log Uid
     */
    intake_agent_log_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/logs/{intake_agent_log_uid}/restart';
};

export type RestartIntakeAgentLogAdminErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RestartIntakeAgentLogAdminError =
  RestartIntakeAgentLogAdminErrors[keyof RestartIntakeAgentLogAdminErrors];

export type RestartIntakeAgentLogAdminResponses = {
  /**
   * Response Restart Intake Agent Log Admin V1 App Review Intake Agent Logs  Intake Agent Log Uid  Restart Get
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type RestartIntakeAgentLogAdminResponse =
  RestartIntakeAgentLogAdminResponses[keyof RestartIntakeAgentLogAdminResponses];

export type SearchEquitySectionsData = {
  body: SearchQuery;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/equity-sections/{intake_agent_uid}/search';
};

export type SearchEquitySectionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchEquitySectionsError =
  SearchEquitySectionsErrors[keyof SearchEquitySectionsErrors];

export type SearchEquitySectionsResponses = {
  /**
   * Successful Response
   */
  200: EquitySectionSearchResult;
};

export type SearchEquitySectionsResponse =
  SearchEquitySectionsResponses[keyof SearchEquitySectionsResponses];

export type SetDefaultSectionData = {
  /**
   * New Data
   */
  body: EquitySectionCreateUser | null;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/equity-sections/{intake_agent_uid}/default';
};

export type SetDefaultSectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetDefaultSectionError = SetDefaultSectionErrors[keyof SetDefaultSectionErrors];

export type SetDefaultSectionResponses = {
  /**
   * Response Set Default Section V1 App Review Intake Agent Equity Sections  Intake Agent Uid  Default Post
   *
   * Successful Response
   */
  200: EquitySectionInDb | null;
};

export type SetDefaultSectionResponse =
  SetDefaultSectionResponses[keyof SetDefaultSectionResponses];

export type CreateSectionData = {
  body: EquitySectionCreateUser;
  path: {
    /**
     * Intake Agent Uid
     */
    intake_agent_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/equity-sections/{intake_agent_uid}/new';
};

export type CreateSectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateSectionError = CreateSectionErrors[keyof CreateSectionErrors];

export type CreateSectionResponses = {
  /**
   * Successful Response
   */
  200: EquitySectionInDb;
};

export type CreateSectionResponse = CreateSectionResponses[keyof CreateSectionResponses];

export type DeleteSectionData = {
  body?: never;
  path: {
    /**
     * Section Uid
     */
    section_uid: string;
  };
  query?: never;
  url: '/v1/app/review/intake-agent/equity-sections/{section_uid}';
};

export type DeleteSectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteSectionError = DeleteSectionErrors[keyof DeleteSectionErrors];

export type DeleteSectionResponses = {
  /**
   * Successful Response
   */
  200: EquitySectionInDb;
};

export type DeleteSectionResponse = DeleteSectionResponses[keyof DeleteSectionResponses];

export type GetDataRoomDirectoryData = {
  body?: never;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: {
    /**
     * Directory Uid
     */
    directory_uid?: string | null;
    /**
     * Sort Key
     */
    sort_key?: 'name' | 'type' | 'created_at' | 'updated_at';
    /**
     * Sort Direction
     */
    sort_direction?: 'asc' | 'desc';
    /**
     * Limit
     */
    limit?: number;
    /**
     * Offset
     */
    offset?: number;
  };
  url: '/v1/app/review/project/{external_uid}/external-access/data-room/directory';
};

export type GetDataRoomDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDataRoomDirectoryError =
  GetDataRoomDirectoryErrors[keyof GetDataRoomDirectoryErrors];

export type GetDataRoomDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryExternalInDb;
};

export type GetDataRoomDirectoryResponse =
  GetDataRoomDirectoryResponses[keyof GetDataRoomDirectoryResponses];

export type CreateDataRoomDirectoryData = {
  body: VaultDirectoryExternalCreate;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/data-room/directory';
};

export type CreateDataRoomDirectoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateDataRoomDirectoryError =
  CreateDataRoomDirectoryErrors[keyof CreateDataRoomDirectoryErrors];

export type CreateDataRoomDirectoryResponses = {
  /**
   * Successful Response
   */
  200: VaultDirectoryExternalInDbSlim;
};

export type CreateDataRoomDirectoryResponse =
  CreateDataRoomDirectoryResponses[keyof CreateDataRoomDirectoryResponses];

export type SearchDataRoomData = {
  body: SearchQuery;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/data-room/search';
};

export type SearchDataRoomErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchDataRoomError = SearchDataRoomErrors[keyof SearchDataRoomErrors];

export type SearchDataRoomResponses = {
  /**
   * Successful Response
   */
  200: VaultExternalSearchResult;
};

export type SearchDataRoomResponse = SearchDataRoomResponses[keyof SearchDataRoomResponses];

export type CreateDataRoomFileData = {
  body: VaultFileExternalCreate;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/data-room/file';
};

export type CreateDataRoomFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateDataRoomFileError = CreateDataRoomFileErrors[keyof CreateDataRoomFileErrors];

export type CreateDataRoomFileResponses = {
  /**
   * Successful Response
   */
  200: VaultFileExternalInDb;
};

export type CreateDataRoomFileResponse =
  CreateDataRoomFileResponses[keyof CreateDataRoomFileResponses];

export type InitializeDataRoomFileUploadData = {
  body: VaultFileRevisionCreateUserInitialize;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/data-room/file/{file_uid}/revision/initialize';
};

export type InitializeDataRoomFileUploadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type InitializeDataRoomFileUploadError =
  InitializeDataRoomFileUploadErrors[keyof InitializeDataRoomFileUploadErrors];

export type InitializeDataRoomFileUploadResponses = {
  /**
   * Successful Response
   */
  200: VaultFileCreateUserInitializeResponse;
};

export type InitializeDataRoomFileUploadResponse =
  InitializeDataRoomFileUploadResponses[keyof InitializeDataRoomFileUploadResponses];

export type UploadDataRoomFileChunkData = {
  body: BodyUploadDataRoomFileChunkV1AppReviewProjectExternalUidExternalAccessDataRoomFileChunkUploadIdPost;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
    /**
     * Upload Id
     */
    upload_id: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/data-room/file/chunk/{upload_id}';
};

export type UploadDataRoomFileChunkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UploadDataRoomFileChunkError =
  UploadDataRoomFileChunkErrors[keyof UploadDataRoomFileChunkErrors];

export type UploadDataRoomFileChunkResponses = {
  /**
   * Successful Response
   */
  200: ApplicationMessage;
};

export type UploadDataRoomFileChunkResponse =
  UploadDataRoomFileChunkResponses[keyof UploadDataRoomFileChunkResponses];

export type FinalizeDataRoomFileUploadData = {
  body?: never;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
    /**
     * File Uid
     */
    file_uid: string;
    /**
     * Upload Id
     */
    upload_id: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/data-room/file/{file_uid}/revision/finalize/{upload_id}';
};

export type FinalizeDataRoomFileUploadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type FinalizeDataRoomFileUploadError =
  FinalizeDataRoomFileUploadErrors[keyof FinalizeDataRoomFileUploadErrors];

export type FinalizeDataRoomFileUploadResponses = {
  /**
   * Successful Response
   */
  200: VaultFileRevisionExternalInDb;
};

export type FinalizeDataRoomFileUploadResponse =
  FinalizeDataRoomFileUploadResponses[keyof FinalizeDataRoomFileUploadResponses];

export type GetExternalAccessProjectMetaData = {
  body?: never;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/meta';
};

export type GetExternalAccessProjectMetaErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetExternalAccessProjectMetaError =
  GetExternalAccessProjectMetaErrors[keyof GetExternalAccessProjectMetaErrors];

export type GetExternalAccessProjectMetaResponses = {
  /**
   * Successful Response
   */
  200: ExternalAccessProjectMeta;
};

export type GetExternalAccessProjectMetaResponse =
  GetExternalAccessProjectMetaResponses[keyof GetExternalAccessProjectMetaResponses];

export type SearchReviewProjectQuestionsExternalData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/question/search';
};

export type SearchReviewProjectQuestionsExternalErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchReviewProjectQuestionsExternalError =
  SearchReviewProjectQuestionsExternalErrors[keyof SearchReviewProjectQuestionsExternalErrors];

export type SearchReviewProjectQuestionsExternalResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectQuestionExternalSearchResult;
};

export type SearchReviewProjectQuestionsExternalResponse =
  SearchReviewProjectQuestionsExternalResponses[keyof SearchReviewProjectQuestionsExternalResponses];

export type AnswerReviewProjectQuestionExternalData = {
  body: ReviewProjectQuestionAnswer;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
    /**
     * Question Uid
     */
    question_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/question/{question_uid}/answer';
};

export type AnswerReviewProjectQuestionExternalErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AnswerReviewProjectQuestionExternalError =
  AnswerReviewProjectQuestionExternalErrors[keyof AnswerReviewProjectQuestionExternalErrors];

export type AnswerReviewProjectQuestionExternalResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectQuestionExternalInDb;
};

export type AnswerReviewProjectQuestionExternalResponse =
  AnswerReviewProjectQuestionExternalResponses[keyof AnswerReviewProjectQuestionExternalResponses];

export type RequestExternalAccessLinkData = {
  body: ExternalAccessRequest;
  headers?: {
    /**
     * X-Recaptcha-Token
     */
    'X-Recaptcha-Token'?: string | null;
  };
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/request-link';
};

export type RequestExternalAccessLinkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RequestExternalAccessLinkError =
  RequestExternalAccessLinkErrors[keyof RequestExternalAccessLinkErrors];

export type RequestExternalAccessLinkResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type RequestExternalAccessLinkResponse =
  RequestExternalAccessLinkResponses[keyof RequestExternalAccessLinkResponses];

export type ExchangeExternalAccessTokenData = {
  body: ExternalAccessTokenExchange;
  headers?: {
    /**
     * X-Recaptcha-Token
     */
    'X-Recaptcha-Token'?: string | null;
  };
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/token';
};

export type ExchangeExternalAccessTokenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExchangeExternalAccessTokenError =
  ExchangeExternalAccessTokenErrors[keyof ExchangeExternalAccessTokenErrors];

export type ExchangeExternalAccessTokenResponses = {
  /**
   * Successful Response
   */
  200: ExternalAccessToken;
};

export type ExchangeExternalAccessTokenResponse =
  ExchangeExternalAccessTokenResponses[keyof ExchangeExternalAccessTokenResponses];

export type RefreshExternalAccessTokenData = {
  body?: never;
  path: {
    /**
     * External Uid
     */
    external_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{external_uid}/external-access/refresh';
};

export type RefreshExternalAccessTokenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RefreshExternalAccessTokenError =
  RefreshExternalAccessTokenErrors[keyof RefreshExternalAccessTokenErrors];

export type RefreshExternalAccessTokenResponses = {
  /**
   * Successful Response
   */
  200: ExternalAccessToken;
};

export type RefreshExternalAccessTokenResponse =
  RefreshExternalAccessTokenResponses[keyof RefreshExternalAccessTokenResponses];

export type CreateOverwriteData = {
  body: ReviewExtractionOverwriteCreate;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
    /**
     * Extraction Uid
     */
    extraction_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}/extraction/{extraction_uid}/overwrite';
};

export type CreateOverwriteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateOverwriteError = CreateOverwriteErrors[keyof CreateOverwriteErrors];

export type CreateOverwriteResponses = {
  /**
   * Successful Response
   */
  200: ReviewExtractionOverwriteInDb;
};

export type CreateOverwriteResponse = CreateOverwriteResponses[keyof CreateOverwriteResponses];

export type DeleteOverwriteData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
    /**
     * Extraction Uid
     */
    extraction_uid: string;
    /**
     * Extraction Overwrite Uid
     */
    extraction_overwrite_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}/extraction/{extraction_uid}/overwrite/{extraction_overwrite_uid}';
};

export type DeleteOverwriteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteOverwriteError = DeleteOverwriteErrors[keyof DeleteOverwriteErrors];

export type DeleteOverwriteResponses = {
  /**
   * Successful Response
   */
  200: ReviewExtractionOverwriteInDb;
};

export type DeleteOverwriteResponse = DeleteOverwriteResponses[keyof DeleteOverwriteResponses];

export type UpdateOverwriteData = {
  body: ReviewExtractionOverwriteUpdate;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
    /**
     * Extraction Uid
     */
    extraction_uid: string;
    /**
     * Extraction Overwrite Uid
     */
    extraction_overwrite_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}/extraction/{extraction_uid}/overwrite/{extraction_overwrite_uid}';
};

export type UpdateOverwriteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateOverwriteError = UpdateOverwriteErrors[keyof UpdateOverwriteErrors];

export type UpdateOverwriteResponses = {
  /**
   * Successful Response
   */
  200: ReviewExtractionOverwriteInDb;
};

export type UpdateOverwriteResponse = UpdateOverwriteResponses[keyof UpdateOverwriteResponses];

export type SearchExtractionsData = {
  body: SearchQuery;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}/extraction/search';
};

export type SearchExtractionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchExtractionsError = SearchExtractionsErrors[keyof SearchExtractionsErrors];

export type SearchExtractionsResponses = {
  /**
   * Successful Response
   */
  200: ReviewExtractionSearchResult;
};

export type SearchExtractionsResponse =
  SearchExtractionsResponses[keyof SearchExtractionsResponses];

export type GetExtractionByUidData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
    /**
     * Extraction Uid
     */
    extraction_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}/extraction/{extraction_uid}';
};

export type GetExtractionByUidErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetExtractionByUidError = GetExtractionByUidErrors[keyof GetExtractionByUidErrors];

export type GetExtractionByUidResponses = {
  /**
   * Successful Response
   */
  200: ReviewExtractionInDb;
};

export type GetExtractionByUidResponse =
  GetExtractionByUidResponses[keyof GetExtractionByUidResponses];

export type UpdateExtractionByUidData = {
  body: ReviewExtractionUpdateUser;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
    /**
     * Extraction Uid
     */
    extraction_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}/extraction/{extraction_uid}';
};

export type UpdateExtractionByUidErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateExtractionByUidError =
  UpdateExtractionByUidErrors[keyof UpdateExtractionByUidErrors];

export type UpdateExtractionByUidResponses = {
  /**
   * Successful Response
   */
  200: ReviewExtractionInDb;
};

export type UpdateExtractionByUidResponse =
  UpdateExtractionByUidResponses[keyof UpdateExtractionByUidResponses];

export type GenerateCommentedDocxData = {
  body: CommentingOptions;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}/extraction/generate-commented-docx';
};

export type GenerateCommentedDocxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenerateCommentedDocxError =
  GenerateCommentedDocxErrors[keyof GenerateCommentedDocxErrors];

export type GenerateCommentedDocxResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateFileReviewsData = {
  body: FileReviewCreateUser;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review';
};

export type CreateFileReviewsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateFileReviewsError = CreateFileReviewsErrors[keyof CreateFileReviewsErrors];

export type CreateFileReviewsResponses = {
  /**
   * Response Create File Reviews V1 App Review  Review Uid  File Review Post
   *
   * Successful Response
   */
  200: Array<FileReviewInDb>;
};

export type CreateFileReviewsResponse =
  CreateFileReviewsResponses[keyof CreateFileReviewsResponses];

export type SearchFileReviewData = {
  body: SearchQuery;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/search';
};

export type SearchFileReviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchFileReviewError = SearchFileReviewErrors[keyof SearchFileReviewErrors];

export type SearchFileReviewResponses = {
  /**
   * Successful Response
   */
  200: FileReviewSearchResult;
};

export type SearchFileReviewResponse = SearchFileReviewResponses[keyof SearchFileReviewResponses];

export type DeleteFileReviewData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}';
};

export type DeleteFileReviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteFileReviewError = DeleteFileReviewErrors[keyof DeleteFileReviewErrors];

export type DeleteFileReviewResponses = {
  /**
   * Successful Response
   */
  200: FileReviewInDb;
};

export type DeleteFileReviewResponse = DeleteFileReviewResponses[keyof DeleteFileReviewResponses];

export type GetFileReviewByUidData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}';
};

export type GetFileReviewByUidErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFileReviewByUidError = GetFileReviewByUidErrors[keyof GetFileReviewByUidErrors];

export type GetFileReviewByUidResponses = {
  /**
   * Successful Response
   */
  200: FileReviewInDb;
};

export type GetFileReviewByUidResponse =
  GetFileReviewByUidResponses[keyof GetFileReviewByUidResponses];

export type UpdateFileReviewData = {
  body: FileReviewUpdate;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
    /**
     * File Review Uid
     */
    file_review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/file-review/{file_review_uid}';
};

export type UpdateFileReviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateFileReviewError = UpdateFileReviewErrors[keyof UpdateFileReviewErrors];

export type UpdateFileReviewResponses = {
  /**
   * Successful Response
   */
  200: FileReviewInDb;
};

export type UpdateFileReviewResponse = UpdateFileReviewResponses[keyof UpdateFileReviewResponses];

export type SearchKanbanStateData = {
  body: SearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/review/kanban/kanban-state/search';
};

export type SearchKanbanStateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchKanbanStateError = SearchKanbanStateErrors[keyof SearchKanbanStateErrors];

export type SearchKanbanStateResponses = {
  /**
   * Successful Response
   */
  200: KanbanStateSearchResult;
};

export type SearchKanbanStateResponse =
  SearchKanbanStateResponses[keyof SearchKanbanStateResponses];

export type CreateKanbanStateData = {
  body: KanbanStateCreate;
  path?: never;
  query?: never;
  url: '/v1/app/review/kanban/kanban-state/new';
};

export type CreateKanbanStateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateKanbanStateError = CreateKanbanStateErrors[keyof CreateKanbanStateErrors];

export type CreateKanbanStateResponses = {
  /**
   * Successful Response
   */
  200: KanbanStateInDb;
};

export type CreateKanbanStateResponse =
  CreateKanbanStateResponses[keyof CreateKanbanStateResponses];

export type DeleteKanbanStateData = {
  body?: never;
  path: {
    /**
     * Kanban State Uid
     */
    kanban_state_uid: string;
  };
  query?: never;
  url: '/v1/app/review/kanban/kanban-state/{kanban_state_uid}';
};

export type DeleteKanbanStateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteKanbanStateError = DeleteKanbanStateErrors[keyof DeleteKanbanStateErrors];

export type DeleteKanbanStateResponses = {
  /**
   * Successful Response
   */
  200: KanbanStateInDb;
};

export type DeleteKanbanStateResponse =
  DeleteKanbanStateResponses[keyof DeleteKanbanStateResponses];

export type UpdateKanbanStateData = {
  body: KanbanStateUpdate;
  path: {
    /**
     * Kanban State Uid
     */
    kanban_state_uid: string;
  };
  query?: never;
  url: '/v1/app/review/kanban/kanban-state/{kanban_state_uid}';
};

export type UpdateKanbanStateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateKanbanStateError = UpdateKanbanStateErrors[keyof UpdateKanbanStateErrors];

export type UpdateKanbanStateResponses = {
  /**
   * Successful Response
   */
  200: KanbanStateInDb;
};

export type UpdateKanbanStateResponse =
  UpdateKanbanStateResponses[keyof UpdateKanbanStateResponses];

export type UpdateSortIndexesData = {
  body: KanbanStateUpdateSortIndexes;
  path: {
    /**
     * Kanban State Uid
     */
    kanban_state_uid: string;
  };
  query?: never;
  url: '/v1/app/review/kanban/kanban-state/{kanban_state_uid}/update-sort-indexes';
};

export type UpdateSortIndexesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateSortIndexesError = UpdateSortIndexesErrors[keyof UpdateSortIndexesErrors];

export type UpdateSortIndexesResponses = {
  /**
   * Successful Response
   */
  200: KanbanStateInDb;
};

export type UpdateSortIndexesResponse =
  UpdateSortIndexesResponses[keyof UpdateSortIndexesResponses];

export type SearchKanbanCommentData = {
  body: SearchQuery;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/kanban/kanban-comment/{review_uid}/search';
};

export type SearchKanbanCommentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchKanbanCommentError = SearchKanbanCommentErrors[keyof SearchKanbanCommentErrors];

export type SearchKanbanCommentResponses = {
  /**
   * Successful Response
   */
  200: KanbanCommentSearchResult;
};

export type SearchKanbanCommentResponse =
  SearchKanbanCommentResponses[keyof SearchKanbanCommentResponses];

export type CreateKanbanCommentData = {
  body: KanbanCommentCreateUser;
  path?: never;
  query?: never;
  url: '/v1/app/review/kanban/kanban-comment/new';
};

export type CreateKanbanCommentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateKanbanCommentError = CreateKanbanCommentErrors[keyof CreateKanbanCommentErrors];

export type CreateKanbanCommentResponses = {
  /**
   * Successful Response
   */
  200: KanbanCommentInDb;
};

export type CreateKanbanCommentResponse =
  CreateKanbanCommentResponses[keyof CreateKanbanCommentResponses];

export type DeleteKanbanCommentData = {
  body?: never;
  path: {
    /**
     * Kanban Comment Uid
     */
    kanban_comment_uid: string;
  };
  query?: never;
  url: '/v1/app/review/kanban/kanban-comment/{kanban_comment_uid}';
};

export type DeleteKanbanCommentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteKanbanCommentError = DeleteKanbanCommentErrors[keyof DeleteKanbanCommentErrors];

export type DeleteKanbanCommentResponses = {
  /**
   * Successful Response
   */
  200: KanbanCommentInDb;
};

export type DeleteKanbanCommentResponse =
  DeleteKanbanCommentResponses[keyof DeleteKanbanCommentResponses];

export type UpdateKanbanCommentData = {
  body: KanbanCommentUpdate;
  path: {
    /**
     * Kanban Comment Uid
     */
    kanban_comment_uid: string;
  };
  query?: never;
  url: '/v1/app/review/kanban/kanban-comment/{kanban_comment_uid}';
};

export type UpdateKanbanCommentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateKanbanCommentError = UpdateKanbanCommentErrors[keyof UpdateKanbanCommentErrors];

export type UpdateKanbanCommentResponses = {
  /**
   * Successful Response
   */
  200: KanbanCommentInDb;
};

export type UpdateKanbanCommentResponse =
  UpdateKanbanCommentResponses[keyof UpdateKanbanCommentResponses];

export type CreateReviewProjectData = {
  body: ReviewProjectCreateUser;
  path?: never;
  query?: never;
  url: '/v1/app/review/project/new';
};

export type CreateReviewProjectErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateReviewProjectError = CreateReviewProjectErrors[keyof CreateReviewProjectErrors];

export type CreateReviewProjectResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectInDb;
};

export type CreateReviewProjectResponse =
  CreateReviewProjectResponses[keyof CreateReviewProjectResponses];

export type DeleteReviewProjectData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}';
};

export type DeleteReviewProjectErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteReviewProjectError = DeleteReviewProjectErrors[keyof DeleteReviewProjectErrors];

export type DeleteReviewProjectResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectInDb;
};

export type DeleteReviewProjectResponse =
  DeleteReviewProjectResponses[keyof DeleteReviewProjectResponses];

export type GetReviewProjectData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}';
};

export type GetReviewProjectErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectError = GetReviewProjectErrors[keyof GetReviewProjectErrors];

export type GetReviewProjectResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectInDbDetail;
};

export type GetReviewProjectResponse = GetReviewProjectResponses[keyof GetReviewProjectResponses];

export type UpdateReviewProjectData = {
  body: ReviewProjectUpdate;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}';
};

export type UpdateReviewProjectErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateReviewProjectError = UpdateReviewProjectErrors[keyof UpdateReviewProjectErrors];

export type UpdateReviewProjectResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectInDb;
};

export type UpdateReviewProjectResponse =
  UpdateReviewProjectResponses[keyof UpdateReviewProjectResponses];

export type SearchReviewProjectsData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/review/project/search';
};

export type SearchReviewProjectsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchReviewProjectsError =
  SearchReviewProjectsErrors[keyof SearchReviewProjectsErrors];

export type SearchReviewProjectsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectSearchResult;
};

export type SearchReviewProjectsResponse =
  SearchReviewProjectsResponses[keyof SearchReviewProjectsResponses];

export type ListReviewProjectMembersData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}/members';
};

export type ListReviewProjectMembersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListReviewProjectMembersError =
  ListReviewProjectMembersErrors[keyof ListReviewProjectMembersErrors];

export type ListReviewProjectMembersResponses = {
  /**
   * Response List Review Project Members V1 App Review Project  Uid  Members Get
   *
   * Successful Response
   */
  200: Array<UserInDb>;
};

export type ListReviewProjectMembersResponse =
  ListReviewProjectMembersResponses[keyof ListReviewProjectMembersResponses];

export type RemoveReviewProjectMemberData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}/members/{user_uid}';
};

export type RemoveReviewProjectMemberErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveReviewProjectMemberError =
  RemoveReviewProjectMemberErrors[keyof RemoveReviewProjectMemberErrors];

export type RemoveReviewProjectMemberResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectInDb;
};

export type RemoveReviewProjectMemberResponse =
  RemoveReviewProjectMemberResponses[keyof RemoveReviewProjectMemberResponses];

export type AddReviewProjectMemberData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * User Uid
     */
    user_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}/members/{user_uid}';
};

export type AddReviewProjectMemberErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddReviewProjectMemberError =
  AddReviewProjectMemberErrors[keyof AddReviewProjectMemberErrors];

export type AddReviewProjectMemberResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectInDb;
};

export type AddReviewProjectMemberResponse =
  AddReviewProjectMemberResponses[keyof AddReviewProjectMemberResponses];

export type ListReviewProjectNonPlatformUsersData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}/non-platform-users';
};

export type ListReviewProjectNonPlatformUsersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListReviewProjectNonPlatformUsersError =
  ListReviewProjectNonPlatformUsersErrors[keyof ListReviewProjectNonPlatformUsersErrors];

export type ListReviewProjectNonPlatformUsersResponses = {
  /**
   * Response List Review Project Non Platform Users V1 App Review Project  Uid  Non Platform Users Get
   *
   * Successful Response
   */
  200: Array<ReviewProjectNonPlatformUserLinkInDb>;
};

export type ListReviewProjectNonPlatformUsersResponse =
  ListReviewProjectNonPlatformUsersResponses[keyof ListReviewProjectNonPlatformUsersResponses];

export type UpsertReviewProjectNonPlatformUserData = {
  body: ReviewProjectNonPlatformUserLinkUpsert;
  path: {
    /**
     * Uid
     */
    uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}/non-platform-users';
};

export type UpsertReviewProjectNonPlatformUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpsertReviewProjectNonPlatformUserError =
  UpsertReviewProjectNonPlatformUserErrors[keyof UpsertReviewProjectNonPlatformUserErrors];

export type UpsertReviewProjectNonPlatformUserResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectNonPlatformUserLinkInDb;
};

export type UpsertReviewProjectNonPlatformUserResponse =
  UpsertReviewProjectNonPlatformUserResponses[keyof UpsertReviewProjectNonPlatformUserResponses];

export type RemoveReviewProjectNonPlatformUserData = {
  body?: never;
  path: {
    /**
     * Uid
     */
    uid: string;
    /**
     * Non Platform User Uid
     */
    non_platform_user_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{uid}/non-platform-users/{non_platform_user_uid}';
};

export type RemoveReviewProjectNonPlatformUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveReviewProjectNonPlatformUserError =
  RemoveReviewProjectNonPlatformUserErrors[keyof RemoveReviewProjectNonPlatformUserErrors];

export type RemoveReviewProjectNonPlatformUserResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type RemoveReviewProjectNonPlatformUserResponse =
  RemoveReviewProjectNonPlatformUserResponses[keyof RemoveReviewProjectNonPlatformUserResponses];

export type ListReviewProjectAnalysesData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/analysis';
};

export type ListReviewProjectAnalysesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListReviewProjectAnalysesError =
  ListReviewProjectAnalysesErrors[keyof ListReviewProjectAnalysesErrors];

export type ListReviewProjectAnalysesResponses = {
  /**
   * Response List Review Project Analyses V1 App Review Project  Project Uid  Analysis Get
   *
   * Successful Response
   */
  200: Array<ReviewProjectAnalysisInDb>;
};

export type ListReviewProjectAnalysesResponse =
  ListReviewProjectAnalysesResponses[keyof ListReviewProjectAnalysesResponses];

export type CreateReviewProjectAnalysisData = {
  body: ReviewProjectAnalysisCreate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/analysis/new';
};

export type CreateReviewProjectAnalysisErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateReviewProjectAnalysisError =
  CreateReviewProjectAnalysisErrors[keyof CreateReviewProjectAnalysisErrors];

export type CreateReviewProjectAnalysisResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectAnalysisInDb;
};

export type CreateReviewProjectAnalysisResponse =
  CreateReviewProjectAnalysisResponses[keyof CreateReviewProjectAnalysisResponses];

export type DeleteReviewProjectAnalysisData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Analysis Uid
     */
    analysis_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/analysis/{analysis_uid}';
};

export type DeleteReviewProjectAnalysisErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteReviewProjectAnalysisError =
  DeleteReviewProjectAnalysisErrors[keyof DeleteReviewProjectAnalysisErrors];

export type DeleteReviewProjectAnalysisResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectAnalysisInDb;
};

export type DeleteReviewProjectAnalysisResponse =
  DeleteReviewProjectAnalysisResponses[keyof DeleteReviewProjectAnalysisResponses];

export type UpdateReviewProjectAnalysisData = {
  body: ReviewProjectAnalysisUpdate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Analysis Uid
     */
    analysis_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/analysis/{analysis_uid}';
};

export type UpdateReviewProjectAnalysisErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateReviewProjectAnalysisError =
  UpdateReviewProjectAnalysisErrors[keyof UpdateReviewProjectAnalysisErrors];

export type UpdateReviewProjectAnalysisResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectAnalysisInDb;
};

export type UpdateReviewProjectAnalysisResponse =
  UpdateReviewProjectAnalysisResponses[keyof UpdateReviewProjectAnalysisResponses];

export type SearchReviewProjectAnalysisDataData = {
  body: PlaybookRunSearchQuery;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Analysis Uid
     */
    analysis_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/analysis/{analysis_uid}/data/search';
};

export type SearchReviewProjectAnalysisDataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchReviewProjectAnalysisDataError =
  SearchReviewProjectAnalysisDataErrors[keyof SearchReviewProjectAnalysisDataErrors];

export type SearchReviewProjectAnalysisDataResponses = {
  /**
   * Successful Response
   */
  200: PlaybookRunSearchResult;
};

export type SearchReviewProjectAnalysisDataResponse =
  SearchReviewProjectAnalysisDataResponses[keyof SearchReviewProjectAnalysisDataResponses];

export type SearchReviewProjectFilesData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/file/search';
};

export type SearchReviewProjectFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchReviewProjectFilesError =
  SearchReviewProjectFilesErrors[keyof SearchReviewProjectFilesErrors];

export type SearchReviewProjectFilesResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectFileSearchResult;
};

export type SearchReviewProjectFilesResponse =
  SearchReviewProjectFilesResponses[keyof SearchReviewProjectFilesResponses];

export type UpdateReviewProjectFileData = {
  body: ReviewProjectFileUpdate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * File Uid
     */
    file_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/file/{file_uid}';
};

export type UpdateReviewProjectFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateReviewProjectFileError =
  UpdateReviewProjectFileErrors[keyof UpdateReviewProjectFileErrors];

export type UpdateReviewProjectFileResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectFileInDb;
};

export type UpdateReviewProjectFileResponse =
  UpdateReviewProjectFileResponses[keyof UpdateReviewProjectFileResponses];

export type MarkReviewProjectFilePageSeenData = {
  body: ReviewProjectFilePageSeenRequest;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Page Number
     */
    page_number: number;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/file/page/{page_number}/seen';
};

export type MarkReviewProjectFilePageSeenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MarkReviewProjectFilePageSeenError =
  MarkReviewProjectFilePageSeenErrors[keyof MarkReviewProjectFilePageSeenErrors];

export type MarkReviewProjectFilePageSeenResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectFilePageViewInDb;
};

export type MarkReviewProjectFilePageSeenResponse =
  MarkReviewProjectFilePageSeenResponses[keyof MarkReviewProjectFilePageSeenResponses];

export type SearchReviewProjectQuestionsData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/question/search';
};

export type SearchReviewProjectQuestionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchReviewProjectQuestionsError =
  SearchReviewProjectQuestionsErrors[keyof SearchReviewProjectQuestionsErrors];

export type SearchReviewProjectQuestionsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectQuestionSearchResult;
};

export type SearchReviewProjectQuestionsResponse =
  SearchReviewProjectQuestionsResponses[keyof SearchReviewProjectQuestionsResponses];

export type CreateReviewProjectQuestionData = {
  body: ReviewProjectQuestionCreate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/question/new';
};

export type CreateReviewProjectQuestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateReviewProjectQuestionError =
  CreateReviewProjectQuestionErrors[keyof CreateReviewProjectQuestionErrors];

export type CreateReviewProjectQuestionResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectQuestionInDb;
};

export type CreateReviewProjectQuestionResponse =
  CreateReviewProjectQuestionResponses[keyof CreateReviewProjectQuestionResponses];

export type DeleteReviewProjectQuestionData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Question Uid
     */
    question_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/question/{question_uid}';
};

export type DeleteReviewProjectQuestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteReviewProjectQuestionError =
  DeleteReviewProjectQuestionErrors[keyof DeleteReviewProjectQuestionErrors];

export type DeleteReviewProjectQuestionResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectQuestionInDb;
};

export type DeleteReviewProjectQuestionResponse =
  DeleteReviewProjectQuestionResponses[keyof DeleteReviewProjectQuestionResponses];

export type UpdateReviewProjectQuestionData = {
  body: ReviewProjectQuestionUpdate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Question Uid
     */
    question_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/question/{question_uid}';
};

export type UpdateReviewProjectQuestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateReviewProjectQuestionError =
  UpdateReviewProjectQuestionErrors[keyof UpdateReviewProjectQuestionErrors];

export type UpdateReviewProjectQuestionResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectQuestionInDb;
};

export type UpdateReviewProjectQuestionResponse =
  UpdateReviewProjectQuestionResponses[keyof UpdateReviewProjectQuestionResponses];

export type GetReviewProjectReportData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/report';
};

export type GetReviewProjectReportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectReportError =
  GetReviewProjectReportErrors[keyof GetReviewProjectReportErrors];

export type GetReviewProjectReportResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectReportInDb;
};

export type GetReviewProjectReportResponse =
  GetReviewProjectReportResponses[keyof GetReviewProjectReportResponses];

export type SetReviewProjectReportData = {
  body: ReviewProjectReportCreate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/report';
};

export type SetReviewProjectReportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetReviewProjectReportError =
  SetReviewProjectReportErrors[keyof SetReviewProjectReportErrors];

export type SetReviewProjectReportResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectReportInDb;
};

export type SetReviewProjectReportResponse =
  SetReviewProjectReportResponses[keyof SetReviewProjectReportResponses];

export type GetReviewProjectFileStatisticsData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/statistics/files';
};

export type GetReviewProjectFileStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectFileStatisticsError =
  GetReviewProjectFileStatisticsErrors[keyof GetReviewProjectFileStatisticsErrors];

export type GetReviewProjectFileStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectFileStatistics;
};

export type GetReviewProjectFileStatisticsResponse =
  GetReviewProjectFileStatisticsResponses[keyof GetReviewProjectFileStatisticsResponses];

export type GetReviewProjectSeenStatisticsData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/statistics/seen';
};

export type GetReviewProjectSeenStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectSeenStatisticsError =
  GetReviewProjectSeenStatisticsErrors[keyof GetReviewProjectSeenStatisticsErrors];

export type GetReviewProjectSeenStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectSeenStatistics;
};

export type GetReviewProjectSeenStatisticsResponse =
  GetReviewProjectSeenStatisticsResponses[keyof GetReviewProjectSeenStatisticsResponses];

export type GetReviewProjectTagStatisticsData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/statistics/tags';
};

export type GetReviewProjectTagStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectTagStatisticsError =
  GetReviewProjectTagStatisticsErrors[keyof GetReviewProjectTagStatisticsErrors];

export type GetReviewProjectTagStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectTagStatistics;
};

export type GetReviewProjectTagStatisticsResponse =
  GetReviewProjectTagStatisticsResponses[keyof GetReviewProjectTagStatisticsResponses];

export type GetReviewProjectQuestionStatisticsData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/statistics/questions';
};

export type GetReviewProjectQuestionStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectQuestionStatisticsError =
  GetReviewProjectQuestionStatisticsErrors[keyof GetReviewProjectQuestionStatisticsErrors];

export type GetReviewProjectQuestionStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectQuestionStatistics;
};

export type GetReviewProjectQuestionStatisticsResponse =
  GetReviewProjectQuestionStatisticsResponses[keyof GetReviewProjectQuestionStatisticsResponses];

export type GetReviewProjectAnalysisStatisticsData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/statistics/analyses';
};

export type GetReviewProjectAnalysisStatisticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectAnalysisStatisticsError =
  GetReviewProjectAnalysisStatisticsErrors[keyof GetReviewProjectAnalysisStatisticsErrors];

export type GetReviewProjectAnalysisStatisticsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectAnalysisStatistics;
};

export type GetReviewProjectAnalysisStatisticsResponse =
  GetReviewProjectAnalysisStatisticsResponses[keyof GetReviewProjectAnalysisStatisticsResponses];

export type SearchReviewProjectTagsData = {
  body: FilterableSearchQuery;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/tag/search';
};

export type SearchReviewProjectTagsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchReviewProjectTagsError =
  SearchReviewProjectTagsErrors[keyof SearchReviewProjectTagsErrors];

export type SearchReviewProjectTagsResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectTagSearchResult;
};

export type SearchReviewProjectTagsResponse =
  SearchReviewProjectTagsResponses[keyof SearchReviewProjectTagsResponses];

export type CreateReviewProjectTagData = {
  body: ReviewProjectTagCreate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/tag/new';
};

export type CreateReviewProjectTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateReviewProjectTagError =
  CreateReviewProjectTagErrors[keyof CreateReviewProjectTagErrors];

export type CreateReviewProjectTagResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectTagInDb;
};

export type CreateReviewProjectTagResponse =
  CreateReviewProjectTagResponses[keyof CreateReviewProjectTagResponses];

export type DeleteReviewProjectTagData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Tag Uid
     */
    tag_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/tag/{tag_uid}';
};

export type DeleteReviewProjectTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteReviewProjectTagError =
  DeleteReviewProjectTagErrors[keyof DeleteReviewProjectTagErrors];

export type DeleteReviewProjectTagResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectTagInDb;
};

export type DeleteReviewProjectTagResponse =
  DeleteReviewProjectTagResponses[keyof DeleteReviewProjectTagResponses];

export type UpdateReviewProjectTagData = {
  body: ReviewProjectTagUpdate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Tag Uid
     */
    tag_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/tag/{tag_uid}';
};

export type UpdateReviewProjectTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateReviewProjectTagError =
  UpdateReviewProjectTagErrors[keyof UpdateReviewProjectTagErrors];

export type UpdateReviewProjectTagResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectTagInDb;
};

export type UpdateReviewProjectTagResponse =
  UpdateReviewProjectTagResponses[keyof UpdateReviewProjectTagResponses];

export type CreateAutoTagTaskGroupData = {
  body: ReviewProjectAutoTagTaskGroupCreate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/task-group/auto-tag';
};

export type CreateAutoTagTaskGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateAutoTagTaskGroupError =
  CreateAutoTagTaskGroupErrors[keyof CreateAutoTagTaskGroupErrors];

export type CreateAutoTagTaskGroupResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectAutoTagTaskGroupInDb;
};

export type CreateAutoTagTaskGroupResponse =
  CreateAutoTagTaskGroupResponses[keyof CreateAutoTagTaskGroupResponses];

export type CreateReportTaskGroupData = {
  body: ReviewProjectReportTaskGroupCreate;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/task-group/report';
};

export type CreateReportTaskGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateReportTaskGroupError =
  CreateReportTaskGroupErrors[keyof CreateReportTaskGroupErrors];

export type CreateReportTaskGroupResponses = {
  /**
   * Successful Response
   */
  200: ReviewProjectReportTaskGroupInDb;
};

export type CreateReportTaskGroupResponse =
  CreateReportTaskGroupResponses[keyof CreateReportTaskGroupResponses];

export type ListReviewProjectTaskGroupsData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/task-group';
};

export type ListReviewProjectTaskGroupsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListReviewProjectTaskGroupsError =
  ListReviewProjectTaskGroupsErrors[keyof ListReviewProjectTaskGroupsErrors];

export type ListReviewProjectTaskGroupsResponses = {
  /**
   * Response List Review Project Task Groups V1 App Review Project  Project Uid  Task Group Get
   *
   * Successful Response
   */
  200: Array<
    | ({
        task_type: 'auto_tag';
      } & ReviewProjectAutoTagTaskGroupSummary)
    | ({
        task_type: 'report';
      } & ReviewProjectReportTaskGroupSummary)
  >;
};

export type ListReviewProjectTaskGroupsResponse =
  ListReviewProjectTaskGroupsResponses[keyof ListReviewProjectTaskGroupsResponses];

export type GetReviewProjectTaskGroupData = {
  body?: never;
  path: {
    /**
     * Project Uid
     */
    project_uid: string;
    /**
     * Task Group Uid
     */
    task_group_uid: string;
  };
  query?: never;
  url: '/v1/app/review/project/{project_uid}/task-group/{task_group_uid}';
};

export type GetReviewProjectTaskGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewProjectTaskGroupError =
  GetReviewProjectTaskGroupErrors[keyof GetReviewProjectTaskGroupErrors];

export type GetReviewProjectTaskGroupResponses = {
  /**
   * Response Get Review Project Task Group V1 App Review Project  Project Uid  Task Group  Task Group Uid  Get
   *
   * Successful Response
   */
  200:
    | ({
        task_type: 'auto_tag';
      } & ReviewProjectAutoTagTaskGroupInDb)
    | ({
        task_type: 'report';
      } & ReviewProjectReportTaskGroupInDb);
};

export type GetReviewProjectTaskGroupResponse =
  GetReviewProjectTaskGroupResponses[keyof GetReviewProjectTaskGroupResponses];

export type CreateReviewData = {
  body: ReviewCreateUser;
  path?: never;
  query?: never;
  url: '/v1/app/review';
};

export type CreateReviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateReviewError = CreateReviewErrors[keyof CreateReviewErrors];

export type CreateReviewResponses = {
  /**
   * Successful Response
   */
  200: ReviewInDb;
};

export type CreateReviewResponse = CreateReviewResponses[keyof CreateReviewResponses];

export type SearchReviewData = {
  body: ReviewSearchQuery;
  path?: never;
  query?: {
    /**
     * Include Hidden
     */
    include_hidden?: boolean;
  };
  url: '/v1/app/review/search';
};

export type SearchReviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchReviewError = SearchReviewErrors[keyof SearchReviewErrors];

export type SearchReviewResponses = {
  /**
   * Successful Response
   */
  200: ReviewSearchResult;
};

export type SearchReviewResponse = SearchReviewResponses[keyof SearchReviewResponses];

export type GetReviewCostData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Num Files
     */
    num_files: number;
    /**
     * Prompt Collection Uid
     */
    prompt_collection_uid?: string | null;
    /**
     * Review Uid
     */
    review_uid?: string | null;
  };
  url: '/v1/app/review/cost';
};

export type GetReviewCostErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewCostError = GetReviewCostErrors[keyof GetReviewCostErrors];

export type GetReviewCostResponses = {
  /**
   * Successful Response
   */
  200: ReviewCostResponse;
};

export type GetReviewCostResponse = GetReviewCostResponses[keyof GetReviewCostResponses];

export type DeleteReviewUuidData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}';
};

export type DeleteReviewUuidErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteReviewUuidError = DeleteReviewUuidErrors[keyof DeleteReviewUuidErrors];

export type DeleteReviewUuidResponses = {
  /**
   * Successful Response
   */
  200: ReviewInDb;
};

export type DeleteReviewUuidResponse = DeleteReviewUuidResponses[keyof DeleteReviewUuidResponses];

export type GetReviewByUidData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}';
};

export type GetReviewByUidErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewByUidError = GetReviewByUidErrors[keyof GetReviewByUidErrors];

export type GetReviewByUidResponses = {
  /**
   * Successful Response
   */
  200: ReviewInDb;
};

export type GetReviewByUidResponse = GetReviewByUidResponses[keyof GetReviewByUidResponses];

export type UpdateReviewData = {
  body: ReviewUpdateUser;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}';
};

export type UpdateReviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateReviewError = UpdateReviewErrors[keyof UpdateReviewErrors];

export type UpdateReviewResponses = {
  /**
   * Successful Response
   */
  200: ReviewInDb;
};

export type UpdateReviewResponse = UpdateReviewResponses[keyof UpdateReviewResponses];

export type GetReviewTableByUidData = {
  body: ReviewTableSearchQuery;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/table';
};

export type GetReviewTableByUidErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewTableByUidError = GetReviewTableByUidErrors[keyof GetReviewTableByUidErrors];

export type GetReviewTableByUidResponses = {
  /**
   * Successful Response
   */
  200: ReviewTableSearchResult;
};

export type GetReviewTableByUidResponse =
  GetReviewTableByUidResponses[keyof GetReviewTableByUidResponses];

export type GetReviewCsvExportData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: {
    /**
     * Include Prompt
     */
    include_prompt?: boolean;
    /**
     * Include Evaluation
     */
    include_evaluation?: boolean;
    /**
     * Include Reasons
     */
    include_reasons?: boolean;
  };
  url: '/v1/app/review/{review_uid}/csv';
};

export type GetReviewCsvExportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetReviewCsvExportError = GetReviewCsvExportErrors[keyof GetReviewCsvExportErrors];

export type GetReviewCsvExportResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DeletePromptColumnData = {
  body?: never;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query: {
    /**
     * Column Prompt Uid
     */
    column_prompt_uid: string;
  };
  url: '/v1/app/review/{review_uid}/column';
};

export type DeletePromptColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePromptColumnError = DeletePromptColumnErrors[keyof DeletePromptColumnErrors];

export type DeletePromptColumnResponses = {
  /**
   * Successful Response
   */
  200: ReviewInDb;
};

export type DeletePromptColumnResponse =
  DeletePromptColumnResponses[keyof DeletePromptColumnResponses];

export type AddPromptColumnData = {
  body: PromptInDb;
  path: {
    /**
     * Review Uid
     */
    review_uid: string;
  };
  query?: never;
  url: '/v1/app/review/{review_uid}/column';
};

export type AddPromptColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddPromptColumnError = AddPromptColumnErrors[keyof AddPromptColumnErrors];

export type AddPromptColumnResponses = {
  /**
   * Successful Response
   */
  200: ReviewInDb;
};

export type AddPromptColumnResponse = AddPromptColumnResponses[keyof AddPromptColumnResponses];

export type GetDocumentsData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/documents';
};

export type GetDocumentsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDocumentsError = GetDocumentsErrors[keyof GetDocumentsErrors];

export type GetDocumentsResponses = {
  /**
   * Response Get Documents V1 App Signing  Signing Request Uid  Documents Get
   *
   * Successful Response
   */
  200: Array<VaultFileSigningRequestInDb>;
};

export type GetDocumentsResponse = GetDocumentsResponses[keyof GetDocumentsResponses];

export type GetSignedDocumentsData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/signed-documents';
};

export type GetSignedDocumentsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSignedDocumentsError = GetSignedDocumentsErrors[keyof GetSignedDocumentsErrors];

export type GetSignedDocumentsResponses = {
  /**
   * Response Get Signed Documents V1 App Signing  Signing Request Uid  Signed Documents Get
   *
   * Successful Response
   */
  200: Array<SignedVaultFileRevisionInDb>;
};

export type GetSignedDocumentsResponse =
  GetSignedDocumentsResponses[keyof GetSignedDocumentsResponses];

export type AddDocumentsData = {
  /**
   * Request Schemas
   */
  body: Array<VaultFileRevisionSigningRequestCreate>;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/documents/add';
};

export type AddDocumentsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddDocumentsError = AddDocumentsErrors[keyof AddDocumentsErrors];

export type AddDocumentsResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type AddDocumentsResponse = AddDocumentsResponses[keyof AddDocumentsResponses];

export type DeleteDocumentData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
    /**
     * Vault File Revision Uid
     */
    vault_file_revision_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/documents/{vault_file_revision_uid}';
};

export type DeleteDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteDocumentError = DeleteDocumentErrors[keyof DeleteDocumentErrors];

export type DeleteDocumentResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type DeleteDocumentResponse = DeleteDocumentResponses[keyof DeleteDocumentResponses];

export type GetCombinedSignedDocumentData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: {
    /**
     * Acknowledge Signature Cleaning
     */
    acknowledge_signature_cleaning?: boolean;
  };
  url: '/v1/app/signing/{signing_request_uid}/signed-documents/combined';
};

export type GetCombinedSignedDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCombinedSignedDocumentError =
  GetCombinedSignedDocumentErrors[keyof GetCombinedSignedDocumentErrors];

export type GetCombinedSignedDocumentResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetFieldsData = {
  body?: never;
  path: {
    /**
     * Participant Uid
     */
    participant_uid: string;
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants/{participant_uid}/fields';
};

export type GetFieldsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFieldsError = GetFieldsErrors[keyof GetFieldsErrors];

export type GetFieldsResponses = {
  /**
   * Response Get Fields V1 App Signing  Signing Request Uid  Participants  Participant Uid  Fields Get
   *
   * Successful Response
   */
  200: Array<SignatureFieldInDb>;
};

export type GetFieldsResponse = GetFieldsResponses[keyof GetFieldsResponses];

export type CreateFieldData = {
  /**
   * Create Schema
   */
  body:
    | ({
        type: 'signature';
      } & YoutrustSignatureFieldCreate)
    | ({
        type: 'checkbox';
      } & CheckboxFieldCreate)
    | ({
        type: 'mention';
      } & MentionFieldCreate)
    | ({
        type: 'text';
      } & TextFieldCreate)
    | ({
        type: 'signature_date';
      } & SignatureDateFieldCreate)
    | ({
        type: 'signer_name';
      } & SignerNameFieldCreate)
    | ({
        type: 'signer_email';
      } & SignerEmailFieldCreate)
    | ({
        type: 'read_only_text';
      } & ReadOnlyTextFieldCreate);
  path: {
    /**
     * Participant Uid
     */
    participant_uid: string;
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants/{participant_uid}/fields/add';
};

export type CreateFieldErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateFieldError = CreateFieldErrors[keyof CreateFieldErrors];

export type CreateFieldResponses = {
  /**
   * Successful Response
   */
  200: SignatureFieldInDb;
};

export type CreateFieldResponse = CreateFieldResponses[keyof CreateFieldResponses];

export type DeleteFieldData = {
  body?: never;
  path: {
    /**
     * Signature Field Uid
     */
    signature_field_uid: string;
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
    /**
     * Participant Uid
     */
    participant_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants/{participant_uid}/fields/{signature_field_uid}';
};

export type DeleteFieldErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteFieldError = DeleteFieldErrors[keyof DeleteFieldErrors];

export type DeleteFieldResponses = {
  /**
   * Successful Response
   */
  200: SignatureFieldInDb;
};

export type DeleteFieldResponse = DeleteFieldResponses[keyof DeleteFieldResponses];

export type UpdateFieldData = {
  body: SignatureFieldUpdate;
  path: {
    /**
     * Signature Field Uid
     */
    signature_field_uid: string;
    /**
     * Participant Uid
     */
    participant_uid: string;
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants/{participant_uid}/fields/{signature_field_uid}';
};

export type UpdateFieldErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateFieldError = UpdateFieldErrors[keyof UpdateFieldErrors];

export type UpdateFieldResponses = {
  /**
   * Successful Response
   */
  200: SignatureFieldInDb;
};

export type UpdateFieldResponse = UpdateFieldResponses[keyof UpdateFieldResponses];

export type GetParticipantsData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants';
};

export type GetParticipantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetParticipantsError = GetParticipantsErrors[keyof GetParticipantsErrors];

export type GetParticipantsResponses = {
  /**
   * Response Get Participants V1 App Signing  Signing Request Uid  Participants Get
   *
   * Successful Response
   */
  200: Array<ParticipantInDb>;
};

export type GetParticipantsResponse = GetParticipantsResponses[keyof GetParticipantsResponses];

export type AddParticipantData = {
  body: CreateParticipants;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants/add';
};

export type AddParticipantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddParticipantError = AddParticipantErrors[keyof AddParticipantErrors];

export type AddParticipantResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type AddParticipantResponse = AddParticipantResponses[keyof AddParticipantResponses];

export type DeleteParticipantData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
    /**
     * Participant Uid
     */
    participant_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants/{participant_uid}';
};

export type DeleteParticipantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteParticipantError = DeleteParticipantErrors[keyof DeleteParticipantErrors];

export type DeleteParticipantResponses = {
  /**
   * Successful Response
   */
  200: ParticipantInDb;
};

export type DeleteParticipantResponse =
  DeleteParticipantResponses[keyof DeleteParticipantResponses];

export type SendParticipantReminderData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
    /**
     * Participant Uid
     */
    participant_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/participants/{participant_uid}/send_reminder';
};

export type SendParticipantReminderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SendParticipantReminderError =
  SendParticipantReminderErrors[keyof SendParticipantReminderErrors];

export type SendParticipantReminderResponses = {
  /**
   * Successful Response
   */
  200: ParticipantInDb;
};

export type SendParticipantReminderResponse =
  SendParticipantReminderResponses[keyof SendParticipantReminderResponses];

export type CreateSigningRequestData = {
  body: SigningRequestCreate;
  path?: never;
  query?: never;
  url: '/v1/app/signing/create';
};

export type CreateSigningRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateSigningRequestError =
  CreateSigningRequestErrors[keyof CreateSigningRequestErrors];

export type CreateSigningRequestResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type CreateSigningRequestResponse =
  CreateSigningRequestResponses[keyof CreateSigningRequestResponses];

export type DeleteSigningRequestData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}';
};

export type DeleteSigningRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteSigningRequestError =
  DeleteSigningRequestErrors[keyof DeleteSigningRequestErrors];

export type DeleteSigningRequestResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type DeleteSigningRequestResponse =
  DeleteSigningRequestResponses[keyof DeleteSigningRequestResponses];

export type GetSigningRequestData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}';
};

export type GetSigningRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSigningRequestError = GetSigningRequestErrors[keyof GetSigningRequestErrors];

export type GetSigningRequestResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type GetSigningRequestResponse =
  GetSigningRequestResponses[keyof GetSigningRequestResponses];

export type UpdateSigningRequestData = {
  body: SigningRequestUpdate;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}';
};

export type UpdateSigningRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateSigningRequestError =
  UpdateSigningRequestErrors[keyof UpdateSigningRequestErrors];

export type UpdateSigningRequestResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type UpdateSigningRequestResponse =
  UpdateSigningRequestResponses[keyof UpdateSigningRequestResponses];

export type ActivateSigningRequestData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/activate';
};

export type ActivateSigningRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ActivateSigningRequestError =
  ActivateSigningRequestErrors[keyof ActivateSigningRequestErrors];

export type ActivateSigningRequestResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type ActivateSigningRequestResponse =
  ActivateSigningRequestResponses[keyof ActivateSigningRequestResponses];

export type CancelSigningRequestData = {
  body: SendYoutrustCancelRequest;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/cancel';
};

export type CancelSigningRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CancelSigningRequestError =
  CancelSigningRequestErrors[keyof CancelSigningRequestErrors];

export type CancelSigningRequestResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestInDb;
};

export type CancelSigningRequestResponse =
  CancelSigningRequestResponses[keyof CancelSigningRequestResponses];

export type SearchSigningRequestData = {
  body: FilterableSearchQuery;
  path?: never;
  query?: {
    /**
     * Include Archived
     */
    include_archived?: boolean;
  };
  url: '/v1/app/signing/search';
};

export type SearchSigningRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchSigningRequestError =
  SearchSigningRequestErrors[keyof SearchSigningRequestErrors];

export type SearchSigningRequestResponses = {
  /**
   * Successful Response
   */
  200: SigningRequestSearchResult;
};

export type SearchSigningRequestResponse =
  SearchSigningRequestResponses[keyof SearchSigningRequestResponses];

export type GetProviderSigningRequestStatusData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/provider-status';
};

export type GetProviderSigningRequestStatusErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetProviderSigningRequestStatusError =
  GetProviderSigningRequestStatusErrors[keyof GetProviderSigningRequestStatusErrors];

export type GetProviderSigningRequestStatusResponses = {
  /**
   * Response Get Provider Signing Request Status V1 App Signing  Signing Request Uid  Provider Status Get
   *
   * Successful Response
   */
  200: string | null;
};

export type GetProviderSigningRequestStatusResponse =
  GetProviderSigningRequestStatusResponses[keyof GetProviderSigningRequestStatusResponses];

export type GetSigningRequestStatusHistoryData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/status-history';
};

export type GetSigningRequestStatusHistoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSigningRequestStatusHistoryError =
  GetSigningRequestStatusHistoryErrors[keyof GetSigningRequestStatusHistoryErrors];

export type GetSigningRequestStatusHistoryResponses = {
  /**
   * Response Get Signing Request Status History V1 App Signing  Signing Request Uid  Status History Get
   *
   * Successful Response
   */
  200: Array<SigningRequestStatusEntry>;
};

export type GetSigningRequestStatusHistoryResponse =
  GetSigningRequestStatusHistoryResponses[keyof GetSigningRequestStatusHistoryResponses];

export type GetAuditTrailData = {
  body?: never;
  path: {
    /**
     * Signing Request Uid
     */
    signing_request_uid: string;
  };
  query?: never;
  url: '/v1/app/signing/{signing_request_uid}/audit-trail';
};

export type GetAuditTrailErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAuditTrailError = GetAuditTrailErrors[keyof GetAuditTrailErrors];

export type GetAuditTrailResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetAvailableLlmProvidersData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/information/llm/available';
};

export type GetAvailableLlmProvidersResponses = {
  /**
   * Response Get Available Llm Providers V1 App Technical Information Llm Available Get
   *
   * Successful Response
   */
  200: {
    [key in LlmProvider]?: LlmProviderSpec;
  };
};

export type GetAvailableLlmProvidersResponse =
  GetAvailableLlmProvidersResponses[keyof GetAvailableLlmProvidersResponses];

export type GetAvailableOcrProvidersData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/information/ocr/available';
};

export type GetAvailableOcrProvidersResponses = {
  /**
   * Response Get Available Ocr Providers V1 App Technical Information Ocr Available Get
   *
   * Successful Response
   */
  200: {
    [key in OcrProvider]?: OcrProviderSpec;
  };
};

export type GetAvailableOcrProvidersResponse =
  GetAvailableOcrProvidersResponses[keyof GetAvailableOcrProvidersResponses];

export type GetAvailableCountriesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/information/countries/available';
};

export type GetAvailableCountriesResponses = {
  /**
   * Response Get Available Countries V1 App Technical Information Countries Available Get
   *
   * Successful Response
   */
  200: Array<Country>;
};

export type GetAvailableCountriesResponse =
  GetAvailableCountriesResponses[keyof GetAvailableCountriesResponses];

export type GetFileLimitsData = {
  body?: never;
  path?: never;
  query: {
    provider: SigningProvider;
  };
  url: '/v1/app/technical/information/signing/file-limits';
};

export type GetFileLimitsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetFileLimitsError = GetFileLimitsErrors[keyof GetFileLimitsErrors];

export type GetFileLimitsResponses = {
  /**
   * Successful Response
   */
  200: ProviderFileLimit;
};

export type GetFileLimitsResponse = GetFileLimitsResponses[keyof GetFileLimitsResponses];

export type GetSignerNamePatternData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/information/signing/name-pattern';
};

export type GetSignerNamePatternResponses = {
  /**
   * Successful Response
   */
  200: SignerNamePattern;
};

export type GetSignerNamePatternResponse =
  GetSignerNamePatternResponses[keyof GetSignerNamePatternResponses];

export type EmailIntakeData = {
  body: EmailSubmitRequest;
  path?: never;
  query?: never;
  url: '/v1/app/technical/webhooks/brevo/email-intake';
};

export type EmailIntakeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EmailIntakeError = EmailIntakeErrors[keyof EmailIntakeErrors];

export type EmailIntakeResponses = {
  /**
   * Response Email Intake V1 App Technical Webhooks Brevo Email Intake Post
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type EmailIntakeResponse = EmailIntakeResponses[keyof EmailIntakeResponses];

export type CreateAgentRunData = {
  body: CreateAgentRunRequest;
  headers: {
    /**
     * X-Auth
     *
     * Authentication token for the intake agent. This token identifies which agentthe request is targeting and must be provided in the X-Auth header.
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/v1/app/technical/webhooks/agent/create_agent_run';
};

export type CreateAgentRunErrors = {
  /**
   * Bad request - validation error or invalid data
   */
  400: unknown;
  /**
   * Unauthorized - agent does not allow API requests
   */
  401: unknown;
  /**
   * Not found - agent not found or no attachments provided
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
  /**
   * Internal server error during processing
   */
  500: unknown;
};

export type CreateAgentRunError = CreateAgentRunErrors[keyof CreateAgentRunErrors];

export type CreateAgentRunResponses = {
  /**
   * Successfully processed agent API request
   */
  200: AgentApiRequestResponse;
};

export type CreateAgentRunResponse = CreateAgentRunResponses[keyof CreateAgentRunResponses];

export type JiraIntakeData = {
  body?: never;
  path: {
    /**
     * Token
     */
    token: string;
  };
  query?: never;
  url: '/v1/app/technical/webhooks/jira/{token}/intake';
};

export type JiraIntakeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type JiraIntakeError = JiraIntakeErrors[keyof JiraIntakeErrors];

export type JiraIntakeResponses = {
  /**
   * Response Jira Intake V1 App Technical Webhooks Jira  Token  Intake Post
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type JiraIntakeResponse = JiraIntakeResponses[keyof JiraIntakeResponses];

export type SlackEventsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/webhooks/slack/events';
};

export type SlackEventsResponses = {
  /**
   * Response Slack Events V1 App Technical Webhooks Slack Events Post
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type SlackEventsResponse = SlackEventsResponses[keyof SlackEventsResponses];

export type SlackCommandsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/webhooks/slack/commands';
};

export type SlackCommandsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SlackInteractionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/webhooks/slack/interactions';
};

export type SlackInteractionsResponses = {
  /**
   * Response Slack Interactions V1 App Technical Webhooks Slack Interactions Post
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type SlackInteractionsResponse =
  SlackInteractionsResponses[keyof SlackInteractionsResponses];

export type YoutrustWebhookIntakeData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/webhooks/youtrust/intake';
};

export type YoutrustWebhookIntakeResponses = {
  /**
   * Response Youtrust Webhook Intake V1 App Technical Webhooks Youtrust Intake Post
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type YoutrustWebhookIntakeResponse =
  YoutrustWebhookIntakeResponses[keyof YoutrustWebhookIntakeResponses];

export type GaiaYoutrustWebhookIntakeData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/webhooks/gaia_youtrust/intake';
};

export type GaiaYoutrustWebhookIntakeResponses = {
  /**
   * Response Gaia Youtrust Webhook Intake V1 App Technical Webhooks Gaia Youtrust Intake Post
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GaiaYoutrustWebhookIntakeResponse =
  GaiaYoutrustWebhookIntakeResponses[keyof GaiaYoutrustWebhookIntakeResponses];

export type CreateWebsocketTicketData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/technical/websocket/ticket';
};

export type CreateWebsocketTicketResponses = {
  /**
   * Successful Response
   */
  200: WebsocketTicket;
};

export type CreateWebsocketTicketResponse =
  CreateWebsocketTicketResponses[keyof CreateWebsocketTicketResponses];

export type ExposeWebsocketSchemasData = {
  /**
   * Request
   */
  body:
    | WebsocketHeartbeatMessage
    | WebsocketSubscriptionMessage
    | WebsocketListSubscriptionsMessage
    | WebsocketEventMessage
    | WebsocketStatusMessage
    | WebsocketGeneralChatStartMessage
    | WebsocketGeneralChatResumeMessage
    | WebsocketTemplatingChatStartMessage
    | WebsocketTemplatingChatResumeMessage
    | WebsocketReviewProjectChatStartMessage
    | WebsocketReviewProjectChatResumeMessage
    | WebsocketChatCancelMessage
    | WebsocketChatThreadInfoMessage
    | WebsocketChatTokenMessage
    | WebsocketChatStatusMessage
    | WebsocketChatInterruptMessage
    | WebsocketChatDoneMessage
    | WebsocketChatErrorMessage;
  path?: never;
  query?: never;
  url: '/v1/app/technical/websocket/schemas';
};

export type ExposeWebsocketSchemasErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExposeWebsocketSchemasError =
  ExposeWebsocketSchemasErrors[keyof ExposeWebsocketSchemasErrors];

export type ExposeWebsocketSchemasResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ExposeToolConfirmationSchemasData = {
  body: BodyExposeToolConfirmationSchemasV1AppTechnicalWebsocketToolConfirmationSchemasPost;
  path?: never;
  query?: never;
  url: '/v1/app/technical/websocket/tool-confirmation-schemas';
};

export type ExposeToolConfirmationSchemasErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExposeToolConfirmationSchemasError =
  ExposeToolConfirmationSchemasErrors[keyof ExposeToolConfirmationSchemasErrors];

export type ExposeToolConfirmationSchemasResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ExposeUserQuestionSchemasData = {
  body: BodyExposeUserQuestionSchemasV1AppTechnicalWebsocketUserQuestionSchemasPost;
  path?: never;
  query?: never;
  url: '/v1/app/technical/websocket/user-question-schemas';
};

export type ExposeUserQuestionSchemasErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExposeUserQuestionSchemasError =
  ExposeUserQuestionSchemasErrors[keyof ExposeUserQuestionSchemasErrors];

export type ExposeUserQuestionSchemasResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SearchThreadsData = {
  body: LimitableSearchableQuery;
  path?: never;
  query?: {
    /**
     * Include Archived
     */
    include_archived?: boolean;
  };
  url: '/v1/app/chat/general/search';
};

export type SearchThreadsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchThreadsError = SearchThreadsErrors[keyof SearchThreadsErrors];

export type SearchThreadsResponses = {
  /**
   * Successful Response
   */
  200: ChatThreadSearchResult;
};

export type SearchThreadsResponse = SearchThreadsResponses[keyof SearchThreadsResponses];

export type DeleteThreadData = {
  body?: never;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/general/{thread_uid}';
};

export type DeleteThreadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteThreadError = DeleteThreadErrors[keyof DeleteThreadErrors];

export type DeleteThreadResponses = {
  /**
   * Successful Response
   */
  200: ChatThreadInDb;
};

export type DeleteThreadResponse = DeleteThreadResponses[keyof DeleteThreadResponses];

export type GetThreadByIdData = {
  body?: never;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/general/{thread_uid}';
};

export type GetThreadByIdErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetThreadByIdError = GetThreadByIdErrors[keyof GetThreadByIdErrors];

export type GetThreadByIdResponses = {
  /**
   * Successful Response
   */
  200: ChatThreadWithMessages;
};

export type GetThreadByIdResponse = GetThreadByIdResponses[keyof GetThreadByIdResponses];

export type UpdateThreadData = {
  body: ChatThreadUpdate;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/general/{thread_uid}';
};

export type UpdateThreadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateThreadError = UpdateThreadErrors[keyof UpdateThreadErrors];

export type UpdateThreadResponses = {
  /**
   * Successful Response
   */
  200: ChatThreadInDb;
};

export type UpdateThreadResponse = UpdateThreadResponses[keyof UpdateThreadResponses];

export type SearchThreads2Data = {
  body: LimitableSearchableQuery;
  path?: never;
  query?: {
    /**
     * Include Archived
     */
    include_archived?: boolean;
  };
  url: '/v1/app/chat/templating/search';
};

export type SearchThreads2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchThreads2Error = SearchThreads2Errors[keyof SearchThreads2Errors];

export type SearchThreads2Responses = {
  /**
   * Successful Response
   */
  200: ChatThreadSearchResult;
};

export type SearchThreads2Response = SearchThreads2Responses[keyof SearchThreads2Responses];

export type DeleteThread2Data = {
  body?: never;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/templating/{thread_uid}';
};

export type DeleteThread2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteThread2Error = DeleteThread2Errors[keyof DeleteThread2Errors];

export type DeleteThread2Responses = {
  /**
   * Successful Response
   */
  200: ChatThreadInDb;
};

export type DeleteThread2Response = DeleteThread2Responses[keyof DeleteThread2Responses];

export type GetThreadById2Data = {
  body?: never;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/templating/{thread_uid}';
};

export type GetThreadById2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetThreadById2Error = GetThreadById2Errors[keyof GetThreadById2Errors];

export type GetThreadById2Responses = {
  /**
   * Successful Response
   */
  200: ChatThreadWithMessages;
};

export type GetThreadById2Response = GetThreadById2Responses[keyof GetThreadById2Responses];

export type UpdateThread2Data = {
  body: ChatThreadUpdate;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/templating/{thread_uid}';
};

export type UpdateThread2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateThread2Error = UpdateThread2Errors[keyof UpdateThread2Errors];

export type UpdateThread2Responses = {
  /**
   * Successful Response
   */
  200: ChatThreadInDb;
};

export type UpdateThread2Response = UpdateThread2Responses[keyof UpdateThread2Responses];

export type SearchProjectThreadsData = {
  body: LimitableSearchableQuery;
  path?: never;
  query: {
    /**
     * Review Project Uid
     */
    review_project_uid: string;
    /**
     * Include Archived
     */
    include_archived?: boolean;
  };
  url: '/v1/app/chat/review-project/search';
};

export type SearchProjectThreadsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchProjectThreadsError =
  SearchProjectThreadsErrors[keyof SearchProjectThreadsErrors];

export type SearchProjectThreadsResponses = {
  /**
   * Successful Response
   */
  200: ChatThreadSearchResult;
};

export type SearchProjectThreadsResponse =
  SearchProjectThreadsResponses[keyof SearchProjectThreadsResponses];

export type DeleteThread3Data = {
  body?: never;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/review-project/{thread_uid}';
};

export type DeleteThread3Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteThread3Error = DeleteThread3Errors[keyof DeleteThread3Errors];

export type DeleteThread3Responses = {
  /**
   * Successful Response
   */
  200: ChatThreadInDb;
};

export type DeleteThread3Response = DeleteThread3Responses[keyof DeleteThread3Responses];

export type GetThreadById3Data = {
  body?: never;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/review-project/{thread_uid}';
};

export type GetThreadById3Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetThreadById3Error = GetThreadById3Errors[keyof GetThreadById3Errors];

export type GetThreadById3Responses = {
  /**
   * Successful Response
   */
  200: ChatThreadWithMessages;
};

export type GetThreadById3Response = GetThreadById3Responses[keyof GetThreadById3Responses];

export type UpdateThread3Data = {
  body: ChatThreadUpdate;
  path: {
    /**
     * Thread Uid
     */
    thread_uid: string;
  };
  query?: never;
  url: '/v1/app/chat/review-project/{thread_uid}';
};

export type UpdateThread3Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateThread3Error = UpdateThread3Errors[keyof UpdateThread3Errors];

export type UpdateThread3Responses = {
  /**
   * Successful Response
   */
  200: ChatThreadInDb;
};

export type UpdateThread3Response = UpdateThread3Responses[keyof UpdateThread3Responses];

export type CreatePlaybookData = {
  body: PlaybookCreateRequest;
  path?: never;
  query?: never;
  url: '/v1/app/playbook/playbook/new';
};

export type CreatePlaybookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookError = CreatePlaybookErrors[keyof CreatePlaybookErrors];

export type CreatePlaybookResponses = {
  /**
   * Successful Response
   */
  200: PlaybookInDb;
};

export type CreatePlaybookResponse = CreatePlaybookResponses[keyof CreatePlaybookResponses];

export type CreatePlaybookFromTemplateData = {
  body?: never;
  path: {
    /**
     * Template Key
     */
    template_key: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/new-from-template/{template_key}';
};

export type CreatePlaybookFromTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookFromTemplateError =
  CreatePlaybookFromTemplateErrors[keyof CreatePlaybookFromTemplateErrors];

export type CreatePlaybookFromTemplateResponses = {
  /**
   * Successful Response
   */
  200: PlaybookInDb;
};

export type CreatePlaybookFromTemplateResponse =
  CreatePlaybookFromTemplateResponses[keyof CreatePlaybookFromTemplateResponses];

export type ListPlaybookTemplatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v1/app/playbook/playbook/templates';
};

export type ListPlaybookTemplatesResponses = {
  /**
   * Response List Playbook Templates V1 App Playbook Playbook Templates Get
   *
   * Successful Response
   */
  200: Array<PlaybookTemplateSummary>;
};

export type ListPlaybookTemplatesResponse =
  ListPlaybookTemplatesResponses[keyof ListPlaybookTemplatesResponses];

export type SearchPlaybookData = {
  body: PlaybookSearchQuery;
  path?: never;
  query?: never;
  url: '/v1/app/playbook/playbook/search';
};

export type SearchPlaybookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchPlaybookError = SearchPlaybookErrors[keyof SearchPlaybookErrors];

export type SearchPlaybookResponses = {
  /**
   * Successful Response
   */
  200: PlaybookSearchResult;
};

export type SearchPlaybookResponse = SearchPlaybookResponses[keyof SearchPlaybookResponses];

export type DeletePlaybookData = {
  body?: never;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}';
};

export type DeletePlaybookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookError = DeletePlaybookErrors[keyof DeletePlaybookErrors];

export type DeletePlaybookResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookResponse = DeletePlaybookResponses[keyof DeletePlaybookResponses];

export type GetPlaybookData = {
  body?: never;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}';
};

export type GetPlaybookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPlaybookError = GetPlaybookErrors[keyof GetPlaybookErrors];

export type GetPlaybookResponses = {
  /**
   * Successful Response
   */
  200: PlaybookInDb;
};

export type GetPlaybookResponse = GetPlaybookResponses[keyof GetPlaybookResponses];

export type UpdatePlaybookData = {
  body: PlaybookUpdate;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}';
};

export type UpdatePlaybookErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookError = UpdatePlaybookErrors[keyof UpdatePlaybookErrors];

export type UpdatePlaybookResponses = {
  /**
   * Successful Response
   */
  200: PlaybookInDb;
};

export type UpdatePlaybookResponse = UpdatePlaybookResponses[keyof UpdatePlaybookResponses];

export type ListPlaybookClausesData = {
  body?: never;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}/clauses';
};

export type ListPlaybookClausesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPlaybookClausesError = ListPlaybookClausesErrors[keyof ListPlaybookClausesErrors];

export type ListPlaybookClausesResponses = {
  /**
   * Response List Playbook Clauses V1 App Playbook Playbook  Playbook Uid  Clauses Get
   *
   * Successful Response
   */
  200: Array<ClauseInDb>;
};

export type ListPlaybookClausesResponse =
  ListPlaybookClausesResponses[keyof ListPlaybookClausesResponses];

export type UpdatePlaybookClauseData = {
  body: ClauseUpdate;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
    /**
     * Clause Uid
     */
    clause_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}/clause/{clause_uid}';
};

export type UpdatePlaybookClauseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookClauseError =
  UpdatePlaybookClauseErrors[keyof UpdatePlaybookClauseErrors];

export type UpdatePlaybookClauseResponses = {
  /**
   * Successful Response
   */
  200: ClauseInDb;
};

export type UpdatePlaybookClauseResponse =
  UpdatePlaybookClauseResponses[keyof UpdatePlaybookClauseResponses];

export type ListPlaybookEvaluationsData = {
  body?: never;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}/evaluations';
};

export type ListPlaybookEvaluationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPlaybookEvaluationsError =
  ListPlaybookEvaluationsErrors[keyof ListPlaybookEvaluationsErrors];

export type ListPlaybookEvaluationsResponses = {
  /**
   * Response List Playbook Evaluations V1 App Playbook Playbook  Playbook Uid  Evaluations Get
   *
   * Successful Response
   */
  200: Array<
    | ({
        evaluation_type: 'classification';
      } & ClassificationEvaluationInDb)
    | ({
        evaluation_type: 'clause_validation';
      } & ClauseValidationEvaluationInDb)
    | ({
        evaluation_type: 'uncommons_check';
      } & UncommonsCheckEvaluationInDb)
    | ({
        evaluation_type: 'extraction';
      } & ExtractionEvaluationInDb)
  >;
};

export type ListPlaybookEvaluationsResponse =
  ListPlaybookEvaluationsResponses[keyof ListPlaybookEvaluationsResponses];

export type CreatePlaybookEvaluationData = {
  body: PlaybookEvaluationCreateRequest;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}/evaluation';
};

export type CreatePlaybookEvaluationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookEvaluationError =
  CreatePlaybookEvaluationErrors[keyof CreatePlaybookEvaluationErrors];

export type CreatePlaybookEvaluationResponses = {
  /**
   * Response Create Playbook Evaluation V1 App Playbook Playbook  Playbook Uid  Evaluation Post
   *
   * Successful Response
   */
  200:
    | ({
        evaluation_type: 'classification';
      } & ClassificationEvaluationInDb)
    | ({
        evaluation_type: 'clause_validation';
      } & ClauseValidationEvaluationInDb)
    | ({
        evaluation_type: 'uncommons_check';
      } & UncommonsCheckEvaluationInDb)
    | ({
        evaluation_type: 'extraction';
      } & ExtractionEvaluationInDb);
};

export type CreatePlaybookEvaluationResponse =
  CreatePlaybookEvaluationResponses[keyof CreatePlaybookEvaluationResponses];

export type DeletePlaybookEvaluationData = {
  body?: never;
  path: {
    /**
     * Evaluation Uid
     */
    evaluation_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation/{evaluation_uid}';
};

export type DeletePlaybookEvaluationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookEvaluationError =
  DeletePlaybookEvaluationErrors[keyof DeletePlaybookEvaluationErrors];

export type DeletePlaybookEvaluationResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookEvaluationResponse =
  DeletePlaybookEvaluationResponses[keyof DeletePlaybookEvaluationResponses];

export type UpdatePlaybookEvaluationData = {
  body: PlaybookEvaluationUpdate;
  path: {
    /**
     * Evaluation Uid
     */
    evaluation_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation/{evaluation_uid}';
};

export type UpdatePlaybookEvaluationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookEvaluationError =
  UpdatePlaybookEvaluationErrors[keyof UpdatePlaybookEvaluationErrors];

export type UpdatePlaybookEvaluationResponses = {
  /**
   * Response Update Playbook Evaluation V1 App Playbook Playbook Evaluation  Evaluation Uid  Put
   *
   * Successful Response
   */
  200:
    | ({
        evaluation_type: 'classification';
      } & ClassificationEvaluationInDb)
    | ({
        evaluation_type: 'clause_validation';
      } & ClauseValidationEvaluationInDb)
    | ({
        evaluation_type: 'uncommons_check';
      } & UncommonsCheckEvaluationInDb)
    | ({
        evaluation_type: 'extraction';
      } & ExtractionEvaluationInDb);
};

export type UpdatePlaybookEvaluationResponse =
  UpdatePlaybookEvaluationResponses[keyof UpdatePlaybookEvaluationResponses];

export type ListPlaybookEvaluationGroupsData = {
  body?: never;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}/evaluation-groups';
};

export type ListPlaybookEvaluationGroupsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPlaybookEvaluationGroupsError =
  ListPlaybookEvaluationGroupsErrors[keyof ListPlaybookEvaluationGroupsErrors];

export type ListPlaybookEvaluationGroupsResponses = {
  /**
   * Response List Playbook Evaluation Groups V1 App Playbook Playbook  Playbook Uid  Evaluation Groups Get
   *
   * Successful Response
   */
  200: Array<PlaybookEvaluationGroupInDb>;
};

export type ListPlaybookEvaluationGroupsResponse =
  ListPlaybookEvaluationGroupsResponses[keyof ListPlaybookEvaluationGroupsResponses];

export type CreatePlaybookEvaluationGroupData = {
  body: PlaybookEvaluationGroupCreateRequest;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}/evaluation-group';
};

export type CreatePlaybookEvaluationGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookEvaluationGroupError =
  CreatePlaybookEvaluationGroupErrors[keyof CreatePlaybookEvaluationGroupErrors];

export type CreatePlaybookEvaluationGroupResponses = {
  /**
   * Successful Response
   */
  200: PlaybookEvaluationGroupInDb;
};

export type CreatePlaybookEvaluationGroupResponse =
  CreatePlaybookEvaluationGroupResponses[keyof CreatePlaybookEvaluationGroupResponses];

export type RemovePlaybookEvaluationGroupEvaluationData = {
  body?: never;
  path: {
    /**
     * Group Uid
     */
    group_uid: string;
    /**
     * Evaluation Uid
     */
    evaluation_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation-group/{group_uid}/evaluation/{evaluation_uid}';
};

export type RemovePlaybookEvaluationGroupEvaluationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemovePlaybookEvaluationGroupEvaluationError =
  RemovePlaybookEvaluationGroupEvaluationErrors[keyof RemovePlaybookEvaluationGroupEvaluationErrors];

export type RemovePlaybookEvaluationGroupEvaluationResponses = {
  /**
   * Successful Response
   */
  200: PlaybookEvaluationGroupInDb;
};

export type RemovePlaybookEvaluationGroupEvaluationResponse =
  RemovePlaybookEvaluationGroupEvaluationResponses[keyof RemovePlaybookEvaluationGroupEvaluationResponses];

export type AddPlaybookEvaluationGroupEvaluationData = {
  body?: never;
  path: {
    /**
     * Group Uid
     */
    group_uid: string;
    /**
     * Evaluation Uid
     */
    evaluation_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation-group/{group_uid}/evaluation/{evaluation_uid}';
};

export type AddPlaybookEvaluationGroupEvaluationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddPlaybookEvaluationGroupEvaluationError =
  AddPlaybookEvaluationGroupEvaluationErrors[keyof AddPlaybookEvaluationGroupEvaluationErrors];

export type AddPlaybookEvaluationGroupEvaluationResponses = {
  /**
   * Successful Response
   */
  200: PlaybookEvaluationGroupInDb;
};

export type AddPlaybookEvaluationGroupEvaluationResponse =
  AddPlaybookEvaluationGroupEvaluationResponses[keyof AddPlaybookEvaluationGroupEvaluationResponses];

export type DeletePlaybookEvaluationGroupData = {
  body?: never;
  path: {
    /**
     * Group Uid
     */
    group_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation-group/{group_uid}';
};

export type DeletePlaybookEvaluationGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookEvaluationGroupError =
  DeletePlaybookEvaluationGroupErrors[keyof DeletePlaybookEvaluationGroupErrors];

export type DeletePlaybookEvaluationGroupResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookEvaluationGroupResponse =
  DeletePlaybookEvaluationGroupResponses[keyof DeletePlaybookEvaluationGroupResponses];

export type UpdatePlaybookEvaluationGroupData = {
  body: PlaybookEvaluationGroupUpdateRequest;
  path: {
    /**
     * Group Uid
     */
    group_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation-group/{group_uid}';
};

export type UpdatePlaybookEvaluationGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookEvaluationGroupError =
  UpdatePlaybookEvaluationGroupErrors[keyof UpdatePlaybookEvaluationGroupErrors];

export type UpdatePlaybookEvaluationGroupResponses = {
  /**
   * Successful Response
   */
  200: PlaybookEvaluationGroupInDb;
};

export type UpdatePlaybookEvaluationGroupResponse =
  UpdatePlaybookEvaluationGroupResponses[keyof UpdatePlaybookEvaluationGroupResponses];

export type DeletePlaybookGroupTriggersData = {
  body?: never;
  path: {
    /**
     * Group Uid
     */
    group_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation-group/{group_uid}/triggers';
};

export type DeletePlaybookGroupTriggersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookGroupTriggersError =
  DeletePlaybookGroupTriggersErrors[keyof DeletePlaybookGroupTriggersErrors];

export type DeletePlaybookGroupTriggersResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookGroupTriggersResponse =
  DeletePlaybookGroupTriggersResponses[keyof DeletePlaybookGroupTriggersResponses];

export type ListPlaybookGroupTriggersData = {
  body?: never;
  path: {
    /**
     * Group Uid
     */
    group_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation-group/{group_uid}/triggers';
};

export type ListPlaybookGroupTriggersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPlaybookGroupTriggersError =
  ListPlaybookGroupTriggersErrors[keyof ListPlaybookGroupTriggersErrors];

export type ListPlaybookGroupTriggersResponses = {
  /**
   * Response List Playbook Group Triggers V1 App Playbook Playbook Evaluation Group  Group Uid  Triggers Get
   *
   * Successful Response
   */
  200: Array<GroupTriggerInDb>;
};

export type ListPlaybookGroupTriggersResponse =
  ListPlaybookGroupTriggersResponses[keyof ListPlaybookGroupTriggersResponses];

export type CreatePlaybookGroupTriggerData = {
  body: GroupTriggerCreateRequest;
  path: {
    /**
     * Group Uid
     */
    group_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation-group/{group_uid}/trigger';
};

export type CreatePlaybookGroupTriggerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookGroupTriggerError =
  CreatePlaybookGroupTriggerErrors[keyof CreatePlaybookGroupTriggerErrors];

export type CreatePlaybookGroupTriggerResponses = {
  /**
   * Successful Response
   */
  200: GroupTriggerInDb;
};

export type CreatePlaybookGroupTriggerResponse =
  CreatePlaybookGroupTriggerResponses[keyof CreatePlaybookGroupTriggerResponses];

export type UpdatePlaybookGroupTriggerData = {
  body: GroupTriggerUpdateRequest;
  path: {
    /**
     * Trigger Uid
     */
    trigger_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/group-trigger/{trigger_uid}';
};

export type UpdatePlaybookGroupTriggerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookGroupTriggerError =
  UpdatePlaybookGroupTriggerErrors[keyof UpdatePlaybookGroupTriggerErrors];

export type UpdatePlaybookGroupTriggerResponses = {
  /**
   * Successful Response
   */
  200: GroupTriggerInDb;
};

export type UpdatePlaybookGroupTriggerResponse =
  UpdatePlaybookGroupTriggerResponses[keyof UpdatePlaybookGroupTriggerResponses];

export type CreatePlaybookOptionData = {
  body: PlaybookOptionCreateRequest;
  path: {
    /**
     * Evaluation Uid
     */
    evaluation_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation/{evaluation_uid}/option';
};

export type CreatePlaybookOptionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookOptionError =
  CreatePlaybookOptionErrors[keyof CreatePlaybookOptionErrors];

export type CreatePlaybookOptionResponses = {
  /**
   * Response Create Playbook Option V1 App Playbook Playbook Evaluation  Evaluation Uid  Option Post
   *
   * Successful Response
   */
  200:
    | ({
        evaluation_type: 'classification';
      } & ClassificationOptionInDb)
    | ({
        evaluation_type: 'clause_validation';
      } & ClauseValidationOptionInDb);
};

export type CreatePlaybookOptionResponse =
  CreatePlaybookOptionResponses[keyof CreatePlaybookOptionResponses];

export type DeletePlaybookOptionData = {
  body?: never;
  path: {
    /**
     * Option Uid
     */
    option_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/option/{option_uid}';
};

export type DeletePlaybookOptionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookOptionError =
  DeletePlaybookOptionErrors[keyof DeletePlaybookOptionErrors];

export type DeletePlaybookOptionResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookOptionResponse =
  DeletePlaybookOptionResponses[keyof DeletePlaybookOptionResponses];

export type UpdatePlaybookOptionData = {
  body: PlaybookOptionUpdateRequest;
  path: {
    /**
     * Option Uid
     */
    option_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/option/{option_uid}';
};

export type UpdatePlaybookOptionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookOptionError =
  UpdatePlaybookOptionErrors[keyof UpdatePlaybookOptionErrors];

export type UpdatePlaybookOptionResponses = {
  /**
   * Response Update Playbook Option V1 App Playbook Playbook Option  Option Uid  Put
   *
   * Successful Response
   */
  200:
    | ({
        evaluation_type: 'classification';
      } & ClassificationOptionInDb)
    | ({
        evaluation_type: 'clause_validation';
      } & ClauseValidationOptionInDb);
};

export type UpdatePlaybookOptionResponse =
  UpdatePlaybookOptionResponses[keyof UpdatePlaybookOptionResponses];

export type CreatePlaybookTriggerData = {
  body: PlaybookTriggerCreateRequest;
  path: {
    /**
     * Evaluation Uid
     */
    evaluation_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/evaluation/{evaluation_uid}/trigger';
};

export type CreatePlaybookTriggerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookTriggerError =
  CreatePlaybookTriggerErrors[keyof CreatePlaybookTriggerErrors];

export type CreatePlaybookTriggerResponses = {
  /**
   * Response Create Playbook Trigger V1 App Playbook Playbook Evaluation  Evaluation Uid  Trigger Post
   *
   * Successful Response
   */
  200:
    | ({
        trigger_type: 'option';
      } & OptionTriggerInDb)
    | ({
        trigger_type: 'finding_threshold';
      } & FindingThresholdTriggerInDb)
    | ({
        trigger_type: 'group';
      } & GroupTriggerInDb);
};

export type CreatePlaybookTriggerResponse =
  CreatePlaybookTriggerResponses[keyof CreatePlaybookTriggerResponses];

export type CreatePlaybookTriggerActionData = {
  body: PlaybookActionCreateRequest;
  path: {
    /**
     * Trigger Uid
     */
    trigger_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/trigger/{trigger_uid}/action';
};

export type CreatePlaybookTriggerActionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePlaybookTriggerActionError =
  CreatePlaybookTriggerActionErrors[keyof CreatePlaybookTriggerActionErrors];

export type CreatePlaybookTriggerActionResponses = {
  /**
   * Response Create Playbook Trigger Action V1 App Playbook Playbook Trigger  Trigger Uid  Action Post
   *
   * Successful Response
   */
  200:
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb);
};

export type CreatePlaybookTriggerActionResponse =
  CreatePlaybookTriggerActionResponses[keyof CreatePlaybookTriggerActionResponses];

export type DeletePlaybookTriggerData = {
  body?: never;
  path: {
    /**
     * Trigger Uid
     */
    trigger_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/trigger/{trigger_uid}';
};

export type DeletePlaybookTriggerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookTriggerError =
  DeletePlaybookTriggerErrors[keyof DeletePlaybookTriggerErrors];

export type DeletePlaybookTriggerResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookTriggerResponse =
  DeletePlaybookTriggerResponses[keyof DeletePlaybookTriggerResponses];

export type UpdatePlaybookTriggerData = {
  body: PlaybookTriggerUpdate;
  path: {
    /**
     * Trigger Uid
     */
    trigger_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/trigger/{trigger_uid}';
};

export type UpdatePlaybookTriggerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookTriggerError =
  UpdatePlaybookTriggerErrors[keyof UpdatePlaybookTriggerErrors];

export type UpdatePlaybookTriggerResponses = {
  /**
   * Successful Response
   */
  200: FindingThresholdTriggerInDb;
};

export type UpdatePlaybookTriggerResponse =
  UpdatePlaybookTriggerResponses[keyof UpdatePlaybookTriggerResponses];

export type ListPlaybookActionsData = {
  body?: never;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: {
    /**
     * Deduplicate
     */
    deduplicate?: boolean;
  };
  url: '/v1/app/playbook/playbook/{playbook_uid}/actions';
};

export type ListPlaybookActionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPlaybookActionsError = ListPlaybookActionsErrors[keyof ListPlaybookActionsErrors];

export type ListPlaybookActionsResponses = {
  /**
   * Response List Playbook Actions V1 App Playbook Playbook  Playbook Uid  Actions Get
   *
   * Successful Response
   */
  200: Array<
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb)
  >;
};

export type ListPlaybookActionsResponse =
  ListPlaybookActionsResponses[keyof ListPlaybookActionsResponses];

export type DeletePlaybookActionData = {
  body?: never;
  path: {
    /**
     * Action Uid
     */
    action_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/action/{action_uid}';
};

export type DeletePlaybookActionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookActionError =
  DeletePlaybookActionErrors[keyof DeletePlaybookActionErrors];

export type DeletePlaybookActionResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookActionResponse =
  DeletePlaybookActionResponses[keyof DeletePlaybookActionResponses];

export type UpdatePlaybookActionData = {
  body: PlaybookActionUpdate;
  path: {
    /**
     * Action Uid
     */
    action_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/action/{action_uid}';
};

export type UpdatePlaybookActionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePlaybookActionError =
  UpdatePlaybookActionErrors[keyof UpdatePlaybookActionErrors];

export type UpdatePlaybookActionResponses = {
  /**
   * Response Update Playbook Action V1 App Playbook Playbook Action  Action Uid  Put
   *
   * Successful Response
   */
  200:
    | ({
        action_type: 'suggestion';
      } & SuggestionActionInDb)
    | ({
        action_type: 'notification';
      } & NotificationActionInDb)
    | ({
        action_type: 'signature_preparation';
      } & SignaturePreparationActionInDb)
    | ({
        action_type: 'slack_notification';
      } & SlackNotificationActionInDb)
    | ({
        action_type: 'jira_notification';
      } & JiraNotificationActionInDb)
    | ({
        action_type: 'evaluation';
      } & EvaluationActionInDb);
};

export type UpdatePlaybookActionResponse =
  UpdatePlaybookActionResponses[keyof UpdatePlaybookActionResponses];

export type StartPlaybookRunData = {
  body: PlaybookRunCreateRequest;
  path: {
    /**
     * Playbook Uid
     */
    playbook_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/{playbook_uid}/run';
};

export type StartPlaybookRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StartPlaybookRunError = StartPlaybookRunErrors[keyof StartPlaybookRunErrors];

export type StartPlaybookRunResponses = {
  /**
   * Successful Response
   */
  200: PlaybookRunSummary;
};

export type StartPlaybookRunResponse = StartPlaybookRunResponses[keyof StartPlaybookRunResponses];

export type SearchPlaybookRunsData = {
  body: PlaybookRunSearchQuery;
  path?: never;
  query?: {
    /**
     * Playbook Uid
     */
    playbook_uid?: string | null;
  };
  url: '/v1/app/playbook/playbook/run/search';
};

export type SearchPlaybookRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchPlaybookRunsError = SearchPlaybookRunsErrors[keyof SearchPlaybookRunsErrors];

export type SearchPlaybookRunsResponses = {
  /**
   * Successful Response
   */
  200: PlaybookRunSearchResult;
};

export type SearchPlaybookRunsResponse =
  SearchPlaybookRunsResponses[keyof SearchPlaybookRunsResponses];

export type DeletePlaybookRunData = {
  body?: never;
  path: {
    /**
     * Run Uid
     */
    run_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/run/{run_uid}';
};

export type DeletePlaybookRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePlaybookRunError = DeletePlaybookRunErrors[keyof DeletePlaybookRunErrors];

export type DeletePlaybookRunResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeletePlaybookRunResponse =
  DeletePlaybookRunResponses[keyof DeletePlaybookRunResponses];

export type GetPlaybookRunData = {
  body?: never;
  path: {
    /**
     * Run Uid
     */
    run_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/run/{run_uid}';
};

export type GetPlaybookRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPlaybookRunError = GetPlaybookRunErrors[keyof GetPlaybookRunErrors];

export type GetPlaybookRunResponses = {
  /**
   * Successful Response
   */
  200: PlaybookRunInDb;
};

export type GetPlaybookRunResponse = GetPlaybookRunResponses[keyof GetPlaybookRunResponses];

export type ExportPlaybookRunSummaryData = {
  body?: never;
  path: {
    /**
     * Run Uid
     */
    run_uid: string;
  };
  query: {
    export_format: PlaybookRunSummaryExportFormat;
  };
  url: '/v1/app/playbook/playbook/run/{run_uid}/summary/export';
};

export type ExportPlaybookRunSummaryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportPlaybookRunSummaryError =
  ExportPlaybookRunSummaryErrors[keyof ExportPlaybookRunSummaryErrors];

export type ExportPlaybookRunSummaryResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ExportPlaybookRunEvaluationsData = {
  body: PlaybookRunEvaluationExportRequest;
  path: {
    /**
     * Run Uid
     */
    run_uid: string;
  };
  query?: never;
  url: '/v1/app/playbook/playbook/run/{run_uid}/evaluations/export';
};

export type ExportPlaybookRunEvaluationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportPlaybookRunEvaluationsError =
  ExportPlaybookRunEvaluationsErrors[keyof ExportPlaybookRunEvaluationsErrors];

export type ExportPlaybookRunEvaluationsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateAgentRun2Data = {
  body: CreateAgentRunRequest;
  headers: {
    /**
     * X-Auth
     *
     * Authentication token for the intake agent. This token identifies which agentthe request is targeting and must be provided in the X-Auth header.
     */
    'x-auth': string;
  };
  path?: never;
  query?: never;
  url: '/agent/create_agent_run';
};

export type CreateAgentRun2Errors = {
  /**
   * Bad request - validation error or invalid data
   */
  400: unknown;
  /**
   * Unauthorized - agent does not allow API requests
   */
  401: unknown;
  /**
   * Not found - agent not found or no attachments provided
   */
  404: unknown;
  /**
   * Validation Error
   */
  422: HttpValidationError;
  /**
   * Internal server error during processing
   */
  500: unknown;
};

export type CreateAgentRun2Error = CreateAgentRun2Errors[keyof CreateAgentRun2Errors];

export type CreateAgentRun2Responses = {
  /**
   * Successfully processed agent API request
   */
  200: AgentApiRequestResponse;
};

export type CreateAgentRun2Response = CreateAgentRun2Responses[keyof CreateAgentRun2Responses];
